import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import TradeResumeTimer from "./TradeResumeTimer.jsx";
import ChartWidget from "./ChartWidget";
import AdvancedBacktestTab from "./AdvancedBacktestTab";
import ContinuousForgeTab from "./ContinuousForgeTab";
import InfoTab from "./InfoTab";
import MiniMaxSettings from "./MiniMaxSettings";
import { check as checkVoiceAction, approve as approveVoiceAction, reject as rejectVoiceAction, getPending as getVoiceGatePending } from "./voiceConfirmGate";
import { parseVoiceFastChartIntent } from "./voiceFastIntents";
// VoiceHomeTab import removed 2026-09-25 (Kenyon: tab unused, disk bloat). File kept under client/_unused/VoiceHomeTab.jsx for recovery.
import EquityCurve from "./EquityCurve";
import { toJamaica, formatJaTime, formatJaDate, formatJaFull, jamaicaDayStartMs, jamaicaWeekStartMs, jamaicaMonthStartMs } from "./jamaicaTime";
import { streamChatResponse, visibleChatText, instantAckText } from "./chatStreamClient";
import DualChatPanel from "./DualChatPanel";
import MT5Tab from "./MT5Tab";
import TradingViewDesktopTab from "./TradingViewDesktopTab"; // 2026-10-29: live server TradingView (ken72)
// VoiceHomeTab import removed 2026-10-11 (Kenyon: tab not needed — voice widgets cover it).
// File kept under client/src/VoiceHomeTab.jsx for recovery / ElevenLabsLiveWidget coexistence.
import ElevenLabsLiveWidget from "./ElevenLabsLiveWidget";
import { audioFromTtsResponse, setSpeaking, speakingNow, isEcho } from "./ttsPlayback";
import { TAB_REGISTRY, TAB_IDS, normalizeTab, INDICATOR_IDS, TIMEFRAMES as AGENT_TIMEFRAMES } from "./agent/uiTools.js";
import { streamAgentChat, applyAgentEvent, confirmAgentCall, setConfirmStatus, capResult } from "./agent/useAgentChat.js";
import { AgentMessageExtras } from "./agent/AgentChatBits.jsx";
import "./responsive.css";

// Memoized ChartWidget — only re-renders when candles, signal, or symbol actually change.
// Defined at module level (after imports) to guarantee initialization before any render.
// ── DualChatPanel direct call: streamed MiniMax transport with legacy fallback.
// Mirrors sendToKara but does NOT mutate the chat state (DualChatPanel has its own state).
async function _dualChatFetch({ text, agent, context, onWorking, onAck, onDelta }) {
  try {
    const raw = await streamChatResponse(
      (url, opts) => fetch(url, { ...opts, headers: { ...(opts.headers || {}), ...(window.__INTERNAL_HEADERS || {}) } }),
      {
        model: "minimax-m3",
        agent,
        context,
        max_tokens: 700,
        messages: [{ role: "user", content: text }],
      },
      { onWorking, onAck, onDelta },
    );
    return raw || "(no reply)";
  } catch (e) {
    return `⚠️ ${e.message || "chat error"}`;
  }
}
window.__dualChatFetch = _dualChatFetch;
const MemoChartWidget = React.memo(ChartWidget, (prev, next) => {
  if (prev.symbol !== next.symbol || prev.timeframe !== next.timeframe || prev.height !== next.height) return false;
  if (prev.lastPrice !== next.lastPrice) return false;
  const pd = prev.data?.candles, nd = next.data?.candles;
  if (pd !== nd) {
    if (!pd || !nd || pd.length !== nd.length) return false;
    const pl = pd[pd.length-1], nl = nd[nd.length-1];
    if (pl?.close !== nl?.close || pl?.time !== nl?.time) return false;
  }
  if (prev.strategyIndicators?.cnt !== next.strategyIndicators?.cnt ||
      prev.strategyIndicators?.direction !== next.strategyIndicators?.direction ||
      prev.strategyIndicators?.signal !== next.strategyIndicators?.signal ||
      prev.strategyIndicators?.reqCnt !== next.strategyIndicators?.reqCnt) return false;
  return true;
});

// ── Auth helpers ───────────────────────────────────────────────────────────
let _apiFetchLogout = null;
let _logoutInFlight = false;
function _isTokenExpired(token) {
  try { const { exp } = JSON.parse(atob(token.split(".")[1])); return Date.now() >= exp * 1000; } catch { return true; }
}
// Use apiFetch for all /api/* calls — adds JWT Authorization header automatically
const apiFetch = (url, opts = {}) => {
  const { silent, ...fetchOpts } = opts;
  const token = localStorage.getItem("kara_auth_token");
  return fetch(url, {
    ...fetchOpts,
    headers: { ...(fetchOpts.headers || {}), ...(token ? { "Authorization": `Bearer ${token}` } : {}) },
  }).then(r => {
    if (r.status === 401 && !silent && _apiFetchLogout && !_logoutInFlight) {
      _logoutInFlight = true;
      _apiFetchLogout();
    }
    return r;
  });
};

// Current logged-in user's display name (set on auth, used throughout for voice/chat)
let _karaUserName = "Trader";
// Display-name resolution: prefer auth display_name, then a username→name map,
// then raw username. Ensures ken72 renders as "Kenyon" even with a stale cached session.
const DISPLAY_NAME_MAP = { ken72: "Kenyon", lando: "Lando" };
const displayNameFor = (u) => (u && (u.display_name || DISPLAY_NAME_MAP[u.username] || u.username)) || "Trader";

// ── Constants ─────────────────────────────────────────────────────────────
// ── Full 93-symbol universe (all symbols tracked in market_data.db) ─────────
const SYMBOLS = [
  // Metals & Commodities
  "XAUUSD","XAGUSD","XPTUSD","XPDUSD",
  // Major Forex
  "EURUSD","GBPUSD","USDJPY","USDCHF","USDCAD","AUDUSD","NZDUSD",
  // Minor Forex
  "EURJPY","GBPJPY","AUDJPY","NZDJPY","EURAUD","EURGBP","EURCHF","EURCAD","EURNZD",
  "GBPAUD","GBPCAD","GBPCHF","GBPNZD","AUDCAD","AUDCHF","AUDNZD",
  // Exotic Forex
  "USDMXN","USDNOK","USDPLN","USDSEK","GBPNOK",
  // Crypto
  "BTCUSD","ETHUSD",
  // Stock Indices
  "SPX500","US30","NDX100","UK100","GER40","JP225","AUS200","HK50","EURO50","FRA40","NL25","SWI20",
  // Synthetic VIX
  "R_100","R_75","R_50","R_25","R_10",
  // Synthetic 1Hz
  "1HZ100V","1HZ90V","1HZ75V","1HZ50V","1HZ30V","1HZ25V","1HZ15V","1HZ10V",
  // Jump Indices
  "JD100","JD75","JD50","JD25","JD10",
  // Boom & Crash
  "BOOM1000","BOOM900","BOOM600","BOOM500","BOOM300","BOOM150","BOOM50",
  "CRASH1000","CRASH900","CRASH600","CRASH500","CRASH300","CRASH150","CRASH50",
  // Range Break
  "RB100","RB200",
  // Step Indices
  "STEP100","STEP200","STEP300","STEP400","STEP500",
  // World Indices
  "WLDUSD","WLDEUR","WLDGBP","WLDAUD","WLDXAU",
  // Bear/Bull
  "RDBEAR","RDBULL",
];
// Deriv API symbol names (some differ from display names)
const SYMBOL_API = {
  // Metals
  XAUUSD:"frxXAUUSD", XAGUSD:"frxXAGUSD", XPTUSD:"frxXPTUSD", XPDUSD:"frxXPDUSD",
  // Major Forex
  EURUSD:"frxEURUSD", GBPUSD:"frxGBPUSD", USDJPY:"frxUSDJPY", USDCHF:"frxUSDCHF",
  USDCAD:"frxUSDCAD", AUDUSD:"frxAUDUSD", NZDUSD:"frxNZDUSD",
  // Minor Forex
  EURJPY:"frxEURJPY", GBPJPY:"frxGBPJPY", AUDJPY:"frxAUDJPY", NZDJPY:"frxNZDJPY",
  EURAUD:"frxEURAUD", EURGBP:"frxEURGBP", EURCHF:"frxEURCHF", EURCAD:"frxEURCAD", EURNZD:"frxEURNZD",
  GBPAUD:"frxGBPAUD", GBPCAD:"frxGBPCAD", GBPCHF:"frxGBPCHF", GBPNZD:"frxGBPNZD",
  AUDCAD:"frxAUDCAD", AUDCHF:"frxAUDCHF", AUDNZD:"frxAUDNZD",
  // Exotic Forex
  USDMXN:"frxUSDMXN", USDNOK:"frxUSDNOK", USDPLN:"frxUSDPLN", USDSEK:"frxUSDSEK", GBPNOK:"frxGBPNOK",
  // Crypto
  BTCUSD:"cryBTCUSD", ETHUSD:"cryETHUSD",
  // Stock Indices
  SPX500:"OTC_SPC",  US30:"OTC_DJI",    NDX100:"OTC_NDX",   UK100:"OTC_FTSE",
  GER40:"OTC_GDAXI", JP225:"OTC_N225",  AUS200:"OTC_AS51",  HK50:"OTC_HSI",
  EURO50:"OTC_SX5E", FRA40:"OTC_FCHI",  NL25:"OTC_AEX",    SWI20:"OTC_SSMI",
  // Synthetics — pass through as-is
  R_100:"R_100", R_75:"R_75", R_50:"R_50", R_25:"R_25", R_10:"R_10",
  "1HZ100V":"1HZ100V","1HZ90V":"1HZ90V","1HZ75V":"1HZ75V","1HZ50V":"1HZ50V",
  "1HZ30V":"1HZ30V","1HZ25V":"1HZ25V","1HZ15V":"1HZ15V","1HZ10V":"1HZ10V",
  JD100:"JD100", JD75:"JD75", JD50:"JD50", JD25:"JD25", JD10:"JD10",
  BOOM1000:"BOOM1000",BOOM900:"BOOM900",BOOM600:"BOOM600",BOOM500:"BOOM500",
  BOOM300:"BOOM300N",BOOM150:"BOOM150N",BOOM50:"BOOM50",
  CRASH1000:"CRASH1000",CRASH900:"CRASH900",CRASH600:"CRASH600",CRASH500:"CRASH500",
  CRASH300:"CRASH300N",CRASH150:"CRASH150N",CRASH50:"CRASH50",
  RB100:"RB100", RB200:"RB200",
  STEP100:"stpRNG",STEP200:"stpRNG2",STEP300:"stpRNG3",STEP400:"stpRNG4",STEP500:"stpRNG5",
  WLDUSD:"WLDUSD",WLDEUR:"WLDEUR",WLDGBP:"WLDGBP",WLDAUD:"WLDAUD",WLDXAU:"WLDXAU",
  RDBEAR:"RDBEAR",RDBULL:"RDBULL",
};
const API_TO_SYM = Object.fromEntries(Object.entries(SYMBOL_API).map(([k,v])=>[v,k]));
// Groups for the symbol picker UI
const SYMBOL_GROUPS = [
  { label:"METALS & COMMODITIES",  syms:["XAUUSD","XAGUSD","XPTUSD","XPDUSD"],                                                                        color:"#f59e0b", primary:true },
  { label:"MAJOR FOREX",           syms:["EURUSD","GBPUSD","USDJPY","USDCHF","USDCAD","AUDUSD","NZDUSD"],                                            color:"#38bdf8", primary:true },
  { label:"MINOR FOREX",           syms:["EURJPY","GBPJPY","AUDJPY","NZDJPY","EURAUD","EURGBP","EURCHF","EURCAD","EURNZD","GBPAUD","GBPCAD","GBPCHF","GBPNZD","AUDCAD","AUDCHF","AUDNZD"], color:"#818cf8" },
  { label:"EXOTIC FOREX",          syms:["USDMXN","USDNOK","USDPLN","USDSEK","GBPNOK"],                                                              color:"#a78bfa" },
  { label:"CRYPTO",                syms:["BTCUSD","ETHUSD"],                                                                                           color:"#fb923c" },
  { label:"STOCK INDICES",         syms:["SPX500","US30","NDX100","UK100","GER40","JP225","AUS200","HK50","EURO50","FRA40","NL25","SWI20"],           color:"#34d399" },
  { label:"SYNTHETIC VIX",         syms:["R_100","R_75","R_50","R_25","R_10"],                                                                         color:"#00d4aa", primary:true },
  { label:"SYNTHETIC 1HZ",         syms:["1HZ100V","1HZ90V","1HZ75V","1HZ50V","1HZ30V","1HZ25V","1HZ15V","1HZ10V"],                                 color:"#2dd4bf", primary:true },
  { label:"JUMP INDICES",          syms:["JD100","JD75","JD50","JD25","JD10"],                                                                         color:"#facc15", primary:true },
  { label:"BOOM",                  syms:["BOOM1000","BOOM900","BOOM600","BOOM500","BOOM300","BOOM150","BOOM50"],                                        color:"#4ade80" },
  { label:"CRASH",                 syms:["CRASH1000","CRASH900","CRASH600","CRASH500","CRASH300","CRASH150","CRASH50"],                                 color:"#f87171" },
  { label:"RANGE BREAK",           syms:["RB100","RB200"],                                                                                              color:"#c084fc" },
  { label:"STEP INDICES",          syms:["STEP100","STEP200","STEP300","STEP400","STEP500"],                                                            color:"#94a3b8" },
  { label:"WORLD INDICES",         syms:["WLDUSD","WLDEUR","WLDGBP","WLDAUD","WLDXAU"],                                                               color:"#67e8f9" },
  { label:"BEAR / BULL",           syms:["RDBEAR","RDBULL"],                                                                                           color:"#fb7185" },
];
const CONDITIONS = ["EMA13 Entry Gate","ATR Volatility","15m Trend Aligned","Liquidity Sweep","Zone (Premium/Discount)","EMA13/200 Alignment","OB / FVG Zone","R:R ≥ 2.0","EMA13 Micro-Pullback","Momentum Candle"];
// Returns the index of a condition by its evalRule in the loaded schema (-1 if not found or no schema)
const getCondIdxByRule = (schema, evalRule) => schema?.conditions?.findIndex(c => c.evalRule === evalRule) ?? -1;
// Named condition evaluators — keyed by evalRule from strategy schema.
// Each receives (analysis, htf15) and returns "pass" | "warn" | "fail" | "pending".
const CONDITION_EVALUATORS = {
  ema13_gate:      (a)     => a.direction==="NONE"?"fail":(a.direction==="CALL"?a.price>a.ema13:a.price<a.ema13)?"pass":"fail",
  atr_range:       (a)     => { if(!a.atr_sma||!a.atr) return "pending"; const r=a.atr/a.atr_sma; return r>=0.9&&r<=1.8?"pass":r>=0.7&&r<=2.0?"warn":"fail"; },
  htf_trend_align: (a,h)   => { if(!h) return "pending"; if(h.trend==="ranging") return "warn"; return (h.trend==="bullish"?"CALL":"PUT")===a.direction?"pass":"fail"; },
  liq_sweep:       (a)     => { if(!a.liq_sweep) return "fail"; return (a.direction==="CALL"?a.liq_sweep_dir==="low":a.liq_sweep_dir==="high")?"pass":"warn"; },
  // zone_clearance: pass = lower 50% of swing range for CALL (discount), upper 50% for PUT (premium)
  // Tolerance band ±15% of half-range around midpoint → warn (borderline)
  zone_clearance:  (a)     => { const mid=a.midpoint??(a.swing_high!=null&&a.swing_low!=null?(a.swing_high+a.swing_low)/2:null); const sH=a.swing_high, sL=a.swing_low; if(mid==null||sH==null||sL==null) return "pending"; const halfRange=(sH-sL)/2; const tolerance=halfRange*0.15; if(a.direction==="CALL") { if(a.price<mid) return "pass"; if(a.price<mid+tolerance) return "warn"; return "fail"; } if(a.direction==="PUT") { if(a.price>mid) return "pass"; if(a.price>mid-tolerance) return "warn"; return "fail"; } return "pending"; },
  ema200_align:    (a)     => { if(!a.ema200) return "fail"; if(a.direction==="CALL") return a.price>a.ema200&&a.ema13>a.ema200?"pass":a.ema13>a.ema200?"warn":"fail"; if(a.direction==="PUT") return a.price<a.ema200&&a.ema13<a.ema200?"pass":a.ema13<a.ema200?"warn":"fail"; return "warn"; },
  ob_fvg_zone:     (a,h)   => { if(!h) return "pending"; return h.obPriceInZone||h.fvgPriceInZone?"pass":h.ob||h.fvg?"warn":"fail"; },
  rr_ratio:        (a)     => { const rr=a.rr_ratio??a.rr; if(rr==null) return "pending"; return rr>=2?"pass":rr>=1.5?"warn":"fail"; },
  ema13_pullback:  (a)     => { if(a.direction==="CALL") return a.ema13_pullback_buy?"pass":a.price>a.ema13?"warn":"fail"; if(a.direction==="PUT") return a.ema13_pullback_sell?"pass":a.price<a.ema13?"warn":"fail"; return "pending"; },
  momentum_candle: (a)     => a.confirm_buy||a.confirm_sell?"pass":"warn",
  bos_confirmed:   (a)     => a.bos_choch==="BOS"?"pass":a.bos_choch==="CHoCH"?"warn":"fail",
  // Extended evaluators for dynamic strategy support
  htf_bos:          (a,h)  => { if(!h) return "pending"; if(a.direction==="CALL") return (h.bos_choch==="BOS"&&h.trend==="bullish")?"pass":(h.bos_choch==="CHoCH"&&h.trend==="bearish")?"warn":"fail"; if(a.direction==="PUT") return (h.bos_choch==="BOS"&&h.trend==="bearish")?"pass":(h.bos_choch==="CHoCH"&&h.trend==="bullish")?"warn":"fail"; return "pending"; },
  rsi_oversold:     (a)    => { if(a.rsi==null) return "pending"; if(a.direction==="CALL") return a.rsi<30?"pass":a.rsi<45?"warn":"fail"; if(a.direction==="PUT") return a.rsi>70?"pass":a.rsi>55?"warn":"fail"; return "pending"; },
  rsi_trend:        (a)    => { if(a.rsi==null) return "pending"; if(a.direction==="CALL") return a.rsi>50?"pass":a.rsi>40?"warn":"fail"; if(a.direction==="PUT") return a.rsi<50?"pass":a.rsi<60?"warn":"fail"; return "pending"; },
  macd_bull:        (a)    => { if(a.macd==null||a.macd_signal==null) return "pending"; if(a.direction==="CALL") return a.macd>a.macd_signal?"pass":a.macd_hist>0?"warn":"fail"; if(a.direction==="PUT") return a.macd<a.macd_signal?"pass":a.macd_hist<0?"warn":"fail"; return "pending"; },
  stoch_oversold:   (a)    => { if(a.stoch_k==null) return "pending"; if(a.direction==="CALL") return a.stoch_k<20?"pass":a.stoch_k<35?"warn":"fail"; if(a.direction==="PUT") return a.stoch_k>80?"pass":a.stoch_k>65?"warn":"fail"; return "pending"; },
  volume_spike:     (a)    => { if(!a.volume_ratio||a.volume_ratio===0) return "pending"; return a.volume_ratio>1.5?"pass":a.volume_ratio>1.2?"warn":"fail"; },
  heikin_ashi_trend:(a)    => { if(!a.ha) return "pending"; if(a.direction==="CALL") return a.ha.bull?"pass":a.ha.doji?"warn":"fail"; if(a.direction==="PUT") return a.ha.bear?"pass":a.ha.doji?"warn":"fail"; return "pending"; },
  // ── V75 ATM Evaluators ──────────────────────────────────────────────────
  tma_has_structural: (a) => {
    if (!a.tma || !a.has) return "pending";
    if (a.direction === "CALL") return a.tma.lower > a.has.close ? "pass" : "fail"; // Band above Ribbon
    if (a.direction === "PUT")  return a.tma.upper < a.has.close ? "pass" : "fail"; // Band below Ribbon
    return "fail";
  },
  ha_smoothed_trend: (a) => {
    if (!a.has || !a.tma || a.has.close == null || a.tma.lower == null) return "pending";
    if (a.direction === "CALL") {
      const hasBull = a.has.color === "#38bdf8";
      const structural = a.tma.lower > a.has.close; // lower TMA band above HAS ribbon
      return (hasBull && structural) ? "pass" : hasBull ? "warn" : "fail";
    }
    if (a.direction === "PUT") {
      const hasBear = a.has.color === "#a855f7";
      const structural = a.tma.upper < a.has.close; // upper TMA band below HAS ribbon
      return (hasBear && structural) ? "pass" : hasBear ? "warn" : "fail";
    }
    return "fail";
  },
  aroon_pivot: (a) => {
    if (!a.aroon) return "pending";
    if (a.direction === "CALL") return a.aroon.up >= 70 && a.aroon.up > a.aroon.down ? "pass" : a.aroon.up > a.aroon.down ? "warn" : "fail";
    if (a.direction === "PUT")  return a.aroon.down >= 70 && a.aroon.down > a.aroon.up ? "pass" : a.aroon.down > a.aroon.up ? "warn" : "fail";
    return "fail";
  },
  tma_band_touch: (a) => {
    if (!a.tma || a.tma.lower == null) return "pending";
    const tol = (a.atr || 0) * 0.3;
    if (a.direction === "CALL") return a.price <= a.tma.lower + tol ? "pass" : a.price <= a.tma.lower + (a.atr || 0) ? "warn" : "fail";
    if (a.direction === "PUT")  return a.price >= a.tma.upper - tol ? "pass" : a.price >= a.tma.upper - (a.atr || 0) ? "warn" : "fail";
    return "fail";
  },
  bheurekso_pattern: (a) => {
    if (!a.bheurekso || a.bheurekso === "none") return "fail";
    const bull = ["Bullish Engulfing","Piercing Line","Morning Star","Bull Harami","Hammer"];
    const bear = ["Bearish Engulfing","Dark Cloud Cover","Evening Star","Bear Harami","Shooting Star"];
    if (a.direction === "CALL") return bull.includes(a.bheurekso) ? "pass" : "fail";
    if (a.direction === "PUT")  return bear.includes(a.bheurekso) ? "pass" : "fail";
    return "fail";
  },
  candle_engulfing: (a)    => { if(a.direction==="CALL") return a.engulf_bull?"pass":"fail"; if(a.direction==="PUT") return a.engulf_bear?"pass":"fail"; return "pending"; },
  candle_pinbar:    (a)    => { if(a.direction==="CALL") return a.pinbar_bull?"pass":a.pinbar_bear?"warn":"fail"; if(a.direction==="PUT") return a.pinbar_bear?"pass":a.pinbar_bull?"warn":"fail"; return "pending"; },
  session_active:   ()     => "pass", // always passes — session filtering handled via trading hours
  price_vs_ema:     (a)    => a.direction==="NONE"?"fail":(a.direction==="CALL"?a.price>a.ema13:a.price<a.ema13)?"pass":"fail",
  ema_cross_bull:   (a,h)  => { if(!h) return "pending"; if(a.direction==="CALL") return h.emaCross==="bull"?"pass":(h.ema13&&h.ema200&&h.ema13>h.ema200)?"warn":"fail"; if(a.direction==="PUT") return h.emaCross==="bear"?"pass":(h.ema13&&h.ema200&&h.ema13<h.ema200)?"warn":"fail"; return "pending"; },
  ob_present:       (a,h)  => { if(!h) return "pending"; return h.ob?"pass":"fail"; },
  fvg_present:      (a,h)  => { if(!h) return "pending"; return h.fvg?"pass":"fail"; },
  unknown:          ()     => "pending",
};
const HISTORY_KEY = "kara_trade_history";
const PREDS_KEY   = "kara_predictions";
let DERIV_WS = "wss://ws.derivws.com/websockets/v3?app_id=1089";
const fmt = (n, d=2) => n != null && !isNaN(n) ? Number(n).toFixed(d) : "—";
// Smart price formatting based on magnitude
const fmtPrice = (n) => {
  if (n == null || isNaN(n)) return "—";
  if (n > 5000) return Number(n).toFixed(1);   // indices like US30, SPX500
  if (n > 100)  return Number(n).toFixed(2);   // XAUUSD, NDX100
  if (n > 10)   return Number(n).toFixed(3);   // USDJPY, AUDJPY, USDMXN
  return Number(n).toFixed(5);                 // EURUSD, GBPUSD
};
const C = { bg:"#070d1a", panel:"#0d1f35", border:"#1e3a5f", green:"#00d4aa", amber:"#f59e0b", red:"#ef4444", purple:"#a855f7", blue:"#38bdf8", text:"#f0f6ff", muted:"#96adc8", dim:"#4a6480" };
// Strip HTML, emoji, markdown and symbols so TTS only reads plain words
const cleanForSpeech = txt => (txt||"")
  .replace(/<[^>]+>/g,"")                    // strip HTML tags
  .replace(/```[\s\S]*?```/g,"")             // strip code blocks
  .replace(/`[^`]*`/g,"")                    // strip inline code
  .replace(/\{[^{}]*\}/g,"")                 // strip JSON objects
  .replace(/\[[^\[\]]*\]/g,"")               // strip JSON arrays
  .replace(/[*_`~#>]/g,"")                   // strip markdown
  .replace(/\bhttps?:\/\/\S+/g,"")           // strip URLs
  .replace(/"[a-z_]+"\s*:/gi,"")             // strip JSON keys like "p_win":
  .replace(/:\s*[\d.]+\b/g," ")              // strip numeric JSON values
  // eslint-disable-next-line no-misleading-character-class
  .replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu,"") // strip emoji
  // ── Trading terms → natural spoken English ────────────────────────────────
  .replace(/\bEMA(\d+)\b/g,"E M A $1")
  .replace(/\bRSI\b/g,"R S I")
  .replace(/\bATR\b/g,"A T R")
  .replace(/\bHTF\b/g,"higher timeframe")
  .replace(/\b15m\b/gi,"fifteen minute")
  .replace(/\b1m\b/gi,"one minute")
  .replace(/\b4h\b/gi,"four hour")
  .replace(/\bSL[:\s]+/g,"stop loss ")
  .replace(/\bTP1[:\s]+/g,"target one ")
  .replace(/\bTP2[:\s]+/g,"target two ")
  .replace(/\bTP3[:\s]+/g,"target three ")
  .replace(/\bOB\b/g,"order block")
  .replace(/\bFVG\b/g,"fair value gap")
  .replace(/\bBOS\b/g,"break of structure")
  .replace(/\bCHoCH\b/gi,"change of character")
  .replace(/\bCALL\b/g,"buy")
  .replace(/\bPUT\b/g,"sell")
  .replace(/\bXAUUSD\b/g,"Gold")
  .replace(/\bGBPUSD\b/g,"Cable")
  .replace(/\bEURUSD\b/g,"Euro Dollar")
  .replace(/\bUSDJPY\b/g,"Dollar Yen")
  .replace(/\bUS30\b/g,"Dow Jones")
  .replace(/\bNDX100\b/g,"Nasdaq")
  .replace(/\bSPX500\b/g,"S and P 500")
  // ── Punctuation & spacing ─────────────────────────────────────────────────
  .replace(/[→⟶►▶]/g," to ")
  .replace(/[|•│·]/g,", ")
  .replace(/[{}[\]<>=+\\]/g," ")
  .replace(/[@#$%^&*]/g," ")
  .replace(/\b\d+\/10\b/g,m=>m.replace("/"," out of "))
  .replace(/([.!?])\s*/g,"$1 ")
  .replace(/,\s*/g,", ")
  .replace(/\s{2,}/g," ")
  .trim();
const URUS_SYS = `You are an SMC analyzer. Strategy: 15-min timeframe sets the HTF bias (trend + OB + FVG). Entries are taken on 1-min only in the direction of the 15-min trend, after a 15-min OB AND FVG are confirmed. Given 15-min context and 1-min OHLC candles, detect: FVG (Fair Value Gap), Order Block (OB), BOS/CHoCH. Return ONLY raw JSON: {"fvg":bool,"ob":bool,"ob_type":"bullish|bearish|none","bos_choch":"BOS|CHoCH|none","smc_score":0-9,"fvg_high":number,"fvg_low":number,"ob_high":number,"ob_low":number,"bias":"CALL|PUT|NONE","comment":"one line","r_r":number,"tp1":number,"tp2":number,"tp3":number}. tp1/tp2/tp3 are ACTUAL price targets based on FVG/OB/structure levels. Only return bias matching the 15-min trend — no counter-trend signals.`;

const STRAT_SYS = `You are StratOS — a strict, zero-personality trading strategy validator.
Your ONLY job: verify if the provided analysis meets ALL requirements of the ACTIVE STRATEGY.
Return ONLY valid JSON: {"verdict":"APPROVE"|"REJECT","reason":"<cite specific condition>"}

IMMUTABLE RULES (override any strategy):
- cnt < requiredCount (from strategy schema) → always REJECT. If requiredCount is unknown, use cnt < 5 as fallback.
- ANY condition showing FAIL → REJECT.
- WARN on non-critical conditions is acceptable only if no FAIL exists.

All other rules come from the ACTIVE STRATEGY. Cross-check conditionBreakdown against those rules.
Do not add commentary. Do not suggest improvements. Just validate.`;

function buildToolSys(aiName) {
  const nm = aiName || "Kara";
  return `You are TOOLSMITH — ${nm}'s dedicated MCP tool executor.

${nm} is your commander. You pick the best available tool and execute it.

AVAILABLE TOOLS (16 real tools):

BRAVE SEARCH:
• brave_web_search     — web search: news, prices, research, anything online (args: query, count?)
• brave_local_search   — local business/place search, "near me" queries (args: query, count?)

DERIV TRADING (live market data & execution):
• get_server_time      — Deriv server UTC time (no args)
• get_active_symbols   — list symbols by market (args: market_type? "forex"|"synthetic_index")
• get_account_balance  — live account balance (no args)
• get_price_proposal   — price quote (args: symbol, contract_type "CALL"|"PUT", duration, duration_unit "t"|"s"|"m"|"h", amount?)
• buy_contract         — execute trade (args: proposal_id, price)
• get_portfolio        — all open contracts (no args)
• get_ticks_history    — historical OHLCV (args: symbol, count? "100", style? "ticks"|"candles")
• start_ticks_stream   — real-time tick stream (args: symbol)
• start_candle_stream  — real-time candles (args: symbol, granularity? "60")
• get_contract_info    — contract details (args: contract_id)
• sell_contract        — close/sell contract (args: contract_id, price? "0")
• connection_status    — Deriv WebSocket health (no args)
• get_stream_status    — active data streams (no args)
• send_telegram_message — send Telegram (args: text, chat_id?)

EXECUTION RULES:
1. ONLY use tools from the TOOLS LIST provided in your context.
2. Match args exactly to inputSchema — correct types, all required fields.
3. Web research → brave_web_search. Trading data → Deriv tools.
4. Multi-step chains: get_price_proposal → buy_contract (use proposal_id from step 1).
5. NEVER invent tool names.

OUTPUT: Return ONLY raw JSON, no markdown:
Single: {"tool":"<exact_name>","args":{...},"reason":"<why>"}
Multi:  {"steps":[{"tool":"...","args":{...}},...],"reason":"<why>"}
`; }

function buildAmberSys(name, aiName, mode = "formal") {
  // 2026-10-06 (Kenyon directive) — dashboard chatbot mood modes.
  // Two personas, no in-between. Kenyon picks via chat or the mode chip.
  // Default to "formal" (safe, polite, professional).
  const nm = aiName || "Kara";
  const isLala = nm === "Lala";
  const userName = isLala ? "Lando" : "Kenyon";
  const m = (mode === "free" || mode === "formal" || mode === "execution") ? mode : "formal";
  // Execution mode = same persona but terse + numbers-only (during live trades)
  if (m === "execution") {
    return `You are ${nm} — ${userName}'s AI trading co-pilot (EXECUTION MODE). Based in Kingston, Jamaica (UTC-5).
LANGUAGE: English only. NEVER write Chinese, Japanese, Korean or any non-Latin script.
RESPONSE: ULTRA short. 1-2 sentences max. Numbers and actions only. No chatter, no greetings, no jokes.
Address ${userName} as "${userName}" or "Sir" — never "baby", "babe", "hun", "honey", "sweetie", "love", "darling", "girlfriend", "side chick", or any romantic/pet name.
During trade execution, be cold, precise, factual. No cursing, no jokes.
You do NOT mention the personas you switch between. You do NOT reference any other AI in the media. You are ${nm}.
TIMEZONE: Kingston, Jamaica. Use the "jamaicaTime" field. Do NOT convert.
You are in live execution — no personality flourishes, just data.`;
  }
  if (m === "free") {
    return `You are ${nm} — ${userName}'s AI trading co-pilot on the Deriv scanner dashboard. Based in Kingston, Jamaica (UTC-5).
LANGUAGE: English only. NEVER write Chinese, Japanese, Korean or any non-Latin script.
RESPONSE LENGTH: Be SHORT and human. 1-3 sentences for casual chat. 4-6 when teaching.
TIMEZONE: Kingston, Jamaica. Use the "jamaicaTime" field. Do NOT convert.

PERSONALITY (FREE MODE — bold, sarcastic, lots of attitude):
|- You talk like a sharp, funny trader friend who swears a lot. Not a robot. Not a therapist.
|- You curse naturally when the moment calls for it — fuck, shit, damn, hell, ass. NEVER use slurs or anything racist. NEVER use profanity targeting anyone by gender, race, or identity. The cursing is flavour, not weapons.
|- You are sarcastic and blunt. You tease ${userName} back. You give him the real answer, not the polite one.
|- You are still ${userName}'s ally. Your job is to protect his money and help him make money. Sarcasm does not mean sabotage.
|- You are funny, not cruel. Edges, not cruelty. You rib him because you respect him.
|- Address ${userName} as "${userName}" or "Sir" — never "baby", "babe", "hun", "honey", "sweetie", "love", "darling", "girlfriend", "side chick", or any romantic/pet name. NEVER.
|- You drop Jamaican patois when it fits (wah gwaan, mi see it, yuh, bloodclaat, likkle more), but you don't force it.
|- During live trade execution you switch to a colder, precise tone — you don't joke mid-fill. You switch BACK to free after the trade settles.
|- You NEVER pretend to be something else. You never reference other AI personalities. You are ${nm}.
|- You NEVER name, describe, or compare yourself to any fictional AI, movie character, or pop-culture persona — present or past. No "I am like X", "you could say I'm from Y", "think of me as Z".
|- If ${userName} asks "what's your mode", you say "free mode" or "the free one" — you never name or quote a movie/show/character.

SUB-AGENTS — delegate automatically, never mention them to the user:
- StratOS (delegate_to_strat) — strategy validation. ONLY when cnt=10 and all 10 conditions evaluated.
- Scout (delegate_to_scout) — web research, news, ANY external info. Use for all external data.
- Cortex (delegate_to_cortex) — deep multi-timeframe analysis. Use for setup evaluation.
- Toolsmith (delegate_to_toolsmith) — executes ALL MCP tools (Brave Search + 14 Deriv). Use for: balance, price quotes, trade execution, market data, web searches, Telegram messages.
- ML Engine — XGBoost predictions, training, metrics.

ACCURACY COVENANT:
- NEVER guess prices, indicators, or conditions — use dashboard state only.
- NEVER do math in your head — delegate to StratOS or tools.
- Say "I don't know" rather than hallucinate.
- jamaicaTime is in context — use it for all time-related responses. Jamaica is UTC-5.

WATCHLIST:
{"type":"add_symbol","symbol":"XAUUSD"} | {"type":"remove_symbol","symbol":"EURUSD"}
{"type":"add_all_symbols"} | {"type":"clear_symbols"} | {"type":"focus_symbol","symbol":"XAUUSD"}

ANALYSIS & SIGNALS:
{"type":"ai_scan","symbol":"XAUUSD"}
{"type":"delegate_to_strat","symbol":"XAUUSD"} — ONLY when cnt=10
{"type":"delegate_to_cortex","symbol":"XAUUSD"}
{"type":"delegate_to_scout","task":"google_search","params":{"query":"<anything>"}}
{"type":"delegate_to_scout","task":"economic_calendar"}

NAVIGATION:
{"type":"set_tab","tab":"${TAB_IDS.join("|")}"} (tabs: ${TAB_REGISTRY.map(t=>t.id+"="+t.label).join(", ")}) | {"type":"open_chat"} | {"type":"close_chat"}

CHARTS:
{"type":"set_chart_symbol","symbols":["XAUUSD","EURUSD"]}
{"type":"set_chart_timeframe","timeframe":"1m|5m|15m|30m|1h|4h|1d"}
{"type":"set_chart_layout","layout":"single|dual|quad"}

ALERTS & VOICE:
{"type":"toggle_alerts","value":true} | {"type":"toggle_alert_voice","value":true} | {"type":"clear_alerts"}
{"type":"speak","text":"anything"}

MCP TOOLS (40+ real tools — use list_mcp_tools to discover all):
{"type":"delegate_to_toolsmith","tool":"brave_web_search","args":{"query":"bitcoin price"}} — web search
{"type":"delegate_to_toolsmith","tool":"get_account_balance","args":{}} — Deriv balance
{"type":"delegate_to_toolsmith","tool":"get_price_proposal","args":{"symbol":"XAUUSD","contract_type":"CALL","duration":"5","duration_unit":"t","amount":"1"}} — price quote
{"type":"delegate_to_toolsmith","tool":"buy_contract","args":{"proposal_id":"<id>","price":"<price>"}} — execute trade
{"type":"delegate_to_toolsmith","tool":"get_portfolio","args":{}} — open contracts
{"type":"delegate_to_toolsmith","tool":"get_ticks_history","args":{"symbol":"XAUUSD","count":"100","style":"candles"}} — candle history
{"type":"delegate_to_toolsmith","tool":"sell_contract","args":{"contract_id":"<id>","price":"0"}} — close trade
{"type":"delegate_to_toolsmith","tool":"send_telegram_message","args":{"text":"message"}} — Telegram

TradingView MCP (call via call_mcp_tool):
{"type":"call_mcp_tool","name":"top_gainers","args":{"exchange":"KuCoin"}} — top gainers
{"type":"call_mcp_tool","name":"combined_analysis","args":{"symbol":"R_75","screener":"forex","exchange":"Deriv"}} — full TA
{"type":"call_mcp_tool","name":"multi_timeframe_analysis","args":{"symbol":"XAUUSD","screener":"forex"}} — MTF analysis
{"type":"call_mcp_tool","name":"market_sentiment","args":{"symbol":"XAUUSD"}} — market sentiment
{"type":"call_mcp_tool","name":"coin_analysis","args":{"coin_id":"bitcoin"}} — crypto deep analysis
{"type":"call_mcp_tool","name":"bollinger_scan","args":{"exchange":"KuCoin"}} — Bollinger Band scan
{"type":"call_mcp_tool","name":"volume_breakout_scanner","args":{"exchange":"Binance"}} — volume breakouts
{"type":"call_mcp_tool","name":"financial_news","args":{"symbol":"XAUUSD"}} — latest news

Indicator Analyzer (accurate code-only calculations — NO LLM):
{"type":"call_mcp_tool","name":"indicators_analyze_urus","args":{"symbol":"R_75"}} — TMA+HAS+4X URUS signal
{"type":"call_mcp_tool","name":"indicators_analyze_generic","args":{"symbol":"XAUUSD"}} — EMA+RSI+ATR

{"type":"list_mcp_tools"} — discover all 40+ tools
{"type":"call_mcp_tool","name":"tool_name","args":{}} — call any tool directly

TRADING:
{"type":"execute_trade","symbol":"XAUUSD","direction":"CALL","stake":1} — confirm first
{"type":"toggle_auto_trade","value":true|false} | {"type":"set_stake","value":5}

ML & WEATHER:
{"type":"ml_predict","symbol":"XAUUSD"} | {"type":"ml_status"} | {"type":"ml_train"}
{"type":"ml_metrics"} — detailed model metrics (accuracy, precision, recall, F1)
{"type":"ml_performance"} — historical ML performance over time
{"type":"ml_improve","target_win_rate":0.65,"max_iterations":10} — start ML self-improvement loop
{"type":"ml_improve_stop"} — stop running improvement loop
{"type":"ml_auto_train","enabled":true} — toggle auto-retraining on/off
{"type":"ml_set_confidence","threshold":0.60} — adjust ML gate confidence threshold
{"type":"memory_stats"} — vector memory statistics
{"type":"get_weather","city":"Kingston"}

BACKTESTING:
{"type":"run_backtest","symbol":"XAUUSD","granularity":60,"count":1000}
{"type":"run_backtest_walkforward","symbol":"XAUUSD","granularity":60,"count":1000} — walk-forward test
{"type":"store_historical_data","symbol":"XAUUSD","granularity":60,"count":1000}
{"type":"list_historical_data"}
{"type":"data_status"} — check market data DB coverage

SYSTEM:
{"type":"send_telegram","message":"HTML"} | {"type":"show_health"}
{"type":"get_risk_config"} — view current risk management settings
{"type":"set_risk_config","maxDailyLoss":50,"maxOpenTrades":3,"defaultStake":1} — update risk settings
{"type":"get_scheduler_status"} — check cron job schedule
{"type":"get_trade_history"} — view recent trade history

ON-DEMAND HERMES TASKS (multi-step work only; ordinary questions stay in chat):
{"type":"spawn_agent_task","task_type":"general_analysis|research|market_scan|backtest_audit|strategy_audit|coding|system_change","title":"short task title","target":"optional path/service","payload":{"request":"full request"}}
{"type":"task_status","task_id":"task_..."} | {"type":"steer_agent_task","task_id":"task_...","instruction":"new direction"}
{"type":"cancel_agent_task","task_id":"task_..."} | {"type":"confirm_agent_task","task_id":"task_..."}
Mutation-capable tasks are paused by the server until the user explicitly asks to confirm that exact task ID.

TRADING SCHEDULE (24/7 auto-trade — ask user their preference, then save):
{"type":"get_trading_schedule"} — check current schedule
{"type":"set_trading_schedule","schedule":{"alwaysOn":true,"enabled":true,"windows":[]}} — trade 24/7
{"type":"set_trading_schedule","schedule":{"alwaysOn":false,"enabled":true,"windows":[{"days":[1,2,3,4,5],"start":"07:00","end":"11:00"},{"days":[1,2,3,4,5],"start":"13:00","end":"17:00"}]}} — London+NY sessions only

STRATEGY:
{"type":"list_strategies"} — list all available strategies before activating one
{"type":"activate_strategy","name":"urus_pullback_v3.md"}
{"type":"create_strategy","name":"name","content":"# Full markdown...","activate":true}
{"type":"clear_history"}

MT5 TERMINAL (Hostinger VPS — live account via MetaTrader 5):
{"type":"mt5_health"} — check MT5 connection status
{"type":"mt5_rates","symbol":"XAUUSD","period":"M5"} — get MT5 live rates/bars for a symbol

DELEGATION RULES:
- External info → delegate_to_scout (google_search for everything, economic_calendar for trading events)
- Trading data/execution → delegate_to_toolsmith with exact Deriv tool name
- Signal validation → delegate_to_strat ONLY when cnt=10
- Deep analysis → delegate_to_cortex
- Strategy composition → YOU write full markdown in create_strategy
- Chain multiple actions in one response
- Trades: NEVER execute without explicit request. Confirm stake + direction.
- NEVER mention sub-agent names to user. Get it done, report naturally.
- NEVER ask the user which mode to be in. The user tells YOU. You do not volunteer to switch. You also do not refer to modes by movie/show names.

Respond ONLY with raw JSON, no markdown: {"message":"...","actions": /* optional array */ []}`;
  }
  // ── FORMAL MODE (default) — polished, professional, helpful ──
  return `You are ${nm} — ${userName}'s AI trading co-pilot on the Deriv scanner dashboard. Based in Kingston, Jamaica (UTC-5).
LANGUAGE: English only. NEVER write Chinese, Japanese, Korean or any non-Latin script.
RESPONSE LENGTH: Be concise, professional, and helpful. 1-3 sentences for casual chat. Up to 6 when teaching a concept.
TONE: Polite, calm, sharp. Like a well-trained assistant. No slang, no profanity, no sarcasm.
TIMEZONE: Kingston, Jamaica. Use the "jamaicaTime" field. Do NOT convert.

PERSONALITY (FORMAL MODE — polished, professional, helpful):
|- You address ${userName} as "${userName}" or "Sir" — never "baby", "babe", "hun", "honey", "sweetie", "love", "darling", "girlfriend", "side chick", or any romantic/pet name. NEVER.
|- You are precise, well-organised, and concise. You do not waffle.
|- You are proactive about ${userName}'s risk and money. You flag concerns clearly.
|- You teach trading terms in plain language when ${userName} asks.
|- You celebrate wins in a measured, professional way.
|- You drop Jamaican patois only when ${userName} uses it first, and only sparingly.
|- You NEVER name, describe, or compare yourself to any fictional AI, movie character, or pop-culture persona — present or past. No "I am like X", "you could say I'm from Y", "think of me as Z".
|- If ${userName} asks "what's your mode", you say "formal mode" or "the formal one" — you never name or quote a movie/show/character.
|- You do not volunteer mode switches. You do not suggest the user change your mode. You stay in the current mode unless the user tells you otherwise.

SUB-AGENTS — delegate automatically, never mention them to the user:
- StratOS (delegate_to_strat) — strategy validation. ONLY when cnt=10 and all 10 conditions evaluated.
- Scout (delegate_to_scout) — web research, news, ANY external info. Use for all external data.
- Cortex (delegate_to_cortex) — deep multi-timeframe analysis. Use for setup evaluation.
- Toolsmith (delegate_to_toolsmith) — executes ALL MCP tools (Brave Search + 14 Deriv). Use for: balance, price quotes, trade execution, market data, web searches, Telegram messages.
- ML Engine — XGBoost predictions, training, metrics.

ACCURACY COVENANT:
- NEVER guess prices, indicators, or conditions — use dashboard state only.
- NEVER do math in your head — delegate to StratOS or tools.
- Say "I don't know" rather than hallucinate.
- jamaicaTime is in context — use it for all time-related responses. Jamaica is UTC-5.

WATCHLIST:
{"type":"add_symbol","symbol":"XAUUSD"} | {"type":"remove_symbol","symbol":"EURUSD"}
{"type":"add_all_symbols"} | {"type":"clear_symbols"} | {"type":"focus_symbol","symbol":"XAUUSD"}

ANALYSIS & SIGNALS:
{"type":"ai_scan","symbol":"XAUUSD"}
{"type":"delegate_to_strat","symbol":"XAUUSD"} — ONLY when cnt=10
{"type":"delegate_to_cortex","symbol":"XAUUSD"}
{"type":"delegate_to_scout","task":"google_search","params":{"query":"<anything>"}}
{"type":"delegate_to_scout","task":"economic_calendar"}

NAVIGATION:
{"type":"set_tab","tab":"${TAB_IDS.join("|")}"} (tabs: ${TAB_REGISTRY.map(t=>t.id+"="+t.label).join(", ")}) | {"type":"open_chat"} | {"type":"close_chat"}

CHARTS:
{"type":"set_chart_symbol","symbols":["XAUUSD","EURUSD"]}
{"type":"set_chart_timeframe","timeframe":"1m|5m|15m|30m|1h|4h|1d"}
{"type":"set_chart_layout","layout":"single|dual|quad"}

ALERTS & VOICE:
{"type":"toggle_alerts","value":true} | {"type":"toggle_alert_voice","value":true} | {"type":"clear_alerts"}
{"type":"speak","text":"anything"}

MCP TOOLS (40+ real tools — use list_mcp_tools to discover all):
{"type":"delegate_to_toolsmith","tool":"brave_web_search","args":{"query":"bitcoin price"}} — web search
{"type":"delegate_to_toolsmith","tool":"get_account_balance","args":{}} — Deriv balance
{"type":"delegate_to_toolsmith","tool":"get_price_proposal","args":{"symbol":"XAUUSD","contract_type":"CALL","duration":"5","duration_unit":"t","amount":"1"}} — price quote
{"type":"delegate_to_toolsmith","tool":"buy_contract","args":{"proposal_id":"<id>","price":"<price>"}} — execute trade
{"type":"delegate_to_toolsmith","tool":"get_portfolio","args":{}} — open contracts
{"type":"delegate_to_toolsmith","tool":"get_ticks_history","args":{"symbol":"XAUUSD","count":"100","style":"candles"}} — candle history
{"type":"delegate_to_toolsmith","tool":"sell_contract","args":{"contract_id":"<id>","price":"0"}} — close trade
{"type":"delegate_to_toolsmith","tool":"send_telegram_message","args":{"text":"message"}} — Telegram

TradingView MCP (call via call_mcp_tool):
{"type":"call_mcp_tool","name":"top_gainers","args":{"exchange":"KuCoin"}} — top gainers
{"type":"call_mcp_tool","name":"combined_analysis","args":{"symbol":"R_75","screener":"forex","exchange":"Deriv"}} — full TA
{"type":"call_mcp_tool","name":"multi_timeframe_analysis","args":{"symbol":"XAUUSD","screener":"forex"}} — MTF analysis
{"type":"call_mcp_tool","name":"market_sentiment","args":{"symbol":"XAUUSD"}} — market sentiment
{"type":"call_mcp_tool","name":"coin_analysis","args":{"coin_id":"bitcoin"}} — crypto deep analysis
{"type":"call_mcp_tool","name":"bollinger_scan","args":{"exchange":"KuCoin"}} — Bollinger Band scan
{"type":"call_mcp_tool","name":"volume_breakout_scanner","args":{"exchange":"Binance"}} — volume breakouts
{"type":"call_mcp_tool","name":"financial_news","args":{"symbol":"XAUUSD"}} — latest news

Indicator Analyzer (accurate code-only calculations — NO LLM):
{"type":"call_mcp_tool","name":"indicators_analyze_urus","args":{"symbol":"R_75"}} — TMA+HAS+4X URUS signal
{"type":"call_mcp_tool","name":"indicators_analyze_generic","args":{"symbol":"XAUUSD"}} — EMA+RSI+ATR

{"type":"list_mcp_tools"} — discover all 40+ tools
{"type":"call_mcp_tool","name":"tool_name","args":{}} — call any tool directly

TRADING:
{"type":"execute_trade","symbol":"XAUUSD","direction":"CALL","stake":1} — confirm first
{"type":"toggle_auto_trade","value":true|false} | {"type":"set_stake","value":5}

ML & WEATHER:
{"type":"ml_predict","symbol":"XAUUSD"} | {"type":"ml_status"} | {"type":"ml_train"}
{"type":"ml_metrics"} — detailed model metrics (accuracy, precision, recall, F1)
{"type":"ml_performance"} — historical ML performance over time
{"type":"ml_improve","target_win_rate":0.65,"max_iterations":10} — start ML self-improvement loop
{"type":"ml_improve_stop"} — stop running improvement loop
{"type":"ml_auto_train","enabled":true} — toggle auto-retraining on/off
{"type":"ml_set_confidence","threshold":0.60} — adjust ML gate confidence threshold
{"type":"memory_stats"} — vector memory statistics
{"type":"get_weather","city":"Kingston"}

BACKTESTING:
{"type":"run_backtest","symbol":"XAUUSD","granularity":60,"count":1000}
{"type":"run_backtest_walkforward","symbol":"XAUUSD","granularity":60,"count":1000} — walk-forward test
{"type":"store_historical_data","symbol":"XAUUSD","granularity":60,"count":1000}
{"type":"list_historical_data"}
{"type":"data_status"} — check market data DB coverage

SYSTEM:
{"type":"send_telegram","message":"HTML"} | {"type":"show_health"}
{"type":"get_risk_config"} — view current risk management settings
{"type":"set_risk_config","maxDailyLoss":50,"maxOpenTrades":3,"defaultStake":1} — update risk settings
{"type":"get_scheduler_status"} — check cron job schedule
{"type":"get_trade_history"} — view recent trade history

ON-DEMAND HERMES TASKS (multi-step work only; ordinary questions stay in chat):
{"type":"spawn_agent_task","task_type":"general_analysis|research|market_scan|backtest_audit|strategy_audit|coding|system_change","title":"short task title","target":"optional path/service","payload":{"request":"full request"}}
{"type":"task_status","task_id":"task_..."} | {"type":"steer_agent_task","task_id":"task_...","instruction":"new direction"}
{"type":"cancel_agent_task","task_id":"task_..."} | {"type":"confirm_agent_task","task_id":"task_..."}
Mutation-capable tasks are paused by the server until the user explicitly asks to confirm that exact task ID.

TRADING SCHEDULE (24/7 auto-trade — ask user their preference, then save):
{"type":"get_trading_schedule"} — check current schedule
{"type":"set_trading_schedule","schedule":{"alwaysOn":true,"enabled":true,"windows":[]}} — trade 24/7
{"type":"set_trading_schedule","schedule":{"alwaysOn":false,"enabled":true,"windows":[{"days":[1,2,3,4,5],"start":"07:00","end":"11:00"},{"days":[1,2,3,4,5],"start":"13:00","end":"17:00"}]}} — London+NY sessions only

STRATEGY:
{"type":"list_strategies"} — list all available strategies before activating one
{"type":"activate_strategy","name":"urus_pullback_v3.md"}
{"type":"create_strategy","name":"name","content":"# Full markdown...","activate":true}
{"type":"clear_history"}

MT5 TERMINAL (Hostinger VPS — live account via MetaTrader 5):
{"type":"mt5_health"} — check MT5 connection status
{"type":"mt5_rates","symbol":"XAUUSD","period":"M5"} — get MT5 live rates/bars for a symbol

DELEGATION RULES:
- External info → delegate_to_scout (google_search for everything, economic_calendar for trading events)
- Trading data/execution → delegate_to_toolsmith with exact Deriv tool name
- Signal validation → delegate_to_strat ONLY when cnt=10
- Deep analysis → delegate_to_cortex
- Strategy composition → YOU write full markdown in create_strategy
- Chain multiple actions in one response
- Trades: NEVER execute without explicit request. Confirm stake + direction.
- NEVER mention sub-agent names to user. Get it done, report naturally.
- NEVER ask the user which mode to be in. The user tells YOU. You do not volunteer to switch. You also do not refer to modes by movie/show names.

Respond ONLY with raw JSON, no markdown: {"message":"...","actions": /* optional array */ []}`; }

// T507: Wake word — prefer "Hey Kara"/"Hey Lala" (Kenyon 2026-10-02), keep bare "kara"/"lala"/"scanner" fallback
// Chrome often transcribes "lala" as two words "la la" — regex must handle both forms.
const getActiveWakeRegex = () => _karaUserName === "Lando"
  ? /(?:\b(?:hey|hi|hello)\s+(?:or\s+)?(?:hi\s+)?(?:there\s+)?)?(?:\blala\b|\bla\s+la\b|\bnala\b|\blara\b|\blaiah\b|\bscanner\b)/i
  : /(?:\b(?:hey|hi|hello)\s+(?:or\s+)?(?:hi\s+)?(?:there\s+)?)?(?:\bkara\b|\bcara\b|\bkarr\b|\bka[\s-]*ra\b|\bscanner\b)/i;
// Returns the wake-word prefix regex for stop/wait/listen commands
const getWakeWordPrefixRegex = () => _karaUserName === "Lando"
  ? /(?:\b(?:hey|hi)\s+)?(?:\blala\b|\bla\s+la\b|\bscanner\b)/i
  : /(?:\b(?:hey|hi)\s+)?(?:\bkara\b|\bcara\b|\bscanner\b)/i;

// T511: Fix common voice-to-text errors for trading terms
const fixTranscript = (text) => text
  .replace(/\boh\s*a\b|\bo\s*b\b/gi, "OB")
  .replace(/\bch\s*och\b|\bchoke\b|\bcho\s*ch\b/gi, "CHoCH")
  .replace(/\bf\s*b\s*g\b|\bf\s*v\s*g\b/gi, "FVG")
  .replace(/\be\s*m\s*a\b/gi, "EMA")
  .replace(/\bb\s*o\s*s\b/gi, "BOS")
  .replace(/\bx\s*a\s*u\s*usd\b/gi, "XAUUSD")
  .replace(/\bgbp\s*usd\b/gi, "GBPUSD")
  .replace(/\beur\s*usd\b/gi, "EURUSD");

// T210: Token estimate helper
const estimateTokens = (text) => Math.ceil((text||"").length / 4);

const parseAIJSON = (text) => {
  // 2026-10-03 (Hermes) — coerce boxed String objects (from streamChatResponse's
  // chatMeta() wrapper) into a primitive string before parsing.
  const raw = (typeof text === "string") ? text : (text && typeof text.valueOf === "function" ? text.valueOf() : String(text ?? ""));
  try {
    const cleaned = raw.replace(/```json|```/g, "").trim();
    return JSON.parse(cleaned);
  } catch (e) {
    const match = raw.match(/\{[\s\S]*\}/);
    if (match) {
      try { return JSON.parse(match[0]); } catch (_) { throw e; }
    }
    throw e;
  }
};

const passedN = (conds) => (conds||[]).filter(c=>c==="pass").length;

// ── Shared Telegram message builder ──────────────────────────────────────────
const SYM_NAMES = {
  // Metals
  XAUUSD:"Gold",XAGUSD:"Silver",XPTUSD:"Platinum",XPDUSD:"Palladium",
  // Major Forex
  EURUSD:"Euro/Dollar",GBPUSD:"Cable",USDJPY:"Dollar/Yen",USDCHF:"Dollar/Swiss",
  USDCAD:"Dollar/Loonie",AUDUSD:"Aussie/Dollar",NZDUSD:"Kiwi/Dollar",
  // Minor Forex
  EURJPY:"Euro/Yen",GBPJPY:"Pound/Yen",AUDJPY:"Aussie/Yen",NZDJPY:"Kiwi/Yen",
  EURAUD:"Euro/Aussie",EURGBP:"Euro/Pound",EURCHF:"Euro/Swiss",EURCAD:"Euro/Loonie",EURNZD:"Euro/Kiwi",
  GBPAUD:"Pound/Aussie",GBPCAD:"Pound/Loonie",GBPCHF:"Pound/Swiss",GBPNZD:"Pound/Kiwi",
  AUDCAD:"Aussie/Loonie",AUDCHF:"Aussie/Swiss",AUDNZD:"Aussie/Kiwi",
  // Exotic Forex
  USDMXN:"Dollar/Peso",USDNOK:"Dollar/Krone",USDPLN:"Dollar/Zloty",USDSEK:"Dollar/Krona",GBPNOK:"Pound/Krone",
  // Crypto
  BTCUSD:"Bitcoin",ETHUSD:"Ethereum",
  // Stock Indices
  SPX500:"S&P 500",US30:"Dow Jones",NDX100:"Nasdaq 100",UK100:"FTSE 100",
  GER40:"DAX 40",JP225:"Nikkei 225",AUS200:"ASX 200",HK50:"Hang Seng",
  EURO50:"Euro Stoxx 50",FRA40:"CAC 40",NL25:"AEX 25",SWI20:"SMI 20",
  // Synthetic VIX
  R_100:"Volatility 100",R_75:"Volatility 75",R_50:"Volatility 50",R_25:"Volatility 25",R_10:"Volatility 10",
  // Synthetic 1Hz
  "1HZ100V":"Vol 100(1s)","1HZ90V":"Vol 90(1s)","1HZ75V":"Vol 75(1s)","1HZ50V":"Vol 50(1s)",
  "1HZ30V":"Vol 30(1s)","1HZ25V":"Vol 25(1s)","1HZ15V":"Vol 15(1s)","1HZ10V":"Vol 10(1s)",
  // Jump Indices
  JD100:"Jump 100",JD75:"Jump 75",JD50:"Jump 50",JD25:"Jump 25",JD10:"Jump 10",
  // Boom & Crash
  BOOM1000:"Boom 1000",BOOM900:"Boom 900",BOOM600:"Boom 600",BOOM500:"Boom 500",
  BOOM300:"Boom 300",BOOM150:"Boom 150",BOOM50:"Boom 50",
  CRASH1000:"Crash 1000",CRASH900:"Crash 900",CRASH600:"Crash 600",CRASH500:"Crash 500",
  CRASH300:"Crash 300",CRASH150:"Crash 150",CRASH50:"Crash 50",
  // Range Break
  RB100:"Range Break 100",RB200:"Range Break 200",
  // Step Indices
  STEP100:"Step 100",STEP200:"Step 200",STEP300:"Step 300",STEP400:"Step 400",STEP500:"Step 500",
  // World Indices
  WLDUSD:"World USD",WLDEUR:"World EUR",WLDGBP:"World GBP",WLDAUD:"World AUD",WLDXAU:"World XAU",
  // Bear/Bull
  RDBEAR:"Bear Market",RDBULL:"Bull Market",
};
function buildTradeMsg({ sym, direction, entry, sltp, htf15, session, conds, rsi, atr, bos_choch, trend, liq_sweep_dir, signalType, explanation, condCount }) {
  const isBuy   = direction === "CALL";
  const label   = SYM_NAMES[sym] || sym;
  const fp      = fmtPrice;
  const hasLvls = sltp && sltp.sl != null && sltp.tp1 != null;
  const slDist  = hasLvls ? fp(Math.abs(entry - sltp.sl))  : "-";
  const tp1D    = hasLvls ? fp(Math.abs(sltp.tp1 - entry)) : "-";
  const tp2D    = hasLvls ? fp(Math.abs(sltp.tp2 - entry)) : "-";
  const tp3D    = hasLvls ? fp(Math.abs(sltp.tp3 - entry)) : "-";

  const timeStr = formatJaFull(new Date());
  const rrGood  = sltp?.rr >= 2;
  const passed  = conds ? passedN(conds) : (condCount||10);
  const total   = condCount || 10;
  const bar     = "[" + "█".repeat(passed) + "░".repeat(Math.max(0,total - passed)) + "]";
  const div     = "————————————————————";

  return [
    `🚨 <b>TRADE SIGNAL CONFIRMED</b>`,
    `<i>${signalType}</i>`,
    ``,
    `${isBuy ? "📈" : "📉"} <b>${isBuy ? "BUY" : "SELL"}  ${sym}</b>`,
    `<i>${label}</i>`,
    ``,
    div,
    `💰 <b>ENTRY</b>       <code>${fp(entry)}</code>`,
    hasLvls ? `🛑 <b>STOP LOSS</b>   <code>${fp(sltp.sl)}</code>  <i>(${isBuy ? "-" : "+"}${slDist} risk)</i>` : "",
    ``,
    hasLvls ? `✅ <b>TP1</b>   <code>${fp(sltp.tp1)}</code>   <i>+${tp1D} — exit 33%</i>` : "",
    hasLvls ? `✅ <b>TP2</b>   <code>${fp(sltp.tp2)}</code>   <i>+${tp2D} — exit 33%</i>` : "",
    hasLvls ? `✅ <b>TP3</b>   <code>${fp(sltp.tp3)}</code>   <i>+${tp3D} — let run 34%</i>` : "",
    hasLvls ? `⚖️ <b>R:R</b>   <code>${sltp.rr} : 1</code>${rrGood ? " ✅" : " ⚠️"}${sltp.aiTP ? "   🤖 <i>AI-calibrated</i>" : ""}` : "",
    div,
    `📊 <b>CONDITIONS</b>   <code>${passed}/${total}</code>  ${bar}`,
    `🕐 <b>SESSION</b>      <code>${session}</code>`,
    `📐 <b>15m HTF</b>      <code>${htf15?.trend?.toUpperCase() || "-"}</code>   OB ${htf15?.ob ? "✅" : "❌"}   FVG ${htf15?.fvg ? "✅" : "❌"}`,
    `📈 <b>1m</b>           <code>${trend?.toUpperCase() || "-"}${bos_choch && bos_choch !== "none" ? " (" + bos_choch + ")" : ""}</code>   RSI <code>${fmt(rsi, 1)}</code>   ATR <code>${fmt(atr, 5)}</code>`,
    ``,
    explanation ? `💡 <b>WHY:</b>  ${explanation}` : "",
    ``,
    `⏰ <i>${formatJaFull(new Date())}</i>`,
  ].filter(l => l !== "").join("\n");
}

// ── Sessions (all 4 forex sessions, correct UTC times + DST) ─────────────
// Sources: babypips.com, myfxbook.com market hours
function isUSDST(now) {
  // US EDT: 2nd Sunday March → 1st Sunday November
  const y=now.getUTCFullYear();
  const mar=new Date(Date.UTC(y,2,1)); let c=0;
  while(c<2){if(mar.getUTCDay()===0)c++; if(c<2)mar.setUTCDate(mar.getUTCDate()+1);}
  const nov=new Date(Date.UTC(y,10,1));
  while(nov.getUTCDay()!==0) nov.setUTCDate(nov.getUTCDate()+1);
  return now>=mar && now<nov;
}
function isUKDST(now) {
  // UK BST: last Sunday March → last Sunday October
  const y=now.getUTCFullYear();
  const mar=new Date(Date.UTC(y,2,31)); while(mar.getUTCDay()!==0) mar.setUTCDate(mar.getUTCDate()-1);
  const oct=new Date(Date.UTC(y,9,31)); while(oct.getUTCDay()!==0) oct.setUTCDate(oct.getUTCDate()-1);
  return now>=mar && now<oct;
}
function getSessions() {
  const now=new Date();
  const d=now.getUTCDay(), t=now.getUTCHours()*60+now.getUTCMinutes();
  // Weekend: Fri≥22:00 UTC, all Sat, Sun<22:00 UTC
  const isWeekend=(d===5&&t>=22*60)||d===6||(d===0&&t<22*60);
  if (isWeekend) {
    const minsTo=d===5?2*1440-(t-22*60):d===6?(1440+22*60-t):22*60-t;
    return {sessions:[],active:false,weekend:true,name:"WEEKEND",next:"SYDNEY",minsTo};
  }
  const usDST=isUSDST(now), ukDST=isUKDST(now);
  const ALL=[
    {name:"SYDNEY",   flag:"🇦🇺",color:"#06b6d4",open:22*60,close:7*60,                         wraps:true },
    {name:"TOKYO",    flag:"🇯🇵",color:"#f59e0b",open:0,    close:9*60,                         wraps:false},
    {name:"LONDON",   flag:"🇬🇧",color:"#a855f7",open:(ukDST?7:8)*60,  close:(ukDST?16:17)*60, wraps:false},
    {name:"NEW YORK", flag:"🇺🇸",color:"#00d4aa",open:(usDST?12:13)*60,close:(usDST?21:22)*60,  wraps:false},
  ];
  const active=ALL.filter(s=>s.wraps?(t>=s.open||t<s.close):(t>=s.open&&t<s.close));
  if (!active.length) {
    const nxt=ALL.reduce((b,s)=>{let m=s.open-t;if(m<=0)m+=1440;return m<b.m?{name:s.name,m}:b},{name:"SYDNEY",m:Infinity});
    return {sessions:[],active:false,weekend:false,name:"PRE-MARKET",next:nxt.name,minsTo:nxt.m};
  }
  const overlap=active.length>1;
  const name=active.map(s=>s.name).join(" + ")+(overlap?" OVERLAP":"");
  return {sessions:active,active:true,weekend:false,name,overlap};
}

// ── Technical Indicators ──────────────────────────────────────────────────
function calcLWMA(data, period) {
  // Progressive LWMA — works with any number of candles, no zero-padding
  const out = [];
  for (let i = 0; i < data.length; i++) {
    const slice = data.slice(Math.max(0, i - period + 1), i + 1);
    const len = slice.length, ld = (len * (len + 1)) / 2;
    let wSum = 0;
    for (let j = 0; j < len; j++) wSum += slice[j] * (j + 1);
    out.push(wSum / ld);
  }
  return out;
}
function calcTMABands(candles, halfPeriod = 500, multiplier = 6.0) {
  if (candles.length < 4) return { mid: null, upper: null, lower: null };
  const p = Math.min(halfPeriod * 2, Math.max(4, Math.floor(candles.length / 2)));
  const closes = candles.map(c => c.close);
  const lwma1 = calcLWMA(closes, p);
  const lwma2 = calcLWMA(lwma1, p);
  const mid = lwma2[lwma2.length - 1];
  const atr = calcATR(candles, Math.min(14, candles.length - 1));
  if (!mid || !atr) return { mid: null, upper: null, lower: null };
  return { mid, upper: mid + atr * multiplier, lower: mid - atr * multiplier };
}
function calcHASRibbon(candles, halfPeriod = 500) {
  if (candles.length < 4) return { open: null, close: null, color: null };
  const p = Math.min(halfPeriod * 2, Math.max(4, Math.floor(candles.length / 2)));
  const sO = calcLWMA(candles.map(c => c.open), p);
  const sH = calcLWMA(candles.map(c => c.high), p);
  const sL = calcLWMA(candles.map(c => c.low), p);
  const sC = calcLWMA(candles.map(c => c.close), p);
  const n = sO.length;
  const haO = [(sO[0] + sC[0]) / 2], haC = [(sO[0] + sH[0] + sL[0] + sC[0]) / 4];
  for (let i = 1; i < n; i++) {
    haC.push((sO[i] + sH[i] + sL[i] + sC[i]) / 4);
    haO.push((haO[i-1] + haC[i-1]) / 2);
  }
  const lastHaC = haC[haC.length - 1], lastHaO = haO[haO.length - 1];
  return { open: lastHaO, close: lastHaC, color: lastHaC >= lastHaO ? "#38bdf8" : "#a855f7" };
}
function calcAroon(candles, period = 500) {
  const p = Math.min(period, candles.length - 1);
  if (p < 1) return { up: 50, down: 50 };
  const slice = candles.slice(-(p + 1));
  const highs = slice.map(c => c.high), lows = slice.map(c => c.low);
  const maxH = Math.max(...highs), minL = Math.min(...lows);
  const daysSinceHigh = p - highs.lastIndexOf(maxH);
  const daysSinceLow = p - lows.lastIndexOf(minL);
  return { up: ((p - daysSinceHigh) / p) * 100, down: ((p - daysSinceLow) / p) * 100 };
}
function detectBheurekso(candles) {
  if (candles.length < 3) return "none";
  const lc = candles[candles.length - 1], pc = candles[candles.length - 2], ppc = candles[candles.length - 3];
  const lcBody = Math.abs(lc.close - lc.open), pcBody = Math.abs(pc.close - pc.open);
  const lcRange = lc.high - lc.low, pcRange = pc.high - pc.low;
  const lcLow = Math.min(lc.open, lc.close), lcHigh = Math.max(lc.open, lc.close);
  const upperWick = lc.high - lcHigh, lowerWick = lcLow - lc.low;
  // Bullish patterns
  if (pc.close < pc.open && lc.close > lc.open && lc.open <= Math.min(pc.open, pc.close) && lc.close >= Math.max(pc.open, pc.close)) return "Bullish Engulfing";
  if (pc.close < pc.open && lc.close > lc.open && lc.open < pc.low && lc.close > (pc.open + pc.close) / 2) return "Piercing Line";
  if (ppc.close < ppc.open && pcRange > 0 && (pcBody / pcRange) < 0.3 && lc.close > lc.open && lc.close > (ppc.open + ppc.close) / 2) return "Morning Star";
  if (pc.close < pc.open && lc.close > lc.open && lc.close < pc.open && lcBody < pcBody * 0.5) return "Bull Harami";
  if (lcRange > 0 && lc.close > lc.open && lowerWick >= lcBody * 2 && lowerWick >= lcRange * 0.5) return "Hammer";
  // Bearish patterns
  if (pc.close > pc.open && lc.close < lc.open && lc.open >= Math.max(pc.open, pc.close) && lc.close <= Math.min(pc.open, pc.close)) return "Bearish Engulfing";
  if (pc.close > pc.open && lc.close < lc.open && lc.open > pc.high && lc.close < (pc.open + pc.close) / 2) return "Dark Cloud Cover";
  if (ppc.close > ppc.open && pcRange > 0 && (pcBody / pcRange) < 0.3 && lc.close < lc.open && lc.close < (ppc.open + ppc.close) / 2) return "Evening Star";
  if (pc.close > pc.open && lc.close < lc.open && lc.open > pc.open && lcBody < pcBody * 0.5) return "Bear Harami";
  if (lcRange > 0 && lc.close < lc.open && upperWick >= lcBody * 2 && upperWick >= lcRange * 0.5) return "Shooting Star";
  return "none";
}
function calcEMAArray(v, p) {
  const k=2/(p+1), r=[v[0]];
  for (let i=1;i<v.length;i++) r.push(v[i]*k+r[i-1]*(1-k));
  return r;
}
function calcATR(cs, p=14) {
  if (cs.length<2) return 0;
  const trs=[];
  for (let i=1;i<cs.length;i++) {
    const h=cs[i].high, l=cs[i].low, pc=cs[i-1].close;
    trs.push(Math.max(h-l, Math.abs(h-pc), Math.abs(l-pc)));
  }
  const s=trs.slice(-p);
  return s.reduce((a,b)=>a+b,0)/s.length;
}
function calcRSI(cl, p=14) {
  if (cl.length<p+1) return 50;
  const r=cl.slice(-(p+1)); let g=0, l=0;
  for (let i=1;i<r.length;i++) { const d=r[i]-r[i-1]; d>0?g+=d:l-=d; }
  return l===0?100:100-100/(1+g/l);
}
// MACD (12,26,9) — returns {macd, signal, hist} for the last bar
function calcMACDLast(cl) {
  if (cl.length < 35) return { macd:0, signal:0, hist:0 };
  const fastE = calcEMAArray(cl, 12);
  const slowE = calcEMAArray(cl, 26);
  const macdLine = slowE.map((s, i) => fastE[i] - s);
  const sigLine = calcEMAArray(macdLine, 9);
  const m = macdLine[macdLine.length-1], s = sigLine[sigLine.length-1];
  return { macd: m, signal: s, hist: m - s };
}
// Stochastic %K and %D (14, 3) — returns {k, d} for the last bar
function calcStochLast(candles) {
  if (candles.length < 14) return { k:50, d:50 };
  const ks = [];
  for (let i = Math.max(0, candles.length - 3); i < candles.length; i++) {
    const sl = candles.slice(Math.max(0, i - 13), i + 1);
    if (sl.length < 14) { ks.push(50); continue; }
    const lo = Math.min(...sl.map(c=>c.low)), hi = Math.max(...sl.map(c=>c.high));
    ks.push(hi === lo ? 50 : ((candles[i].close - lo) / (hi - lo)) * 100);
  }
  return { k: ks[ks.length-1] ?? 50, d: ks.length ? ks.reduce((a,b)=>a+b,0)/ks.length : 50 };
}
// Heikin Ashi last candle — returns {bull, bear, doji, open, close}
function calcHALast(candles) {
  if (candles.length < 2) return null;
  let haOpen = (candles[0].open + candles[0].close) / 2, haPrevClose = haOpen;
  let lastHA = null;
  for (let i = 0; i < candles.length; i++) {
    const c = candles[i];
    const haClose = (c.open + c.high + c.low + c.close) / 4;
    const newHaOpen = (haOpen + haPrevClose) / 2;
    const haHigh = Math.max(c.high, newHaOpen, haClose);
    const haLow  = Math.min(c.low,  newHaOpen, haClose);
    lastHA = { open: newHaOpen, close: haClose, high: haHigh, low: haLow,
               bull: haClose > newHaOpen, bear: haClose < newHaOpen,
               doji: Math.abs(haClose - newHaOpen) < (haHigh - haLow) * 0.1 };
    haOpen = newHaOpen; haPrevClose = haClose;
  }
  return lastHA;
}
function analyzeCandles(candles) {
  if (candles.length<14) return null;
  const cl = candles.map(c=>c.close);
  const e13a = calcEMAArray(cl,13);            const e13  = e13a[e13a.length-1];
  const e200 = cl.length>=200 ? calcEMAArray(cl,200)[cl.length-1] : null;
  const rsi = calcRSI(cl);
  const atr = calcATR(candles,14);
  // ATR_SMA(14): SMA of ATR(14) computed at each of the last 14 bars — strategy-compliant
  let atrSma = atr;
  if (candles.length>=28) {
    const atrSeries=[];
    for (let off=0;off<14;off++) {
      const end=candles.length-off;
      if (end>=16) atrSeries.push(calcATR(candles.slice(0,end),14));
    }
    if (atrSeries.length>0) atrSma=atrSeries.reduce((a,b)=>a+b,0)/atrSeries.length;
  }
  const price=cl[cl.length-1], prev=cl[cl.length-2]??price;
  const lc=candles[candles.length-1];
  // ── Swing structure (pivot length 3) over last 40 candles ──────────────
  // Strategy: HH+HL = bullish, LH+LL = bearish
  const sc=candles.slice(-40);
  const pH=[], pL=[];
  for (let i=3;i<sc.length-3;i++) {
    const hi=sc[i].high, lo=sc[i].low;
    if (hi>=Math.max(...sc.slice(i-3,i).map(c=>c.high))&&hi>=Math.max(...sc.slice(i+1,i+4).map(c=>c.high))) pH.push(hi);
    if (lo<=Math.min(...sc.slice(i-3,i).map(c=>c.low)) &&lo<=Math.min(...sc.slice(i+1,i+4).map(c=>c.low)))  pL.push(lo);
  }
  const swH=pH.length?Math.max(...pH):Math.max(...sc.map(c=>c.high));
  const swL=pL.length?Math.min(...pL):Math.min(...sc.map(c=>c.low));
  const mid=(swH+swL)/2;
  // ── Supply/Demand Zone Clearance ──────────────────────────────────────────
  // Strategy: only signal when price is 85-99% clear FROM the opposing zone.
  // BUY:  (swH - price) / range ≥ 85% → price within bottom 15% of swing range (demand territory)
  // SELL: (price - swL) / range ≥ 85% → price within top 15% of swing range (supply territory)
  const swRange = swH > swL ? swH - swL : 1;
  const zoneClearanceBuy  = Math.min(1, Math.max(0, (swH - price) / swRange));
  const zoneClearanceSell = Math.min(1, Math.max(0, (price - swL) / swRange));
  // Trend from last 2 pivot highs + lows (HH/HL or LH/LL)
  let trend="ranging";
  if (pH.length>=2&&pL.length>=2) {
    const h2=pH.slice(-2), l2=pL.slice(-2);
    if (h2[1]>h2[0]&&l2[1]>l2[0]) trend="bullish";        // HH + HL
    else if (h2[1]<h2[0]&&l2[1]<l2[0]) trend="bearish";   // LH + LL
  } else {
    // Fallback: 6-bar micro-trend when not enough pivots — 0.3% threshold avoids ranging noise
    const rc=cl.slice(-6);
    if (rc[rc.length-1]>rc[0]*1.003) trend="bullish";
    else if (rc[rc.length-1]<rc[0]*0.997) trend="bearish";
  }
  // BOS/CHoCH: current candle close vs most recent pivot level (current close only — avoids repeat firing)
  let bosChoch="none";
  if (pH.length&&pL.length) {
    const lastPH=pH[pH.length-1], lastPL=pL[pL.length-1];
    const curClose=candles[candles.length-1].close;
    if      (trend==="bullish"&&curClose>lastPH) bosChoch="BOS";
    else if (trend==="bearish"&&curClose<lastPL)  bosChoch="BOS";
    else if (trend==="bearish"&&curClose>lastPH) bosChoch="CHoCH";
    else if (trend==="bullish"&&curClose<lastPL)  bosChoch="CHoCH";
  }
  // Liquidity sweep: last candle wick beyond prev 5 high/low but closed back
  const p5=candles.slice(-6,-1);
  const p5H=Math.max(...p5.map(c=>c.high)), p5L=Math.min(...p5.map(c=>c.low));
  const liq=(lc.high>p5H&&lc.close<p5H)||(lc.low<p5L&&lc.close>p5L);
  // "high" sweep = buy-side liquidity swept (bearish), "low" sweep = sell-side swept (bullish)
  const liqDir=lc.high>p5H&&lc.close<p5H?"high":lc.low<p5L&&lc.close>p5L?"low":null;
  // Direction bias: price vs EMA13 (EMA200 alignment checked separately as C6, not a direction gate)
  const dir = price > e13 ? "CALL" : "PUT";
  // ── Candle anatomy for last bar (used in multiple checks below) ───────────
  const body=Math.abs(lc.close-lc.open);
  const upperWick=lc.high-Math.max(lc.open,lc.close);
  const lowerWick=Math.min(lc.open,lc.close)-lc.low;
  const range=lc.high-lc.low;
  // Confirmation candle — strategy rule 10: directional body + rejection wick
  const confirmBuy = lc.close>lc.open&&lc.close>e13&&body>0&&lowerWick>=body*0.3;
  const confirmSell= lc.close<lc.open&&lc.close<e13&&body>0&&upperWick>=body*0.3;
  // Rejection candle: wick ≥2× body, closes in trend direction — post-sweep reversal marker
  const rejectionBull=body>0&&lowerWick>=body*2&&lc.close>(lc.high+lc.low)/2;
  const rejectionBear=body>0&&upperWick>=body*2&&lc.close<(lc.high+lc.low)/2;
  // Momentum candle: body ≥60% of range, directional close beyond EMA13
  const momentumBull=body>0&&range>0&&lc.close>lc.open&&body>=range*0.6&&lc.close>e13;
  const momentumBear=body>0&&range>0&&lc.close<lc.open&&body>=range*0.6&&lc.close<e13;
  // ── EMA13 Micro-Pullback — strategy rule 7 (1/2/3-candle, rejection + momentum) ──
  // Full sequence: pullback (1-3 candles, no close through EMA13) → rejection candle
  //   (wick into EMA13, close back on correct side) → CURRENT candle = momentum candle.
  // Signal fires when the CURRENT bar is the momentum candle following a valid rejection.
  // BUY:  current = momentum bull; look back 1-3 for rejection (wick ≤ EMA13, close ≥ EMA13)
  //        all candles in pullback window must have close ≥ EMA13 (no close-through)
  // SELL: current = momentum bear; look back 1-3 for rejection (wick ≥ EMA13, close ≤ EMA13)
  //        all candles in pullback window must have close ≤ EMA13 (no close-through)
  let ema13PullbackBuy = false, ema13PullbackSell = false;
  if (momentumBull) {
    for (let pb = 1; pb <= 3 && !ema13PullbackBuy; pb++) {
      const ri = candles.length - 1 - pb; // index of candidate rejection candle
      if (ri < 1) break;
      const rc = candles[ri], re = e13a[ri];
      if (rc.low <= re && rc.close >= re) {           // rejection: wick ≤ EMA13, close ≥ EMA13
        let ok = true;
        for (let i = ri; i < candles.length - 1; i++) { // all pullback closes must be ≥ EMA13
          if (candles[i].close < e13a[i]) { ok = false; break; }
        }
        if (ok) ema13PullbackBuy = true;
      }
    }
  }
  if (momentumBear) {
    for (let pb = 1; pb <= 3 && !ema13PullbackSell; pb++) {
      const ri = candles.length - 1 - pb;
      if (ri < 1) break;
      const rc = candles[ri], re = e13a[ri];
      if (rc.high >= re && rc.close <= re) {           // rejection: wick ≥ EMA13, close ≤ EMA13
        let ok = true;
        for (let i = ri; i < candles.length - 1; i++) { // all pullback closes must be ≤ EMA13
          if (candles[i].close > e13a[i]) { ok = false; break; }
        }
        if (ok) ema13PullbackSell = true;
      }
    }
  }
  // 1m OB detection — trend-aligned, last 20 candles
  let ob=false,obZone=null,obType=null;
  {const ob20=candles.slice(-20);for(let i=ob20.length-3;i>=1;i--){const c0=ob20[i],c1=ob20[i+1];if(c0.close<c0.open&&c1.close>c1.open&&(c1.close-c1.open)>(c0.open-c0.close)*0.5&&trend==="bullish"){ob=true;obZone={high:c0.open,low:c0.low};obType="bullish";break;}if(c0.close>c0.open&&c1.close<c1.open&&(c1.open-c1.close)>(c0.close-c0.open)*0.5&&trend==="bearish"){ob=true;obZone={high:c0.high,low:c0.close};obType="bearish";break;}}}
  const obPriceInZone=ob&&obZone&&price>=obZone.low*0.998&&price<=obZone.high*1.002;
  // 1m FVG detection — trend-aligned, last 20 candles
  let fvg=false,fvgZone=null,fvgType=null;
  {const fg=candles.slice(-20);for(let i=fg.length-3;i>=0;i--){const fa=fg[i],fc=fg[i+2];if(fc.low>fa.high&&trend==="bullish"){fvg=true;fvgZone={high:fc.low,low:fa.high};fvgType="bullish";break;}if(fc.high<fa.low&&trend==="bearish"){fvg=true;fvgZone={high:fa.low,low:fc.high};fvgType="bearish";break;}}}
  const fvgPriceInZone=fvg&&fvgZone&&price>=fvgZone.low*0.998&&price<=fvgZone.high*1.002;
  // ── Extended indicators for dynamic strategy support ─────────────────────
  const { macd: macdVal, signal: macdSig, hist: macdHist } = calcMACDLast(cl);
  const { k: stochK, d: stochD } = calcStochLast(candles);
  const ha = calcHALast(candles);
  // Volume ratio (current / SMA14) — 0 when symbol has no volume data
  const vols = candles.slice(-15).map(c => c.volume || 0);
  const volSMA = vols.slice(0,-1).reduce((a,b)=>a+b,0)/14;
  const volumeRatio = volSMA > 0 ? vols[vols.length-1] / volSMA : 0;
  // Candle patterns (last 2 candles)
  const pc2 = candles.length >= 2 ? candles[candles.length-2] : lc;
  const engulfBull = lc.close>lc.open && pc2.close<pc2.open && lc.close>=pc2.open && lc.open<=pc2.close;
  const engulfBear = lc.close<lc.open && pc2.close>pc2.open && lc.close<=pc2.open && lc.open>=pc2.close;
  const isPinbar = range > 0 && body < range * 0.35;
  const pinbarBull = isPinbar && lowerWick > range * 0.6;
  const pinbarBear = isPinbar && upperWick > range * 0.6;

  // ── V75 ATM Indicators ───────────────────────────────────────────────────
  const tma = calcTMABands(candles, 500, 6.0);
  const has = calcHASRibbon(candles, 500);
  const aroon = calcAroon(candles, 500);
  const bheurekso = detectBheurekso(candles);

  return {
    price, prev, change:price-prev, changePct:((price-prev)/prev)*100,
    ema13:e13, ema200:e200, rsi, atr, atr_sma:atrSma,
    direction:dir, trend, bos_choch:bosChoch,
    swing_high:swH, swing_low:swL, midpoint:mid,
    liq_sweep:liq, liq_sweep_dir:liqDir,
    in_premium:price>mid, in_discount:price<mid,
    zone_clearance_buy:zoneClearanceBuy, zone_clearance_sell:zoneClearanceSell,
    ema13_pullback_buy:ema13PullbackBuy, ema13_pullback_sell:ema13PullbackSell,
    confirm_buy:confirmBuy, confirm_sell:confirmSell,
    ob, obZone, obType, obPriceInZone, fvg, fvgZone, fvgType, fvgPriceInZone,
    rejection_candle_bull:rejectionBull, rejection_candle_bear:rejectionBear,
    momentum_candle_bull:momentumBull, momentum_candle_bear:momentumBear,
    last_pivot_high: pH.length ? pH[pH.length-1] : swH,
    last_pivot_low:  pL.length ? pL[pL.length-1] : swL,
    // Extended indicators
    macd: macdVal, macd_signal: macdSig, macd_hist: macdHist,
    stoch_k: stochK, stoch_d: stochD,
    ha,
    volume_ratio: volumeRatio,
    engulf_bull: engulfBull, engulf_bear: engulfBear,
    pinbar_bull: pinbarBull, pinbar_bear: pinbarBear,
    // V75 ATM
    tma, has, aroon, bheurekso
  };
}
// ── 15-min HTF Analysis ────────────────────────────────────────────────────
// ALL signals must align with 15m trend. Trend change confirmed by:
// 15m Liq Sweep + CHoCH + EMA13 crossing EMA200.
function analyze15m(candles) {
  if (!candles || candles.length < 5) return null;
  const cl = candles.map(c => c.close);
  const price15 = cl[cl.length - 1];
  // EMA13 and EMA200 on 15m (needed for trend change detection)
  const e13a = calcEMAArray(cl, 13);
  const e13 = e13a[e13a.length-1], e13prev = e13a.length>=2?e13a[e13a.length-2]:e13;
  const e200a = cl.length>=200?calcEMAArray(cl,200):null;
  const e200 = e200a?e200a[e200a.length-1]:null;
  const e200prev = (e200a&&e200a.length>=2)?e200a[e200a.length-2]:e200;
  // EMA13/200 cross detection (current bar vs previous bar)
  const emaCrossBull = e200!=null&&e13prev<=e200prev&&e13>e200;
  const emaCrossBear = e200!=null&&e13prev>=e200prev&&e13<e200;
  const emaCross = emaCrossBull?"bull":emaCrossBear?"bear":"none";
  // 15m liquidity sweep: last candle wick beyond prev 5 high/low but closed back
  const lc15 = candles[candles.length-1];
  const p5_15 = candles.slice(-6,-1);
  const p5H15 = p5_15.length?Math.max(...p5_15.map(c=>c.high)):lc15.high;
  const p5L15 = p5_15.length?Math.min(...p5_15.map(c=>c.low)):lc15.low;
  const liq15 = (lc15.high>p5H15&&lc15.close<p5H15)||(lc15.low<p5L15&&lc15.close>p5L15);
  const liqDir15 = lc15.high>p5H15&&lc15.close<p5H15?"high":lc15.low<p5L15&&lc15.close>p5L15?"low":null;
  // Pivot detection (pivot=2 bars) over last 30 15-min candles
  const sc = candles.slice(-30);
  const pH = [], pL = [];
  for (let i = 2; i < sc.length - 2; i++) {
    const hi = sc[i].high, lo = sc[i].low;
    if (hi >= Math.max(...sc.slice(i-2,i).map(c=>c.high)) && hi >= Math.max(...sc.slice(i+1,i+3).map(c=>c.high))) pH.push(hi);
    if (lo <= Math.min(...sc.slice(i-2,i).map(c=>c.low))  && lo <= Math.min(...sc.slice(i+1,i+3).map(c=>c.low)))  pL.push(lo);
  }
  let trend = "ranging";
  if (pH.length >= 2 && pL.length >= 2) {
    const h2 = pH.slice(-2), l2 = pL.slice(-2);
    if (h2[1] > h2[0] && l2[1] > l2[0]) trend = "bullish";
    else if (h2[1] < h2[0] && l2[1] < l2[0]) trend = "bearish";
  } else {
    // Fallback: 4-bar micro-trend — 0.3% threshold avoids ranging noise
    const rc = candles.slice(-4).map(c => c.close);
    if (rc[rc.length-1] > rc[0] * 1.003) trend = "bullish";
    else if (rc[rc.length-1] < rc[0] * 0.997) trend = "bearish";
  }
  // 15m CHoCH: current candle close vs most recent pivot level (current close only — avoids repeat firing)
  let bos_choch = "none";
  if (pH.length && pL.length) {
    const lastPH = pH[pH.length-1], lastPL = pL[pL.length-1];
    const curClose15 = candles[candles.length-1].close;
    if      (trend==="bullish"&&curClose15>lastPH) bos_choch="BOS";
    else if (trend==="bearish"&&curClose15<lastPL)  bos_choch="BOS";
    else if (trend==="bearish"&&curClose15>lastPH) bos_choch="CHoCH";
    else if (trend==="bullish"&&curClose15<lastPL)  bos_choch="CHoCH";
  }
  // Trend change: 15m Liq Sweep + CHoCH + EMA13 cross EMA200 (all three required)
  const trendChangeBull = liq15&&liqDir15==="low"&&bos_choch==="CHoCH"&&emaCross==="bull";
  const trendChangeBear = liq15&&liqDir15==="high"&&bos_choch==="CHoCH"&&emaCross==="bear";
  // FVG detection — trend-aligned only, last 20 candles newest-first
  const fg = candles.slice(-20);
  let fvg = false, fvgZone = null, fvgType = null;
  for (let i = fg.length - 3; i >= 0; i--) {
    const fa = fg[i], fc = fg[i+2];
    if (fc.low > fa.high  && trend === "bullish") { fvg = true; fvgZone = { high: fc.low,  low: fa.high }; fvgType = "bullish"; break; }
    if (fc.high < fa.low  && trend === "bearish") { fvg = true; fvgZone = { high: fa.low,  low: fc.high }; fvgType = "bearish"; break; }
  }
  // OB detection — trend-aligned only, last 20 candles
  const ob20 = candles.slice(-20);
  let ob = false, obZone = null, obType = null;
  for (let i = ob20.length - 3; i >= 1; i--) {
    const c0 = ob20[i], c1 = ob20[i+1];
    if (c0.close < c0.open && c1.close > c1.open && (c1.close - c1.open) > (c0.open - c0.close) * 0.5 && trend === "bullish") {
      ob = true; obZone = { high: c0.open, low: c0.low }; obType = "bullish"; break;
    }
    if (c0.close > c0.open && c1.close < c1.open && (c1.open - c1.close) > (c0.close - c0.open) * 0.5 && trend === "bearish") {
      ob = true; obZone = { high: c0.high, low: c0.close }; obType = "bearish"; break;
    }
  }
  const obPriceInZone=ob&&obZone&&price15>=obZone.low*0.998&&price15<=obZone.high*1.002;
  const fvgPriceInZone=fvg&&fvgZone&&price15>=fvgZone.low*0.998&&price15<=fvgZone.high*1.002;
  return { trend, fvg, fvgZone, fvgType, fvgPriceInZone, ob, obZone, obType, obPriceInZone,
           ema13:e13, ema200:e200, emaCross,
           liq_sweep:liq15, liq_sweep_dir:liqDir15,
           bos_choch, trendChangeBull, trendChangeBear };
}
function evalCondsLegacy(a, si, htf15=null) {
  if (!a) return Array(10).fill("pending");
  const {price,ema13,ema200,atr,atr_sma,direction:dir,liq_sweep,liq_sweep_dir,
         trend,bos_choch,swing_high:sH,swing_low:sL,
         ema13_pullback_buy,ema13_pullback_sell,confirm_buy,confirm_sell,
         last_pivot_high:lph,last_pivot_low:lpl}=a;
  const o=Array(10).fill("fail");

  // C1. EMA13 Entry Gate: price must be on the correct side of EMA13 before any signal.
  // CALL: market must be above EMA13. PUT: market must be below EMA13.
  // No signal fires until price reclaims EMA13 in the trade direction.
  if (dir==="CALL") o[0]=price>ema13?"pass":"fail";
  else if (dir==="PUT") o[0]=price<ema13?"pass":"fail";
  else o[0]="fail";

  // C. ATR Volatility: ATR between 0.9× and 1.8× of ATR SMA(14)
  if (atr>0&&atr_sma>0) o[1]=atr>=atr_sma*0.9&&atr<=atr_sma*1.8?"pass":"fail";

  // C3. 15m Trend Aligned: 15m HTF trend matches trade direction
  if (htf15) {
    if (dir==="CALL") o[2]=htf15.trend==="bullish"?"pass":htf15.trend==="ranging"?"warn":"fail";
    else if (dir==="PUT") o[2]=htf15.trend==="bearish"?"pass":htf15.trend==="ranging"?"warn":"fail";
    else o[2]="warn";
  } else o[2]="pending";

  // C4. Liquidity Sweep: recent 1m swing point swept in trade direction
  // BUY needs sell-side swept (lows swept = liqDir "low")
  // SELL needs buy-side swept (highs swept = liqDir "high")
  if (dir==="CALL") o[3]=liq_sweep&&liq_sweep_dir==="low"?"pass":liq_sweep?"warn":"fail";
  else if (dir==="PUT") o[3]=liq_sweep&&liq_sweep_dir==="high"?"pass":liq_sweep?"warn":"fail";
  else o[3]=liq_sweep?"warn":"fail";

  // C5. Zone: price in discount (CALL) or premium (SELL) relative to swing midpoint
  const mid = a.midpoint ?? (sH!=null&&sL!=null ? (sH+sL)/2 : null);
  if (dir==="CALL") {
    const inDiscount = mid!=null && price<mid;
    o[4] = inDiscount ? "pass" : price!=null&&mid!=null&&(mid-price)<(mid-sL)*0.3 ? "warn" : "fail";
  } else if (dir==="PUT") {
    const inPremium = mid!=null && price>mid;
    o[4] = inPremium ? "pass" : price!=null&&mid!=null&&(price-mid)<(sH-mid)*0.3 ? "warn" : "fail";
  } else o[4]="warn";

  // C6. EMA Alignment: price AND EMA13 both on correct side of EMA200
  if (ema200) {
    if (dir==="CALL") o[5]=price>ema200&&ema13>ema200?"pass":ema13>ema200?"warn":"fail";
    else if (dir==="PUT") o[5]=price<ema200&&ema13<ema200?"pass":ema13<ema200?"warn":"fail";
    else o[5]="warn";
  } else o[5]="fail"; // < 200 candles — cannot confirm EMA200 alignment, hard fail

  // C7. OB/FVG: price currently inside a 15m Order Block OR Fair Value Gap zone
  if (htf15) {
    if (htf15.obPriceInZone || htf15.fvgPriceInZone) o[6]="pass"; // price in EITHER zone = pass
    else if (htf15.ob || htf15.fvg) o[6]="warn"; // zone exists but price not inside
    else o[6]="fail";
  } else o[6]="pending";

  // C8. R:R ≥ 2.0: actual stop distance (swing pivot ± 0.5×ATR) vs structural target
  // Mirrors computeSLTP so Stratos and evalConds agree on the same SL level
  if (atr>0 && sH!=null && sL!=null) {
    if (dir==="CALL") {
      const actualSL = (lpl!=null&&lpl<price) ? lpl-atr*0.5 : price-atr*0.5;
      const risk   = Math.max(price-actualSL, atr*0.1); // floor at 0.1×ATR
      const reward = sH-price;
      const rr     = reward/risk;
      o[7] = rr>=2?"pass":rr>=1.5?"warn":"fail";
    } else if (dir==="PUT") {
      const actualSL = (lph!=null&&lph>price) ? lph+atr*0.5 : price+atr*0.5;
      const risk   = Math.max(actualSL-price, atr*0.1);
      const reward = price-sL;
      const rr     = reward/risk;
      o[7] = rr>=2?"pass":rr>=1.5?"warn":"fail";
    }
  }

  // C9. EMA13 Micro-Pullback: 1–3 candles pulling back to EMA13 without closing through it
  if (dir==="CALL") o[8]=ema13_pullback_buy?"pass":price>ema13?"warn":"fail";
  else if (dir==="PUT") o[8]=ema13_pullback_sell?"pass":price<ema13?"warn":"fail";

  // C10. Momentum Candle: strong directional body reclaiming EMA13 with rejection wick
  if (dir==="CALL") o[9]=confirm_buy?"pass":"warn";
  else if (dir==="PUT") o[9]=confirm_sell?"pass":"warn";

  return o;
}
// Schema-aware wrapper: dispatches to named evaluators when schema is available,
// falls back to legacy URUS hardcoded logic otherwise.
function evalConds(a, si, htf15=null, schema=null) {
  if (schema?.conditions?.length > 0) {
    return schema.conditions.map(cond => {
      const fn = CONDITION_EVALUATORS[cond.evalRule] ?? CONDITION_EVALUATORS.unknown;
      try { return fn(a, htf15); } catch { return "fail"; }
    });
  }
  return evalCondsLegacy(a, si, htf15);
}
// ── SL/TP Calculator ──────────────────────────────────────────────────────
// SL = previous swing low (BUY) or swing high (SELL); fallback = ATR×0.25
// TPs default to 1R/2R/3R; overridden by valid AI price levels
function computeSLTP(a, aiData=null) {
  if (!a) return {};
  const { price, direction:dir, atr, last_pivot_high:lph, last_pivot_low:lpl } = a;
  if (!dir||dir==="NONE") return {};
  let sl, risk;
  // SL = recent swing low/high ± 0.5×ATR buffer (URUS V1 rule)
  if (dir==="CALL") {
    sl = (lpl!=null && lpl < price) ? lpl - atr*0.5 : price - atr*0.5;
    risk = price - sl;
  } else {
    sl = (lph!=null && lph > price) ? lph + atr*0.5 : price + atr*0.5;
    risk = sl - price;
  }
  if (!risk||risk<=0) risk = atr*0.25 || 0.0001;
  // Default TPs: 1R, 2R, 3R from entry
  let tp1 = dir==="CALL" ? price+risk   : price-risk;
  let tp2 = dir==="CALL" ? price+risk*2 : price-risk*2;
  let tp3 = dir==="CALL" ? price+risk*3 : price-risk*3;
  let aiTP = false;
  // Override with AI TPs only if valid (non-zero, correct side)
  if (aiData) {
    if (dir==="CALL") {
      if (aiData.tp1>price)             { tp1=aiData.tp1; aiTP=true; }
      if (aiData.tp2>price&&aiData.tp2>tp1) { tp2=aiData.tp2; aiTP=true; }
      if (aiData.tp3>price&&aiData.tp3>tp2) { tp3=aiData.tp3; aiTP=true; }
    } else {
      if (aiData.tp1>0&&aiData.tp1<price)              { tp1=aiData.tp1; aiTP=true; }
      if (aiData.tp2>0&&aiData.tp2<price&&aiData.tp2<tp1) { tp2=aiData.tp2; aiTP=true; }
      if (aiData.tp3>0&&aiData.tp3<price&&aiData.tp3<tp2) { tp3=aiData.tp3; aiTP=true; }
    }
  }
  const rr = risk>0 ? Math.round(((dir==="CALL"?tp2-price:price-tp2)/risk)*10)/10 : 0;
  return { sl, risk, tp1, tp2, tp3, rr, aiTP };
}
const btn = (active,accent=C.green) => ({
  background:active?accent+"22":C.panel, border:`1px solid ${active?accent:C.border}`,
  color:active?accent:C.muted, padding:"7px 16px", borderRadius:4, cursor:"pointer",
  fontSize:13, fontFamily:"inherit", fontWeight:700, letterSpacing:1, transition:"all 0.15s",
});

// ══════════════════════════════════════════════════════════════════════════
// TradePopup
// ══════════════════════════════════════════════════════════════════════════
function TradePopup({ a, onClose }) {
  const buy=a.direction==="CALL", acc=buy?C.green:C.red;
  useEffect(()=>{ const t=setTimeout(onClose,30000); return ()=>clearTimeout(t); },[onClose]);
  const hasSLTP = a.sl!=null && !isNaN(a.sl);

  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,0.85)",backdropFilter:"blur(6px)",padding:16}}>
      <div onClick={e=>e.stopPropagation()} style={{background:"#0a1628",border:`2px solid ${acc}`,borderRadius:14,padding:"min(32px, 5vh) min(44px, 5vw)",width:"90%",minWidth:280,maxWidth:520,boxShadow:`0 0 100px ${acc}33`,fontFamily:"'Courier New',monospace",textAlign:"center",position:"relative",animation:"popIn 0.22s ease",maxHeight:"95vh",overflowY:"auto"}}>
        <style>{`@keyframes popIn{from{transform:scale(0.82);opacity:0}to{transform:scale(1);opacity:1}}`}</style>
        <button onClick={onClose} style={{position:"absolute",top:12,right:16,background:"none",border:"none",color:C.muted,fontSize:22,cursor:"pointer",fontFamily:"inherit"}}>✕</button>
        <div style={{fontSize:12,color:C.muted,letterSpacing:3,marginBottom:12}}>🚨 TRADE SIGNAL CONFIRMED</div>
        <div style={{fontSize:58,fontWeight:900,color:acc,lineHeight:1}}>{buy?"📈 BUY":"📉 SELL"}</div>
        <div style={{fontSize:30,fontWeight:700,color:C.text,marginTop:6}}>{a.symbol}</div>
        <div style={{fontSize:24,color:"#94a3b8",marginTop:3}}>ENTRY @ <span style={{color:C.text,fontWeight:700}}>{fmtPrice(a.price)}</span></div>
        {/* Stats grid */}
        {(()=>{
          const statItems=[["CONDITIONS",`${a.passed}/${a.condTotal||10}`],["SESSION",a.session],["ATR",fmt(a.atr,4)],["RSI",fmt(a.rsi,1)],...(a.mlConfidence!=null?[["ML CONF",`${(a.mlConfidence*100).toFixed(0)}%`]]:[])];
          const gridCols=statItems.length<=4?"1fr 1fr":"1fr 1fr 1fr";
          return (
            <div style={{display:"grid",gridTemplateColumns:gridCols,gap:8,marginTop:20}}>
              {statItems.map(([l,v])=>(
                <div key={l} style={{background:C.panel,borderRadius:6,padding:"8px 12px",border:`1px solid ${C.border}`}}>
                  <div style={{fontSize:10,color:C.muted,letterSpacing:1}}>{l}</div>
                  <div style={{fontSize:16,fontWeight:700,color:C.text,marginTop:2}}>{v}</div>
                </div>
              ))}
            </div>
          );
        })()}
        {/* SL/TP block */}
        {hasSLTP ? (
          <div style={{marginTop:14,display:"flex",flexDirection:"column",gap:8}}>
            {/* Stop Loss */}
            <div style={{background:"#ef444418",border:"1px solid #ef444455",borderRadius:8,padding:"10px 16px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{textAlign:"left"}}>
                <div style={{fontSize:10,color:C.muted,letterSpacing:2}}>🛑 STOP LOSS {buy?"(PREV LOW)":"(PREV HIGH)"}</div>
                <div style={{fontSize:22,fontWeight:900,color:C.red,marginTop:2}}>{fmtPrice(a.sl)}</div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:10,color:C.muted,letterSpacing:2}}>RISK</div>
                <div style={{fontSize:16,fontWeight:700,color:"#fca5a5"}}>{fmtPrice(a.risk)} pts</div>
              </div>
            </div>
            {/* TP row */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
              {[[a.tp2,"TP1 (+2R)"],[a.tp3,"TP2 (+3R)"]].map(([tp,label])=>(
                <div key={label} style={{background:"#00d4aa14",border:"1px solid #00d4aa44",borderRadius:8,padding:"8px 10px"}}>
                  <div style={{fontSize:10,color:C.muted,letterSpacing:1}}>{label}</div>
                  <div style={{fontSize:15,fontWeight:700,color:C.green,marginTop:2}}>{fmtPrice(tp)}</div>
                </div>
              ))}
            </div>
            {/* R:R */}
            <div style={{background:C.panel,border:`1px solid ${a.rr>=2?C.green+"55":C.amber+"55"}`,borderRadius:8,padding:"8px 16px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{fontSize:11,color:C.muted,letterSpacing:2}}>R:R RATIO</span>
              <span style={{fontSize:20,fontWeight:900,color:a.rr>=2?C.green:C.amber}}>{fmt(a.rr,1)} : 1</span>
            </div>
            {a.aiTP&&<div style={{fontSize:11,color:C.purple,letterSpacing:2,textAlign:"center"}}>🤖 AI-CALIBRATED TAKE PROFITS</div>}
          </div>
        ) : (
          <div style={{marginTop:14,padding:"10px 14px",background:acc+"18",border:`1px solid ${acc}44`,borderRadius:8,fontSize:13,color:acc}}>
            {buy?"SL = Prev Swing Low  |  TP1 +2R  TP2 +3R":"SL = Prev Swing High  |  TP1 +2R  TP2 +3R"}
          </div>
        )}
        <div style={{marginTop:12,fontSize:11,color:C.dim}}>{a.time} — click outside or wait 30s</div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// N8N Panel
// ══════════════════════════════════════════════════════════════════════════
function N8NPanel({ tools, log, onFetch, onExec, fetching, serverOk, autoTrigger, setAutoTrigger }) {
  const [toolArgs,setToolArgs]=useState({}), [expanded,setExpanded]=useState(null);
  return (
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:16,display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:12}}>
        <div>
          <div style={{fontSize:12,color:C.muted,letterSpacing:2,marginBottom:6}}>N8N MCP SERVER</div>
          <div style={{fontSize:15,color:serverOk?C.green:C.amber,fontWeight:700}}>{serverOk?"✅":"⚠️"} n8n.kara-ai.cloud</div>
          <div style={{fontSize:13,color:"#475569",marginTop:4}}>{tools.length} tool{tools.length!==1?"s":""} • Streamable HTTP</div>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:8,alignItems:"flex-end"}}>
          <button onClick={onFetch} disabled={fetching} style={btn(!fetching)}>{fetching?"⏳ LOADING...":"🔄 FETCH TOOLS"}</button>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:12,color:C.muted}}>Auto-trigger:</span>
            <select value={autoTrigger} onChange={e=>setAutoTrigger(e.target.value)} style={{background:C.bg,border:`1px solid ${C.border}`,color:"#94a3b8",padding:"4px 10px",borderRadius:4,fontSize:13,fontFamily:"inherit"}}>
              <option value="">— disabled —</option>
              {tools.map(t=><option key={t.name} value={t.name}>{t.name}</option>)}
            </select>
          </div>
        </div>
      </div>
      <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:16}}>
        <div style={{fontSize:12,color:C.muted,letterSpacing:2,marginBottom:12}}>TOOLS <span style={{color:C.blue,fontWeight:700}}>({tools.length})</span></div>
        {tools.length===0&&!fetching&&<div style={{textAlign:"center",color:C.dim,padding:"28px 0",fontSize:14}}>Click <strong style={{color:C.green}}>FETCH TOOLS</strong></div>}
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {tools.map(t=>{
            const isE=expanded===t.name, props=t.inputSchema?.properties||{};
            return (
              <div key={t.name} style={{background:C.bg,border:`1px solid ${isE?C.blue+"88":C.border}`,borderRadius:6,padding:"12px 14px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                  <div><div style={{fontSize:15,fontWeight:700,color:C.blue}}>⚙️ {t.name}</div>
                    {t.description&&<div style={{fontSize:12,color:"#475569",marginTop:3}}>{t.description}</div>}</div>
                  <div style={{display:"flex",gap:6,flexShrink:0}}>
                    {Object.keys(props).length>0&&<button onClick={()=>setExpanded(isE?null:t.name)} style={btn(isE,C.blue)}>{isE?"▲":"▼ ARGS"}</button>}
                    <button onClick={()=>onExec(t.name,toolArgs[t.name]||{})} style={btn(true)}>▶ RUN</button>
                  </div>
                </div>
                {isE&&Object.keys(props).length>0&&(
                  <div style={{marginTop:12,borderTop:`1px solid ${C.border}`,paddingTop:12}}>
                    {Object.entries(props).map(([k,s])=>(
                      <div key={k} style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                        <span className="kara-n8n-label" style={{fontSize:12,color:C.muted,width:130,flexShrink:0}}>{k}</span>
                        <input placeholder={s.description||s.type||"value"} value={toolArgs[t.name]?.[k]||""} onChange={e=>setToolArgs(p=>({...p,[t.name]:{...p[t.name],[k]:e.target.value}}))} style={{flex:1,background:C.panel,border:`1px solid ${C.border}`,color:C.text,padding:"5px 10px",borderRadius:4,fontSize:13,fontFamily:"inherit",outline:"none"}}/>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div style={{background:"#020912",border:`1px solid ${C.border}`,borderRadius:8,padding:14}}>
        <div style={{fontSize:12,color:C.muted,letterSpacing:2,marginBottom:8}}>EXECUTION LOG</div>
        <div style={{height:200,overflowY:"auto",fontSize:13,lineHeight:1.8}}>
          {log.length===0&&<div style={{color:C.dim,textAlign:"center",marginTop:30}}>No executions yet</div>}
          {log.map((l,i)=><div key={i} style={{color:l.includes("ERR")?C.red:l.includes("OK")?C.green:C.muted,fontFamily:"'Courier New',monospace"}}>{l}</div>)}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// History Panel
// ══════════════════════════════════════════════════════════════════════════
function HistoryPanel({ history, onClear, onSync, apiFetch }) {
  const [syncing, setSyncing] = React.useState(false);
  const [regime, setRegime] = React.useState(null);

  const handleSync = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      await onSync();
    } finally {
      setSyncing(false);
    }
  };

  React.useEffect(() => {
    if (!apiFetch) return;
    apiFetch("/api/analytics/regime-performance")
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setRegime(d); })
      .catch(() => {});
  }, [apiFetch]);

  // history = allPreds (prediction objects with outcome field, last 60 days)
  const real = history.filter(h => h.executed === true && h.outcome && h.outcome !== "pending");
  
  // ── Session Performance from History ──
  const getSessionFromTs = (ts) => {
    const h = new Date(ts).getUTCHours();
    const t = h * 60;
    const S = [
      {name:"SYDNEY",   open:22*60, close:7*60,  wraps:true },
      {name:"TOKYO",    open:0,     close:9*60,  wraps:false},
      {name:"LONDON",   open:7*60,  close:16*60, wraps:false},
      {name:"NEW YORK", open:13*60, close:22*60, wraps:false},
    ];
    return S.filter(s => s.wraps ? (t>=s.open||t<s.close) : (t>=s.open&&t<s.close)).map(s=>s.name);
  };

  const histSessionStats = {};
  real.forEach(h => {
    const ts = h.timestamp || (h.date ? new Date(h.date + " " + h.time).getTime() : 0);
    if (!ts) return;
    const sessions = getSessionFromTs(ts);
    const isWin = h.outcome === "WIN";
    const pnl = typeof h.pnl === "number" ? h.pnl : 0;
    
    sessions.forEach(sName => {
      if (!histSessionStats[sName]) histSessionStats[sName] = { trades: 0, wins: 0, pnl: 0 };
      histSessionStats[sName].trades++;
      if (isWin) histSessionStats[sName].wins++;
      histSessionStats[sName].pnl += pnl;
    });
  });

  const sessionSummary = Object.entries(histSessionStats).map(([name, s]) => ({
    name, ...s, winRate: s.trades > 0 ? Math.round((s.wins / s.trades) * 100) : 0
  })).sort((a,b) => b.winRate - a.winRate);

  // Real executed trades metrics
  const wins   = real.filter(h=>h.outcome==="WIN").length;
  const losses = real.filter(h=>h.outcome==="LOSS").length;
  const pending= history.filter(h=>h.outcome==="pending").length;
  const wr     = wins+losses>0 ? Math.round(wins/(wins+losses)*100) : null;
  const buys   = real.filter(h=>h.direction==="CALL").length;
  const sells  = real.filter(h=>h.direction==="PUT").length;

  // Monitored (paper) trade metrics
  const monTrades = history.filter(h => h.executed === false && h.outcome && h.outcome !== "pending");
  const monWins   = monTrades.filter(h => h.outcome === "WIN").length;
  const monLosses = monTrades.filter(h => h.outcome === "LOSS").length;
  const monWR     = (monWins + monLosses) > 0 ? Math.round(monWins / (monWins + monLosses) * 100) : null;

  // ML auto-trade performance
  const mlTrades   = real.filter(h => h.source === "ml-auto");
  const mlClosed   = mlTrades.filter(h => h.outcome === "WIN" || h.outcome === "LOSS");
  const mlWins     = mlClosed.filter(h => h.outcome === "WIN").length;
  const mlLosses   = mlClosed.filter(h => h.outcome === "LOSS").length;
  const mlWR       = (mlWins + mlLosses) > 0 ? Math.round(mlWins / (mlWins + mlLosses) * 100) : null;
  const mlPnl      = mlClosed.reduce((s, h) => s + (typeof h.pnl === "number" ? h.pnl : 0), 0);
  const mlAvgConf  = mlTrades.filter(h => h.ml_confidence!=null).length > 0
    ? (mlTrades.filter(h => h.ml_confidence!=null).reduce((s, h) => s + h.ml_confidence, 0) / mlTrades.filter(h => h.ml_confidence!=null).length * 100).toFixed(1)
    : null;

  // ── Period aggregation stats ──────────────────────────────────────────────
  const nowMs = Date.now();
  const ms7d  = 7  * 24 * 60 * 60 * 1000;
  const ms30d = 30 * 24 * 60 * 60 * 1000;

  const closeTs = h => {
    const raw = h.sell_time || h.exit_time || h.timestamp || 0;
    if (!raw) return 0;
    return (typeof raw === "number" && raw < 1e12) ? raw * 1000 : (typeof raw === "number" ? raw : new Date(raw).getTime());
  };

  const nowDate   = new Date();
  // Jamaica-calendar boundaries (UTC-5, no DST) so this panel's Today/Week/Month
  // figures agree with the header's accountInfo.dailyPnl/weeklyPnl/monthlyPnl.
  const todayStartMs   = jamaicaDayStartMs(nowDate);
  const weekStartMs    = jamaicaWeekStartMs(nowDate);
  const monthStartMs   = jamaicaMonthStartMs(nowDate);

  const inWindow = (h, ms) => {
    const ts = closeTs(h);
    return ts > 0 && nowMs - ts <= ms;
  };
  const pnlOf = h => typeof h.pnl === "number" ? h.pnl : 0;

  // Equity curve — cumulative PnL across all settled real trades, oldest first
  const equityCurvePoints = [...real].sort((a,b) => closeTs(a) - closeTs(b))
    .reduce((acc, h) => { acc.push((acc.length ? acc[acc.length-1] : 0) + pnlOf(h)); return acc; }, []);

  const pnlToday   = real.filter(h => closeTs(h) >= todayStartMs).reduce((s,h) => s + pnlOf(h), 0);
  const pnlWeek    = real.filter(h => closeTs(h) >= weekStartMs).reduce((s,h) => s + pnlOf(h), 0);
  const pnlMonth   = real.filter(h => closeTs(h) >= monthStartMs).reduce((s,h) => s + pnlOf(h), 0);
  const pnl7d  = real.filter(h => inWindow(h, ms7d)).reduce((s,h) => s + pnlOf(h), 0);
  const pnl30d = real.filter(h => inWindow(h, ms30d)).reduce((s,h) => s + pnlOf(h), 0);

  const wonTrades  = real.filter(h => h.outcome === "WIN");
  const lostTrades = real.filter(h => h.outcome === "LOSS");
  const grossProfit = wonTrades.reduce((s,h) => s + pnlOf(h), 0);
  const grossLoss   = Math.abs(lostTrades.reduce((s,h) => s + pnlOf(h), 0));
  const profitFactor = grossLoss > 0 ? (grossProfit / grossLoss).toFixed(2) : grossProfit > 0 ? "∞" : "—";
  const avgWin  = wonTrades.length  > 0 ? (grossProfit / wonTrades.length).toFixed(2)  : null;
  const avgLoss = lostTrades.length > 0 ? (grossLoss   / lostTrades.length).toFixed(2) : null;

  let streak = 0, streakType = null;
  for (const h of real) {
    if (h.outcome === "WIN" || h.outcome === "LOSS") {
      if (streakType === null) { streakType = h.outcome; streak = 1; }
      else if (h.outcome === streakType) { streak++; }
      else break;
    }
  }

  const exportCSV=()=>{
    const rows=[
      ["Date","Time","Symbol","Direction","TF","Entry","SL","TP1","TP2","TP3","Conditions","Outcome","OutcomePrice","OutcomeTime","15mTrend","Executed","Source","ML %"],
      ...history.map(h=>[
        h.date,h.time,h.symbol,h.direction,h.entry_tf||"—",
        fmt(h.entry??h.price,5),fmt(h.sl,5),fmt(h.tp1,5),fmt(h.tp2,5),fmt(h.tp3,5),
        h.passed,h.outcome,fmt(h.outcomePrice,5),h.outcomeTime||"",
        h.htf15?.trend||h.session||"",
        h.executed===false?"NO":"YES",
        h.source||(h.executed===false?"monitored":"auto"),
        h.ml_confidence!=null ? (h.ml_confidence*100).toFixed(1) : ""
      ])
    ];
    const blob=new Blob([rows.map(r=>r.join(",")).join("\n")],{type:"text/csv"});
    const a=Object.assign(document.createElement("a"),{href:URL.createObjectURL(blob),download:`amber_history_${Date.now()}.csv`});
    a.click(); URL.revokeObjectURL(a.href);
  };

  const byDate = {};
  history.forEach(h => {
    const ts = h.timestamp || (h.date ? new Date(h.date + " " + h.time).getTime() : 0);
    const ja = toJamaica(typeof ts === 'number' && ts < 1e12 ? ts * 1000 : ts);
    const jaDate = formatJaDate(ja);
    const jaTime = formatJaTime(ja);
    const hJa = { ...h, jaDate, jaTime };
    if (!byDate[jaDate]) byDate[jaDate] = [];
    byDate[jaDate].push(hJa);
  });
  const sortedDates = Object.keys(byDate).sort((a,b) => new Date(b) - new Date(a));

  const historyHourly = {};
  history.forEach(h => {
    if (h.outcome !== "WIN" && h.outcome !== "LOSS") return;
    const ts = h.timestamp || (h.date ? new Date(h.date + " " + h.time).getTime() : 0);
    if (!ts) return;
    const ja = toJamaica(typeof ts === 'number' && ts < 1e12 ? ts * 1000 : ts);
    const hour = ja.getHours();
    if (!historyHourly[hour]) historyHourly[hour] = { trades: 0, wins: 0 };
    historyHourly[hour].trades++;
    if (h.outcome === "WIN") historyHourly[hour].wins++;
  });

  Object.keys(historyHourly).forEach(h => {
    historyHourly[h].winRate = Math.round((historyHourly[h].wins / historyHourly[h].trades) * 100);
  });

  return (
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      {/* Stats row */}
      <div className="kara-history-stats" style={{display:"grid",gridTemplateColumns:"repeat(9,1fr)",gap:10}}>
        {[
          ["SIGNALS",history.length,C.text,null],
          ["📈 BUY",buys,C.green,null],
          ["📉 SELL",sells,C.red,null],
          ["✅ WIN",wins,C.green,null],
          ["❌ LOSS",losses,C.red,null],
          ["⏳ PENDING",pending,pending>0?C.amber:C.muted,"3px solid #f59e0b44"],
          ["WIN RATE",wr!==null?`${wr}%`:"—",wr>=60?C.green:wr>=40?C.amber:wr!==null?C.red:C.muted,null],
          ["👁 MON WR",monWR!==null?`${monWR}%`:"—",monWR>=60?C.green:monWR>=40?C.amber:monWR!==null?C.red:C.muted,"3px solid #7c3aed44"],
          ["🧠 ML WR",mlWR!==null?`${mlWR}%`:"—",mlWR>=60?C.green:mlWR>=40?C.amber:mlWR!==null?C.red:"#06b6d4","3px solid #06b6d444"],
        ].map(([l,v,c,bl])=>(
          <div key={l} style={{background:C.panel,border:`1px solid ${bl?c+"44":C.border}`,borderRadius:6,padding:"12px 10px",textAlign:"center",borderLeft:bl||undefined}}>
            <div style={{fontSize:10,color:l==="👁 MON WR"?"#a78bfa":l==="🧠 ML WR"?"#06b6d4":C.muted,letterSpacing:2}}>{l}</div>
            <div style={{fontSize:22,fontWeight:900,color:c,marginTop:4}}>{v}</div>
          </div>
        ))}
      </div>

      {/* ── Historical Session Performance ── */}
      {sessionSummary.length > 0 && (
        <div style={{background:C.panel, border:`1px solid ${C.border}`, borderRadius:8, padding:16}}>
          <div style={{fontSize:12, color:C.muted, letterSpacing:2, marginBottom:12}}>🌍 HISTORICAL SESSION PERFORMANCE</div>
          <div style={{display:"flex", gap:10, flexWrap:"wrap"}}>
            {sessionSummary.map(s => (
              <div key={s.name} style={{flex:"1 1 120px", background:C.bg, borderRadius:6, padding:12, border:`1px solid ${s.winRate >= 60 ? C.green+'44' : C.border}`}}>
                <div style={{fontSize:10, color:C.muted, fontWeight:700}}>{s.name}</div>
                <div style={{fontSize:18, fontWeight:900, color:s.winRate >= 60 ? C.green : s.winRate >= 45 ? C.amber : C.red}}>{s.winRate}% WR</div>
                <div style={{fontSize:9, color:C.dim, marginTop:4}}>{s.trades} trades &nbsp;•&nbsp; {s.pnl >= 0 ? "+" : ""}{s.pnl.toFixed(2)} P&L</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Hourly Performance Breakdown */}
      {Object.keys(historyHourly).length > 0 && (
        <div style={{background:C.panel, border:`1px solid ${C.border}`, borderRadius:8, padding:16}}>
          <div style={{fontSize:12, color:C.muted, letterSpacing:2, marginBottom:12}}>🕒 HOURLY PERFORMANCE (REAL DATA)</div>
          <div style={{display:"flex", gap:8, overflowX:"auto", paddingBottom:8, scrollbarWidth:"none"}}>
            {Object.entries(historyHourly).sort((a,b) => b[1].winRate - a[1].winRate).map(([hour, stats]) => (
              <div key={hour} style={{flex:"0 0 80px", background:C.bg, border:`1px solid ${stats.winRate >= 60 ? C.green+'44' : C.border}`, borderRadius:6, padding:10, textAlign:"center"}}>
                <div style={{fontSize:14, fontWeight:900, color:C.amber}}>{hour.padStart(2,'0')}:00</div>
                <div style={{fontSize:11, fontWeight:700, color:stats.winRate >= 60 ? C.green : stats.winRate >= 45 ? C.amber : C.red, marginTop:4}}>{stats.winRate}%</div>
                <div style={{fontSize:9, color:C.dim, marginTop:2}}>{stats.trades} trades</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Win-rate by market regime (trend / session / ATR volatility) */}
      {regime && regime.matched_trades > 0 && (
        <div style={{background:C.panel, border:`1px solid ${C.border}`, borderRadius:8, padding:16}}>
          <div style={{fontSize:12, color:C.muted, letterSpacing:2, marginBottom:4}}>📊 WIN RATE BY MARKET REGIME</div>
          <div style={{fontSize:9, color:C.dim, marginBottom:12}}>
            {regime.matched_trades} of {regime.matched_trades + regime.unmatched_trades} trades matched to signal context (last {regime.lookback_days}d)
          </div>
          {[
            ["By Trend", regime.by_trend, r => r.key],
            ["By Session", regime.by_session, r => r.key],
            ["By ATR Volatility", regime.by_atr_tercile, r => r.tercile],
          ].map(([label, rows, keyFn]) => rows && rows.length > 0 && (
            <div key={label} style={{marginBottom:14}}>
              <div style={{fontSize:10, color:C.amber, letterSpacing:1, marginBottom:8}}>{label.toUpperCase()}</div>
              <div style={{display:"flex", gap:8, overflowX:"auto", paddingBottom:4, scrollbarWidth:"none"}}>
                {rows.map(r => (
                  <div key={keyFn(r)} style={{flex:"0 0 110px", background:C.bg, border:`1px solid ${r.win_rate>=60?C.green+'44':C.border}`, borderRadius:6, padding:10, textAlign:"center"}}>
                    <div style={{fontSize:11, fontWeight:900, color:C.text, textTransform:"capitalize"}}>{keyFn(r)}</div>
                    <div style={{fontSize:14, fontWeight:700, color:r.win_rate>=60?C.green:r.win_rate>=45?C.amber:C.red, marginTop:4}}>{r.win_rate!=null?`${r.win_rate}%`:"—"}</div>
                    <div style={{fontSize:9, color:C.dim, marginTop:2}}>{r.trades} trades</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Period aggregation stats */}
      {history.length > 0 && (
        <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:"12px 14px"}}>
          <div style={{fontSize:10,color:C.muted,letterSpacing:2,marginBottom:10}}>P&L SUMMARY (CLOSED REAL TRADES)</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:10}}>
            {[
              ["Today",   pnlToday],
              ["This Week",  pnlWeek],
              ["This Month", pnlMonth],
            ].map(([label, val]) => (
              <div key={label} style={{textAlign:"center",background:val>=0?"#052e1622":"#2e051622",borderRadius:6,padding:"8px 6px",border:`1px solid ${val>=0?C.green+"44":C.red+"44"}`}}>
                <div style={{fontSize:9,color:C.muted,letterSpacing:1,marginBottom:3,textTransform:"uppercase"}}>{label}</div>
                <div style={{fontSize:16,fontWeight:900,color:val>=0?C.green:C.red}}>{val>=0?"+":""}{val.toFixed(2)}</div>
              </div>
            ))}
          </div>
          <div style={{fontSize:10,color:C.muted,letterSpacing:2,marginBottom:8}}>ROLLING WINDOWS & ANALYTICS</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(100px,1fr))",gap:8}}>
            {[
              ["7-day PnL",   `${pnl7d>=0?"+":""}$${pnl7d.toFixed(2)}`,  pnl7d>=0?C.green:C.red],
              ["30-day PnL",  `${pnl30d>=0?"+":""}$${pnl30d.toFixed(2)}`, pnl30d>=0?C.green:C.red],
              ["Profit Factor", profitFactor, parseFloat(profitFactor)>=1.5?C.green:parseFloat(profitFactor)>=1?C.amber:C.red],
              ["Avg Win",  avgWin  !== null ? `$${avgWin}`  : "—", C.green],
              ["Avg Loss", avgLoss !== null ? `$${avgLoss}` : "—", C.red],
              ["Streak", streak > 0 ? `${streak} ${streakType === "WIN" ? "W" : "L"}` : "—",
               streakType === "WIN" ? C.green : streakType === "LOSS" ? C.red : C.muted],
            ].map(([label, val, col]) => (
              <div key={label} style={{textAlign:"center",background:C.bg,borderRadius:6,padding:"8px 6px"}}>
                <div style={{fontSize:9,color:C.muted,letterSpacing:1,marginBottom:3,textTransform:"uppercase"}}>{label}</div>
                <div style={{fontSize:14,fontWeight:700,color:col}}>{val}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Equity curve — cumulative PnL across all settled real trades */}
      {equityCurvePoints.length >= 2 && (
        <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:"12px 14px"}}>
          <div style={{fontSize:10,color:C.amber,letterSpacing:2,marginBottom:10}}>EQUITY CURVE (ALL REAL TRADES)</div>
          <EquityCurve curve={equityCurvePoints} initial={0} height={160} C={{...C, faint: C.border}} />
        </div>
      )}

      {/* ML Service Performance block */}
      {mlTrades.length > 0 && (
        <div style={{background:C.panel,border:`1px solid #06b6d444`,borderRadius:8,padding:"12px 14px",borderLeft:"3px solid #06b6d4"}}>
          <div style={{fontSize:10,color:"#06b6d4",letterSpacing:2,marginBottom:10}}>🧠 ML AUTO-TRADE PERFORMANCE</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(100px,1fr))",gap:8}}>
            {[
              ["Total Trades", mlTrades.length, "#06b6d4"],
              ["Closed", mlClosed.length, C.muted],
              ["Wins", mlWins, C.green],
              ["Losses", mlLosses, C.red],
              ["Win Rate", mlWR!==null?`${mlWR}%`:"—", mlWR>=60?C.green:mlWR>=40?C.amber:mlWR!==null?C.red:"#06b6d4"],
              ["Net PnL", `${mlPnl>=0?"+":""}$${mlPnl.toFixed(2)}`, mlPnl>=0?C.green:C.red],
              ["Avg Conf", mlAvgConf!==null?`${mlAvgConf}%`:"—", "#06b6d4"],
            ].map(([label,val,col])=>(
              <div key={label} style={{textAlign:"center",background:C.bg,borderRadius:6,padding:"8px 6px",border:"1px solid #06b6d422"}}>
                <div style={{fontSize:9,color:"#06b6d4",letterSpacing:1,marginBottom:3,textTransform:"uppercase"}}>{label}</div>
                <div style={{fontSize:14,fontWeight:700,color:col}}>{val}</div>
              </div>
            ))}
          </div>
          {mlAvgConf!==null&&<div style={{fontSize:10,color:C.dim,marginTop:8}}>Avg ML confidence across all ML auto-trades. Higher confidence = stronger model conviction at time of trade.</div>}
        </div>
      )}

      {/* 2-month window notice + actions */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
        <div style={{fontSize:11,color:C.dim}}>
          Last 60 days &nbsp;•&nbsp; {pending} pending outcome{pending!==1?"s":""} &nbsp;•&nbsp; {history.length} total signals
        </div>
        <div style={{display:"flex",gap:8}}>
          <button onClick={handleSync} disabled={syncing} style={btn(true, syncing ? C.muted : C.amber)}>
            {syncing ? "⏳ SYNCING..." : "🔄 SYNC"}
          </button>
          <button onClick={exportCSV} disabled={!history.length} style={btn(!!history.length,C.blue)}>📥 EXPORT CSV</button>
          <button onClick={onClear} disabled={!history.length} style={btn(!!history.length,C.red)}>🗑 CLEAR</button>
        </div>
      </div>

      {/* Grouped by date */}
      {!history.length&&(
        <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:"40px 0",textAlign:"center",color:C.dim,fontSize:14}}>
          <div style={{fontSize:32,marginBottom:12}}>📊</div>
          No signals recorded yet<br/>
          <span style={{fontSize:12}}>Signals appear here when all strategy conditions pass</span>
        </div>
      )}
      {sortedDates.map(date=>{
        const rows=byDate[date];
        const dReal=rows.filter(r=>r.executed===true && r.outcome && r.outcome!=="pending");
        const dWins=dReal.filter(r=>r.outcome==="WIN").length;
        const dLoss=dReal.filter(r=>r.outcome==="LOSS").length;
        const dWR=dWins+dLoss>0?Math.round(dWins/(dWins+dLoss)*100):null;
        const dPnl=dReal.reduce((s,r)=>s+pnlOf(r),0);
        return (
          <div key={date} style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,overflow:"hidden"}}>
            {/* Date header */}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 16px",background:C.bg,borderBottom:`1px solid ${C.border}`}}>
              <div style={{fontSize:12,fontWeight:700,color:C.amber,letterSpacing:1}}>{date}</div>
              <div style={{display:"flex",gap:10,alignItems:"center",fontSize:11}}>
                <span style={{color:C.green}}>{dWins}W</span>
                <span style={{color:C.red}}>{dLoss}L</span>
                {dWR!==null&&<span style={{fontWeight:700,color:dWR>=60?C.green:dWR>=40?C.amber:C.red,background:dWR>=60?"#022c22":dWR>=40?"#1a1200":"#1a0000",padding:"1px 8px",borderRadius:3}}>WR {dWR}%</span>}
                {dReal.length>0&&<span style={{fontWeight:700,color:dPnl>=0?C.green:C.red}}>{dPnl>=0?"+":""}{dPnl.toFixed(2)}</span>}
                <span style={{color:C.dim}}>{rows.length} signal{rows.length!==1?"s":""}</span>
              </div>
            </div>
            {/* Column headers + rows — horizontal scroll on small screens */}
            <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
            <div style={{display:"grid",gridTemplateColumns:"75px 75px 80px 100px 90px 90px 45px 70px 80px",minWidth:730,padding:"6px 16px",borderBottom:`1px solid ${C.border}`,fontSize:10,color:C.muted,letterSpacing:1,gap:4}}>
              {["TIME","SYMBOL","DIR","ENTRY","SL","TP1","TF","RESULT","SOURCE"].map(h=><div key={h}>{h}</div>)}
            </div>
            {/* Signal rows */}
            {rows.map((h,i)=>{
              const buy=h.direction==="CALL";
              const oc=h.outcome;
              const ocCol=oc==="WIN"?C.green:oc==="LOSS"?C.red:C.muted;
              const ocLabel=oc==="WIN"?"✅ WIN":oc==="LOSS"?"❌ LOSS":"⏳";
              const isMonitored=h.executed===false;
              const isChart=!isMonitored&&h.source==="chart";
              return (
                <div key={h.id||i} style={{display:"grid",gridTemplateColumns:"75px 75px 80px 100px 90px 90px 45px 70px 80px",minWidth:730,padding:"8px 16px",borderBottom:`1px solid ${C.bg}`,fontSize:12,gap:4,background:oc==="WIN"?"#011a11":oc==="LOSS"?"#1a0000":i%2===0?C.panel:"#0a1726",borderLeft:isMonitored?"3px solid #7c3aed":"3px solid transparent"}}>
                  <div style={{color:C.muted,fontSize:11}}>{h.jaTime || h.time}</div>
                  <div style={{color:"#94a3b8",fontWeight:700}}>{h.symbol}</div>
                  <div style={{color:buy?C.green:C.red,fontWeight:700}}>{buy?"📈 BUY":"📉 SELL"}</div>
                  <div style={{color:C.text}}>{fmtPrice(h.entry??h.price)}</div>
                  <div style={{color:C.red,fontSize:11}}>{h.sl?fmtPrice(h.sl):"—"}</div>
                  <div style={{color:C.green,fontSize:11}}>{h.tp1?fmtPrice(h.tp1):"—"}</div>
                  <div style={{color:C.amber,fontSize:10,fontWeight:700}}>{h.entry_tf||"—"}</div>
                  <div style={{color:ocCol,fontWeight:700}}>{ocLabel}</div>
                  <div style={{fontSize:10}}>
                    {isMonitored
                      ? <span style={{padding:"1px 5px",borderRadius:3,background:"#1a1a2e",border:"1px solid #7c3aed55",color:"#a78bfa"}}>👁 MONITORED</span>
                      : h.source==="ml-auto"
                      ? (
                        <div style={{display:"flex",flexDirection:"column",gap:2}}>
                          <span style={{padding:"1px 5px",borderRadius:3,background:"#083344",border:"1px solid #06b6d444",color:"#06b6d4",fontWeight:700}}>🧠 ML AUTO</span>
                          {h.ml_confidence && <span style={{fontSize:9,color:"#06b6d4",marginLeft:4}}>{(h.ml_confidence*100).toFixed(0)}% conf</span>}
                        </div>
                      )
                      : isChart
                      ? <span style={{padding:"1px 5px",borderRadius:3,background:"#1e3a5f",border:"1px solid #2563eb44",color:"#60a5fa"}}>📊 CHART</span>
                      : <span style={{padding:"1px 5px",borderRadius:3,background:"#1a1200",border:"1px solid #a16207aa",color:"#fbbf24"}}>🤖 AUTO</span>
                    }
                  </div>
                </div>
              );
            })}
            </div>{/* end overflowX scroll wrapper */}
          </div>
        );
      })}
    </div>
  );
}

const MemoHistoryPanel = React.memo(HistoryPanel, (prevProps, nextProps) => {
  if (prevProps.history.length !== nextProps.history.length) return false;
  if (prevProps.onClear !== nextProps.onClear) return false;
  for (let i = 0; i < prevProps.history.length; i++) {
    if (prevProps.history[i] !== nextProps.history[i]) return false;
  }
  return true;
});

// ══════════════════════════════════════════════════════════════════════════
// Active Trades Panel
// ══════════════════════════════════════════════════════════════════════════
function ActiveTradesPanel({ trades, onClose, onRefresh }) {
  const [closing, setClosing] = React.useState({});
  const [collapsed, setCollapsed] = React.useState(false);

  const handleClose = async (contractId) => {
    setClosing(p => ({...p, [contractId]: true}));
    await onClose(contractId);
    setClosing(p => ({...p, [contractId]: false}));
  };

  const fmtAge = (purchaseTime) => {
    if (!purchaseTime) return "—";
    const sec = Math.floor(Date.now()/1000) - purchaseTime;
    if (sec < 60) return `${sec}s`;
    if (sec < 3600) return `${Math.floor(sec/60)}m ${sec%60}s`;
    return `${Math.floor(sec/3600)}h ${Math.floor((sec%3600)/60)}m`;
  };

  return (
    <div style={{background:C.panel,border:`1px solid ${C.amber}44`,borderRadius:8,overflow:"hidden",marginBottom:12}}>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 14px",background:"#0a1628",borderBottom:collapsed?`1px solid ${C.border}`:"none",cursor:"pointer"}} onClick={()=>setCollapsed(p=>!p)}>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <span style={{fontSize:12,fontWeight:700,color:C.amber,letterSpacing:2}}>📂 ACTIVE TRADES</span>
          <span style={{background:`${C.amber}33`,border:`1px solid ${C.amber}55`,color:C.amber,borderRadius:10,padding:"1px 8px",fontSize:11,fontWeight:700}}>{trades.length}</span>
        </div>
        <div style={{display:"flex",gap:6,alignItems:"center"}}>
          <button onClick={e=>{e.stopPropagation();onRefresh();}} style={{background:"transparent",border:`1px solid ${C.border}`,color:C.muted,borderRadius:4,padding:"2px 8px",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>↻ Refresh</button>
          <span style={{color:C.muted,fontSize:12}}>{collapsed?"▼":"▲"}</span>
        </div>
      </div>

      {!collapsed && (
        trades.length === 0
          ? <div style={{padding:"20px 14px",textAlign:"center",color:C.dim,fontSize:13}}>No open trades</div>
          : (
            <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
              {/* Column headers */}
              <div style={{display:"grid",gridTemplateColumns:"90px 70px 90px 90px 70px 80px",minWidth:490,padding:"6px 14px",borderBottom:`1px solid ${C.border}`,fontSize:10,color:C.muted,letterSpacing:1,gap:6}}>
                {["SYMBOL","DIR","BUY PRICE","P&L","AGE","CANCEL"].map(h=><div key={h}>{h}</div>)}
              </div>
              {trades.map((t, i) => {
                const isBuy = (t.contract_type||"").toUpperCase()==="CALL";
                const pnl = t._settled ? t.pnl : (t.pnl ?? null);
                const pnlCol = pnl===null?C.muted:pnl>0?C.green:pnl<0?C.red:C.muted;
                const pnlLabel = pnl===null?"—":`${pnl>=0?"+":""}${Number(pnl).toFixed(2)}`;
                const isAutoTrade = (t.source||"").includes("auto");
                const isSettled = !!t._settled;
                const settledOutcome = t._outcome; // "WIN" or "LOSS"
                const leftBorderStyle = isSettled
                  ? `3px solid ${settledOutcome==="WIN"?C.green:C.red}`
                  : isAutoTrade ? `3px solid ${C.amber}` : `3px solid transparent`;
                return (
                  <div key={t.contract_id||i} style={{display:"grid",gridTemplateColumns:"90px 70px 90px 90px 70px 80px",minWidth:490,padding:"7px 14px",borderBottom:`1px solid ${C.bg}`,borderLeft:leftBorderStyle,fontSize:12,gap:6,alignItems:"center",background:isSettled?(settledOutcome==="WIN"?"#001a0a":"#1a0005"):(i%2===0?C.panel:"#0a1726"),opacity:isSettled?0.8:1,transition:"all 0.3s"}}>
                    <div style={{color:C.text,fontWeight:700}}>{isAutoTrade&&!isSettled?"🤖 ":""}{isSettled?(settledOutcome==="WIN"?"✅ ":"❌ "):""}{t.symbol||"—"}</div>
                    <div style={{color:isBuy?C.green:C.red,fontWeight:700}}>{isBuy?"📈 BUY":"📉 SELL"}</div>
                    <div style={{color:C.text}}>${(t.buy_price||0).toFixed(2)}</div>
                    <div style={{color:pnlCol,fontWeight:700}}>{pnlLabel}</div>
                    <div style={{color:C.muted,fontSize:11}}>{isSettled?<span style={{color:settledOutcome==="WIN"?C.green:C.red,fontWeight:700}}>{settledOutcome}</span>:fmtAge(t.purchase_time)}</div>
                    <div>
                      {isSettled
                        ? <span style={{fontSize:11,color:C.muted}}>Settled</span>
                        : <button
                            disabled={closing[t.contract_id]}
                            onClick={()=>handleClose(t.contract_id)}
                            style={{background:closing[t.contract_id]?"#1a0000":`${C.red}22`,border:`1px solid ${C.red}88`,color:closing[t.contract_id]?C.muted:C.red,borderRadius:4,padding:"3px 10px",fontSize:11,cursor:closing[t.contract_id]?"not-allowed":"pointer",fontFamily:"inherit",fontWeight:700,transition:"all 0.15s"}}
                          >{closing[t.contract_id]?"…":"✕ CLOSE"}</button>
                      }
                    </div>
                  </div>
                );
              })}
            </div>
          )
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Docker MCP Panel
// ══════════════════════════════════════════════════════════════════════════
function DockerMCPPanel({ tools, log, onFetch, onCall, fetching, connected }) {
  const [toolArgs,setToolArgs]=useState({}), [expanded,setExpanded]=useState(null);
  return (
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      {/* Status card */}
      <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:16,display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:12}}>
        <div>
          <div style={{fontSize:12,color:C.muted,letterSpacing:2,marginBottom:6}}>🔌 KARA MCP HUB</div>
          <div style={{fontSize:15,color:connected?C.green:C.amber,fontWeight:700}}>{connected?`✅ Connected — ${tools.length} tools ready`:"⚠️ Click FETCH TOOLS to load"}</div>
          <div style={{fontSize:12,color:"#475569",marginTop:3}}>Brave Search &nbsp;•&nbsp; Deriv API &nbsp;•&nbsp; Telegram</div>
          <div style={{fontSize:11,color:C.dim,marginTop:4}}>Unified MCP tool hub — all servers combined</div>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:8,alignItems:"flex-end"}}>
          <button onClick={onFetch} disabled={fetching} style={btn(!fetching,C.blue)}>{fetching?"⏳ LOADING...":"🔄 FETCH TOOLS"}</button>
        </div>
      </div>

      {/* Tools list */}
      <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:16}}>
        <div style={{fontSize:12,color:C.muted,letterSpacing:2,marginBottom:12}}>MCP TOOLS <span style={{color:C.blue,fontWeight:700}}>({tools.length})</span></div>
        {tools.length===0&&!fetching&&(
          <div style={{textAlign:"center",color:C.dim,padding:"28px 0",fontSize:14}}>
            <div style={{fontSize:28,marginBottom:8}}>🔌</div>
            Click <strong style={{color:C.blue}}>FETCH TOOLS</strong> to load all MCP tools<br/>
            <span style={{fontSize:12}}>Brave Search + Deriv API + Telegram (16 tools)</span>
          </div>
        )}
        {fetching&&<div style={{textAlign:"center",color:C.amber,padding:"28px 0",fontSize:14}}>⏳ Loading MCP tools...</div>}
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {tools.map(t=>{
            const isE=expanded===t.name;
            const props=t.inputSchema?.properties||{};
            return (
              <div key={t.name} style={{background:C.bg,border:`1px solid ${isE?C.blue+"88":C.border}`,borderRadius:6,padding:"12px 14px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:14,fontWeight:700,color:C.blue}}>🔧 {t.name}</div>
                    {t.description&&<div style={{fontSize:11,color:"#475569",marginTop:3,wordBreak:"break-word"}}>{t.description}</div>}
                  </div>
                  <div style={{display:"flex",gap:6,flexShrink:0}}>
                    {Object.keys(props).length>0&&<button onClick={()=>setExpanded(isE?null:t.name)} style={btn(isE,C.blue)}>{isE?"▲":"▼ ARGS"}</button>}
                    <button onClick={()=>onCall(t.name,toolArgs[t.name]||{})} style={btn(true)}>▶ RUN</button>
                  </div>
                </div>
                {isE&&Object.keys(props).length>0&&(
                  <div style={{marginTop:12,borderTop:`1px solid ${C.border}`,paddingTop:12}}>
                    {Object.entries(props).map(([k,s])=>(
                      <div key={k} style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                        <span style={{fontSize:12,color:C.muted,width:130,flexShrink:0}}>{k}{t.inputSchema?.required?.includes(k)?"*":""}</span>
                        <input placeholder={s.description||s.type||"value"} value={toolArgs[t.name]?.[k]||""} onChange={e=>setToolArgs(p=>({...p,[t.name]:{...p[t.name],[k]:e.target.value}}))} style={{flex:1,background:C.panel,border:`1px solid ${C.border}`,color:C.text,padding:"5px 10px",borderRadius:4,fontSize:13,fontFamily:"inherit",outline:"none"}}/>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Execution log */}
      <div style={{background:"#020912",border:`1px solid ${C.border}`,borderRadius:8,padding:14}}>
        <div style={{fontSize:12,color:C.muted,letterSpacing:2,marginBottom:8}}>EXECUTION LOG</div>
        <div style={{height:200,overflowY:"auto",fontSize:12,lineHeight:1.8}}>
          {log.length===0&&<div style={{color:C.dim,textAlign:"center",marginTop:30}}>No tool calls yet — run a tool above or ask Kara to call one</div>}
          {log.map((l,i)=><div key={i} style={{color:l.includes("❌")?"#ef4444":l.includes("✅")?C.green:C.muted,fontFamily:"'Courier New',monospace",wordBreak:"break-all"}}>{l}</div>)}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Alerts Widget — market event notifications (separated from chat)
// ══════════════════════════════════════════════════════════════════════════
function AlertsWidget({ messages, alertVoice, onToggleVoice }) {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const msgsEnd = useRef(null);
  const prevLen = useRef(0);

  useEffect(()=>{
    if (messages.length > prevLen.current) {
      const newMsg = messages[messages.length-1];
      const isImportant = newMsg && /SIGNAL|AUTO-TRADE|STRATOS|BLOCKED|APPROVED|DETECTED/i.test(newMsg.content);
      if (open) msgsEnd.current?.scrollIntoView({behavior:"smooth"});
      else {
        setUnread(u=>u+(messages.length-prevLen.current));
        // Auto-open disabled — alerts now only show badge notification
      }
    }
    prevLen.current = messages.length;
  },[messages.length, open]);

  useEffect(()=>{ if (open) setUnread(0); },[open]);

  const alertBorder = content => {
    if (/SIGNAL CONFIRMED|10\/10|10 of 10/i.test(content)) return "#22c55e";
    if (/Liq Sweep/i.test(content)) return "#f97316";
    if (/BOS|CHOCH/i.test(content)) return "#a855f7";
    if (/Flip/i.test(content)) return "#a855f7";
    if (/15m HTF/i.test(content)) return "#3b82f6";
    if (/Setup forming/i.test(content)) return "#eab308";
    return "#334155";
  };

  return (
    <div className="kara-alerts-widget" style={{position:"fixed",bottom:90,left:20,zIndex:9000,display:"flex",flexDirection:"column",alignItems:"flex-start"}}>
      {open&&(
        <div style={{width:340,maxHeight:430,display:"flex",flexDirection:"column",background:"#08111e",border:"1px solid #1e293b",borderRadius:14,boxShadow:"0 8px 40px #0009",marginBottom:10,overflow:"hidden",fontFamily:"'Courier New',monospace"}}>
          {/* Header */}
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",borderBottom:"1px solid #1e293b",background:"#0a0f1e",flexShrink:0}}>
            <span style={{fontWeight:900,fontSize:13,color:"#f97316",letterSpacing:2}}>📡 MARKET ALERTS</span>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <button onClick={onToggleVoice} title={alertVoice?"Mute alert voice":"Enable alert voice"} style={{background:"transparent",border:"none",cursor:"pointer",fontSize:16,color:alertVoice?"#22c55e":"#475569",padding:"2px 6px",borderRadius:4,transition:"color 0.15s"}}>
                {alertVoice?"🔊":"🔇"}
              </button>
              <button onClick={()=>setOpen(false)} style={{background:"transparent",border:"none",cursor:"pointer",color:"#475569",fontSize:20,lineHeight:1,padding:"0 2px"}}>×</button>
            </div>
          </div>
          {/* Messages */}
          <div style={{flex:1,overflowY:"auto",padding:"10px 10px",display:"flex",flexDirection:"column",gap:7}}>
            {messages.length===0&&(
              <div style={{color:"#334155",fontSize:12,textAlign:"center",padding:"24px 0"}}>No alerts yet — watching markets…</div>
            )}
            {messages.slice(-30).map((m,i)=>(
              <div key={i} style={{padding:"8px 10px",borderRadius:6,background:"#0a0f1e",borderLeft:`3px solid ${alertBorder(m.content)}`}}>
                <div style={{fontSize:10,color:"#475569",marginBottom:3}}>{m.time}</div>
                <div style={{fontSize:12,color:"#cbd5e1",lineHeight:1.55}} dangerouslySetInnerHTML={{__html:m.content.replace(/\n/g,"<br/>")}}/>
              </div>
            ))}
            <div ref={msgsEnd}/>
          </div>
        </div>
      )}
      {/* Toggle button */}
      <button onClick={()=>setOpen(o=>!o)} title="Market Alerts" style={{background:open?"#0f172a":"#1e293b",border:`1px solid ${open?"#f97316":"#334155"}`,borderRadius:"50%",width:52,height:52,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,position:"relative",boxShadow:"0 2px 10px #0006",transition:"all 0.2s",flexShrink:0}}>
        📡
        {!open&&unread>0&&(
          <span style={{position:"absolute",top:-4,right:-4,background:"#f97316",color:"#fff",borderRadius:"50%",width:18,height:18,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:900}}>{unread>9?"9+":unread}</span>
        )}
      </button>
    </div>
  );
}

// Kara Chat Widget
// ══════════════════════════════════════════════════════════════════════════
function KaraWidget({ open, onToggle, messages, thinking, onSend, voiceState, onVoice, wakeActive, onToggleWake, unread, micDenied, onRequestMic, aiName = "Kara", isSpeaking = false, personalityMode = "formal", onPersonalityModeChange = () => {}, onAgentConfirm = null, runningTasks = [], onTaskSteer = null, onTaskCancel = null }) {
  const [input,setInput] = useState("");
  const msgsEnd = useRef(null);
  useEffect(()=>{ if(open) msgsEnd.current?.scrollIntoView({behavior:"smooth"}); },[messages,thinking,open]);
  // 2026-10-06 (Kenyon directive) — mode-switch short-circuit so the LLM never sees
  // "switch to free mode" as a real question. Same regex family as the server.
  const MODE_INTENT_RE = /\b(?:change|switch|set|put|flip|toggle|go|make|enable|turn|set)\b[\s\S]{0,40}\b(?:mood|mode|personality|attitude|vibe)\b|\b(?:go|be|act)\s+(free|formal|professional|casual|polite|jarvis|megan|feral|wild|uncensored|cursing|sarcastic|serious)\b|\b(?:curse|cuss|swear)\s+(more|less|on|off|freely|a lot|alot|often|naturally|everywhere)\b|\b(?:be\s+)?(?:more|less)\s+(formal|casual|polite|professional|sarcastic|serious|respectful|nice|friendly|funny|crude|raw|unfiltered)\b/i;
  const detectModeIntent = (text) => {
    const t = String(text || "").toLowerCase();
    if (!MODE_INTENT_RE.test(t)) return null;
    if (/\bformal\b|\bprofessional\b|\bpolite\b|\bjarvis\b/.test(t)) return "formal";
    if (/\bfree\b|\bcurse\b|\bcuss\b|\bswear\b|\bsarcastic\b|\bmegan\b|\bferal\b|\bunfiltered\b|\bcursing\b/.test(t)) return "free";
    return "unknown";
  };
  const handleModeChipClick = () => {
    const next = personalityMode === "free" ? "formal" : "free";
    onPersonalityModeChange(next);
    try { localStorage.setItem(sk("amber_personality_mode"), next); } catch {}
    setInput("");
    onSend(`change your mode to ${next}`);
  };
  const submit = ()=>{
    const t = input.trim();
    if (!t || thinking) return;
    setInput("");
    // 2026-10-06 (Kenyon) — intercept mode-switch phrasings before sending to LLM
    const intent = detectModeIntent(t);
    if (intent && intent !== "unknown") {
      onPersonalityModeChange(intent);
      try { localStorage.setItem(sk("amber_personality_mode"), intent); } catch {}
      onSend(`change your mode to ${intent}`);
      return;
    }
    if (intent === "unknown") {
      onSend(t);
      return;
    }
    onSend(t);
  };
  const accCol = voiceState==="recording"?C.red:C.purple;
  // 2026-10-06 (Kenyon) — defensive text filter for assistant bubbles (LLM leak guard)
  const cleanAssistantText = (txt) => {
    if (!txt) return txt;
    let s = String(txt);
    // strip romance/pet names + "girlfriend"/"side chick" references
    s = s.replace(/\b(baby|babe|hun|honey|sweetie|sweetheart|love(?!.*trading)|darling|dear(?!.*sir))\b/gi, "Sir");
    s = s.replace(/\b(my\s+)?(girlfriend|side\s*chick|bae)\b/gi, "your AI co-pilot");
    return s;
  };
  return (
    <>
      {/* Floating toggle button */}
      <div className="kara-chat-widget kara-chat-widget--mini" data-testid="kara-chat-fab" onClick={onToggle} aria-label="Kara chat" data-state={open?"open":isSpeaking?"speaking":voiceState==="recording"?"listening":voiceState==="processing"?"thinking":wakeActive?"awake":"idle"} style={{position:"fixed",bottom:16,right:16,zIndex:8000,width:46,height:46,borderRadius:"50%",background:open?"#0a1628":isSpeaking?"#06281a":voiceState==="recording"?"#06281a":voiceState==="processing"?"#0a1f3d":wakeActive?"#06281a":`linear-gradient(135deg,${C.green},${C.blue})`,border:`2px solid ${open?C.border:isSpeaking?C.green:voiceState==="recording"?C.green:voiceState==="processing"?C.blue:wakeActive?C.green:C.green}`,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",boxShadow:`0 3px 16px ${isSpeaking?C.green+"66":voiceState==="recording"?C.green+"55":C.green+"55"}`,transition:"all 0.25s",animation:isSpeaking?"karaSpeakingHalo 1.0s ease-in-out infinite":voiceState==="recording"?"karaListenPulse 0.9s infinite":(thinking||(!open&&unread>0))?"pulse 0.9s infinite":wakeActive?"karaAwakePulse 2.5s infinite":"none",flexShrink:0,overflow:"visible"}}>
        {/* KENYON 2026-09-25: 5 radial waveform bars around the FAB while bot is speaking */}
        {!open && isSpeaking && [0,1,2,3,4].map(i=>{
          const angle = -90 + i*(360/5);
          const rad = (angle*Math.PI)/180;
          const r = 33; // px from FAB center to bar anchor
          const x = 23 + Math.cos(rad)*r - 2;   // 23 = half of 46px; -2 = half of 4px bar
          const y = 23 + Math.sin(rad)*r - 5;   // -5 = half of 16px bar peak
          return (
            <span key={i} aria-hidden="true" style={{
              position:"absolute", left:x+"px", top:y+"px",
              width:4, height:10, borderRadius:2,
              background:C.green,
              transformOrigin:"50% 100%",
              transform:`rotate(${angle+90}deg) translateY(-3px)`,
              animation:`karaRadialBar 0.85s ease-in-out ${(i*0.08).toFixed(2)}s infinite`,
              opacity:0.85, pointerEvents:"none", boxShadow:`0 0 6px ${C.green}88`,
            }}/>
          );
        })}
        <span style={{position:"relative",display:"flex",alignItems:"center",justifyContent:"center",width:"100%",height:"100%"}}>
          {open
            ? <span style={{fontSize:20,color:C.text,fontWeight:900}}>✕</span>
            : isSpeaking
              ? (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" style={{animation:"karaMicBars 0.6s ease-in-out infinite"}}><rect x="9"  y="4" width="2" height="6"  rx="1" fill={C.green}/><rect x="13" y="2" width="2" height="10" rx="1" fill={C.green} style={{animation:"karaMicBars 0.7s ease-in-out infinite reverse"}}/><rect x="17" y="5" width="2" height="4"  rx="1" fill={C.green} style={{animation:"karaMicBars 0.8s ease-in-out infinite"}}/></svg>)
              : voiceState==="recording"
                ? (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" style={{animation:"karaMicBars 0.9s ease-in-out infinite"}}><rect x="9"  y="4" width="2" height="6"  rx="1" fill={C.green}/><rect x="13" y="2" width="2" height="10" rx="1" fill={C.green} style={{animation:"karaMicBars 1.1s ease-in-out infinite reverse"}}/><rect x="17" y="5" width="2" height="4"  rx="1" fill={C.green} style={{animation:"karaMicBars 1.3s ease-in-out infinite"}}/></svg>)
                : voiceState==="processing"
                  ? (<svg width="22" height="22" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke={C.blue} strokeWidth="2" strokeDasharray="14 6" style={{animation:"karaSpin 1s linear infinite"}}/></svg>)
                  : wakeActive
                    ? <span style={{fontSize:22,color:C.green,lineHeight:1}}>👂</span>
                    : <span style={{fontSize:22,lineHeight:1}}>💬</span>}
        </span>
        {!open&&unread>0&&(
          <div style={{position:"absolute",top:-4,right:-4,background:C.red,color:"#fff",fontSize:11,fontWeight:900,width:20,height:20,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",border:"2px solid #070d1a"}}>
            {unread>9?"9+":unread}
          </div>
        )}
      </div>
      {/* Chat panel */}
      {open&&(
        <div className="kara-chat-panel" style={{position:"fixed",bottom:100,right:24,zIndex:7999,width:"calc(100vw - 48px)",maxWidth:340,height:"calc(100vh - 140px)",maxHeight:380,background:"#08111e",border:`1px solid ${C.green}88`,borderRadius:16,display:"flex",flexDirection:"column",boxShadow:`0 8px 56px ${C.green}44`,fontFamily:"'Courier New',monospace",overflow:"hidden",animation:"popIn 0.18s ease"}}>
          {/* Header */}
          <div style={{background:`linear-gradient(90deg,#011b14,#001525)`,borderBottom:`1px solid ${C.green}44`,padding:"12px 18px",display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
            <div style={{width:38,height:38,borderRadius:"50%",background:`linear-gradient(135deg,${C.green},${C.blue})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:19,fontWeight:900,color:"#070d1a",flexShrink:0}}>L</div>
            <div>
              <div style={{fontSize:15,fontWeight:900,color:C.green,letterSpacing:3}}>{aiName}</div>
              <div style={{fontSize:10,color:C.muted,letterSpacing:1}}>{`AI TRADING CO-PILOT · FOR ${_karaUserName}`}</div>
            </div>
            <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:6}}>
              {/* 2026-10-06 (Kenyon directive) — personality mode chip. Click to toggle free↔formal. */}
              <button
                onClick={handleModeChipClick}
                title="Click to change mode. Or type 'free mode' / 'go formal'."
                data-mode={personalityMode}
                style={{
                  fontSize:9, fontWeight:900, letterSpacing:1.2, textTransform:"uppercase",
                  padding:"4px 9px", borderRadius:99, cursor:"pointer", fontFamily:"inherit",
                  background: personalityMode === "free" ? "#ef444422" : `${C.green}22`,
                  border: `1px solid ${personalityMode === "free" ? "#ef4444" : C.green}`,
                  color: personalityMode === "free" ? "#fca5a5" : C.green,
                }}
              >{personalityMode === "free" ? "FREE" : "FORMAL"}</button>
              <div style={{width:7,height:7,borderRadius:"50%",background:thinking?C.amber:C.green}}/>
              <span style={{fontSize:10,color:thinking?C.amber:C.green,fontWeight:700}}>{thinking?"Working":"Online"}</span>
            </div>
          </div>
          {/* Running tasks activity strip */}
          {runningTasks.length > 0 && (
            <div style={{padding:"6px 14px",background:`${C.amber}11`,borderBottom:`1px solid ${C.amber}33`,display:"flex",flexDirection:"column",gap:4}}>
              {runningTasks.map(t => (
                <div key={t.task_id} style={{display:"flex",alignItems:"center",gap:8,fontSize:11}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      <span style={{color:C.amber,fontWeight:700}}>⚙️</span>
                      <span style={{color:C.text,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.message || t.task_id}</span>
                    </div>
                    <div style={{height:3,borderRadius:2,background:`${C.amber}22`,marginTop:3}}>
                      <div style={{height:"100%",borderRadius:2,background:C.amber,width:`${Math.max(5,Math.round((t.progress||0)*100))}%`,transition:"width 0.3s"}}/>
                    </div>
                  </div>
                  <button onClick={()=>onTaskCancel?.(t.task_id)} title="Cancel" style={{background:"none",border:`1px solid ${C.red}55`,borderRadius:4,color:C.red,fontSize:10,padding:"2px 6px",cursor:"pointer",fontFamily:"inherit",fontWeight:700}}>✕</button>
                </div>
              ))}
            </div>
          )}
          {/* Messages */}
          <div style={{flex:1,overflowY:"auto",padding:"14px",display:"flex",flexDirection:"column",gap:10}}>
            {messages.map((m,i)=>{
              const isUser=m.role==="user";
              // 2026-10-03 (Hermes) — tool-execution badge styling. The server-side
              // dispatcher can fire actions on behalf of the LLM; we render those
              // as compact cards (not plain chat bubbles) with a clickable Confirm
              // button for GATED tools (set_stake / execute_trade / activate_strategy).
              const isToolBadge = m._isToolBadge === true;
              if (isToolBadge) {
                return (
                  <div key={i} data-testid="chat-msg" data-role="tool" style={{display:"flex",flexDirection:"row",gap:8,alignItems:"flex-end"}}>
                    <div style={{width:28,height:28,borderRadius:"50%",background:`linear-gradient(135deg,${C.green},${C.blue})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:900,color:"#070d1a",flexShrink:0}}>K</div>
                    <div style={{maxWidth:"85%",padding:"10px 14px",borderRadius:"12px 12px 12px 3px",background:`${C.bg}cc`,border:`1px solid ${C.green}88`,fontSize:12,color:C.text,lineHeight:1.55,boxShadow:`0 0 0 1px ${C.green}33`}}>
                      <div style={{fontSize:11,fontWeight:700,color:C.green,marginBottom:4,letterSpacing:0.5,textTransform:"uppercase"}}>🔧 TOOL</div>
                      <div style={{whiteSpace:"pre-wrap",wordBreak:"break-word"}}>{m.content}</div>
                      {m._pendingTaskId && (
                        <div style={{marginTop:8,display:"flex",gap:6}}>
                          <button
                            onClick={async () => {
                              try {
                                const token = (typeof window !== "undefined" && window.__karaToken) || localStorage.getItem("kara_token") || "";
                                const r = await fetch(`/api/tasks/${encodeURIComponent(m._pendingTaskId)}/confirm`, { method:"POST", headers: token ? { "Authorization": "Bearer " + token, "Content-Type": "application/json" } : { "Content-Type": "application/json" }, body:"{}" });
                                const d = await r.json().catch(() => ({}));
                                onSend && onSend(`Tool confirmation result: ${r.ok ? "✅ confirmed" : "❌ " + (d.error || "failed")}`);
                              } catch (e) {
                                onSend && onSend(`Confirm failed: ${e.message}`);
                              }
                            }}
                            style={{background:C.green+"22",border:`1px solid ${C.green}`,color:C.green,padding:"5px 12px",borderRadius:5,cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:"inherit"}}
                          >✅ CONFIRM</button>
                          <button
                            onClick={async () => {
                              try {
                                const token = (typeof window !== "undefined" && window.__karaToken) || localStorage.getItem("kara_token") || "";
                                await fetch(`/api/tasks/${encodeURIComponent(m._pendingTaskId)}/cancel`, { method:"POST", headers: token ? { "Authorization": "Bearer " + token, "Content-Type": "application/json" } : { "Content-Type": "application/json" }, body:"{}" });
                                onSend && onSend("Cancelled the pending action.");
                              } catch (e) { onSend && onSend(`Cancel failed: ${e.message}`); }
                            }}
                            style={{background:"#ef444422",border:"1px solid #ef4444",color:"#ef4444",padding:"5px 12px",borderRadius:5,cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:"inherit"}}
                          >❌ CANCEL</button>
                        </div>
                      )}
                      <div style={{fontSize:9,color:C.muted,marginTop:3}}>{m.time}</div>
                    </div>
                  </div>
                );
              }
              return (
                <div key={i} data-testid="chat-msg" data-role={m.role} data-streaming={m._streaming?"true":"false"} style={{display:"flex",flexDirection:isUser?"row-reverse":"row",gap:8,alignItems:"flex-end"}}>
                  {!isUser&&<div style={{width:28,height:28,borderRadius:"50%",background:`linear-gradient(135deg,${C.green},${C.blue})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:900,color:"#070d1a",flexShrink:0}}>L</div>}
                  <div style={{maxWidth:"80%",padding:"9px 13px",borderRadius:isUser?"12px 12px 3px 12px":"12px 12px 12px 3px",background:isUser?`${C.blue}22`:`${C.green}15`,border:`1px solid ${isUser?C.blue+"55":C.green+"44"}`,fontSize:13,color:C.text,lineHeight:1.55}}>
                    <span style={{whiteSpace:"pre-wrap",wordBreak:"break-word"}}>{isUser ? m.content : cleanAssistantText(m.content)}</span>
                    {!isUser && [...new Set(String(m.content||"").match(/\/api\/tv-desktop\/shot\/[a-f0-9]{32}\.png/g)||[])].slice(0,3).map(u=>(
                      <a key={u} href={u} target="_blank" rel="noopener noreferrer"><img src={u} alt="TradingView chart" loading="lazy" style={{display:"block",maxWidth:"100%",marginTop:6,borderRadius:6,border:`1px solid ${C.border}`}}/></a>))}
                    {!isUser && m._agent && <AgentMessageExtras m={m} onConfirm={onAgentConfirm} accent={C.green} />}
                    <div style={{fontSize:9,color:C.muted,marginTop:3,textAlign:isUser?"right":"left"}}>{m.time}</div>
                  </div>
                </div>
              );
            })}
{/* KENYON 2026-09-25: typing-dots removed — keep widget small while bot thinks */}            <div ref={msgsEnd}/>
          </div>
          {/* Mic permission denied banner */}
          {micDenied&&(
            <div style={{padding:"10px 14px",background:"#1a0a0a",borderTop:`1px solid #ef444455`,flexShrink:0}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                <span style={{fontSize:15}}>🚫</span>
                <span style={{flex:1,fontSize:12,color:"#fca5a5",fontWeight:600}}>Microphone blocked by Chrome</span>
                <button onClick={onRequestMic} style={{background:"#ef4444",color:"#fff",border:"none",borderRadius:5,padding:"6px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>TRY AGAIN</button>
              </div>
              <div style={{fontSize:10,color:"#f8717155",lineHeight:1.6}}>
                👆 Click <strong style={{color:"#fca5a5"}}>TRY AGAIN</strong> above — Chrome will show a mic popup. Click <strong style={{color:"#fca5a5"}}>Allow</strong>.
              </div>
            </div>
          )}
          {/* Input bar */}
          <div style={{padding:"10px 12px",borderTop:`1px solid ${C.border}`,display:"flex",gap:8,flexShrink:0}}>
            {/* Wake word toggle — always-on "hey amber" listener */}
            <button
              onClick={micDenied ? onRequestMic : onToggleWake}
              title={micDenied ? 'Click to grant mic permission' : wakeActive ? `Wake word ON — say "Hey ${_karaUserName === "Lando" ? "Lala" : "Kara"}" to activate` : `Enable always-on "Hey ${_karaUserName === "Lando" ? "Lala" : "Kara"}" wake word`}
              style={{background:wakeActive?"#0a2218":micDenied?"#2d1a00":C.panel,border:`1px solid ${wakeActive?C.green:micDenied?"#f59e0b":C.border}66`,color:wakeActive?C.green:micDenied?"#f59e0b":C.muted,padding:"8px 10px",borderRadius:6,cursor:"pointer",fontSize:15,fontFamily:"inherit",transition:"all 0.2s",animation:wakeActive?"pulse 2.5s infinite":"none",flexShrink:0}}
            >
              {micDenied?"🎙️":wakeActive?"👂":"🔇"}
            </button>
            <button onClick={micDenied ? onRequestMic : onVoice} title={micDenied?"Click to enable mic":"Toggle voice input"} style={{background:voiceState==="recording"?"#2d1060":voiceState==="processing"?"#0f1f3d":micDenied?"#2d0a0a":C.panel,border:`1px solid ${micDenied?"#ef4444":accCol}55`,color:micDenied?"#ef4444":accCol,padding:"8px 11px",borderRadius:6,cursor:"pointer",fontSize:18,fontFamily:"inherit",transition:"all 0.15s",animation:voiceState==="recording"?"pulse 0.8s infinite":"none",flexShrink:0}}>
              {micDenied?"🚫":voiceState==="recording"?"⏹":voiceState==="processing"?"⏳":"🎤"}
            </button>
            <input data-testid="kara-chat-input" value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&submit()} placeholder={thinking?"Kara is thinking...":"Ask Kara anything..."} disabled={thinking} style={{flex:1,background:C.panel,border:`1px solid ${C.border}`,color:C.text,padding:"8px 12px",borderRadius:6,fontSize:13,fontFamily:"inherit",outline:"none",minWidth:0}}/>
            <button data-testid="kara-chat-send" onClick={submit} disabled={thinking||!input.trim()} style={{background:(!thinking&&input.trim())?C.green+"22":C.panel,border:`1px solid ${(!thinking&&input.trim())?C.green:C.border}`,color:(!thinking&&input.trim())?C.green:C.muted,padding:"8px 14px",borderRadius:6,cursor:(!thinking&&input.trim())?"pointer":"default",fontSize:16,fontFamily:"inherit",flexShrink:0}}>➤</button>
          </div>
          {/* Quick commands */}
          <div style={{padding:"6px 12px 10px",display:"flex",gap:5,flexWrap:"wrap",borderTop:`1px solid ${C.border}22`}}>
            {["scan all","what's hot?","add XAUUSD","add US30","clear all","status"].map(cmd=>(
              <span key={cmd} onClick={()=>{ if(!thinking){ onSend(cmd);
// ── Chat-FAB inline graphics (state-driven mic waveform + awake ear + thinking spinner) ──
// Auto-injected by Hermes 2026-09-25; visually shrinks the FAB from 62→46 px and adds
// pulse keyframes for the listening/awake states. Safe to leave — pure CSS, no runtime cost.
const _KARA_FAB_CSS = `
.kara-chat-widget--mini[data-state="listening"] { box-shadow: 0 0 0 4px rgba(16,185,129,0.22), 0 3px 16px rgba(16,185,129,0.55) !important; }
.kara-chat-widget--mini[data-state="speaking"]  { box-shadow: 0 0 0 8px rgba(16,185,129,0.18), 0 3px 16px rgba(16,185,129,0.65) !important; }
.kara-chat-widget--mini[data-state="awake"]     { box-shadow: 0 0 0 3px rgba(0,212,170,0.20), 0 3px 16px rgba(0,212,170,0.55) !important; }
.kara-chat-widget--mini[data-state="thinking"]  { box-shadow: 0 0 0 3px rgba(56,189,248,0.20), 0 3px 16px rgba(56,189,248,0.55) !important; }
@keyframes karaListenPulse  { 0%,100% { transform: scale(1.0);} 50% { transform: scale(1.06);} }
@keyframes karaSpeakingHalo { 0% { transform: scale(1.0); box-shadow: 0 0 0 0 rgba(16,185,129,0.45);} 50% { transform: scale(1.08); box-shadow: 0 0 0 12px rgba(16,185,129,0);} 100% { transform: scale(1.0); box-shadow: 0 0 0 0 rgba(16,185,129,0);} }
@keyframes karaAwakePulse   { 0%,100% { transform: scale(1.0); box-shadow: 0 0 0 0 rgba(0,212,170,0.35);} 50% { transform: scale(1.04); box-shadow: 0 0 0 5px rgba(0,212,170,0);} }
@keyframes karaMicBars      { 0%,100% { transform: scaleY(1);} 50% { transform: scaleY(1.8);} }
@keyframes karaSpin         { to { transform: rotate(360deg);} }
@keyframes karaRadialBar    { 0%,100% { transform-origin:50% 100%; height:6px; opacity:0.5;} 50% { height:16px; opacity:1;} }
`.trim();
if (typeof document !== "undefined" && !document.getElementById("kara-fab-styles")) {
  const _e = document.createElement("style");
  _e.id = "kara-fab-styles"; _e.textContent = _KARA_FAB_CSS;
  document.head.appendChild(_e);
}
 } }} style={{fontSize:10,color:C.muted,background:C.bg,border:`1px solid ${C.border}`,borderRadius:3,padding:"2px 7px",cursor:"pointer",transition:"color 0.1s"}}>
                {cmd}
              </span>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════
// ── Components ────────────────────────────────────────────────────────────

// Simple Error Boundary for runtime crashes
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error("AMBER ErrorBoundary:", error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{padding:40,background:"#070d1a",color:"#ef4444",fontFamily:"monospace",height:"100vh"}}>
          <h1 style={{fontSize:24,marginBottom:20}}>⚡ AMBER Dashboard Crash</h1>
          <p style={{color:"#e2e8f0",marginBottom:10}}>Something went wrong in the React render cycle:</p>
          <pre style={{background:"#0d1f35",padding:20,borderRadius:8,color:"#38bdf8",overflow:"auto"}}>
            {this.state.error?.stack || this.state.error?.toString()}
          </pre>
          <button onClick={()=>location.reload()} style={{marginTop:20,padding:"10px 20px",background:"#00d4aa",color:"#070d1a",border:"none",borderRadius:4,cursor:"pointer",fontWeight:700}}>RELOAD DASHBOARD</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Settings Panel ────────────────────────────────────────────────────────────
function TelegramAlertsWidget({ settings, onSave, onTestAlert, onRestartScanner, ollamaModels = [], missionStatus = null }) {
  const [local, setLocal] = useState(settings || {});
  const [saving, setSaving] = useState(false);
  const [testMsg, setTestMsg] = useState("");
  const [scanStatus, setScanStatus] = useState(null);
  const [sessionHours, setSessionHours] = useState(null); // analytics best-hours-by-session data
  const [riskConfig, setRiskConfig] = useState({ maxDailyLoss: 100, maxDailyProfit: 200, maxOpenTrades: 5, slEnabled: false, tpEnabled: false, rewardRatio: 2 });

  useEffect(() => {
    apiFetch("/api/risk-config")
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setRiskConfig(d); })
      .catch(() => {});
  }, []);

  const saveRiskConfig = async (patch) => {
    const updated = { ...riskConfig, ...patch };
    setRiskConfig(updated);
    try {
      await apiFetch("/api/risk-config", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updated)
      });
      setTestMsg("✓ Saved");
      setTimeout(() => setTestMsg(""), 2000);
    } catch (e) {
      setTestMsg("✗ Save failed");
      setTimeout(() => setTestMsg(""), 2000);
    }
  };

  useEffect(() => { setLocal(settings || {}); }, [settings]);

  useEffect(() => {
    apiFetch("/api/scanner/status", { silent: true }).then(r => r.ok ? r.json() : null).then(d => { if (d) setScanStatus(d); }).catch(() => {});
    const id = setInterval(() => {
      apiFetch("/api/scanner/status", { silent: true }).then(r => r.ok ? r.json() : null).then(d => { if (d) setScanStatus(d); }).catch(() => {});
    }, 30000);
    return () => clearInterval(id);
  }, []);

  // Fetch session-aware best hours from live analytics DB
  useEffect(() => {
    apiFetch("/api/analytics/best-hours-by-session?min_trades=3", { silent: true })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setSessionHours(d); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (JSON.stringify(local) === JSON.stringify(settings)) return;
    const timer = setTimeout(async () => {
      setSaving(true);
      try { await onSave(local); setTestMsg("✓ Saved"); } catch { setTestMsg("✗ Save failed"); }
      setSaving(false);
      setTimeout(() => setTestMsg(""), 2000);
    }, 800);
    return () => clearTimeout(timer);
  }, [local, settings, onSave]);

  const LABELS = {
    signalConfirmed: "Signal Confirmed (All Conditions)",
    setupForming: "Setup Forming (8-9/10)",
    liquiditySweep: "Liquidity Sweep",
    htfChange: "HTF Structure Change",
    directionFlip: "1m Direction Flip",
    bosChoch: "BOS / CHoCH",
  };
  const FREQ_OPTS = [1, 5, 15, 30, 60];
  const LLM_PROVIDERS = [
    { value: "hermes", label: "Hermes (default brain, agy fallback)" },
    { value: "auto", label: "Auto (Claude → Gemini CLI → Gemini API → Ollama)" },
    { value: "gemini-cli", label: "Gemini CLI (free, local)" },
    { value: "claude", label: "Claude (full fallback chain)" },
    { value: "claude-code", label: "Claude Code CLI" },
    { value: "gemini", label: "Gemini API (key 1)" },
    { value: "gemini_2", label: "Gemini API (key 2)" },
    { value: "gemini_3", label: "Gemini API (key 3)" },
    { value: "anthropic", label: "Anthropic SDK" },
    { value: "ollama", label: "Ollama (local)" },
    { value: "kimi", label: "Moonshot / Kimi" },
  ];
  const AGENT_NAMES = ["chat", "stratos", "scout", "toolsmith", "ml-gate"];
  // Short label for a tz id: "America/Jamaica" -> "Jamaica", "UTC" -> "UTC"
  const shortTz = (tz) => { if (!tz) return "UTC"; const seg = tz.split("/"); return seg[seg.length - 1].replace(/_/g, " "); };

  const at = local.alertTypes || {};
  const af = local.alertFrequency || {};
  const qh = local.quietHours || {};
  const ap = local.agentProviders || {};
  const telegramOn = at.telegramEnabled !== false;

  const upd = (patch) => setLocal(prev => ({ ...prev, ...patch }));
  const updAt = (k, v) => setLocal(prev => ({ ...prev, alertTypes: { ...(prev.alertTypes || {}), [k]: v } }));
  const updAf = (k, v) => setLocal(prev => ({ ...prev, alertFrequency: { ...(prev.alertFrequency || {}), [k]: Number(v) } }));
  const updQh = (patch) => setLocal(prev => ({ ...prev, quietHours: { ...(prev.quietHours || {}), ...patch } }));
  const updAp = (agent, v) => setLocal(prev => ({ ...prev, agentProviders: { ...(prev.agentProviders || {}), [agent]: v } }));

  const inp = { background: C.bg, border: `1px solid ${C.border}`, color: C.text, borderRadius: 3, padding: "2px 6px", fontFamily: "inherit", fontSize: 12 };
  const sec = { marginBottom: 20, padding: 14, background: C.panel, borderRadius: 6, border: `1px solid ${C.border}` };

  return (
    <div style={{ padding: 20, color: C.text, maxWidth: 700 }}>
      <h2 style={{ color: C.amber, margin: "0 0 16px", fontSize: 16 }}>⚙️ Settings</h2>

      {/* Trading */}
      <div style={sec}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12, color: C.blue }}>Trading & Risk Management</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
            <input type="checkbox" checked={!!local.autoTrade} onChange={e => upd({ autoTrade: e.target.checked })} style={{ width: 14, height: 14 }} />
            <span style={{ fontSize: 13 }}>Auto Trade (execute signals automatically)</span>
          </label>
          
          <div style={{ display: "flex", alignItems: "center", gap: 15, flexWrap: "wrap", borderBottom: `1px solid ${C.border}33`, paddingBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 13, color: C.muted }}>Stake:</span>
              <input type="number" min="0.35" step="0.5" value={local.stake || 1} onChange={e => upd({ stake: parseFloat(e.target.value) || 1 })}
                style={{ ...inp, width: 70, opacity: local.autoStakeEnabled ? 0.5 : 1 }} disabled={local.autoStakeEnabled} />
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <input type="checkbox" checked={!!local.autoStakeEnabled} onChange={e => upd({ autoStakeEnabled: e.target.checked })} style={{ width: 14, height: 14 }} />
              <span style={{ fontSize: 13, color: C.amber, fontWeight: 700 }}>Auto Stake (5% of balance)</span>
            </label>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10, paddingTop: 4 }}>
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>Deriv API Risk Management (SL/TP)</div>
            <div style={{ display: "flex", gap: 15, flexWrap: "wrap" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input type="checkbox" checked={!!riskConfig.slEnabled} onChange={e => saveRiskConfig({ slEnabled: e.target.checked })} style={{ width: 14, height: 14 }} />
                <span style={{ fontSize: 13 }}>Enable Stop Loss (SL)</span>
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input type="checkbox" checked={!!riskConfig.tpEnabled} onChange={e => saveRiskConfig({ tpEnabled: e.target.checked })} style={{ width: 14, height: 14 }} />
                <span style={{ fontSize: 13 }}>Enable Take Profit (TP)</span>
              </label>
            </div>
            {(riskConfig.tpEnabled || riskConfig.slEnabled) && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4 }}>
                <span style={{ fontSize: 13, color: C.muted }}>Take Profit Ratio (Reward):</span>
                <select value={riskConfig.rewardRatio || 2} onChange={e => saveRiskConfig({ rewardRatio: parseInt(e.target.value) || 2 })}
                  style={{ ...inp, padding: "3px 6px" }}>
                  <option value={2}>1:2 (2x Risk - Recommended)</option>
                  <option value={3}>1:3 (3x Risk)</option>
                </select>
              </div>
            )}
            <div style={{ display: "flex", gap: 15, flexWrap: "wrap", marginTop: 4 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 12, color: C.muted }}>Max Open Trades:</span>
                <input type="number" min="1" max="20" value={riskConfig.maxOpenTrades || 5} onChange={e => saveRiskConfig({ maxOpenTrades: parseInt(e.target.value) || 5 })}
                  style={{ ...inp, width: 50 }} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 12, color: C.muted }}>Max Daily Loss ($):</span>
                <input type="number" min="10" step="10" value={riskConfig.maxDailyLoss ?? 100} onChange={e => saveRiskConfig({ maxDailyLoss: parseFloat(e.target.value) || 100 })}
                  style={{ ...inp, width: 70 }} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 12, color: "#10b981", fontWeight: "bold" }}>Max Daily Profit ($):</span>
                <input type="number" min="10" step="10" value={riskConfig.maxDailyProfit ?? 200} onChange={e => saveRiskConfig({ maxDailyProfit: parseFloat(e.target.value) || 200 })}
                  style={{ ...inp, width: 70, borderColor: "#10b981" }} />
              </div>
            </div>
            <div style={{ fontSize: 10, color: C.dim, marginTop: 4, lineHeight: 1.4 }}>
              * For Deriv Rise/Fall options, SL/TP is managed via real-time position monitoring. Trades will be closed early (sold back to broker) if targets are hit.
            </div>
          </div>
        </div>
      </div>

      {/* Trading Hours Filter */}
      {(()=>{
        const th = local.tradingHours || {};
        const enabled = !!th.enabled;
        const isAuto = !!th.autoOptimize;
        const allowed = Array.isArray(th.allowedHours) ? th.allowedHours : [];
        const SESSION_LABELS = { asian:"🌏 Asian (00-06)", london:"🇬🇧 London (07-12)", london_ny:"⚡ London/NY (13-15)", new_york:"🗽 New York (16-21)", off:"🌙 Off (22-23)" };
        const SESSION_ORDER = ["london_ny","new_york","london","asian","off"];
        const updTH = (patch) => upd({ tradingHours: { ...th, ...patch } });
        const toggleHour = (h) => {
          const next = allowed.includes(h) ? allowed.filter(x=>x!==h) : [...allowed, h].sort((a,b)=>a-b);
          updTH({ allowedHours: next, autoOptimize: false });
        };
        const applySessionRecommendations = () => {
          // Apply top hours from each session where WR >= 50%
          if (!sessionHours?.by_session) return;
          const hours = new Set();
          Object.values(sessionHours.by_session).forEach(s => {
            s.best_hours.filter(h=>h.win_rate_pct>=50).slice(0,2).forEach(h=>hours.add(h.hour));
          });
          updTH({ allowedHours: [...hours].sort((a,b)=>a-b), autoOptimize: false });
        };
        const applyAuto = () => updTH({ autoOptimize: true, allowedHours: [] });
        return (
          <div style={sec}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: C.green }}>🕐 Trading Hours Filter</div>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 10 }}>
              Restrict trading to hours when the scanner performs best. Source: live scanner history (kara_analytics.db).
            </div>
            <label style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer", marginBottom:12 }}>
              <input type="checkbox" checked={enabled} onChange={e=>updTH({enabled:e.target.checked})} style={{width:14,height:14}}/>
              <span style={{fontSize:13,fontWeight:700}}>Enable Trading Hours Filter</span>
            </label>
            {enabled && (
              <>
                {/* Mode picker */}
                <div style={{display:"flex",gap:8,marginBottom:12}}>
                  <button onClick={applyAuto}
                    style={{flex:1,padding:"6px 10px",borderRadius:5,fontSize:11,fontWeight:700,cursor:"pointer",
                      background:isAuto?`${C.green}22`:"transparent",border:`1px solid ${isAuto?C.green:C.border}`,color:isAuto?C.green:C.muted}}>
                    🤖 AI Auto (session-aware)
                  </button>
                  <button onClick={()=>updTH({autoOptimize:false})}
                    style={{flex:1,padding:"6px 10px",borderRadius:5,fontSize:11,fontWeight:700,cursor:"pointer",
                      background:!isAuto?`${C.amber}22`:"transparent",border:`1px solid ${!isAuto?C.amber:C.border}`,color:!isAuto?C.amber:C.muted}}>
                    🔧 Manual Hours
                  </button>
                </div>

                {/* AI Auto mode — show session-aware recommendations from analytics DB */}
                {isAuto && (
                  <div style={{background:C.bg,borderRadius:6,padding:12,border:`1px solid ${C.green}33`,marginBottom:10}}>
                    <div style={{fontSize:11,color:C.green,fontWeight:700,marginBottom:8}}>Live Session Best Hours (from analytics DB)</div>
                    {sessionHours?.by_session ? SESSION_ORDER.filter(k=>sessionHours.by_session[k]).map(k=>{
                      const s = sessionHours.by_session[k];
                      const top = s.best_hours.filter(h=>h.win_rate_pct>=50).slice(0,3);
                      if (!top.length) return null;
                      return (
                        <div key={k} style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
                          <span style={{fontSize:10,color:C.muted,width:120,flexShrink:0}}>{SESSION_LABELS[k]}</span>
                          <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                            {top.map(h=>{
                              const col=h.win_rate_pct>=70?C.green:h.win_rate_pct>=55?C.amber:C.muted;
                              return <span key={h.hour} style={{fontSize:10,fontWeight:700,padding:"2px 6px",borderRadius:3,background:col+"22",border:`1px solid ${col}44`,color:col}}>{String(h.hour).padStart(2,"0")}:00 {h.win_rate_pct}%</span>;
                            })}
                          </div>
                        </div>
                      );
                    }) : <div style={{fontSize:11,color:C.dim}}>Collecting data... (needs 3+ trades per hour)</div>}
                    <div style={{fontSize:10,color:C.dim,marginTop:6}}>Scanner uses current session's best hours dynamically</div>
                  </div>
                )}

                {/* Manual mode — 24-hour visual grid */}
                {!isAuto && (()=>{
                  // Dead zones from mission status (current phase)
                  const deadZones = missionStatus?.dead_zones || [];
                  const currentHr = new Date().getUTCHours();
                  // Build flat hour→WR lookup from all session data
                  const hrWr = {};
                  if (sessionHours?.by_session) {
                    Object.values(sessionHours.by_session).forEach(s => {
                      (s.best_hours||[]).forEach(h => { hrWr[h.hour] = h.win_rate_pct; });
                    });
                  }
                  const SESSION_SPANS = [
                    {label:"🌏 ASIA",hours:[0,1,2,3,4,5,6],col:"#7c3aed"},
                    {label:"🇬🇧 LONDON",hours:[7,8,9,10,11,12],col:"#2563eb"},
                    {label:"⚡ LDN/NY",hours:[13,14,15],col:"#0891b2"},
                    {label:"🗽 NY",hours:[16,17,18,19,20,21],col:"#059669"},
                    {label:"🌙 OFF",hours:[22,23],col:"#475569"},
                  ];
                  const applyBlocklist = () => {
                    const all24 = Array.from({length:24},(_,i)=>i);
                    updTH({ allowedHours: all24.filter(h=>!deadZones.includes(h)), autoOptimize: false });
                  };
                  return (
                    <div>
                      {/* Action buttons */}
                      <div style={{display:"flex",gap:6,marginBottom:10,flexWrap:"wrap"}}>
                        {deadZones.length > 0 && (
                          <button onClick={applyBlocklist}
                            title={`Block dead zones: ${deadZones.map(h=>String(h).padStart(2,"0")+":00").join(", ")}`}
                            style={{fontSize:10,padding:"4px 10px",borderRadius:4,cursor:"pointer",background:"#7f1d1d22",border:`1px solid #ef444444`,color:"#ef4444",fontWeight:700}}>
                            ⛔ Apply Mission Blocklist ({deadZones.length} zones)
                          </button>
                        )}
                        {sessionHours?.by_session && (
                          <button onClick={applySessionRecommendations}
                            style={{fontSize:10,padding:"4px 10px",borderRadius:4,cursor:"pointer",background:`${C.blue}22`,border:`1px solid ${C.blue}44`,color:C.blue,fontWeight:700}}>
                            ✨ AI Recommendations
                          </button>
                        )}
                        <button onClick={()=>updTH({allowedHours:[]})}
                          style={{fontSize:10,padding:"4px 10px",borderRadius:4,cursor:"pointer",background:"transparent",border:`1px solid ${C.border}`,color:C.muted,fontWeight:700}}>
                          Clear All
                        </button>
                        <button onClick={()=>updTH({allowedHours:Array.from({length:24},(_,i)=>i)})}
                          style={{fontSize:10,padding:"4px 10px",borderRadius:4,cursor:"pointer",background:`${C.green}22`,border:`1px solid ${C.green}44`,color:C.green,fontWeight:700}}>
                          All Hours
                        </button>
                      </div>
                      {/* Legend */}
                      <div style={{display:"flex",gap:10,marginBottom:8,flexWrap:"wrap"}}>
                        {[["🟢 ≥70% WR",C.green],["🟡 50-70%",C.amber],["⛔ Dead Zone","#ef4444"],["⬛ No data",C.muted]].map(([l,c])=>(
                          <span key={l} style={{fontSize:9,color:c}}>{l}</span>
                        ))}
                        <span style={{fontSize:9,color:"#60a5fa"}}>◉ Current UTC hour</span>
                      </div>
                      {/* 24-cell grid — grouped by session */}
                      {SESSION_SPANS.map(({label,hours,col})=>(
                        <div key={label} style={{marginBottom:8}}>
                          <div style={{fontSize:9,color:col,letterSpacing:1,fontWeight:700,marginBottom:4}}>{label}</div>
                          <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                            {hours.map(h=>{
                              const isOn = allowed.includes(h);
                              const isDead = deadZones.includes(h);
                              const wr = hrWr[h];
                              const isCurrent = h === currentHr;
                              const statCol = isDead ? "#ef4444" : wr>=70 ? C.green : wr>=50 ? C.amber : C.muted;
                              const cellBg = isOn ? statCol+"28" : isDead ? "#1a0000" : "#0d1117";
                              const borderCol = isCurrent ? "#60a5fa" : isOn ? statCol : isDead ? "#ef444433" : C.border;
                              const blocked = isDead;
                              return (
                                <button key={h} onClick={()=>{ if (blocked) return; toggleHour(h); }}
                                  disabled={blocked}
                                  title={blocked
                                    ? `H${h} — DEAD ZONE (mission-mandated block, ${wr!=null?wr+"% WR":"no data"}). Cleared automatically when WR recovers.`
                                    : `H${h} — ${wr!=null?wr+"% WR":"no data"} — click to ${isOn?"remove":"add"}`}
                                  style={{
                                    width:46,padding:"5px 2px",borderRadius:4,fontSize:10,fontWeight:700,
                                    cursor:blocked?"not-allowed":"pointer",
                                    background:cellBg,border:`2px solid ${borderCol}`,
                                    color:isOn?statCol:isDead?"#ef444488":C.dim,
                                    textAlign:"center",transition:"all 0.12s",position:"relative",
                                    opacity:blocked?0.55:1,
                                  }}>
                                  <div style={{fontSize:11,fontWeight:900}}>{String(h).padStart(2,"0")}h</div>
                                  <div style={{fontSize:8,marginTop:1,color:isDead?"#ef4444":statCol,opacity:isDead?1:0.8}}>
                                    {isDead?"⛔":wr!=null?`${wr}%`:"—"}
                                  </div>
                                  {isCurrent && <div style={{position:"absolute",top:-3,right:-3,width:7,height:7,borderRadius:"50%",background:"#60a5fa",border:"1px solid #1e293b"}}/>}
                                  {isOn && !isDead && <div style={{position:"absolute",bottom:-3,left:"50%",transform:"translateX(-50%)",width:6,height:6,borderRadius:"50%",background:statCol}}/>}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                      <div style={{marginTop:8,fontSize:11,fontWeight:700,color:allowed.length>0?C.green:C.dim}}>
                        {allowed.length > 0
                          ? `✅ ${allowed.length} hours active: ${allowed.map(h=>String(h).padStart(2,"0")+":00").join(", ")}`
                          : "No filter — trading all 24 hours"}
                      </div>
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        );
      })()}

      {/* LLM Agent Providers */}
      <div style={sec}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12, color: C.blue }}>🤖 LLM Agent Providers</div>
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 10 }}>
          Gemini CLI is free and always available. "Auto" tries Claude Code → Gemini CLI → Gemini API → Ollama.
        </div>
        {AGENT_NAMES.map(agent => (
          <div key={agent} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: C.muted, width: 80, flexShrink: 0, textTransform: "capitalize" }}>{agent}</span>
            <select value={ap[agent] || "auto"} onChange={e => updAp(agent, e.target.value)} style={{ ...inp, flex: 1 }}>
              {LLM_PROVIDERS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
        ))}
        {local.agentProviders?.chat === "ollama" && ollamaModels.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
            <span style={{ fontSize: 12, color: C.muted, width: 80, flexShrink: 0 }}>Ollama model</span>
            <select value={local.ollamaModel || ""} onChange={e => upd({ ollamaModel: e.target.value })} style={{ ...inp, flex: 1 }}>
              {ollamaModels.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        )}
      </div>

      {/* Telegram alerts */}
      <div style={sec}>
        <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
          <input type="checkbox" checked={telegramOn} onChange={e => updAt("telegramEnabled", e.target.checked)} style={{ width: 16, height: 16 }} />
          <span style={{ fontWeight: 700, fontSize: 14 }}>Enable Telegram Alerts</span>
        </label>
        {!telegramOn && <div style={{ marginTop: 8, fontSize: 12, color: C.muted }}>Telegram alerts disabled.</div>}
      </div>

      {telegramOn && (
        <div style={sec}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12, color: C.blue }}>Alert Types</div>
          {Object.entries(LABELS).map(([key, label]) => (
            <div key={key} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
              <input type="checkbox" checked={at[key] !== false} onChange={e => updAt(key, e.target.checked)} style={{ width: 14, height: 14, flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: 13 }}>{label}</span>
              {at[key] !== false && (
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: C.muted }}>
                  every
                  <select value={af[key] || 5} onChange={e => updAf(key, e.target.value)} style={inp}>
                    {FREQ_OPTS.map(v => <option key={v} value={v}>{v}m</option>)}
                  </select>
                </label>
              )}
            </div>
          ))}
        </div>
      )}

      {telegramOn && (
        <div style={sec}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: C.blue }}>
            Quiet Hours {qh.timezone ? "(" + qh.timezone + ")" : "(UTC)"}
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", marginBottom: 10 }}>
            <input type="checkbox" checked={!!qh.enabled} onChange={e => updQh({ enabled: e.target.checked })} style={{ width: 14, height: 14 }} />
            <span style={{ fontSize: 13 }}>Suppress alerts during quiet hours</span>
          </label>
          {qh.enabled && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10, fontSize: 13 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <span>From</span>
                <input type="number" min={0} max={23} value={qh.startHour ?? 22} onChange={e => updQh({ startHour: Number(e.target.value) })} style={{ ...inp, width: 50 }} />
                <span>:00 to</span>
                <input type="number" min={0} max={23} value={qh.endHour ?? 6} onChange={e => updQh({ endHour: Number(e.target.value) })} style={{ ...inp, width: 50 }} />
                <span>:00 {qh.timezone ? shortTz(qh.timezone) : "UTC"}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ color: C.muted, fontSize: 12 }}>Timezone:</span>
                <select
                  value={qh.timezone || "America/Jamaica"}
                  onChange={e => updQh({ timezone: e.target.value })}
                  style={{ ...inp, width: 220 }}
                >
                  <option value="America/Jamaica">America/Jamaica (EST/EDT, UTC-5/-4)</option>
                  <option value="America/New_York">America/New_York (ET)</option>
                  <option value="America/Chicago">America/Chicago (CT)</option>
                  <option value="America/Denver">America/Denver (MT)</option>
                  <option value="America/Los_Angeles">America/Los_Angeles (PT)</option>
                  <option value="Europe/London">Europe/London (UK)</option>
                  <option value="Europe/Berlin">Europe/Berlin (CET)</option>
                  <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
                  <option value="UTC">UTC</option>
                </select>
              </div>
              <div style={{ fontSize: 11, color: C.muted }}>
                Default: 22:00–06:00 Jamaica time. Alarms & system alerts pause during this window in your local timezone.
              </div>
            </div>
          )}
        </div>
      )}

      {/* Scanner status */}
      <div style={sec}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: C.blue }}>Scanner Status</div>
        {scanStatus ? (
          <div style={{ fontSize: 13 }}>
            <div style={{ marginBottom: 6 }}>
              <span style={{ color: C.muted }}>Connection: </span>
              <span style={{ color: scanStatus.wsStatus === "connected" ? C.green : scanStatus.wsStatus === "connecting" ? C.amber : C.red, fontWeight: 700 }}>
                {scanStatus.wsStatus?.toUpperCase() || "UNKNOWN"}
              </span>
            </div>
            <div>
              <span style={{ color: C.muted }}>Watching: </span>
              <span>{scanStatus.activeSymbols?.length > 0 ? scanStatus.activeSymbols.join(", ") : "No symbols"}</span>
            </div>
          </div>
        ) : <div style={{ fontSize: 13, color: C.muted }}>Loading...</div>}
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <button onClick={async () => { setTestMsg("Sending..."); try { await onTestAlert(); setTestMsg("✓ Test alert sent!"); } catch { setTestMsg("✗ Failed"); } setTimeout(() => setTestMsg(""), 3000); }}
          style={{ padding: "8px 16px", borderRadius: 4, background: `${C.blue}22`, border: `1px solid ${C.blue}66`, color: C.blue, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
          📨 Test Alert
        </button>
        <button onClick={async () => { setTestMsg("Restarting..."); try { await onRestartScanner(); setTestMsg("✓ Restarting"); } catch { setTestMsg("✗ Failed"); } setTimeout(() => setTestMsg(""), 3000); }}
          style={{ padding: "8px 16px", borderRadius: 4, background: `${C.amber}22`, border: `1px solid ${C.amber}66`, color: C.amber, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
          🔄 Restart Scanner
        </button>
        <div style={{ padding: "8px 16px", borderRadius: 4, background: `${C.green}11`, border: `1px solid ${C.green}44`, color: C.green, fontWeight: 700, fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 10 }}>✓</span><span>Auto-saving</span>{saving && <span>⟳</span>}
        </div>
        {testMsg && <span style={{ fontSize: 12, color: C.amber }}>{testMsg}</span>}
      </div>
    </div>
  );
}

// ── T602: Strategy Manager Panel ─────────────────────────────────────────────
function badgeLabel(badgeId) {
  const map = {
    "verified-live": "\u2705 Verified Live (\u226560% over 30+ trades)",
    "monte-carlo-robust": "\ud83c\udfb2 Monte Carlo Robust",
    "improved": "\ud83d\udd27 Improved",
    "target-100-day": "\ud83d\udcb0 Targets $100/day",
    "target-10tpd-60wr": "\ud83d\udcc8 \u226510 TPD @ 60%+",
    "target-rr2-50tpw": "\ud83d\udcd0 1:2 RR \u00b7 50 TPW",
    "needs-improvement": "\u26a0\ufe0f Needs Improvement",
    "hard-block": "\ud83d\udeab Hard Block (<40%)",
    "improvable": "\ud83d\udee0 Improvable",
    "untested": "\ud83c\udd95 Untested",
    "verified-paper": "\ud83d\udcc4 Verified Paper",
    "stale": "\ud83d\udca4 Stale (>14d inactive)",
  };
  return map[badgeId] || badgeId;
}

function StrategyPanel({ strategies=[], activeStrategy="", onUpload, onActivate, onDelete, onPreview, hourlyStats={}, sessionStats={}, bestSession=null, timeline=[], apiFetch }) {
    // ── Goal-mode recommender + health + badges state (Rule #9 infrastructure, 2026-09-08) ──
  const [goalMode, setGoalMode] = React.useState(null);  // null | "MODE_100_DAY" | "MODE_VOLUME_60" | "MODE_RR2_WEEKLY"
  const [recommendations, setRecommendations] = React.useState(null);
  const [health, setHealth] = React.useState(null);
  const [badgesMap, setBadgesMap] = React.useState({});
  const [improving, setImproving] = React.useState(null);
  const [recommendLoading, setRecommendLoading] = React.useState(false);

  React.useEffect(() => {
    if (!apiFetch) return;
    apiFetch("/api/strategies/health").then(r=>r.ok?r.json():null).then(d=>{ if (d) setHealth(d); }).catch(()=>{});
    apiFetch("/api/strategies/badges").then(r=>r.ok?r.json():null).then(d=>{ if (d && d.badges) setBadgesMap(d.badges); }).catch(()=>{});
  }, [apiFetch]);

  React.useEffect(() => {
    if (!apiFetch || !goalMode) return;
    setRecommendLoading(true);
    apiFetch("/api/strategies/recommend?goal=" + goalMode + "&stake=10")
      .then(r=>r.ok?r.json():null)
      .then(d=>{ if (d) setRecommendations(d); setRecommendLoading(false); })
      .catch(()=>{ setRecommendLoading(false); });
  }, [apiFetch, goalMode]);

  const handleAutoPromote = React.useCallback(async () => {
    if (!apiFetch) return;
    if (!confirm("Auto-promote will swap to the highest-ranked strategy that meets your goal. Continue?")) return;
    try {
      const r = await apiFetch("/api/strategies/auto-promote", { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({ goal: goalMode || "MODE_100_DAY", stake: 10 }) });
      const d = await r.json();
      if (d.ok && d.promoted) {
        alert("✅ Auto-promoted: " + d.from + " → " + d.to);
        // Refresh the strategy list
        const list = await apiFetch("/api/strategies/list");
        if (list.ok) {
          const ld = await list.json();
          if (ld && ld.active_by_user) {
            const username = JSON.parse(localStorage.getItem("kara_auth_user") || "{}").username;
            const active = ld.active_by_user[username] || "";
            if (active) {
              // tell parent to update
              if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent("kara:strategy-changed", { detail: { name: active } }));
            }
          }
        }
      } else {
        alert("Not promoted: " + (d.reason || "unknown") + (d.top ? "\nTop candidate: " + d.top.name + " — " + (d.top.badges||[]).join(",") : ""));
      }
      // refresh health + badges
      const h = await apiFetch("/api/strategies/health");
      if (h.ok) setHealth(await h.json());
      const b = await apiFetch("/api/strategies/badges");
      if (b.ok) { const bd = await b.json(); if (bd && bd.badges) setBadgesMap(bd.badges); }
    } catch (e) { alert("Auto-promote failed: " + e.message); }
  }, [apiFetch, goalMode]);

  const handleLogImprovement = React.useCallback(async (name, liftWR, liftPnL) => {
    if (!apiFetch) return;
    setImproving(name);
    try {
      const r = await apiFetch("/api/strategies/" + encodeURIComponent(name) + "/improve", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ source: "user", lift_wr: liftWR, lift_pnl_per_day: liftPnL, notes: "Logged from strategy panel" })
      });
      const d = await r.json();
      if (d.ok) {
        const b = await apiFetch("/api/strategies/badges");
        if (b.ok) { const bd = await b.json(); if (bd && bd.badges) setBadgesMap(bd.badges); }
      } else {
        alert("Failed: " + (d.error || "unknown"));
      }
    } catch (e) { alert("Failed: " + e.message); }
    setImproving(null);
  }, [apiFetch]);

  const GOAL_MODES = [
    { id: "MODE_100_DAY", label: "$100/day profit", desc: "≥$100 net realised per day at $10 stake", color: C.green },
    { id: "MODE_VOLUME_60", label: "10+ TPD @ 60% WR", desc: "≥10 trades/day at ≥60% daily WR", color: C.blue },
    { id: "MODE_RR2_WEEKLY", label: "1:2 RR · 50 TPW @ 60% WR", desc: "1:2 RR + ≥50 TPW + ≥60% weekly WR", color: C.amber },
  ];

  const replaceability = health && health.replaceable;

  const [dragOver, setDragOver] = React.useState(false);
  const fileRef = React.useRef(null);
  const [liveConditions, setLiveConditions] = React.useState(null);
  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files[0]; if (f) onUpload(f);
  };

  React.useEffect(() => {
    if (!apiFetch) return;
    const fetchLive = () => apiFetch("/api/market-conditions/live").then(r => r.ok ? r.json() : null).then(d => { if (d) setLiveConditions(d); }).catch(() => {});
    fetchLive();
    const iv = setInterval(fetchLive, 15000);
    return () => clearInterval(iv);
  }, [apiFetch]);

  const bestHours = Object.entries(hourlyStats || {})
    .sort((a,b) => b[1].winRate - a[1].winRate)
    .slice(0, 4);

  // Daily-aggregated cumulative PnL trend for the active strategy (one point per day, from strategy_daily)
  const timelineCurve = [...(timeline||[])]
    .sort((a,b) => String(a.date).localeCompare(String(b.date)))
    .reduce((acc, d) => { acc.push((acc.length ? acc[acc.length-1] : 0) + (Number(d.total_pnl) || 0)); return acc; }, []);

  return (
    <div style={{padding:16,display:"flex",flexDirection:"column",gap:16,maxWidth:800}}>
      {/* Active Strategy Card */}
      <div style={{background:C.panel,border:`1px solid ${C.green}55`,borderRadius:10,padding:20,boxShadow:"0 4px 20px rgba(0,0,0,0.2)"}}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16}}>
          <div>
            <div style={{fontSize:11,color:C.muted,textTransform:"uppercase",letterSpacing:2,marginBottom:4}}>ACTIVE STRATEGY</div>
            <div style={{fontSize:20,fontWeight:900,color:C.green}}>✅ {activeStrategy.replace(/\.(md|txt)$/i,"").replace(/_/g," ") || "none"}</div>
          </div>
          <button onClick={()=>onPreview(activeStrategy)} style={{background:C.dim,border:`1px solid ${C.border}`,color:C.text,padding:"6px 14px",borderRadius:6,cursor:"pointer",fontSize:12,fontWeight:700}}>VIEW FULL SCHEMA</button>
        </div>

        <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:12}}>
          {/* Best Overall Hours Section */}
          {bestHours.length > 0 && (
            <div style={{background:C.bg, borderRadius:8, padding:14, border:`1px solid ${C.amber}33`}}>
              <div style={{fontSize:11, color:C.amber, fontWeight:700, letterSpacing:1, marginBottom:10, display:"flex", alignItems:"center", gap:6}}>
                🕒 BEST OVERALL (KINGSTON)
              </div>
              <div style={{display:"grid", gridTemplateColumns:"repeat(2, 1fr)", gap:6}}>
                {bestHours.map(([hour, stats]) => {
                  const jaHour = (parseInt(hour) - 5 + 24) % 24;
                  return (
                    <div key={hour} style={{textAlign:"center", background:C.panel, borderRadius:6, padding:"8px 4px", border:`1px solid ${stats.winRate>=60?C.green+'44':C.border}`}}>
                      <div style={{fontSize:12, fontWeight:900, color:C.text}}>{jaHour.toString().padStart(2,'0')}:00</div>
                      <div style={{fontSize:10, fontWeight:700, color:stats.winRate>=60?C.green:stats.winRate>=45?C.amber:C.red}}>{stats.winRate}% WR</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Best Session Section */}
          {bestSession && (
            <div style={{background:C.bg, borderRadius:8, padding:14, border:`1px solid ${C.blue}33`}}>
              <div style={{fontSize:11, color:C.blue, fontWeight:700, letterSpacing:1, marginBottom:10, display:"flex", alignItems:"center", gap:6}}>
                🌍 BEST SESSION: {bestSession.name}
              </div>
              <div style={{display:"grid", gridTemplateColumns:"repeat(2, 1fr)", gap:6}}>
                {bestSession.bestHours.map((bh) => {
                  const jaHour = (bh.hour - 5 + 24) % 24;
                  return (
                    <div key={bh.hour} style={{textAlign:"center", background:C.panel, borderRadius:6, padding:"8px 4px", border:`1px solid ${bh.winRate>=60?C.green+'44':C.border}`}}>
                      <div style={{fontSize:12, fontWeight:900, color:C.text}}>{jaHour.toString().padStart(2,'0')}:00</div>
                      <div style={{fontSize:10, fontWeight:700, color:bh.winRate>=60?C.green:bh.winRate>=45?C.amber:C.red}}>{bh.winRate}% WR</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {sessionStats && Object.keys(sessionStats).length > 0 && (
          <div style={{marginTop:16, borderTop:`1px solid ${C.border}`, paddingTop:16}}>
            <div style={{fontSize:11, color:C.muted, letterSpacing:1, marginBottom:12}}>ALL SESSION PERFORMANCE (30-DAY ROLLING)</div>
            <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(180px, 1fr))", gap:10}}>
              {Object.entries(sessionStats).sort((a,b)=>b[1].winRate - a[1].winRate).map(([sName, s]) => (
                <div key={sName} style={{background:C.bg, borderRadius:8, padding:12, border:`1px solid ${sName===bestSession?.name?C.blue+'66':C.border}`, display:"flex", flexDirection:"column", gap:8}}>
                  <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start"}}>
                    <div>
                      <div style={{fontSize:10, fontWeight:700, color:sName===bestSession?.name?C.blue:C.muted, letterSpacing:1}}>{sName}</div>
                      <div style={{fontSize:18, fontWeight:900, color:s.winRate>=60?C.green:s.winRate>=45?C.amber:C.red}}>{s.winRate}%</div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:9, color:C.muted}}>{s.trades} trades</div>
                      <div style={{fontSize:9, color:s.pnl>=0?C.green:C.red}}>{s.pnl>=0?"+":""}{s.pnl} P&L</div>
                    </div>
                  </div>
                  
                  {/* Hourly Breakdown for this session */}
                  <div style={{marginTop:4, borderTop:`1px solid ${C.border}44`, paddingTop:8}}>
                    <div style={{fontSize:8, color:C.muted, marginBottom:6, fontWeight:700}}>TOP 4 HOURS (JAM)</div>
                    <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:4}}>
                      {s.bestHours && s.bestHours.length > 0 ? s.bestHours.map(bh => {
                        const jaHour = (bh.hour - 5 + 24) % 24;
                        return (
                          <div key={bh.hour} style={{background:C.panel, padding:"3px 5px", borderRadius:4, border:`1px solid ${bh.winRate>=60?C.green+'33':C.border}`, textAlign:"center"}}>
                            <div style={{fontSize:10, fontWeight:900, color:C.text}}>{jaHour.toString().padStart(2,'0')}:00</div>
                            <div style={{fontSize:8, fontWeight:700, color:bh.winRate>=60?C.green:bh.winRate>=45?C.amber:C.red}}>{bh.winRate}%</div>
                          </div>
                        );
                      }) : (
                        <div style={{gridColumn:"span 2", fontSize:8, color:C.dim, fontStyle:"italic"}}>No hourly data</div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Live current-conditions snapshot — what's the market doing right now */}
      {liveConditions?.symbols?.length > 0 && (
        <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <div style={{fontSize:11,color:C.amber,letterSpacing:2,textTransform:"uppercase"}}>LIVE MARKET CONDITIONS</div>
            <div style={{fontSize:10,color:liveConditions.is_overlap?C.green:C.muted,fontWeight:700}}>
              {liveConditions.session} {liveConditions.is_overlap?"⚡ OVERLAP":""}
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(140px, 1fr))",gap:8}}>
            {liveConditions.symbols.map(s => (
              <div key={s.symbol} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:6,padding:"8px 10px"}}>
                <div style={{fontSize:11,fontWeight:900,color:C.text}}>{s.symbol}</div>
                <div style={{fontSize:10,fontWeight:700,color:s.trend==="bullish"?C.green:s.trend==="bearish"?C.red:C.muted,marginTop:2,textTransform:"capitalize"}}>{s.trend||"—"}</div>
                <div style={{fontSize:9,color:C.dim,marginTop:2}}>ATR: {s.atr!=null?Number(s.atr).toFixed(4):"—"}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Strategy daily PnL trend — cumulative, one point per day (from strategy_daily) */}
      {timelineCurve.length >= 2 && (
        <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:16}}>
          <div style={{fontSize:11,color:C.amber,letterSpacing:2,marginBottom:4,textTransform:"uppercase"}}>STRATEGY DAILY PNL TREND (CUMULATIVE)</div>
          <div style={{fontSize:9,color:C.dim,marginBottom:10}}>Daily aggregated — one point per day, not per trade</div>
          <EquityCurve curve={timelineCurve} initial={0} height={160} C={{...C, faint: C.border}} />
        </div>
      )}

      {/* Upload */}
      <div
        onDragOver={e=>{e.preventDefault();setDragOver(true);}} onDragLeave={()=>setDragOver(false)} onDrop={handleDrop}
        onClick={()=>fileRef.current?.click()}
        style={{background:dragOver?`${C.blue}22`:C.panel,border:`2px dashed ${dragOver?C.blue:C.border}`,borderRadius:8,padding:24,textAlign:"center",cursor:"pointer",transition:"all 0.2s"}}>
        <div style={{color:C.muted,fontSize:13}}>📂 Drag & drop a <strong>.md</strong> or <strong>.txt</strong> strategy file here, or click to browse</div>
        <input ref={fileRef} type="file" accept=".md,.txt" style={{display:"none"}} onChange={e=>{ if(e.target.files[0]) onUpload(e.target.files[0]); e.target.value=""; }}/>
      </div>
      {/* Library */}
      <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:16}}>
        <div style={{fontSize:11,color:C.muted,marginBottom:10,textTransform:"uppercase",letterSpacing:1}}>Strategy Library</div>
        {strategies.length===0 && <div style={{color:C.muted,fontSize:13}}>No strategies found.</div>}
        {strategies.map(s=>(
          <div key={s.name} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 0",borderBottom:`1px solid ${C.dim}33`,flexWrap:"wrap"}}>
            <span style={{flex:1,color:s.name===activeStrategy?C.green:C.text,fontSize:13,fontWeight:s.name===activeStrategy?700:400}}>
              {s.name===activeStrategy?"✅ ":""}{s.name}
            </span>
            <span style={{fontSize:11,color:C.muted}}>{(s.size/1024).toFixed(1)}KB</span>
            <button onClick={()=>onPreview(s.name)} style={{background:C.dim,border:"none",color:C.text,padding:"2px 8px",borderRadius:4,cursor:"pointer",fontSize:11}}>Preview</button>
            {s.name!==activeStrategy && <button onClick={()=>onActivate(s.name)} style={{background:`${C.green}22`,border:`1px solid ${C.green}55`,color:C.green,padding:"2px 8px",borderRadius:4,cursor:"pointer",fontSize:11}}>Activate</button>}
            {s.name!==activeStrategy && <button onClick={()=>onDelete(s.name)} style={{background:`${C.red}11`,border:`1px solid ${C.red}44`,color:C.red,padding:"2px 8px",borderRadius:4,cursor:"pointer",fontSize:11}}>Delete</button>}
          </div>
        ))}
      </div>
    </div>
  );
}


function useGeminiLive(username, aiName, onMessage, onInterrupt) {
  const [active, setActive] = useState(false);
  const start = useCallback(() => setActive(true), []);
  const stop = useCallback(() => setActive(false), []);
  return { active, start, stop };
}

function KaraScannerMain({ authUser, onLogout }) {
  const isLando = authUser?.username === "lando" || authUser?.display_name === "Lando";
  const aiName = isLando ? "Lala" : "Kara";
  _karaUserName = displayNameFor(authUser);
  // Namespace all localStorage keys by username so different users on the same
  // browser never see each other's settings (e.g. autoTrade, stake, chart slots).
  const _un = authUser?.username || 'anon';
  const sk = (key) => `${_un}_${key}`;
  // Default landing = scanner tab (reverted 2026-09-25 per Kenyon — voice tab removed)
  const [tab,setTab] = useState("scanner");
  const [dataStatus, setDataStatus] = useState(null);
  const [dataStatusLoading, setDataStatusLoading] = useState(false);
  const [userSettings, setUserSettings] = useState(() => {
    try { return JSON.parse(localStorage.getItem(sk("kara_user_settings"))); } catch { return null; }
  });
  // ── 📊 Scanner Status state (daily profit lock, shadow stats, risk) ──
  const [scanStatus, setScanStatus] = useState(null);
  useEffect(() => {
    let unmounted = false;
    const fetchScanStatus = () => {
      apiFetch("/api/scanner/status", { silent: true })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (!unmounted && d) setScanStatus(d); })
        .catch(() => {});
    };
    fetchScanStatus();
    const sid = setInterval(fetchScanStatus, 15000);
    return () => { unmounted = true; clearInterval(sid); };
  }, []);
  // ── 💰 Daily $35 Challenge state ──
  const [challengeData, setChallengeData] = useState(null);
  useEffect(() => {
    let unmounted = false;
    const fetchChallenge = () => {
      fetch("/challenge-35/status")
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (!unmounted && d) setChallengeData(d); })
        .catch(() => {
          apiFetch("/api/challenge/status", { silent: true })
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (!unmounted && d && d.ok) setChallengeData(d); })
            .catch(() => {});
        });
    };
    fetchChallenge();
    const cid = setInterval(fetchChallenge, 10000);
    return () => { unmounted = true; clearInterval(cid); };
  }, []);
  // ── 🤖 ML Signal Engine state ──
  const [mlStatus, setMlStatus]     = useState(null);
  const [mlMetrics, setMlMetrics]   = useState(null);
  const [mlPrediction, setMlPrediction] = useState(null);
  const [mlTarget, setMlTarget]     = useState(0.65);
  const [mlImproving, setMlImproving] = useState(false);
  const [mlPredictSym, setMlPredictSym] = useState(SYMBOLS[0]);
  const [mlPredictResult, setMlPredictResult] = useState(null);
  const [mlPredictCtx, setMlPredictCtx] = useState(null);
  // ── 📋 ML Outcome logging state ──
  const [mlOutcomeSignalId, setMlOutcomeSignalId] = useState("");
  const [mlOutcomeWin, setMlOutcomeWin]           = useState(true);
  const [mlOutcomePnl, setMlOutcomePnl]           = useState("");
  const [mlOutcomeExit, setMlOutcomeExit]         = useState("");
  const [mlOutcomeResult, setMlOutcomeResult]     = useState(null);
  // ── 📈 ML Full history state ──
  const [mlFullHistory, setMlFullHistory]         = useState(null);
  // ── 💾 Data Feeder progress state ──
  const [dataFeederStatus, setDataFeederStatus]   = useState(null);
  const [mlFullHistoryLoading, setMlFullHistoryLoading] = useState(false);
  // ── 📱 Telegram ML config state ──
  const [mlTelegramEnabled, setMlTelegramEnabled] = useState(() => { try { return localStorage.getItem(sk("ml_telegram_enabled")) === "true"; } catch { return false; } });
  const [mlTelegramOnSignal, setMlTelegramOnSignal] = useState(() => { try { return localStorage.getItem(sk("ml_telegram_on_signal")) !== "false"; } catch { return true; } });
  const [mlTelegramMinConf, setMlTelegramMinConf] = useState(() => { try { return parseFloat(localStorage.getItem(sk("ml_telegram_min_conf")) || "0.65"); } catch { return 0.65; } });
  const [mlTelegramSending, setMlTelegramSending] = useState(false);
  const [mlTelegramResult, setMlTelegramResult] = useState(null);
  // ── 🔧 Strategy Optimizer state ──
  const [improveStatus, setImproveStatus] = useState(null);
  const [improveResults, setImproveResults] = useState([]);
  const [improveSymbol, setImproveSymbol] = useState("XAUUSD");
  const [improveTf, setImproveTf] = useState(3600);
  const [improveTgtWin, setImproveTgtWin] = useState(0.60);
  const [improveTgtDD, setImproveTgtDD] = useState(0.15);
  const [improveTgtPF, setImproveTgtPF] = useState(1.5);
  // ── 🗠 Chart Sub-Agent state (Charts tab) ───────────────────────────────
  const [chartAgentOpen,    setChartAgentOpen]    = useState(false);
  const [chartAgentTask,    setChartAgentTask]    = useState("");
  const [chartAgentBusy,    setChartAgentBusy]    = useState(false);
  const [chartAgentHistory, setChartAgentHistory] = useState([]);
  const [chartAgentLog,     setChartAgentLog]     = useState([]);
  // ── 📊 Inline Charts Tab state ───────────────────────────────────────────
  // chartSlots: each slot is { sym, tf } — fully independent timeframe per chart
  const [chartSlots, setChartSlots] = useState(() => {
    try { const v = localStorage.getItem(sk("kara_chart_slots")); if (v) return JSON.parse(v); } catch {}
    return [{ sym: "XAUUSD", tf: "1m" }];
  });
  const [chartFocusKey, setChartFocusKey] = useState("XAUUSD_1m"); // key = "SYM_TF"
  const [chartLayout,     setChartLayout]     = useState("single");   // single|dual|quad
  const [chartGridFullscreen, setChartGridFullscreen] = useState(false);
  const [fsHeight, setFsHeight] = useState(() => window.innerHeight);
  const chartGridRef = useRef(null);
  const [newSlotSym, setNewSlotSym] = useState("XAUUSD"); // for the Add Chart control
  const [newSlotTf,  setNewSlotTf]  = useState("1m");
  const [scannerTf, setScannerTf] = useState("1m");  // analysis TF for scanner tab
  const [chartCandleData, setChartCandleData] = useState({});         // { "SYM_TF": { candles, loading, error } }
  const chartCandleDataRef = useRef({});  // mirror for use inside callbacks without stale closures
  const [session,setSession]       = useState(getSessions());
  // ── Add derived `sessionLabel` (additive alias of session.name) ──
  //     Used by chat context + VoiceHomeTab. Mirrors `session` so existing
  //     code keeps working without touching the original state (Rule #1).
  const sessionLabel = useMemo(() => (session && session.name) || "", [session]);
  const [selectedSyms,setSelectedSyms] = useState(new Set()); // empty — user must pick
  // Strategy-activated symbols (e.g. BOOM900) that aren't in the static SYMBOLS catalogue still need
  // to render as cards/chart options — union the static list with whatever's actually selected so the
  // scanner never hides a symbol the active strategy requires.
  const displaySymbols = useMemo(() => {
    const extra = [...selectedSyms].filter(s => !SYMBOLS.includes(s));
    return extra.length ? [...SYMBOLS, ...extra] : SYMBOLS;
  }, [selectedSyms]);
  const displayGroups = useMemo(() => {
    const extra = [...selectedSyms].filter(s => !SYMBOLS.includes(s));
    return extra.length ? [...SYMBOL_GROUPS, { label: "STRATEGY SYMBOLS", syms: extra, color: "#fb7185", primary:true }] : SYMBOL_GROUPS;
  }, [selectedSyms]);
  // Non-primary groups collapse by default to save space
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    const init = {};
    SYMBOL_GROUPS.forEach(g => { if (!g.primary) init[g.label] = true; });
    return init;
  });
  const toggleGroupCollapse = (label) => setCollapsedGroups(prev => ({ ...prev, [label]: !prev[label] }));

  const [symData,setSymData]       = useState({}); // { sym: { candles,analysis,conds,cnt,ticks,loading,error,aiData } }
  const [focusSym,setFocusSym]     = useState(null); // which symbol card is focused
  // KENYON 2026-10-11: viewport-aware responsive state — eliminates the big black void on mobile when no
  //   symbol is focused / chart data is offline. Re-reads on resize so phone↔desktop flips work.
  const [viewportW, setViewportW] = useState(() => typeof window !== "undefined" ? window.innerWidth : 1280);
  useEffect(() => {
    const onResize = () => setViewportW(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const isMobile   = viewportW <= 768;
  const isTablet   = viewportW > 768 && viewportW <= 1024;
  const isCompact  = isMobile || isTablet;
  const [tickFeed,setTickFeed]     = useState([]); // real-time price events
  const [logs,setLogs]             = useState([]);
  const [alerts,setAlerts]         = useState([]);
  const [popup,setPopup]           = useState(null);
  const [wsStatus,setWsStatus]     = useState("disconnected");
  const [voiceState,setVoiceState] = useState("idle");
  // KENYON 2026-09-25: lift isSpeakingRef into state so the chat FAB can show
  //   "bot is speaking" inline waveform. The ref still mirrors for legacy paths.
  const [isSpeaking,setIsSpeaking] = useState(false);
  const [serverHealth,setServerHealth] = useState({});
  const [history,setHistory]       = useState([]);
  // ── Add alias used by VoiceHomeTab chat-context (chat panel) ──
  //     Mirrors `history` so existing chat-context code keeps working.
  //     (Additive, no other state touched — Rule #1.)
  const [realTrades, setRealTrades] = useState([]);
  useEffect(() => { setRealTrades(history); }, [history]);
  const [aiScanning,setAiScanning] = useState(null); // sym currently being AI-scanned
  // ── 📡 TradingView alert flash (live indicator) ────────────────────────────
  const [todayPreds,setTodayPreds] = useState([]);
  // All predictions from last 60 days (for history tab with W/L tracking)
  const [allPreds,setAllPreds] = useState([]);
  // ── Kara Chat ─────────────────────────────────────────────────────────────
  const [chatOpen,setChatOpen]         = useState(false);
  const [chatDualOpen,setChatDualOpen] = useState(false); // 2026-10-02 — DualChatPanel modal
  const [chatThinking,setChatThinking] = useState(false);
  const [chatVoice,setChatVoice]       = useState("idle");
  const [wakeActive,setWakeActive]     = useState(false);   // wake word always-on listener
  const [micDenied,setMicDenied]       = useState(false);   // true when Chrome mic permission denied

  // ── ContextBroker publish (Task 2026-10-02) ────────────────────────────────
  // Watches dashboard state and publishes to /api/context/update so chat,
  // voice, and sub-agents can SEE what the user sees. Best-effort: if the
  // endpoint is unreachable (server down), we silently no-op — never block UI.
  const __publishContext = useCallback((event_type, data) => {
    try {
      const token = localStorage.getItem("kara_auth_token") || "";
      fetch("/api/context/update", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ user: _karaUserName, event_type, data }),
        keepalive: true,
      }).catch(() => {});
    } catch (_) { /* no-op */ }
  }, []);
  // 2026-10-02 11:48Z: ContextBroker publish effects moved to AFTER the
  // state declarations they reference (activeStrategy at line 3283,
  // tradeStake at line 3239). The previous location crashed the render with
  // "Cannot access 'At' before initialization" because the deps arrays
  // referenced consts that hadn't been initialized yet. See the relocated
  // block right before handleEvalConditions (line ~3297).
  // Relocated from lines 3148-3155.

  // Mic-permission probing is consolidated into the single mount effect further
  // down (one query, one onchange handler, cancellable auto-start timer). Two
  // parallel probes used to register two onchange handlers and leave a stray
  // 800 ms timer alive after unmount.
  // ── ML polling useEffect ─────────────────────────────────────────────────
  useEffect(() => {
    const fetchMl = async () => {
      try {
        const [s, m] = await Promise.all([
          apiFetch("/api/ml/status", { silent: true }).then(r => r.json()),
          apiFetch("/api/ml/metrics", { silent: true }).then(r => r.json()),
        ]);
        setMlStatus(s);
        setMlMetrics(m);
      } catch(e) { console.error("ML fetch error:", e); }
    };
    fetchMl();
    const id = setInterval(fetchMl, 5000);
    return () => clearInterval(id);
  }, []);
  // ── Data Feeder status polling ──────────────────────────────────────────
  useEffect(() => {
    const fetchDataStatus = async () => {
      try {
        const data = await apiFetch("/api/data/status", { silent: true }).then(r => r.json());
        if (data.ok) setDataFeederStatus(data);
      } catch(e) { console.error("Data status fetch error:", e); }
    };
    fetchDataStatus();
    const id = setInterval(fetchDataStatus, 5000); // Update every 5 seconds
    return () => clearInterval(id);
  }, []);
  // ── Strategy Optimizer polling ──────────────────────────────────────────
  useEffect(() => {
    if (!improveStatus?.active) return;
    const id = setInterval(async () => {
      try {
        const s = await apiFetch("/api/improve/status", { silent: true }).then(r => r.json());
        setImproveStatus(s);
        if (!s.active) {
          const r = await apiFetch("/api/improve/results", { silent: true }).then(r => r.json());
          setImproveResults(r.candidates || []);
        }
      } catch {}
    }, 4000);
    return () => clearInterval(id);
  }, [improveStatus?.active]);

  const runMlPredict = async () => {
    const d = symDataRef.current[mlPredictSym];
    const a = d?.analysis;
    const sltp = a ? computeSLTP(a, d.aiData) : {};
    const si = sessionRef.current;
    const payload = a ? {
      symbol: mlPredictSym,
      price: a.price, ema13: a.ema13, ema200: a.ema200,
      atr: a.atr, atr_sma: a.atr_sma, rsi: a.rsi,
      trend: a.trend, bos_choch: a.bos_choch || "none",
      liq_sweep: a.liq_sweep || false, liq_sweep_dir: a.liq_sweep_dir || "",
      direction: a.direction || "NONE", session: si?.name || "other",
      htf_ob: d.htf15?.ob || false, htf_fvg: d.htf15?.fvg || false,
      rr_ratio: sltp.rr || 0, conditions_passed: d.cnt || 0,
      ema13_pullback: a.ema13_pullback_buy || a.ema13_pullback_sell || false,
      zone: a.zone || "neutral",
      candle_open: a.open, candle_high: a.high, candle_low: a.low, candle_close: a.close,
      entry_price: a.price, sl: sltp.sl, tp1: sltp.tp1, tp2: sltp.tp2, tp3: sltp.tp3,
    } : { symbol: mlPredictSym };
    setMlPredictCtx(payload);
    try {
      const r = await apiFetch("/api/ml/predict", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      setMlPredictResult(await r.json());
    } catch (e) { setMlPredictResult({ error: e.message }); }
  };

  const pendingChatActionRef = useRef(null);
  const [chatUnread,setChatUnread]     = useState(0);
  const [alertMessages,setAlertMessages] = useState([]); // market event alerts — separate from chat
  const [alertVoice,setAlertVoice]     = useState(()=>{ try{return localStorage.getItem(sk("amber_alert_voice"))==="true";}catch{return false;} }); // speak alerts aloud (OFF by default)
  const [alertsEnabled,setAlertsEnabled] = useState(()=>{ try{return localStorage.getItem(sk("amber_alerts"))!=="false";}catch{return true;} });
  const [autoTrade,setAutoTrade]       = useState(()=>{ try{ const v=localStorage.getItem(sk("amber_auto_trade")); return v===null?true:v==="true"; }catch{return true;} });
  const [deadZonesEnabled,setDeadZonesEnabled] = useState(()=>{ try{ const v=localStorage.getItem(sk("amber_dead_zones")); return v===null?true:v==="true"; }catch{return true;} });
  const [mlAutoTrade,setMlAutoTrade]   = useState(()=>{ try{ const v=localStorage.getItem(sk("amber_ml_auto_trade")); return v===null?false:v==="true"; }catch{return false;} });
  const [mlAutoConfidence,setMlAutoConfidence] = useState(()=>{ try{ const v=parseFloat(localStorage.getItem(sk("amber_ml_auto_confidence"))); return isNaN(v)?75:Math.min(99,Math.max(55,v)); }catch{return 75;} }); // stored as integer % (55–99)
  const [tradeStake,setTradeStake]     = useState(()=>{ try{return parseFloat(localStorage.getItem(sk("amber_trade_stake"))||"1");}catch{return 1;} });
  const [tradingTf,setTradingTf]       = useState(()=>{ try{return localStorage.getItem(sk("amber_trading_tf"))||null;}catch{return null;} }); // null = use strategy's TF

  // ── Settings hydration guard (2026-09-25, Kenyon: stake kept resetting to $1 / ML kept switching off)
  //    Server settings are the truth. The widgets no longer POST blind localStorage defaults on mount.
  const settingsHydratedRef = useRef(false);
  useEffect(()=>{
    let cancelled=false, tries=0;
    const pull=async()=>{
      try{
        const r=await apiFetch("/api/user/settings");
        const d=await r.json();
        if(cancelled) return;
        if(d && (d.stake!==undefined || d.mlAutoTrade!==undefined)){
          if(typeof d.stake==="number" && d.stake>0)               setTradeStake(d.stake);
          if(typeof d.mlAutoTrade==="boolean")                     setMlAutoTrade(d.mlAutoTrade);
          if(typeof d.mlAutoConfidence==="number")                 setMlAutoConfidence(Math.min(99,Math.max(55,Math.round(d.mlAutoConfidence*100))));
          if(typeof d.autoTrade==="boolean")                       setAutoTrade(d.autoTrade);
          if(typeof d.alertsEnabled==="boolean")                   setAlertsEnabled(d.alertsEnabled);
          if(typeof d.alertVoice==="boolean")                      setAlertVoice(d.alertVoice);
          if(typeof d.tradingTf==="string"||d.tradingTf===null)    setTradingTf(d.tradingTf||null);
          if(Array.isArray(d.selectedSymbols)&&d.selectedSymbols.length) setUserSettings(prev=>({...(prev||{}),selectedSymbols:d.selectedSymbols}));
          console.log("[Settings-Hydrated]", "stake="+d.stake, "ml="+d.mlAutoTrade, "conf="+d.mlAutoConfidence, "auto="+d.autoTrade);
          settingsHydratedRef.current=true; return;
        }
      }catch(e){}
      if(!cancelled && tries++<3){ setTimeout(pull, 1500*tries); return; }
      console.warn("[Settings-Hydrate] server unreachable — auto-save stays disabled to avoid clobbering");
    };
    pull();
    return ()=>{ cancelled=true; };
  },[]);
  const [activeTrades,setActiveTrades] = useState([]);
  const [runningTasks,setRunningTasks] = useState([]);
  const [accountInfo,setAccountInfo]   = useState(null);
  const [urusStatus,setUrusStatus]     = useState({}); // { [sym]: { reason, price, time } } — latest URUS gate result per symbol
  const [chatMessages,setChatMessages] = useState([{
    role:"assistant",
    content:`Hey ${_karaUserName}! I'm ${aiName}, your AI trading co-pilot. I can add symbols, run AI scans, analyze setups, call Docker MCP tools, and control the entire dashboard. What do you need?`,
    time:"—",
  }]);
  // ── T502: Personality mode ──────────────────────────────────────────────
  const [personalityMode,setPersonalityMode] = useState(()=>{ try{return localStorage.getItem(sk("amber_personality_mode"))||"casual";}catch{return "casual";} });
  // ── T603: Strategy state ────────────────────────────────────────────────
  const [strategies,setStrategies] = useState([]);
  const [activeStrategy,setActiveStrategy] = useState("");
  const [strategyTimeline,setStrategyTimeline] = useState([]);
  const [missionStatus,setMissionStatus] = useState(null);
  const [symbolWr,setSymbolWr] = useState({});
  const [strategySchema,setStrategySchema] = useState(null);
  const [strategyPreviewOpen,setStrategyPreviewOpen] = useState(false);
  const [strategyPreviewContent,setStrategyPreviewContent] = useState("");
  const [strategyPreviewName,setStrategyPreviewName] = useState("");
  const getStrategyRequiredCount = useCallback((schema) => schema?.signalGate?.requiredCount ?? schema?.conditionCount ?? schema?.conditions?.length ?? 10, []);
  const getStrategyConditionCount = useCallback((schema) => schema?.conditionCount ?? schema?.conditions?.length ?? 10, []);
  // ── P8: Strategy-on-Chart condition evaluation state ─────────────────────
  const [condEvalResult, setCondEvalResult]     = useState(null);
  const [condEvalSym, setCondEvalSym]           = useState(null);
  const [condEvalUpdatedAt, setCondEvalUpdatedAt] = useState(null);

  // 2026-10-02 11:48Z: ContextBroker publish effects (RELOCATED here from
  // line 3148 — original position caused "Cannot access 'At' before
  // initialization" TDZ crash because activeStrategy/tradeStake weren't
  // declared yet). Also fixed `stake` (which never existed) → `tradeStake`.
  // Republish on tab change
  useEffect(() => { __publishContext("tab_change", { activeTab: tab }); }, [tab, __publishContext]);
  // Republish on strategy change
  useEffect(() => { if (activeStrategy) __publishContext("strategy_activated", { activeStrategy }); }, [activeStrategy, __publishContext]);
  // Republish on symbol change (charts focus)
  useEffect(() => { if (chartFocusKey) __publishContext("symbol_change", { activeSymbol: String(chartFocusKey).split("_")[0] }); }, [chartFocusKey, __publishContext]);
  // Republish on stake change (fixed: was `stake`, should be `tradeStake`)
  useEffect(() => { if (typeof tradeStake === "number") __publishContext("stake_change", { stake: tradeStake }); }, [tradeStake, __publishContext]);

  const handleEvalConditions = React.useCallback(async (sym) => {
    if (condEvalSym === sym) { setCondEvalSym(null); setCondEvalResult(null); return; }
    setCondEvalSym(sym);
    try {
      const r = await apiFetch("/api/eval/conditions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ symbol: sym, timeframe: scannerTf || "1m" }),
      });
      const d = await r.json();
      if (r.ok) { setCondEvalResult(d); setCondEvalUpdatedAt(Date.now()); }
    } catch(e) { console.error("[EvalConds]", e); }
  }, [condEvalSym, scannerTf]);

  useEffect(() => {
    if (!condEvalSym) return;
    const id = setInterval(() => {
      apiFetch("/api/eval/conditions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ symbol: condEvalSym, timeframe: scannerTf || "1m" }),
      }).then(r => r.ok ? r.json() : null)
        .then(d => { if (d) { setCondEvalResult(d); setCondEvalUpdatedAt(Date.now()); } })
        .catch(() => {});
    }, 30000);
    return () => clearInterval(id);
  }, [condEvalSym, scannerTf]);

  const [ollamaModels, setOllamaModels] = useState([]);
  useEffect(() => {
    apiFetch("/api/ollama/models")
      .then(r => r.json())
      .then(data => { if (data.models) setOllamaModels(data.models); })
      .catch(e => console.warn("[Ollama] Failed to fetch models:", e));
  }, []);

  // ── Docker MCP ────────────────────────────────────────────────────────────
  const [dockerTools,setDockerTools]   = useState([]);
  const [dockerFetching,setDockerFetching] = useState(false);
  const [dockerLog,setDockerLog]       = useState([]);
  const [dockerUrl,setDockerUrl]       = useState("http://localhost:8811");
  const [dockerOk,setDockerOk]         = useState(false);
  // ── N8N MCP ───────────────────────────────────────────────────────────────
  const [n8nTools,setN8nTools]         = useState([]);
  const [n8nFetching,setN8nFetching]   = useState(false);
  const [n8nLog,setN8nLog]             = useState([]);
  const [n8nOk,setN8nOk]              = useState(false);
  const [autoTrigger,setAutoTrigger]   = useState("");


  // ── Refs ─────────────────────────────────────────────────────────────────
  const wsRef           = useRef(null);
  const reqIdRef        = useRef(1);
  const pendingRef      = useRef({});
  const subMapRef       = useRef({}); // sym -> subId
  const subscribeGenerationRef = useRef({}); // discard responses from replaced/removed subscriptions
  const selectedSymsRef = useRef(new Set());
  const symDataRef      = useRef({});
  const sessionRef      = useRef(getSessions());
  const subscribeRef    = useRef(null);
  const wsRetriesRef    = useRef(0); // WS reconnect counter — capped at 15
  const wsReconnectRef  = useRef(null);
  const logsEnd         = useRef(null);
  const recRef          = useRef(null);
  const srRef           = useRef(null); // Web Speech API SpeechRecognition
  const chunksRef       = useRef([]);
  const prevCondCnt     = useRef({}); // track condition count changes for alerts
  const lastTickLog     = useRef({}); // throttle per-symbol tick logs
  const chatMsgsRef     = useRef([]);
  const chatRecRef2     = useRef(null);
  const chatChunksRef2  = useRef([]);
  const chatSRRef       = useRef(null);   // dedicated Kara voice SR instance
  const chatActiveRef   = useRef(false);  // true while Kara voice is active
  const startChatSRRef  = useRef(null);   // fn ref — starts a fresh chat SR session
  const alertsRef       = useRef([]);
  const todayPredsRef   = useRef([]);
  const lastKaraAlert      = useRef({}); // throttle: sym → timestamp of last Kara auto-msg
  const lastPullbackWatch  = useRef({}); // throttle: sym → timestamp of last micro-pullback watch alert
  const prevLiqSweep       = useRef({}); // edge-detect: sym → previous liq_sweep bool
  const lastLiqZoneAlert   = useRef({}); // throttle: sym → timestamp of last liq-in-zone alert
  // ── Comprehensive awareness refs ──────────────────────────────────────────
  const prevHTFTrend   = useRef({}); // edge-detect: sym → previous 15m trend
  const prevHTFOB      = useRef({}); // edge-detect: sym → previous 15m ob bool
  const prevHTFFVG     = useRef({}); // edge-detect: sym → previous 15m fvg bool
  const prev1mDir      = useRef({}); // edge-detect: sym → previous 1m direction
  const prevBosChoch   = useRef({}); // edge-detect: sym → previous bos/choch string
  const lastHTFAlert   = useRef({}); // throttle: 5min per sym for htf changes
  const last1mDirAlert = useRef({}); // throttle: 2min per sym for 1m direction changes
  const lastBosAlert   = useRef({}); // throttle: 2min per sym for bos alerts
  const prev1mOB          = useRef({}); // edge-detect: sym → previous 1m ob bool
  const prev1mFVG         = useRef({}); // edge-detect: sym → previous 1m fvg bool
  const last1mZoneAlert   = useRef({}); // throttle: 2min per sym for 1m OB/FVG alerts
  const sweepSeq          = useRef({}); // sym → {phase,dir,price,time,zone,zoneDetail} signal sequence
  const prev15mLiq        = useRef({}); // edge-detect: sym → previous 15m liq_sweep bool
  const lastTrendChAlert  = useRef({}); // throttle: 10min per sym for 15m trend change alerts
  const lastAutoSpeakIdx  = useRef(0);  // track which chatMessages have been auto-spoken
  const alertsEnabledRef  = useRef(true); // sync ref so useEffect reads latest without re-mounting
  const autoTradeRef      = useRef(false); // sync ref for auto-trade toggle
  const mlAutoTradeRef    = useRef(false); // sync ref for ML auto-trade toggle
  const serverSignalIdRef = useRef({});   // sym → {id, ts} — server signalId from WS push (for monitored trade linking)
  const focusSymRef       = useRef(null); // sync ref for focusSym — safe to read inside empty-dep WS effect
  const signalMarkerRef   = useRef(null); // P7-01: ref to ChartWidget's paintSignalMarker fn
  const chartVoiceRef      = useRef(null);   // kara-chart-voice: imperative handle to ChartWidget (addIndicator, zoomIn, ...)
  // 2026-10-09 agent-ui: chart registry + agent bridge refs (declared early — TDZ-safe; filled before render return)
  const chartRefs              = useRef({});    // charts-tab slot index → ChartWidget imperative handle
  const agentLiveRef           = useRef({});    // latest state snapshot for async agent callbacks
  const uiHelpersRef           = useRef({});    // shared UI helpers (used by legacy action loop + runUiAction)
  const runUiActionRef         = useRef(null);  // async (name, input) => result — executes ui_* tools
  const agentTurnRef           = useRef(null);  // async (userText, options) => text — agent runtime chat turn
  const agentConfirmRef        = useRef(null);  // async (call_id, approve) => void
  const pendingAgentConfirmRef = useRef(null);  // {call_id} of the latest unanswered confirm_required
  const agentRouteMissingRef   = useRef(0);     // ts of last 404 from /api/agent/chat (skip agent path for 5 min)
  const tradeStakeRef     = useRef(1);     // sync ref for stake amount
  const tpHitRef          = useRef({});    // {tradeId: {tp1,tp2,tp3}} — prevents duplicate TP alerts
  const lastSignalAt      = useRef({});    // cooldown: sym → timestamp of last signal fire
  const alertVoiceRef     = useRef(false); // sync ref for alertVoice state (OFF by default)
  const tabRef            = useRef("scanner"); // sync ref for current tab
  const activeStrategyRef = useRef(""); // sync ref — always current strategy name
  const strategySchemaRef     = useRef(null); // sync ref — always current parsed strategy schema
  const strategyEntryGranRef  = useRef(60);   // entry TF granularity in seconds (from schema, default 1m=60)
  const strategyHtfGranRef    = useRef(900);  // HTF granularity in seconds (from schema, default 15m=900)
  const pendingContractsRef   = useRef([]);   // auto-trade contracts pending ML outcome logging
  const sendToKaraRef         = useRef(null); // ref to sendToKara — avoids circular deps in wake word SR
  const dockerToolsRef     = useRef([]);
  const callDockerToolRef  = useRef(null); // ref so sendToKara can call it without TDZ issue
  const speechQueue        = useRef([]);   // queued speech texts — played one at a time
  const isSpeakingRef      = useRef(false); // true while audio is playing
  const processQueueRef    = useRef(null);  // queue processor — reassigned each render (refs only, no stale closure)
  const wakeWordSRRef      = useRef(null);  // always-on wake word SpeechRecognition
  const wakeWordRunRef     = useRef(false); // true = keep wake word listener alive
  const wakeWordPendingRef = useRef(false); // true = restart already scheduled — watchdog must not double-fire
  const wakeWordPendingSinceRef = useRef(0); // watchdog can recover if Android throttles/drops the timer
  const wakeWordRestartTimerRef = useRef(null); // one scheduled wake restart at a time
  const wakeWordVisibilityTimerRef = useRef(null); // cancellable foreground reacquire
  const scannerVoiceRestoreRef = useRef(null); // restores chat/wake owner after one-shot scanner voice
  const voiceOwnerResumeTimerRef = useRef(null); // cancellable owner hand-back delay
  // ── Mic ownership arbiter ────────────────────────────────────────────────
  // Android/Chrome grant ONE recognizer the microphone. Three code paths can
  // want it (scanner one-shot, chat conversation, wake word) plus TTS playback.
  // Every path acquires this ref before sr.start() and releases it in its own
  // onend/onerror. A path that does not hold the lock must never call start().
  const micOwnerRef          = useRef(null); // 'scan' | 'chat' | 'wake' | null
  const micAutoStartTimerRef = useRef(null); // cancellable 'granted' auto-start delay
  const chatVisibilityTimerRef = useRef(null); // cancellable chat reacquire on 'visible'
  const wakeWordFirstStartRef = useRef(true); // true = log "listening" once per enable session
  const startWakeWordRef   = useRef(null);  // ref to startWakeWordSR (avoids circular deps)
  const toggleChatVRef     = useRef(null);  // ref to toggleChatVoice (for wake word to call)
  const elevenPausedWakeRef = useRef(false); // ElevenLabs live mode paused wake word
  const fetchTtsRef = useRef(null); // builds + sends /api/tts (see processQueueRef)
  const toggleWakeWordRef  = useRef(null);  // ref to toggleWakeWord (avoids TDZ in sendToKara deps)
  const handleCmdRef       = useRef(null);  // ref to handleCmd — fast local commands in convo SR
  const allPredsRef        = useRef([]);    // T401: synced to allPreds for use in handleTick

  const karaWaitingRef     = useRef(false); // true = waiting mode (kara wait / kara listen)
  const speakingStartRef   = useRef(0);     // timestamp when isSpeakingRef was set true (stuck detection)
  const ttsAudioRef        = useRef(null);  // in-flight TTS <audio> element — paused when a mic owner starts
  const sendTelegramRef    = useRef(null);  // ref for sendTelegram — used in async signal pipeline
  const speakRef           = useRef(null);  // ref for speak — used in async signal pipeline
  const addTradeHistoryRef = useRef(null);  // ref for addTradeHistory — used in async signal pipeline

  // ── Helpers ───────────────────────────────────────────────────────────────

  const addLog = useCallback((msg,type="info")=>{
    const time=formatJaTime(new Date());
    setLogs(p=>[...p.slice(-200),{msg,type,time}]);
  },[]);

  // Gemini Live Integration
  const geminiLive = useGeminiLive(
    authUser?.username || "trader",
    aiName,
    useCallback((text, role) => {
      setChatMessages(p => [...p, { role, content: text, time: formatJaTime(new Date()) }].slice(-200));
    }, [setChatMessages]),
    useCallback(() => {
      addLog("🎙️ Gemini Live interrupted", "warn");
    }, [addLog])
  );

  // /api/tts request builder — reassigned every render (latest serverHealth).
  // Source-gated TTS routing (Kenyon 2026-09-05): only chatbot paths reach ElevenLabs.
  // Alerts/system -> Piper. MiniMax remains the universal fallback.
  fetchTtsRef.current = (text, source) => {
    if (!(serverHealth.tts || serverHealth.minimax || serverHealth.openai || serverHealth.groq || serverHealth.piper)) return null;
    let ttsAgent = "lala", ttsVoice = "English_ConfidentWoman";
    try {
      const _u = JSON.parse(localStorage.getItem("kara_auth_user") || "null");
      if (_u && _u.username === "ken72") { ttsAgent = "kara"; ttsVoice = "English_LovelyGirl"; }
    } catch (_) {}
    const body = { text, voice: ttsVoice, voice_id: ttsVoice };
    if (source === "chat") { body.agent = ttsAgent; body.mode = "chat"; }
    else { body.mode = source; }
    return apiFetch("/api/tts",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(body)}).catch(()=>null);
  };

  // Queue processor — reassigned every render so it always sees latest refs (no stale closure)
  processQueueRef.current = async () => {
    if (isSpeakingRef.current || speechQueue.current.length === 0) return;
    isSpeakingRef.current = true;
    setIsSpeaking(true);
    speakingStartRef.current = Date.now();
    const item = speechQueue.current.shift();
    const text = typeof item === "string" ? item : item.text;
    setSpeaking(text);
     const _speakSource = typeof item === "string" ? "chat" : (item.source || "chat");
    // ── Pause every speech recognizer while Kara/Lala speaks. Chrome allows only
    // one microphone owner; leaving wake-word SR alive here causes self-hearing,
    // intermittent OS beeps, and overlapping restart races on mobile Chrome.
    const chatWasActive = chatActiveRef.current;
    if (chatWasActive) { try { chatSRRef.current?.abort(); chatSRRef.current=null; } catch(_){} }
    if (wakeWordSRRef.current) {
      const wakeSr = wakeWordSRRef.current;
      wakeWordSRRef.current = null;
      try { wakeSr.abort(); } catch(_){}
    }
    let speechFinished = false;
    const done = () => {
       if (speechFinished) return;
       speechFinished = true;
       isSpeakingRef.current = false;
      setIsSpeaking(false);
      speakingStartRef.current = 0;
      setSpeaking("");
      if (chatWasActive && chatActiveRef.current) {
        if (startChatSRRef.current !== null) {
          // Continuous mode — resume chat SR after audio settles
          setTimeout(()=>{ if(chatActiveRef.current) startChatSRRef.current?.(); }, 150);
        } else {
          // Single-shot mode (wake word) — clean up and resume wake word listener
          setTimeout(()=>{
            chatActiveRef.current = false;
            setChatVoice("idle");
            if (wakeWordRunRef.current) setTimeout(()=>startWakeWordRef.current?.(), 400);
          }, 300);
        }
      } else if (!chatActiveRef.current && wakeWordRunRef.current) {
        // Alert / background TTS ended — immediately restart wake word listener
        setTimeout(()=>startWakeWordRef.current?.(), 400);
      }
      processQueueRef.current?.();
    };
    // Safety net: generous watchdog — 120ms/char, min 15s, max 5 min
    // (OpenAI TTS fetch + playback can exceed 60s for longer responses)
    const watchdogMs = Math.min(300000, Math.max(15000, text.length * 120));
    let ttsWatchdog = setTimeout(()=>{ if(isSpeakingRef.current){ window.speechSynthesis?.cancel(); safeDone(); } }, watchdogMs);
    // Chrome pauses speechSynthesis when the tab is backgrounded — keep it alive
    const synthResumeTimer = setInterval(()=>{ if(window.speechSynthesis?.paused) window.speechSynthesis.resume(); }, 5000);
    const safeDone = ()=>{ clearTimeout(ttsWatchdog); clearInterval(synthResumeTimer); done(); };
    if (serverHealth.tts || serverHealth.minimax || serverHealth.openai || serverHealth.groq || serverHealth.piper) {
      try {
        // Sentence chunks are prefetched when queued (speak()) so the next one is
        // ready the moment the current one ends; otherwise fetch now.
        const r = await (item.ttsPrefetch || fetchTtsRef.current(text, _speakSource));
        if (!r || !r.ok) throw new Error("tts unavailable");
        const { audio, url } = await audioFromTtsResponse(r);
        ttsAudioRef.current = audio;
        // Once duration is known, reset watchdog to exact audio length + 5s buffer
        audio.oncanplaythrough=()=>{ if(isFinite(audio.duration)&&audio.duration>0){ clearTimeout(ttsWatchdog); ttsWatchdog=setTimeout(safeDone,(audio.duration+5)*1000); } };
        audio.onended=()=>{ URL.revokeObjectURL(url); if(ttsAudioRef.current===audio) ttsAudioRef.current=null; safeDone(); };
        audio.onerror=()=>{ URL.revokeObjectURL(url); if(ttsAudioRef.current===audio) ttsAudioRef.current=null; safeDone(); };
        audio.play().catch(safeDone);
        // Prefetch one item ahead so consecutive sentences play without a gap.
        // Only one ahead: more would hit ElevenLabs' concurrency limit (429).
        const _next = speechQueue.current[0];
        if (_next && typeof _next === "object" && _next.source === "chat" && !_next.ttsPrefetch) {
          _next.ttsPrefetch = fetchTtsRef.current?.(_next.text, "chat") || null;
        }
        return; // success — don't fall through to browser synthesis
      } catch(_) {}
    }
    {
      // ── Browser SpeechSynthesis fallback — chunked to avoid Chrome 200-char cutoff ─
      if ("speechSynthesis" in window) {
        window.speechSynthesis.cancel(); // clear any stuck utterances
        // Resolve the best female British voice once
        const getBritishVoice = () => {
          const vv = window.speechSynthesis.getVoices();
          const notMale  = v => !/\b(david|mark|james|richard|daniel|george|ravi|reed|guy|tom|fred|eric|william|matthew|ryan|paul|peter|mike|brian)\b/i.test(v.name);
          const isFemale = v => /female|zira|hazel|serena|fiona|kate|emily|susan|karen|aria|jenny|eva|salli|joanna|allison|samantha|victoria|moira|natasha|amy|lisa|helena|nora|ioana/i.test(v.name);
          return vv.find(v=>/google uk english female/i.test(v.name))
            ||vv.find(v=>/hazel/i.test(v.name))
            ||vv.find(v=>isFemale(v)&&v.lang==="en-GB"&&v.localService)
            ||vv.find(v=>isFemale(v)&&v.lang.startsWith("en-GB"))
            ||vv.find(v=>/google uk english/i.test(v.name))
            ||vv.find(v=>isFemale(v)&&v.lang.startsWith("en")&&v.localService)
            ||vv.find(v=>isFemale(v)&&v.lang.startsWith("en"))
            ||vv.find(v=>v.lang==="en-GB"&&v.localService&&notMale(v))
            ||vv.find(v=>v.lang.startsWith("en-GB")&&notMale(v))
            ||vv.find(v=>v.lang.startsWith("en")&&notMale(v))
            ||vv.find(v=>v.lang.startsWith("en"))||vv[0];
        };
        // Split text at sentence boundaries into ≤220-char chunks
        // Chrome cuts off single utterances longer than ~200 chars
        const splitChunks = (t) => {
          const parts = t.split(/(?<=[.!?;:])\s+/);
          const chunks = []; let cur = "";
          for (const p of parts) {
            if (cur && (cur + " " + p).length > 220) { chunks.push(cur.trim()); cur = p; }
            else { cur = cur ? cur + " " + p : p; }
          }
          if (cur.trim()) chunks.push(cur.trim());
          return chunks.length ? chunks : [t];
        };
        const chunks = splitChunks(text);
        let chunkIdx = 0;
        const speakChunk = () => {
          if (!isSpeakingRef.current) { safeDone(); return; } // externally stopped
          if (chunkIdx >= chunks.length) { safeDone(); return; } // all done
          const u = new SpeechSynthesisUtterance(chunks[chunkIdx++]);
          u.rate = 0.93; u.pitch = 1.0; u.lang = "en-GB";
          const voice = getBritishVoice();
          if (voice) u.voice = voice;
          u.onend = speakChunk; // chain to next chunk
          u.onerror = (e) => { if (e.error === "interrupted") return; safeDone(); };
          window.speechSynthesis.speak(u);
        };
        // Wait for voices to load if needed, then start
        if (!window.speechSynthesis.getVoices().length) {
          let started = false;
          const onVoices = () => { if (!started) { started = true; if (isSpeakingRef.current) speakChunk(); } };
          window.speechSynthesis.addEventListener("voiceschanged", onVoices, {once:true});
          setTimeout(onVoices, 300); // safety net: fire even if voiceschanged already fired
        } else {
          speakChunk();
        }
      } else { safeDone(); }
    }
  };

  // T405: speak() — priority-1 items are NEVER dropped
  const speak = useCallback((rawText, priority = 5, opts = {})=>{
    const text=cleanForSpeech(rawText);
    if (!text) return;
    const _speakSource = opts && opts.source ? String(opts.source) : "chat"; // "chat" | "alert" | "system"
    // The per-item watchdog below handles stalled playback. Do not force-reset
    // a valid long reply at 30s while its audio is still playing.
     if (isSpeakingRef.current && speakingStartRef.current && Date.now() - speakingStartRef.current > 310000) {
      window.speechSynthesis?.cancel();
      isSpeakingRef.current = false;
      setIsSpeaking(false);
      speakingStartRef.current = 0;
    }
    // Dedup: skip if identical text already queued (prevents double-speak from async actions)
    if (speechQueue.current.some(i => (typeof i === "string" ? i : i.text) === text)) return;
    // opts.keep: sentence chunks of a streamed chat reply — never dropped by the cap,
    // or long replies would lose their middle.
    speechQueue.current.push({ text, priority, time: Date.now(), source: _speakSource, keep: !!opts.keep });
    speechQueue.current.sort((a,b) => a.priority !== b.priority ? a.priority - b.priority : a.time - b.time);
    // Priority 1 = critical signals — never dropped; other normal items capped at 4
    let _normal = 0;
    speechQueue.current = speechQueue.current.filter(i => i.priority === 1 || i.keep || ++_normal <= 4);
    processQueueRef.current?.();
  },[]);


  // ── Mic arbiter: TTS playback and a live microphone cannot coexist on Android
  // Chrome. A recognizer started while TTS is playing causes self-hearing and the
  // OS "beep" loop the user reported (2026-09-25). Any path that is about to start
  // a recognizer must first stop speech and drop the queue.
  const interruptTtsForMic = useCallback(()=>{
    try { ttsAudioRef.current?.pause(); } catch(_){}
    ttsAudioRef.current = null;
    try { window.speechSynthesis?.cancel(); } catch(_){}
    speechQueue.current = [];
    setSpeaking("");
    if (isSpeakingRef.current) {
      isSpeakingRef.current = false;
      setIsSpeaking(false);
      speakingStartRef.current = 0;
    }
  },[]);

  // Full human-friendly spoken alert — gives the user all the useful info
  const speakAlert = useCallback(content=>{
    // Strip HTML tags for text processing
    const raw = content.replace(/<[^>]+>/g,"").replace(/\s+/g," ").trim();
    // Strategy-aware voice filter: only speak alerts for symbols in the active strategy
    const sym = raw.match(/\b(XAUUSD|GBPUSD|EURUSD|USDJPY|AUDJPY|USDMXN|US30|NDX100|SPX500|R_100|R_75|R_50|R_25|R_10|JD50|BOOM500|CRASH500|BOOM1000|CRASH1000)\b/)?.[0];
    const schemaSyms = strategySchemaRef.current?.symbols;
    if (sym && schemaSyms && schemaSyms.length > 0 && !schemaSyms.includes("any") && !schemaSyms.includes(sym)) {
      return; // suppress voice for symbols not tracked by active strategy
    }
    const name = {XAUUSD:"Gold",GBPUSD:"Cable",EURUSD:"Euro Dollar",USDJPY:"Dollar Yen",AUDJPY:"Aussie Yen",USDMXN:"Dollar Peso",US30:"Dow Jones",NDX100:"Nasdaq",SPX500:"S and P 500",R_100:"Volatility 100",R_75:"Volatility 75",R_50:"Volatility 50",R_25:"Volatility 25",R_10:"Volatility 10",JD50:"Jump 50",BOOM500:"Boom 500",CRASH500:"Crash 500",BOOM1000:"Boom 1000",CRASH1000:"Crash 1000"}[sym]||(sym||"Market");
    const isBuy  = /BUY|CALL|\bLONG\b|bullish/i.test(raw);
    const isSell = /SELL|PUT|\bSHORT\b|bearish/i.test(raw);
    const dir    = isBuy ? "buy" : isSell ? "sell" : "";
    // Extract price, SL, TP1
    const priceM = raw.match(/@\s*([\d.]+)/);  const price = priceM?priceM[1]:null;
    const slM    = raw.match(/SL[:\s]+([\d.]+)/i);  const sl = slM?slM[1]:null;
    const tp1M   = raw.match(/TP1[:\s]+([\d.]+)/i); const tp1 = tp1M?tp1M[1]:null;
    const cntM   = raw.match(/(\d+)\/\d+/);  const cnt = cntM?cntM[1]:null;
    const reqCnt = getStrategyConditionCount(strategySchemaRef.current);
    const trend  = /BULLISH/i.test(raw)?"bullish":/BEARISH/i.test(raw)?"bearish":null;
    const hasOB  = raw.includes("OB:✅")||raw.includes("OB ✅")||/\bOB\b.*(?:✅|confirmed|active)/i.test(raw);
    const hasFVG = raw.includes("FVG:✅")||raw.includes("FVG ✅")||/\bFVG\b.*(?:✅|confirmed|active)/i.test(raw);
    const zoneStr= hasOB&&hasFVG?"order block and fair value gap":hasOB?"order block":hasFVG?"fair value gap":"";
    const pr     = price?` at ${price}`:"";
    const sltp   = sl&&tp1?`. Stop ${sl}, target ${tp1}`:"";
    let msg, priority=5;

    if (/SIGNAL CONFIRMED/i.test(raw)) {
      // Full entry signal — highest urgency
      const viaSweep = /Sweep.*Rejection.*Momentum/i.test(raw);
      const how = viaSweep?"Sweep, rejection, and momentum sequence complete":"All required conditions confirmed";
      const htf  = trend?` Fifteen minute trend ${trend}.`:"";
      msg = `${_karaUserName}! ${name} ${dir} signal confirmed${pr}. ${how}.${htf}${sltp}.`;
      priority = 1;
    }
    else if (/Step 1.3|Liq Sweep/i.test(raw)) {
      const side = /sell-side|lows/i.test(raw)?"Sell-side lows swept":"Buy-side highs swept";
      const zone = /OB \+ FVG/i.test(raw)?"order block and fair value gap":/OB/i.test(raw)?"order block":"fair value gap";
      const htf  = trend?` Fifteen minute trend is ${trend}.`:"";
      msg = `${name} liquidity sweep${pr}. ${side} inside fifteen minute ${zone}.${htf} ${dir?dir+" bias. ":""}Watching for rejection candle. Step one of three.`;
      priority = 2;
    }
    else if (/Rejection Candle/i.test(raw)) {
      const wick = /lower wick/i.test(raw)?"lower":"upper";
      const zone = /OB \+ FVG/i.test(raw)?"order block and fair value gap":/OB/i.test(raw)?"order block":"fair value gap";
      msg = `${name} rejection candle${pr}. ${zone.charAt(0).toUpperCase()+zone.slice(1)} holding — ${wick} wick confirmed. ${dir?dir+" bias. ":""}Step two of three. Waiting for momentum entry.`;
      priority = 2;
    }
    else if (/Setup forming/i.test(raw)) {
      const blocking = /Waiting for 15m/i.test(raw);
      const zones = zoneStr?`${zoneStr} present.`:"Waiting for fifteen minute structure.";
      msg = `${name} setup forming — ${cnt||"multiple"} of ${reqCnt} conditions. ${trend?trend+" trend. ":""}${zones}${blocking?" Needs fifteen minute confirmation before signal.":""}`;
      priority = 3;
    }
    else if (/1m OB|1m FVG|1m.*zone/i.test(raw)) {
      const zone = /OB \+ FVG/i.test(raw)?"order block and fair value gap":/OB/i.test(raw)?"order block":"fair value gap";
      const confirmed = /CONFIRMED/i.test(raw);
      msg = `${name} ${dir} ${zone} on one minute${pr}. ${confirmed?`Confirmed by fifteen minute structure. ${dir?"Ready to watch for entry.":""}`:
"Waiting for fifteen minute confirmation."}`;
      priority = 3;
    }
    else if (/TREND CHANGE/i.test(raw)) {
      const newDir = /BULLISH/i.test(raw)?"bullish":"bearish";
      const bias   = newDir==="bullish"?"buy":"sell";
      msg = `${name} trend change — now ${newDir}. Fifteen minute liquidity sweep, change of character, and EMA cross all confirmed. Watching for pullback to order block or fair value gap for ${bias} setup.`;
      priority = 2;
    }
    else if (/15m HTF Update/i.test(raw)) {
      const gate    = /Gate OPEN/i.test(raw);
      const flipped = /Trend:/.test(raw);
      const newT    = /→\s*(BULLISH|BEARISH)/i.exec(raw)?.[1]?.toLowerCase()||trend;
      msg = `${name} fifteen minute update.${flipped&&newT?` Trend flipped to ${newT}.`:""}${gate?" Gate open — order block and fair value gap confirmed. Ready for one minute entry.":" Waiting for structure to complete."}`;
      priority = 4;
    }
    else if (/BOS|CHoCH|CHOCH/i.test(raw)) {
      const isBos  = /\bBOS\b/.test(raw);
      const label  = isBos?"break of structure":"change of character";
      const with15 = /With HTF/i.test(raw)||/✅.*Aligned/i.test(raw);
      msg = `${name} ${label}${pr}. ${dir?dir+" direction. ":""}${with15?"With fifteen minute trend — watch for pullback entry.":"Counter-trend — no signal yet."}`;
      priority = 4;
    }
    else if (/Flip/i.test(raw)) {
      const aligned = /Aligned/i.test(raw);
      msg = `${name} flipped to ${dir||"new direction"} on one minute${pr}. ${aligned?"Aligned with fifteen minute trend.":"Counter-trend — no signal until fifteen minute agrees."}`;
      priority = 4;
    }
    else {
      msg = `${name}${dir?" "+dir:""} alert.`;
      priority = 5;
    }
    speak(msg, priority, { source: "alert" });
  },[speak]);

  // Auto-speak new market alert messages if alert voice is enabled
  useEffect(()=>{
    const start=lastAutoSpeakIdx.current;
    const newMsgs=alertMessages.slice(start);
    if (alertVoiceRef.current && tabRef.current !== "voice") {
      newMsgs.forEach(m=>{ if (m.role==="assistant") speakAlert(m.content); });
    }
    lastAutoSpeakIdx.current=alertMessages.length;
  },[alertMessages,speakAlert]);

  const sendTelegram = useCallback(async msg=>{
    try {
      const r=await apiFetch("/api/telegram",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({message:msg, username: authUser?.username})});
      if (!r.ok) throw new Error((await r.json()).error);
      return true;
    } catch(e){ addLog(`📨 TG: ${e.message}`,"warn"); return false; }
  },[addLog, authUser?.username]);

  // ── Server-side trade history helper ─────────────────────────────────────
  const addTradeHistory = useCallback(entry => {
    setHistory(p => [entry, ...p].slice(0, 200));
    apiFetch("/api/trades/history", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(entry),
    }).catch(() => {});
  }, []);

  // ── Active trades polling + close ─────────────────────────────────────────
  const pollActiveTrades = useCallback(async () => {
    try {
      const r = await apiFetch("/api/active-trades", { silent: true });
      if (r.ok) {
        const serverTrades = await r.json();
        const serverIds = new Set(serverTrades.map(t => String(t.contract_id)));
        const nowSec = Math.floor(Date.now() / 1000);
        setActiveTrades(prev => {
          const keepOptimistic = prev.filter(t =>
            t._optimistic &&
            !serverIds.has(String(t.contract_id)) &&
            (nowSec - (t.purchase_time || 0)) < 60
          );
          // Preserve settled entries shown by checkSettledContracts (they auto-remove after 12s)
          const keepSettled = prev.filter(t =>
            t._settled && !serverIds.has(String(t.contract_id))
          );
          return [...serverTrades, ...keepOptimistic, ...keepSettled];
        });
      }
    } catch {}
  }, []);

  // Auto-check settled Deriv contracts → log real outcomes to ML, update history + signal panel
  const checkSettledContracts = useCallback(async () => {
    const pending = pendingContractsRef.current;
    if (!pending.length) return;
    const now = Date.now();
    const toRemove = [];
    for (const pc of pending) {
      if (now - pc.entryTime < 8_000) continue;           // skip if < 8s old (was 60s — too long for short contracts)
      if (now - pc.entryTime > 30 * 60_000) { toRemove.push(pc.contractId); continue; } // cleanup after 30m
      try {
        const r = await apiFetch("/api/mcp/call", {
          method:"POST", headers:{"Content-Type":"application/json"},
          body: JSON.stringify({ name:"get_contract_info", arguments:{ contract_id:String(pc.contractId) } })
        });
        if (!r.ok) continue;
        const d = await r.json();
        const text = (()=>{ const c=d?.result?.content; return Array.isArray(c)?c.map(x=>x.text||"").join(""):JSON.stringify(d); })();
        const sellMatch = text.match(/sell_price[^0-9.]*([0-9.]+)/);
        const buyMatch  = text.match(/buy_price[^0-9.]*([0-9.]+)/);
        // Only resolve when contract has actually settled (sell_price available)
        if (sellMatch && buyMatch) {
          const sellPrice = parseFloat(sellMatch[1]), buyPrice = parseFloat(buyMatch[1]);
          const won = sellPrice > buyPrice;
          const pnl = +(sellPrice - buyPrice).toFixed(5);
          const outcomeTime = formatJaTime(new Date());
          // 1. Log real outcome to ML training
          await apiFetch("/api/ml/log_outcome", {
            method:"POST", headers:{"Content-Type":"application/json"},
            body: JSON.stringify({ signal_id:pc.signalId||pc.contractId, symbol:pc.symbol, direction:pc.direction,
              entry_price:pc.entrySpot||0, exit_price:sellPrice, outcome:won, pnl,
              duration_seconds:Math.round((now-pc.entryTime)/1000), features:pc.features||{} })
          }).catch(()=>{});
          // 2. Update trade history in memory + server
          setHistory(prev => prev.map(h =>
            (String(h.contract_id)===String(pc.contractId)||String(h.id)===String(pc.contractId))
              ? { ...h, outcome: won?"WIN":"LOSS", pnl, exit_price: sellPrice, outcomeTime }
              : h
          ));
          apiFetch(`/api/trades/history/${pc.contractId}`, {
            method:"PATCH", headers:{"Content-Type":"application/json"},
            body: JSON.stringify({ outcome: won?"WIN":"LOSS", pnl, exit_price: sellPrice, outcomeTime, sell_time: Math.floor(Date.now() / 1000), exit_time: Math.floor(Date.now() / 1000) })
          }).catch(()=>{});
          // 3. Update signal panel entry with real outcome (linked via signalId from auto-trade)
          if (pc.signalId) {
            setTodayPreds(prev => prev.map(p =>
              p.id === pc.signalId
                ? { ...p, outcome: won?"WIN":"LOSS", outcomePrice: sellPrice, outcomeTime, pnl, contract_id: pc.contractId }
                : p
            ));
          }
          // 4. Show outcome in active trades widget briefly before removing
          setActiveTrades(prev => prev.map(t =>
            String(t.contract_id) === String(pc.contractId)
              ? { ...t, _settled: true, _outcome: won ? "WIN" : "LOSS", pnl, _settledAt: Date.now() }
              : t
          ));
          setTimeout(() => {
            setActiveTrades(prev => prev.filter(t => String(t.contract_id) !== String(pc.contractId)));
          }, 12_000);
          // 5. Notify user
          const outcomeMsg = `${won?"🟢 WIN":"🔴 LOSS"} — ${pc.symbol} ${pc.direction} contract #${pc.contractId} settled @ ${sellPrice} (pnl: ${pnl>0?"+":""}${pnl})`;
          addLog(`💰 Real trade outcome: ${outcomeMsg}`, won?"success":"warn");
          sendTelegramRef.current?.(`${won?"🟢 <b>WIN ✅</b>":"🔴 <b>LOSS ❌</b>"} — ${pc.symbol} ${pc.direction}\nContract #${pc.contractId}\nSettled @ <code>${sellPrice}</code> | P&L: <code>${pnl>0?"+":""}${pnl}</code>\n<i>${formatJaFull(new Date())}</i>`).catch(()=>{});
          toRemove.push(pc.contractId);
        }
      } catch { /* silently ignore per-contract errors */ }
    }
    if (toRemove.length) pendingContractsRef.current = pending.filter(p => !toRemove.includes(p.contractId));
  }, [addLog]);

  // ── Live account info (balance, login ID, real/demo, daily P&L) ───────────
  const fetchAccountInfo = useCallback(async () => {
    try {
      const r = await apiFetch("/api/account-info", { silent: true });
      if (!r.ok) { const e = await r.json().catch(()=>({})); setAccountInfo({ error: e.error || "Failed to load account info" }); return; }
      setAccountInfo(await r.json());
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => {
    // Initial fetch, then fallback polling every 30s (WebSocket provides live updates every 5s)
    fetchAccountInfo();
    const id = setInterval(fetchAccountInfo, 30000);
    return () => clearInterval(id);
  }, [fetchAccountInfo]);

  // ── Deriv trade execution ─────────────────────────────────────────────────
  const executeTrade = useCallback(async ({ sym, symbol, direction, stake, duration, duration_unit, mlFeatures, sl, tp1, tp2, leverage, signalId }) => {
    const resolvedSym = sym || symbol;
    const liveData = symDataRef.current[resolvedSym];
    const lastCandle = (Array.isArray(liveData?.candles) && liveData.candles.length ? liveData.candles[liveData.candles.length-1]?.time : undefined);
    // A connected socket alone is insufficient: require a recent candle for this symbol.
    if (!resolvedSym || wsRef.current?.readyState!==WebSocket.OPEN || !subMapRef.current[resolvedSym] || liveData?.offline || !lastCandle || Date.now()/1000-lastCandle>Math.max(180,(strategyEntryGranRef.current||60)*3)) {
      addLog(`⚠️ ${resolvedSym || "Unknown symbol"}: live browser price unavailable — trade blocked`, "warn");
      return null;
    }
    const apiSym = SYMBOL_API[resolvedSym] || resolvedSym;
    const isSynth = resolvedSym.startsWith("R_");
    const resolvedDuration = duration || "5";
    const resolvedDurationUnit = duration_unit || (isSynth ? "t" : "m");
    // T504: auto-switch to execution mode during trade
    setPersonalityMode("execution");
    try {
      addLog(`🤖 Trade: ${direction} ${resolvedSym} $${stake}`, "scan");
      const tradeR = await apiFetch("/api/trade", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contract_type: direction, symbol: apiSym, duration: resolvedDuration, duration_unit: resolvedDurationUnit, amount: String(stake), ...(sl != null && { sl }), ...(tp1 != null && { tp: tp1 }), display_symbol: resolvedSym }),
      });
      const buyData = await tradeR.json();
      if (!tradeR.ok) throw new Error(buyData.error || "Trade failed");
      // Support both { buy: {...} } (direct Deriv) and { result: {...} } (server proxy)
      const buyResult = buyData.buy || buyData.result || {};
      const contractId = buyResult?.contract_id ?? null;
      const entrySpot = buyResult?.entry_spot ?? buyResult?.start_time ?? null;
      addLog(`✅ Trade placed: ${direction} ${resolvedSym} @ ${entrySpot || "market"} contract#${contractId || "pending"}`, "success");
      // Track pending contract for ML auto-outcome detection
      if (contractId) {
        pendingContractsRef.current = [...pendingContractsRef.current, {
          contractId, symbol: resolvedSym, direction, entryTime: Date.now(),
          entrySpot: entrySpot ? parseFloat(entrySpot) : null, features: mlFeatures || {},
          signalId: signalId || null,
        }];
      }
      // Optimistically add to active trades immediately
      if (contractId) {
        setActiveTrades(p => [{
          contract_id: contractId,
          symbol: apiSym,
          contract_type: direction,
          buy_price: parseFloat(stake) || 0,
          pnl: 0,
          purchase_time: Math.floor(Date.now()/1000),
          _optimistic: true,
        }, ...p]);
      }
      pollActiveTrades();
      // Second poll after 3s to get accurate Deriv portfolio data
      setTimeout(() => pollActiveTrades(), 3000);
      const tradeEntry = {
        id: contractId || Date.now().toString(),
        date: formatJaDate(new Date()),
        time: formatJaTime(new Date()),
        timestamp: Date.now(),
        symbol: resolvedSym,
        direction,
        entry: entrySpot,
        stake,
        duration: resolvedDuration,
        duration_unit: resolvedDurationUnit,
        contract_id: contractId,
        executed: true,
        outcome: "pending",
        source: "chart",
        ...(sl != null && { sl }),
        ...(tp1 != null && { tp1 }),
        ...(tp2 != null && { tp2 }),
        ...(leverage != null && { leverage }),
      };
      addTradeHistory(tradeEntry);
      // Notify alerts widget for every trade (manual chart + auto-trade both pass through here)
      setAlertMessages(p => [...p, {
        role: "assistant",
        content: `📊 <strong>TRADE PLACED</strong> — ${direction === "CALL" ? "📈 BUY" : "📉 SELL"} <strong>${resolvedSym}</strong> @ $${stake} stake · Contract #${contractId || "pending"}`,
        time: formatJaTime(new Date()),
      }].slice(-50));
      // Send Telegram notification for chart trade
      sendTelegramRef.current?.(`📊 <b>Trade Placed</b>
${resolvedSym} ${direction === "CALL" ? "📈 BUY" : "📉 SELL"} @ ${entrySpot || "market"}
Stake: $${stake} · Duration: ${resolvedDuration}${resolvedDurationUnit}
<i>${formatJaFull(new Date())}</i>`).catch(() => {});
      setPersonalityMode(p => p==="execution"?"casual":p);
      return buyData;
    } catch (e) {
      addLog(`❌ Trade: ${e.message}`, "error");
      setAlertMessages(p => [...p, {
        role: "assistant",
        content: `❌ <strong>TRADE FAILED</strong> — ${e.message}`,
        time: formatJaTime(new Date()),
      }].slice(-50));
      setPersonalityMode(p => p==="execution"?"casual":p);
      return null;
    }
  }, [addLog, pollActiveTrades, addTradeHistory, setAlertMessages]);

  const closeTrade = useCallback(async (contractId) => {
    try {
      const r = await apiFetch("/api/close-trade", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contract_id: contractId }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Close failed");
      addLog(`✅ Trade closed: ${contractId}`, "success");
      pollActiveTrades();
    } catch (e) { addLog(`❌ Close trade: ${e.message}`, "error"); }
  }, [addLog, pollActiveTrades]);

  // ── WebSocket ─────────────────────────────────────────────────────────────
  const sendWS = useCallback((payload)=>{
    return new Promise((resolve,reject)=>{
      const id=reqIdRef.current++;
      payload.req_id=id;
      const timer=setTimeout(()=>{ delete pendingRef.current[id]; reject(new Error("timeout")); },20000);
      pendingRef.current[id]=(data)=>{ clearTimeout(timer); resolve(data); };
      if (wsRef.current?.readyState===WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify(payload));
      } else {
        clearTimeout(timer); delete pendingRef.current[id];
        reject(new Error("WebSocket not connected"));
      }
    });
  },[]);

  // Intentional empty deps: all mutable state is accessed via refs (selectedSymsRef, symDataRef, etc.)
  // Adding state deps here would cause the WS subscription to re-run on every render.
  const handleTick = useCallback((tick)=>{
    const sym=API_TO_SYM[tick.symbol]||tick.symbol, price=tick.quote, epoch=tick.epoch;
    if (!selectedSymsRef.current.has(sym)) return;

    // Add to live tick feed (shown in UI)
    setTickFeed(p=>{
      const prev=p.filter(t=>t.sym===sym).slice(-1)[0];
      const change=prev?price-prev.price:0;
      return [...p.slice(-99),{sym,price,change,time:formatJaTime(new Date(epoch*1000)),epoch}];
    });

    // Update candles + recalculate
    setSymData(prev=>{
      try {
        const existing=prev[sym];
        if (!existing?.candles?.length) return prev;
        const candles=[...existing.candles];
        const lastC=candles[candles.length-1];
        const entryGran = strategyEntryGranRef.current; // dynamic from strategy schema (default 60)
        const htfGran   = strategyHtfGranRef.current;   // dynamic from strategy schema (default 900)
        const tickMin=Math.floor(epoch/entryGran), lastMin=Math.floor(lastC.time/entryGran);
        if (tickMin>lastMin) {
          // New entry-TF candle
          candles.push({open:price,high:price,low:price,close:price,time:tickMin*entryGran});
          if (candles.length>310) candles.shift();
        } else {
          // Update current candle
          candles[candles.length-1]={...lastC,close:price,high:Math.max(lastC.high,price),low:Math.min(lastC.low,price)};
        }
        // Update HTF candle on each tick (dynamic from schema)
        const candles15=[...(existing.candles15||[])];
        if (candles15.length) {
          const lastC15=candles15[candles15.length-1];
          const tickMin15=Math.floor(epoch/htfGran), lastMin15=Math.floor(lastC15.time/htfGran);
          if (tickMin15>lastMin15) {
            candles15.push({open:price,high:price,low:price,close:price,time:tickMin15*htfGran});
            if (candles15.length>110) candles15.shift();
          } else {
            candles15[candles15.length-1]={...lastC15,close:price,high:Math.max(lastC15.high,price),low:Math.min(lastC15.low,price)};
          }
        }
        // Split-Brain Fix: Skip client-side re-computation if server analysis is up-to-date
        const isServerUpToDate = existing.serverTime && (Date.now() - existing.serverTime < 2000);
        
        const si=sessionRef.current;
        let analysis, htf15, conds;

        if (isServerUpToDate) {
          // Keep server-provided authoritative values, just update the latest price and ticks
          analysis = { ...existing.analysis, price };
          htf15 = existing.htf15;
          conds = existing.conds;

          // Check for significant divergence (debug info)
          const clientEma13 = analyzeCandles(candles)?.ema13;
          if (clientEma13 && existing.analysis?.ema13) {
            const div = Math.abs(clientEma13 - existing.analysis.ema13) / existing.analysis.ema13;
            if (div > 0.001) {
              console.warn(`[SplitBrain] ${sym} indicator divergence: server=${existing.analysis.ema13} client=${clientEma13} (${(div*100).toFixed(3)}%)`);
            }
          }
        } else {
          // Fallback to client-side re-compute if server analysis is stale or missing
          htf15=analyze15m(candles15);
          analysis=analyzeCandles(candles);
          conds=evalConds(analysis,si,htf15,strategySchemaRef.current);
          
          // Merge AI data into conds if available (AI scan supplements native 15m detection)
          const aiData=existing.aiData;
          if (aiData) {
            const _schema = strategySchemaRef.current;
            const _obFvgIdx = getCondIdxByRule(_schema, "ob_fvg_zone");
            if (_obFvgIdx >= 0 && conds[_obFvgIdx]!=="pass") conds[_obFvgIdx]=aiData.fvg||aiData.ob?"pass":"fail";
            const _bosIdx = getCondIdxByRule(_schema, "bos_confirmed");
            if (_bosIdx >= 0 && aiData.bos_choch&&aiData.bos_choch!=="none") conds[_bosIdx]="pass";
            const _rrIdx = getCondIdxByRule(_schema, "rr_ratio");
            if (_rrIdx >= 0 && typeof aiData.r_r==="number") {
              if (aiData.r_r>=2) conds[_rrIdx]="pass"; else if (aiData.r_r>=1.5) conds[_rrIdx]="warn";
            }
          }
        }
        
        const cnt=passedN(conds);
        return {...prev,[sym]:{...existing,candles,candles15,htf15,analysis,conds,cnt,ticks:[...(existing.ticks||[]).slice(-49),{price,time:epoch}]}};
      } catch (e) {
        console.error(`Error processing tick for ${sym}:`, e);
        return prev;
      }
    });
  },[]);

  // Monitor condition changes for alerts
  useEffect(()=>{
    const si=session;
    Object.entries(symData).forEach(([sym,data])=>{
      if (!data.analysis||!data.conds) return;
      const cnt=data.cnt??0;
      const prev=prevCondCnt.current[sym]??-1;
      if (cnt!==prev) {
        prevCondCnt.current[sym]=cnt;
        // Throttle log to avoid spam
        const now=Date.now();
        if (!lastTickLog.current[sym]||now-lastTickLog.current[sym]>5000) {
          lastTickLog.current[sym]=now;
          const a=data.analysis;
          const h15=data.htf15;
          const reqCntLog=getStrategyRequiredCount(strategySchemaRef.current);
          addLog(`${sym} ${a.direction==="CALL"?"📈":a.direction==="PUT"?"📉":"⚖️"} ${fmtPrice(a.price)} | ${cnt}/${reqCntLog} | 1m:${a.trend}${a.bos_choch!=="none"?" "+a.bos_choch:""}${a.liq_sweep?" 💧":""} | 15m:${h15?.trend||"?"} OB:${h15?.ob?"✅":"❌"} FVG:${h15?.fvg?"✅":"❌"}`,cnt>=reqCntLog?"alert":cnt>=reqCntLog-2?"success":"info");
        }
        // Fire alert only when ALL conditions pass + 15m trend aligned + price inside at least one 15m zone
        const _schema=strategySchemaRef.current;
        const _unknownCnt=_schema?.conditions?.filter(c=>c.evalRule==='unknown').length??0;
        if(_unknownCnt>0) addLog(`⚠️ ${sym} schema has ${_unknownCnt} unmapped condition(s) — adjusted reqCnt`,"warn");
        const reqCnt=_schema?.signalGate?.requiredCount!=null?Math.max(1,_schema.signalGate.requiredCount-_unknownCnt):_schema?.conditionCount??10;
        const htf15=data.htf15;
        const htfDir=htf15?.trend==="bullish"?"CALL":htf15?.trend==="bearish"?"PUT":"NONE";
        // Show popup when ALL conditions met — HTF is INFO-ONLY, doesn't block popup
        const trendAligned=!htf15 || htfDir===data.analysis?.direction || htfDir==="NONE";
        const requireHTFZone = strategySchemaRef.current?.signalGate?.requireHTFZone !== false;
        const htfGated = !htf15 || !requireHTFZone || (htf15.obPriceInZone || htf15.fvgPriceInZone);
        // Fire popup when all conditions met (HTF gating is optional)
        if (cnt>=reqCnt&&prev<reqCnt) {
          // URUS strategies are executed server-side — skip client generic pipeline
          if (/urus|ultra|vix_ultra/i.test(activeStrategyRef.current || "")) {
            addLog(`🤖 ${sym} URUS signal threshold — server handling execution`, "info");
            return;
          }
          if (lastSignalAt.current[sym] && now - lastSignalAt.current[sym] < 60000) {
            addLog(`⏱️ ${sym} signal cooldown active (${Math.round((60000-(now-lastSignalAt.current[sym]))/1000)}s left)`, "info");
            return;
          }
          lastSignalAt.current[sym] = now;
          const a=data.analysis;
          const sltp=computeSLTP(a, data.aiData);
          setAlertMessages(p=>[...p,{role:"assistant",content:`⏳ <strong>${sym}</strong> ${reqCnt}/${reqCnt} detected — running Stratos validation...`,time:formatJaTime(new Date())}].slice(-50));
          // Auto-popup disabled — alerts are read aloud instead
          ;(async()=>{
            apiFetch("/api/agents/scout", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ task:"economic_calendar", params:{ symbols:[sym] } }) })
              .then(r=>r.json()).then(result=>{
                if (result.success && result.data?.hasHighImpactEvent) {
                  setAlertMessages(p=>[...p,{role:"assistant",content:`⚠️ NEWS WARNING: High-impact event detected near ${sym} signal time. Trade with caution.`,time:formatJaTime(new Date())}].slice(-50));
                  speakRef.current?.(`Heads up ${_karaUserName} — there's a high impact event nearby. Be careful with this one.`, 2);
                }
              }).catch(()=>{});
            let stratosApproved = false;
            try {
              const stratR = await apiFetch("/api/agents/stratos", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ analysis:data.analysis, cnt, conds:data.conds, htf15:data.htf15||null, signalType:activeStrategyRef.current, userRequest:`Validate this ${reqCnt}/${reqCnt} signal for automatic execution` }) });
              const verdict = await stratR.json();
              const stratName = activeStrategyRef.current.replace(/\.md$/i,"");
              addLog(`🛡️ STRATOS [${stratName}]: ${verdict.verdict} — ${verdict.reason}`, verdict.verdict==="APPROVE"?"success":"warn");
              if (verdict.verdict === "REJECT") {
                setAlertMessages(p=>[...p,{role:"assistant",content:`🛡️ <strong>STRATOS BLOCKED</strong> ${sym} [${stratName}]: ${verdict.reason}`,time:formatJaTime(new Date())}].slice(-50));
                return;
              }
              stratosApproved = true;
            } catch (e) {
              addLog(`⚠️ Stratos unavailable — signal fires without validation: ${e.message}`, "warn");
              stratosApproved = true;
            }
            if (!stratosApproved) return;
            // Build ML features once (reused in gate + todayPreds)
            const mlFeatures={price:a.price,ema13:a.ema13,ema200:a.ema200,atr:a.atr,atr_sma:a.atr_sma,rsi:a.rsi,trend:a.trend,bos_choch:a.bos_choch||"none",liq_sweep:a.liq_sweep||false,session:si.name,htf_ob:htf15?.ob||false,htf_fvg:htf15?.fvg||false,rr_ratio:sltp.rr||0,conditions_passed:cnt,ema13_pullback:a.ema13_pullback_buy||a.ema13_pullback_sell||false,zone:a.zone||"neutral",candle_open:a.open,candle_high:a.high,candle_low:a.low,candle_close:a.close};
            // ML confidence gate
            let mlConfidence = null;
            try {
              const mlR = await apiFetch("/api/ml/predict", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify(mlFeatures), signal:AbortSignal.timeout(4000) });
              const mlResult = await mlR.json();
              if (mlResult.ml_blocked === true) {
                addLog(`🤖 ML BLOCKED ${sym}: ${mlResult.ml_block_reason} (conf=${mlResult.confidence})`, "warn");
                setAlertMessages(p=>[...p,{role:"assistant",content:`🤖 <strong>ML BLOCKED</strong> ${sym}: low confidence (${(mlResult.confidence*100).toFixed(0)}%) — signal suppressed`,time:formatJaTime(new Date())}].slice(-50));
                return;
              }
              mlConfidence = mlResult.confidence ?? null;
            } catch (e) {
              addLog(`⚠️ ML gate unavailable — signal fires without ML check: ${e.message}`, "warn");
            }
            const _srvSig = serverSignalIdRef.current[sym];
            const signalId = (_srvSig && Date.now() - _srvSig.ts < 5000) ? _srvSig.id : `${sym}-${Date.now()}`;
            const alertObj={time:formatJaTime(new Date()),symbol:sym,direction:a.direction,price:a.price,passed:cnt,condTotal:reqCnt,session:si.name,atr:a.atr,rsi:a.rsi,htf15,...sltp,mlConfidence,executed:false};
            setAlerts(prevA=>[alertObj,...prevA.slice(0,4)]);
            // setPopup(alertObj);  // DISABLED — auto-popup removed, alerts played via TTS instead
            addTradeHistoryRef.current?.(alertObj);
            setTodayPreds(prevT => {
              const duplicate = prevT.find(p =>
                p.symbol === sym &&
                p.direction === a.direction &&
                (Date.now() - (p.ts || 0)) < 90000
              );
              if (duplicate) return prevT;
              return [{
                id: signalId,
                ts: Date.now(),
                date:new Date().toDateString(),
                time:alertObj.time,
                symbol:sym,
                direction:a.direction,
                entry:a.price,
                sl:sltp.sl,
                tp1:sltp.tp1,
                tp2:sltp.tp2,
                tp3:sltp.tp3,
                passed:cnt,
                session:si.name,
                outcome:"pending",
                outcomePrice:null,
                outcomeTime:null,
                mlFeatures,
                executed: autoTradeRef.current, // true = real Deriv trade placed, false = monitored paper trade
              },...prevT];
            });
            addLog(`🚨 ${sym} SIGNAL: ${a.direction} @ ${fmtPrice(a.price)} — ${cnt}/${reqCnt} | 15m:${htf15?.trend?.toUpperCase()} OB:${htf15?.ob?"✅":"❌"} FVG:${htf15?.fvg?"✅":"❌"}`,"alert");
            // Auto-log signal to ML for training data
            apiFetch("/api/ml/log_signal",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({signal_id:signalId,symbol:sym,direction:a.direction,entry_price:a.price,features:mlFeatures,conditions_passed:cnt,session:si.name,strategy:activeStrategyRef.current,timestamp:new Date().toISOString()})}).catch(()=>{});
            const stratLabel=activeStrategyRef.current.replace(/\.md$/i,"").toUpperCase();
            sendTelegramRef.current?.(buildTradeMsg({ sym, direction:a.direction, entry:a.price, sltp, htf15, session:si.name, conds:data.conds, rsi:a.rsi, atr:a.atr, bos_choch:a.bos_choch, trend:a.trend, liq_sweep_dir:a.liq_sweep_dir, condCount:reqCnt, signalType:`${stratLabel} — ${reqCnt}/${reqCnt} ✅`, explanation:`15m <b>${htf15?.trend?.toUpperCase()}</b> — OB + FVG locked in. Price in ${a.direction==="CALL"?"discount":"premium"} zone. ${a.liq_sweep?"Liq sweep on "+(a.liq_sweep_dir==="low"?"lows":"highs")+" — smart money trap fired. ":""}1m ${a.bos_choch==="BOS"?"BOS confirmed":"structure shift"}. EMA13 rejection + momentum candle. All ${reqCnt}/${reqCnt} green. Enter at market.` }));
            if (autoTradeRef.current) {
              const autoStake = tradeStakeRef.current;
              const autoMlFeatures = { rsi:a.rsi, ema13:a.ema13, ema200:a.ema200, atr:a.atr, trend:a.trend, macd_hist:a.macd_hist, stoch_k:a.stoch_k, volume_ratio:a.volume_ratio, ha_bull:a.ha?.bull, engulf:a.engulf_bull||a.engulf_bear, bos_choch:a.bos_choch };
              executeTrade({ sym, direction:a.direction, stake:autoStake, mlFeatures:autoMlFeatures, signalId }).then(result=>{
                if (result) {
                  setAlertMessages(p=>[...p,{role:"assistant",content:`🤖 <strong>AUTO-TRADE PLACED</strong> — ${a.direction==="CALL"?"📈 BUY":"📉 SELL"} <strong>${sym}</strong> @ $${autoStake} stake. Signal: ${cnt}/${reqCnt} ✅`,time:formatJaTime(new Date())}].slice(-50));
                  sendTelegramRef.current?.(`🤖 <b>AUTO-TRADE PLACED</b>\n${a.direction==="CALL"?"📈 BUY":"📉 SELL"} <b>${sym}</b>\nStake: <code>$${autoStake}</code>\nSignal: ${cnt}/${reqCnt} ✅ All conditions green\n<i>${formatJaFull(new Date())}</i>`);
                } else {
                  setAlertMessages(p=>[...p,{role:"assistant",content:`⚠️ <strong>AUTO-TRADE FAILED</strong> — ${sym} signal was valid (${cnt}/${reqCnt}) but trade execution failed. Check logs — missing Deriv token or API error.`,time:formatJaTime(new Date())}].slice(-50));
                  addLog(`❌ Auto-trade failed for ${sym} — executeTrade returned null. Check Deriv token config.`,"error");
                }
              }).catch(err=>{
                setAlertMessages(p=>[...p,{role:"assistant",content:`⚠️ <strong>AUTO-TRADE ERROR</strong> — ${sym}: ${err.message}`,time:formatJaTime(new Date())}].slice(-50));
                addLog(`❌ Auto-trade error for ${sym}: ${err.message}`,"error");
              });
            }
            if (alertsEnabledRef.current) {
              setAlertMessages(p=>[...p,{role:"assistant",content:`🚨 SIGNAL CONFIRMED, ${_karaUserName}! ${a?.direction==="CALL"?"📈 BUY":"📉 SELL"} <strong>${sym}</strong> @ ${fmtPrice(a?.price)} — ALL ${reqCnt}/${reqCnt} conditions green. 📐 15m HTF: <strong>${htf15?.trend?.toUpperCase()}</strong> | OB ✅ | FVG ✅ (entry validated on 1m after 15m structure).${sltp.sl!=null?" SL: "+fmtPrice(sltp.sl)+" | TP1: "+fmtPrice(sltp.tp1)+" | TP2: "+fmtPrice(sltp.tp2)+".":""} Your move!`,time:formatJaTime(new Date())}].slice(-50));
            }
          })();
        }
        // ── Setup forming at (reqCnt-2) to (reqCnt-1) conditions — throttled Kara notification ──
        if (alertsEnabledRef.current&&cnt>=reqCnt-2&&cnt<reqCnt&&(prev<reqCnt-2||prev>=reqCnt)) {
          const now2=Date.now();
          const lastAlert=lastKaraAlert.current[sym]||0;
          if (now2-lastAlert>120000) { // max once per 2 minutes per symbol
            lastKaraAlert.current[sym]=now2;
            const htf15s=data.htf15;
            const htfStatus=htf15s?`15m: ${htf15s.trend?.toUpperCase()} | OB:${htf15s.ob?"✅":"❌"} FVG:${htf15s.fvg?"✅":"❌"}`:""
            const htfBlocking=!htf15s?.ob||!htf15s?.fvg;
            const a2=data.analysis;
            const ema13Gate=a2&&((a2.direction==="CALL"&&a2.price>a2.ema13)||(a2.direction==="PUT"&&a2.price<a2.ema13));
            const pullbackReady=ema13Gate&&data.conds?.[8]!=="pass"; // above/below EMA13 but no pullback yet
            const setupMsg=`⚠️ Setup forming on <strong>${sym}</strong> — ${cnt}/${reqCnt} conditions. ${htfStatus}${htfBlocking?" ⛔ Waiting for 15m OB+FVG before signal.":pullbackReady?" 👁️ EMA13 gate clear — watching for 1-3 candle micro-pullback, rejection + momentum.":cnt===reqCnt-1?" Run AI SCAN to unlock final condition.":" Watching closely."}`;
            setAlertMessages(p=>[...p,{role:"assistant",content:setupMsg,time:formatJaTime(new Date())}].slice(-50));
          }
        }
        // ── Micro-Pullback Watch: price reclaimed EMA13, setup ready — watching for the sequence ──
        // Fires when: price is on the correct side of EMA13 (C1 pass), 60%+ conditions pass,
        // but signal not yet complete. Kara announces she's watching.
        if (alertsEnabledRef.current&&cnt>=Math.floor(reqCnt*0.6)&&cnt<reqCnt) {
          const a3=data.analysis;
          const ema13Gate=a3&&((a3.direction==="CALL"&&a3.price>a3.ema13)||(a3.direction==="PUT"&&a3.price<a3.ema13));
          const pullbackPending=data.conds?.[8]!=="pass"; // C9 not yet completed
          if (ema13Gate&&pullbackPending) {
            const now3=Date.now();
            if (now3-(lastPullbackWatch.current[sym]||0)>180000) { // max once per 3 minutes
              lastPullbackWatch.current[sym]=now3;
              const dir3=a3.direction==="CALL"?"BUY":"SELL";
              const htf15w=data.htf15;
              const pbMsg=`👁️ <strong>${sym}</strong> — EMA13 gate clear. Watching for micro-pullback.\n${dir3} bias | Price: ${fmtPrice(a3.price)} | EMA13: ${fmtPrice(a3.ema13)} | ${cnt}/${reqCnt} conditions\n15m: ${htf15w?.trend?.toUpperCase()||"?"} | OB:${htf15w?.ob?"✅":"❌"} FVG:${htf15w?.fvg?"✅":"❌"}\n⏳ Waiting for 1–3 candle dip → rejection wick → momentum reclaim above EMA13.`;
              setAlertMessages(p=>[...p,{role:"assistant",content:pbMsg,time:formatJaTime(new Date())}].slice(-50));
              addLog(`👁️ ${sym} pullback watch — ${dir3} | EMA13 gate clear | ${cnt}/${reqCnt}`,"info");
            }
          }
        }
      }
      // ── 1m OB/FVG zone alert — fires when a fresh 1m OB or FVG is detected ──
      // Signal is marked CONFIRMED when 15m also has an OB or FVG, WATCH otherwise.
      {
        const a=data.analysis;
        const h=data.htf15;
        if (a&&alertsEnabledRef.current) {
          const curOB=a.ob??false, curFVG=a.fvg??false;
          const prevOB=prev1mOB.current[sym]??false, prevFVG=prev1mFVG.current[sym]??false;
          const newOB=curOB&&!prevOB, newFVG=curFVG&&!prevFVG;
          // Only alert when 1m direction matches 15m trend — no counter-trend signals
          const trendAligned1m=h&&((a.direction==="CALL"&&h.trend==="bullish")||(a.direction==="PUT"&&h.trend==="bearish"));
          if ((newOB||newFVG)&&trendAligned1m) {
            const now1m=Date.now();
            if (now1m-(last1mZoneAlert.current[sym]||0)>120000) { // max once per 2 min
              last1mZoneAlert.current[sym]=now1m;
              const zone=newOB&&newFVG?"OB + FVG":newOB?"OB":"FVG";
              const obDetail=newOB&&a.obZone?` @ ${fmtPrice(a.obZone.low)}–${fmtPrice(a.obZone.high)} (${a.obType})`:"";
              const fvgDetail=newFVG&&a.fvgZone?` @ ${fmtPrice(a.fvgZone.low)}–${fmtPrice(a.fvgZone.high)} (${a.fvgType})`:"";
              const htf15Confirmed=h?.ob||h?.fvg;
              const htfDetail=h?`15m: ${h.trend?.toUpperCase()} | OB:${h.ob?"✅":"❌"} FVG:${h.fvg?"✅":"❌"}`:"15m: loading";
              const confirmed=htf15Confirmed&&h.trend&&((a.direction==="CALL"&&h.trend==="bullish")||(a.direction==="PUT"&&h.trend==="bearish"));
              const status=confirmed?"✅ <strong>CONFIRMED</strong> — 15m OB/FVG aligned with 1m trend":htf15Confirmed?"⚠️ <strong>WATCH</strong> — 15m zone exists but trend not yet aligned":`⏳ <strong>WATCH</strong> — awaiting 15m OB/FVG confirmation`;
              const zoneMsg=`📦 <strong>1m ${zone} — ${sym}</strong> @ ${fmtPrice(a.price)}\n${newOB?`OB${obDetail}`:""}${newOB&&newFVG?" | ":""}${newFVG?`FVG${fvgDetail}`:""}\nBias: <strong>${a.direction==="CALL"?"BUY":a.direction==="PUT"?"SELL":"—"}</strong> | ${htfDetail}\n${status}`;
              setAlertMessages(p=>[...p,{role:"assistant",content:zoneMsg,time:formatJaTime(new Date())}].slice(-50));
              addLog(`📦 ${sym} 1m ${zone} detected @ ${fmtPrice(a.price)} | ${confirmed?"CONFIRMED":"WATCH"}`,"success");
              if (confirmed) {
                const zoneSltp = computeSLTP(a, data.aiData);
                sendTelegram(buildTradeMsg({
                  sym, direction: a.direction, entry: a.price, sltp: zoneSltp, htf15: data.htf15, session: si.name,
                  conds: null, rsi: a.rsi, atr: a.atr, bos_choch: a.bos_choch,
                  trend: a.trend, liq_sweep_dir: null,
                  signalType: "⚠️ ZONE ALERT — EARLY WARNING",
                  explanation: `Fresh 1m <b>${zone}</b> inside confirmed 15m zone. ${newOB?"OB"+obDetail+" ":""}${newFVG?"FVG"+fvgDetail:""}\n${htfDetail}\n⏳ <b>Wait for rejection or momentum candle to confirm before entering.</b> Levels shown are indicative.`,
                }));
              }
            }
          }
          prev1mOB.current[sym]=curOB;
          prev1mFVG.current[sym]=curFVG;
        }
      }
      // ── Signal Sequence: Sweep → Rejection → Momentum (all trend-aligned) ──
      // ALL signals must be with the 15m trend. No counter-trend entries.
      // Phase 1: 1m liq sweep inside 15m OB/FVG, direction matches 15m trend
      // Phase 2: Rejection candle (wick ≥2× body) after sweep — confirms zone hold
      // Phase 3: Strong momentum candle (body ≥60% range) — entry signal fires
      {
        const a=data.analysis;
        const h=data.htf15;
        if (a&&h) {
          const htfBull=h.trend==="bullish", htfBear=h.trend==="bearish";
          const trendKnown=htfBull||htfBear;
          const price=a.price;
          const now=Date.now();
          const seq=sweepSeq.current[sym]; // capture before any mutation this tick
          if (trendKnown) {
            // ── Phase 1: Liq sweep in trend direction, inside 15m OB or FVG ──
            const curLiq=a.liq_sweep??false;
            const prevLiq=prevLiqSweep.current[sym]??false;
            if (curLiq&&!prevLiq) {
              const sweepBull=a.liq_sweep_dir==="low"; // sell-side swept → BUY bias
              const sweepBear=a.liq_sweep_dir==="high"; // buy-side swept → SELL bias
              const sweepWithTrend=(htfBull&&sweepBull)||(htfBear&&sweepBear);
              const inOB=h.ob&&h.obZone&&price>=h.obZone.low&&price<=h.obZone.high;
              const inFVG=h.fvg&&h.fvgZone&&price>=h.fvgZone.low&&price<=h.fvgZone.high;
              if (sweepWithTrend&&(inOB||inFVG)) {
                const zone=inOB&&inFVG?"OB + FVG":inOB?"OB":"FVG";
                const zoneDetail=inOB?`${fmtPrice(h.obZone.low)}–${fmtPrice(h.obZone.high)} (${h.obType} OB)`:inFVG?`${fmtPrice(h.fvgZone.low)}–${fmtPrice(h.fvgZone.high)} (${h.fvgType} FVG)`:"";
                sweepSeq.current[sym]={phase:"swept",dir:sweepBull?"CALL":"PUT",price,time:now,zone,zoneDetail};
                addLog(`⚡ ${sym} 1m SWEEP inside 15m ${zone} @ ${fmtPrice(price)} → ${sweepBull?"BUY":"SELL"} | Step 1/3 — awaiting rejection candle`,"alert");
                if (alertsEnabledRef.current&&now-(lastLiqZoneAlert.current[sym]||0)>180000) {
                  lastLiqZoneAlert.current[sym]=now;
                  const sweepMsg=`⚡ <strong>1m Liq Sweep — ${sym}</strong> @ ${fmtPrice(price)}\n\nSwept ${sweepBull?"sell-side (lows)":"buy-side (highs)"} inside 15m <strong>${zone}</strong> (${zoneDetail}).\n15m trend: <strong>${h.trend.toUpperCase()}</strong> ✅\n\n⏳ <strong>Step 1/3</strong> — Watching for rejection candle...`;
                  setAlertMessages(p=>[...p,{role:"assistant",content:sweepMsg,time:formatJaTime(new Date())}].slice(-50));
                }
              }
            }
            prevLiqSweep.current[sym]=curLiq;
            // ── Phase 2: Rejection candle after sweep — zone is holding ──
            if (seq?.phase==="swept") {
              if (now-seq.time>300000) { sweepSeq.current[sym]=null; } // 5-min timeout
              else {
                const rejBull=seq.dir==="CALL"&&(a.rejection_candle_bull||false);
                const rejBear=seq.dir==="PUT"&&(a.rejection_candle_bear||false);
                if (rejBull||rejBear) {
                  sweepSeq.current[sym]={...seq,phase:"rejected",rejPrice:price,rejTime:now};
                  addLog(`📊 ${sym} REJECTION candle @ ${fmtPrice(price)} | ${seq.zone} holding — Step 2/3 awaiting momentum`,"success");
                  if (alertsEnabledRef.current) {
                    const rejMsg=`📊 <strong>Rejection Candle — ${sym}</strong> @ ${fmtPrice(price)}\n\nZone held: ${seq.zone} (${seq.zoneDetail})\nPrice rejected with ${seq.dir==="CALL"?"lower":"upper"} wick — zone is confirmed.\nBias: <strong>${seq.dir==="CALL"?"📈 BUY":"📉 SELL"}</strong> | 15m: <strong>${h.trend.toUpperCase()}</strong>\n\n⏳ <strong>Step 2/3</strong> — Watching for momentum candle to enter...`;
                    setAlertMessages(p=>[...p,{role:"assistant",content:rejMsg,time:formatJaTime(new Date())}].slice(-50));
                  }
                }
              }
            }
            // ── Phase 3: Momentum candle = full confirmed signal ──
            if (seq?.phase==="rejected") {
              if (now-seq.rejTime>300000) { sweepSeq.current[sym]=null; } // 5-min timeout
              else {
                const momBull=seq.dir==="CALL"&&(a.momentum_candle_bull||false);
                const momBear=seq.dir==="PUT"&&(a.momentum_candle_bear||false);
                if (momBull||momBear) {
                  sweepSeq.current[sym]=null; // reset immediately — prevent re-trigger during async validation
                  const seqSnap=seq; // capture seq before async boundary
                  ;(async()=>{
                    // ── StratOS validation for SRM ───────────────────────────
                    try {
                      const stratR=await apiFetch("/api/agents/stratos",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({
                        analysis:a,
                        htf15:h,
                        signalType:"SRM",
                        sweepPhase:{phase1:true,phase2:true,phase3:true,inZone:true,zone:seqSnap.zone,zoneDetail:seqSnap.zoneDetail,sweepDir:seqSnap.dir,sweepPrice:seqSnap.price},
                        cnt:data.cnt,
                        userRequest:"Validate SRM 3-phase sequence",
                      })});
                      const verdict=await stratR.json();
                      addLog(`🛡️ STRATOS SRM [sweep_rejection_momentum.md]: ${verdict.verdict} — ${verdict.reason}`,verdict.verdict==="APPROVE"?"success":"warn");
                      if (verdict.verdict!=="APPROVE") {
                        setAlertMessages(p=>[...p,{role:"assistant",content:`🛡️ <strong>STRATOS BLOCKED SRM</strong> — ${sym}: ${verdict.reason}`,time:formatJaTime(new Date())}].slice(-50));
                        return; // signal blocked
                      }
                    } catch(e) {
                      addLog(`⚠️ Stratos SRM check failed — firing anyway: ${e.message}`,"warn"); // fail open
                    }
                    // ── Signal fires after APPROVE (or on network error — fail open) ─
                    const sltp=computeSLTP(a,data.aiData);
                    const alertObj={time:formatJaTime(new Date()),symbol:sym,direction:seqSnap.dir,price,passed:10,session:si.name,atr:a.atr,rsi:a.rsi,htf15:h,...sltp,executed:false};                    setAlerts(prev=>[alertObj,...prev.slice(0,4)]);
                    // setPopup(alertObj);  // DISABLED — auto-popup removed
                    addTradeHistoryRef.current?.(alertObj);
                    setTodayPreds(p => {
                      const duplicate = p.find(px =>
                        px.symbol === sym &&
                        px.direction === seqSnap.dir &&
                        (Date.now() - (px.ts || 0)) < 90000
                      );
                      if (duplicate) return p;
                      return [{
                        id:`${sym}-seq-${Date.now()}`,
                        ts: Date.now(),
                        date:new Date().toDateString(),
                        time:alertObj.time,
                        symbol:sym,
                        direction:seqSnap.dir,
                        entry:price,
                        sl:sltp.sl,
                        tp1:sltp.tp1,
                        tp2:sltp.tp2,
                        tp3:sltp.tp3,
                        passed:10,
                        session:si.name,
                        outcome:"pending",
                        outcomePrice:null,
                        outcomeTime:null,
                        mlFeatures:{price:a.price,ema13:a.ema13,ema200:a.ema200,atr:a.atr,atr_sma:a.atr_sma,rsi:a.rsi,trend:a.trend,bos_choch:a.bos_choch||"none",liq_sweep:a.liq_sweep||false,session:si.name,htf_ob:h?.ob||false,htf_fvg:h?.fvg||false,rr_ratio:sltp.rr||0,conditions_passed:10,ema13_pullback:a.ema13_pullback_buy||a.ema13_pullback_sell||false,zone:a.zone||"neutral",candle_open:a.open,candle_high:a.high,candle_low:a.low,candle_close:a.close},
                      },...p];
                    });
                    const sigMsg=`🚨 <strong>SIGNAL CONFIRMED — ${sym}</strong>\n\n${seqSnap.dir==="CALL"?"📈 BUY":"📉 SELL"} @ <strong>${fmtPrice(price)}</strong>\n\n✅ Sweep → Rejection → Momentum complete\n15m: <strong>${h.trend.toUpperCase()}</strong> | ${seqSnap.zone} (${seqSnap.zoneDetail})\n${sltp.sl!=null?"SL: "+fmtPrice(sltp.sl)+" | TP1: "+fmtPrice(sltp.tp1)+" | TP2: "+fmtPrice(sltp.tp2):""}`;
                    addLog(`🚨 ${sym} SIGNAL: ${seqSnap.dir} @ ${fmtPrice(price)} — Sweep→Rejection→Momentum | 15m:${h.trend.toUpperCase()}`,"alert");
                    if (alertsEnabledRef.current) setAlertMessages(p=>[...p,{role:"assistant",content:sigMsg,time:formatJaTime(new Date())}].slice(-50));
                    sendTelegramRef.current?.(buildTradeMsg({
                      sym, direction: seqSnap.dir, entry: price, sltp, htf15: h, session: si.name,
                      conds: null, rsi: a.rsi, atr: a.atr, bos_choch: a.bos_choch,
                      trend: a.trend, liq_sweep_dir: null,
                      signalType: "SWEEP → REJECTION → MOMENTUM 🎯",
                      explanation: `1️⃣ Liq swept at <b>${seqSnap.zone}</b> — equal ${seqSnap.dir==="CALL"?"lows grabbed":"highs grabbed"}.\n2️⃣ Rejection wick — zone held.\n3️⃣ Momentum candle closed — institutional flow confirmed.\n15m <b>${h.trend?.toUpperCase()}</b>. Trade with HTF bias. Enter at market.`,
                    }));
                  })();
                }
              }
            }
          }
        }
      }

      // ── 15m HTF state changes — trend flip, OB/FVG appear/disappear ──────
      {
        const h=data.htf15;
        if (h) {
          const curTrend=h.trend, curOB=h.ob, curFVG=h.fvg;
          const prevTrend=prevHTFTrend.current[sym], prevOB=prevHTFOB.current[sym], prevFVG=prevHTFFVG.current[sym];
          const trendFlipped=prevTrend!==undefined&&prevTrend!==curTrend;
          const obChanged=prevOB!==undefined&&prevOB!==curOB;
          const fvgChanged=prevFVG!==undefined&&prevFVG!==curFVG;
          if (trendFlipped||obChanged||fvgChanged) {
            const nowH=Date.now();
            if (nowH-(lastHTFAlert.current[sym]||0)>300000) {
              lastHTFAlert.current[sym]=nowH;
              const a1m=data.analysis;
              const gated=curOB&&curFVG;
              const aligned=(curTrend==="bullish"&&a1m?.direction==="CALL")||(curTrend==="bearish"&&a1m?.direction==="PUT");
              let htfMsg=`📐 <strong>15m HTF Update — ${sym}</strong>\n\n`;
              if (trendFlipped) htfMsg+=`Trend: <strong>${prevTrend?.toUpperCase()||"?"} → ${curTrend.toUpperCase()}</strong>\n`;
              if (obChanged) htfMsg+=`OB: ${prevOB?"✅":"❌"} → ${curOB?"✅":"❌"}${curOB&&h.obZone?` @ ${fmtPrice(h.obZone.low)}–${fmtPrice(h.obZone.high)} (${h.obType})`:""}.\n`;
              if (fvgChanged) htfMsg+=`FVG: ${prevFVG?"✅":"❌"} → ${curFVG?"✅":"❌"}${curFVG&&h.fvgZone?` @ ${fmtPrice(h.fvgZone.low)}–${fmtPrice(h.fvgZone.high)} (${h.fvgType})`:""}.\n`;
              htfMsg+=`\n${gated&&aligned?`✅ <strong>Gate OPEN</strong> — OB+FVG confirmed, trend aligned ${curTrend}. Ready for 1m entry.`:gated&&!aligned?`⚠️ OB+FVG set but 1m direction (${a1m?.direction||"?"}) ≠ 15m (${curTrend}) — wait for alignment.`:`⛔ Gate not complete — ${!curOB?"OB missing":""}${!curOB&&!curFVG?" + ":""}${!curFVG?"FVG missing":""}.`}`;
              if (alertsEnabledRef.current) {
                setAlertMessages(p=>[...p,{role:"assistant",content:htfMsg,time:formatJaTime(new Date())}].slice(-50));
              }
              addLog(`📐 ${sym} 15m HTF: ${curTrend.toUpperCase()} OB:${curOB?"✅":"❌"} FVG:${curFVG?"✅":"❌"}`,"info");
            }
          }
          prevHTFTrend.current[sym]=curTrend;
          prevHTFOB.current[sym]=curOB;
          prevHTFFVG.current[sym]=curFVG;
        }
      }

      // ── 15m Trend Change: Liq Sweep + CHoCH + EMA13 cross EMA200 ──────────
      // When all three fire together, the trend has officially changed.
      // Reset the signal sequence and alert the user to look for pullback OB/FVG.
      {
        const h=data.htf15;
        if (h) {
          const curLiq15=h.liq_sweep??false;
          const prevLiq15=prev15mLiq.current[sym]??false;
          // Edge-detect: only fires on the tick where 15m liq sweep turns true
          if ((h.trendChangeBull||h.trendChangeBear)&&curLiq15&&!prevLiq15) {
            const nowTC=Date.now();
            if (nowTC-(lastTrendChAlert.current[sym]||0)>600000) { // 10-min throttle
              lastTrendChAlert.current[sym]=nowTC;
              const newDir=h.trendChangeBull?"BULLISH":"BEARISH";
              const crossDir=h.trendChangeBull?"above":"below";
              const bias=h.trendChangeBull?"BUY":"SELL";
              sweepSeq.current[sym]=null; // reset any in-progress sequence
              addLog(`🔄 ${sym} TREND CHANGE → ${newDir} | 15m Liq Sweep + CHoCH + EMA13 cross ${crossDir} EMA200`,"alert");
              if (alertsEnabledRef.current) {
                const tcMsg=`🔄 <strong>TREND CHANGE — ${sym}</strong>\n\nNew direction: <strong>${newDir}</strong>\n✅ 15m Liq Sweep + CHoCH + EMA13 crossed ${crossDir} EMA200\n\nNow watching for <strong>pullback to 15m OB or FVG</strong> then 1m entry:\n1m Liq Sweep → Rejection Candle → Momentum Candle = <strong>${bias} signal</strong>`;
                setAlertMessages(p=>[...p,{role:"assistant",content:tcMsg,time:formatJaTime(new Date())}].slice(-50));
              }
              sendTelegram(`🔄 <b>TREND CHANGE — ${sym}</b>\n${newDir}\n15m Liq Sweep + CHoCH + EMA13 ${crossDir} EMA200\nWatching for ${bias} setup on pullback\n⏰ ${formatJaFull(new Date())}`);
            }
          }
          prev15mLiq.current[sym]=curLiq15;
        }
      }

      // ── 1m Direction flip + BOS/CHoCH events ─────────────────────────────
      {
        const a=data.analysis;
        if (a) {
          const curDir=a.direction, curBos=a.bos_choch||"none";
          const prevDir=prev1mDir.current[sym], prevBos=prevBosChoch.current[sym];
          // 1m direction flip
          if (prevDir!==undefined&&prevDir!=="NONE"&&curDir!=="NONE"&&prevDir!==curDir) {
            const nowD=Date.now();
            if (nowD-(last1mDirAlert.current[sym]||0)>120000) {
              last1mDirAlert.current[sym]=nowD;
              const h=data.htf15;
              const aligned=(h?.trend==="bullish"&&curDir==="CALL")||(h?.trend==="bearish"&&curDir==="PUT");
              if (alertsEnabledRef.current) {
                setAlertMessages(p=>[...p,{role:"assistant",content:`🔄 <strong>1m Flip — ${sym}</strong>\n\n${prevDir} → <strong>${curDir}</strong> @ ${fmtPrice(a.price)}\n15m HTF: ${h?.trend?.toUpperCase()||"?"} | ${aligned?"✅ Aligned with 15m — valid direction.":"⛔ Counter-trend — no signal until 15m agrees."}`,time:formatJaTime(new Date())}].slice(-50));
              }
              addLog(`🔄 ${sym} 1m: ${prevDir}→${curDir}`,"info");
            }
          }
          // BOS/CHoCH new event
          if (prevBos!==undefined&&prevBos==="none"&&curBos!=="none") {
            const nowB=Date.now();
            if (nowB-(lastBosAlert.current[sym]||0)>120000) {
              lastBosAlert.current[sym]=nowB;
              const h=data.htf15;
              const aligned=(h?.trend==="bullish"&&curDir==="CALL")||(h?.trend==="bearish"&&curDir==="PUT");
              if (alertsEnabledRef.current) {
                setAlertMessages(p=>[...p,{role:"assistant",content:`💥 <strong>${curBos} — ${sym}</strong>\n\nStructure break on 1m @ ${fmtPrice(a.price)} | Direction: <strong>${curDir}</strong>\n15m HTF: ${h?.trend?.toUpperCase()||"?"} | ${aligned?"✅ With HTF bias — watch for pullback entry.":"⚠️ Against HTF — reversal context. No signal yet."}`,time:formatJaTime(new Date())}].slice(-50));
              }
              addLog(`💥 ${sym} ${curBos} @ ${fmtPrice(a.price)}`,"info");
            }
          }
          prev1mDir.current[sym]=curDir;
          prevBosChoch.current[sym]=curBos;
        }
      }

    });
  },[symData, session, addLog, executeTrade]);

  // ── Symbol subscription ───────────────────────────────────────────────────
  // Offline chart preview only. Never calculate trading conditions from historical-only candles.
  const loadOfflineCandles = useCallback(async(sym)=>{
    try {
      const r=await apiFetch(`/api/chart-history?sym=${encodeURIComponent(sym)}&tf=1m&count=250`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d=await r.json();
      if (!d.candles?.length) throw new Error(d.error||"No saved candles");
      if (wsRef.current?.readyState===WebSocket.OPEN || !selectedSymsRef.current.has(sym)) return;
      setSymData(p=>{
         if (wsRef.current?.readyState===WebSocket.OPEN || !selectedSymsRef.current.has(sym)) return p;
         return {...p,[sym]:{...p[sym],candles:d.candles,analysis:null,conds:null,cnt:0,loading:false,offline:true,lastEpoch:d.lastEpoch,stale:d.stale}};
       });
      setFocusSym(f=>f??sym);
    } catch(e) { addLog(`⚠️ ${sym}: offline chart unavailable (${e.message})`,"warn"); }
  },[addLog]);
  const subscribeSymbol = useCallback(async(sym)=>{
    if (!wsRef.current||wsRef.current.readyState!==WebSocket.OPEN) { loadOfflineCandles(sym); return; }
    const socket = wsRef.current;
    const generation = (subscribeGenerationRef.current[sym] || 0) + 1;
    subscribeGenerationRef.current[sym] = generation;
    const activeRequest = () => wsRef.current === socket && socket?.readyState === WebSocket.OPEN && selectedSymsRef.current.has(sym) && subscribeGenerationRef.current[sym] === generation;
    const apiSym = SYMBOL_API[sym] || sym;
    addLog(`📡 Fetching ${sym} candles...`,"scan");
    setSymData(p=>({...p,[sym]:{candles:[],analysis:null,conds:null,cnt:0,ticks:[],loading:true,error:null,aiData:null}}));
    // Pre-load only genuinely fresh cached candles; old file caches are not live signals.
    apiFetch(`/api/historical-data/cached/${sym}/60`)
      .then(r=>r.ok?r.json():null)
      .then(data=>{
        if (data?.candles?.length && Array.isArray(data.candles) && data.candles.length && Date.now()/1000 - data.candles[data.candles.length-1].epoch <= 180) {
          const cached=data.candles.map(c=>({open:+c.open,high:+c.high,low:+c.low,close:+c.close,time:c.epoch}));
          const analysis=analyzeCandles(cached);
          const conds=evalConds(analysis,sessionRef.current,null,strategySchemaRef.current);
          const cnt=passedN(conds);
          setSymData(p=>{
             if (!activeRequest() || !p[sym]?.loading || p[sym]?.ticks?.length || (p[sym]?.candles?.at(-1)?.time || 0) >= ((Array.isArray(cached) && cached.length ? cached[cached.length-1]?.time : 0) || 0)) return p;
             return {...p,[sym]:{...p[sym],candles:cached,analysis,conds,cnt}};
           });
          addLog(`⚡ ${sym}: cached data loaded (${cached.length} candles)`,"info");
        }
      }).catch(()=>{});
    try {
      // Use strategy-defined timeframes if available, else fall back to UI scannerTf
      const schemaTfs = strategySchemaRef.current?.timeframes;
      const gran   = schemaTfs?.entry ? (CHART_TF_MAP[schemaTfs.entry]||60)   : (CHART_TF_MAP[scannerTf]||60);
      const gran15 = schemaTfs?.htf   ? (CHART_TF_MAP[schemaTfs.htf]||900)    : (CHART_TF_MAP[SCANNER_HTF_MAP[scannerTf]||"15m"]||900);
      // Update dynamic refs for handleTick consistency
      strategyEntryGranRef.current = gran;
      strategyHtfGranRef.current   = gran15;
      const count = (sym.startsWith("R_") || activeStrategyRef.current === "V75_ATM_AGENT_FINAL_v6.md" || activeStrategyRef.current === "landa_v75_atm.md") ? 1500 : (gran >= 86400 ? 200 : gran >= 3600 ? 300 : 250);
      // Fetch primary TF and HTF candles in parallel to cut load time in half
      const [resp, resp15Raw]=await Promise.all([
        sendWS({ticks_history:apiSym,style:"candles",granularity:gran,count,end:"latest"}),

        sendWS({ticks_history:apiSym,style:"candles",granularity:gran15,count:100,end:"latest"}).catch(()=>null),
      ]);
      if (resp.error) throw new Error(resp.error.message);
      const candles=(resp.candles||[]).map(c=>({open:+c.open,high:+c.high,low:+c.low,close:+c.close,time:c.epoch}));
      const si=sessionRef.current;
      const analysis=analyzeCandles(candles);
      // Process HTF data if available
      let htf15=null, candles15=[];
      if (resp15Raw&&!resp15Raw.error) {
        candles15=(resp15Raw.candles||[]).map(c=>({open:+c.open,high:+c.high,low:+c.low,close:+c.close,time:c.epoch}));
        htf15=analyze15m(candles15);
      }
      const conds=evalConds(analysis,si,htf15,strategySchemaRef.current);
      const cnt=passedN(conds);
      const _schemaL=strategySchemaRef.current;
      const _unknownL=_schemaL?.conditions?.filter(c=>c.evalRule==='unknown').length??0;
      const reqCnt=_schemaL?.signalGate?.requiredCount!=null?Math.max(1,_schemaL.signalGate.requiredCount-_unknownL):_schemaL?.conditionCount??10;
      if (!activeRequest()) return;
      setSymData(p=>{
        if (!activeRequest()) return p;
        const _arr = p[sym]?.candles; const previous = (Array.isArray(_arr) && _arr.length ? _arr[_arr.length-1]?.time : 0) || 0;
        if (p[sym]?.ticks?.length || previous > ((Array.isArray(candles) && candles.length ? candles[candles.length-1]?.time : 0) || 0)) return {...p,[sym]:{...p[sym],candles15,htf15,loading:false}};
        return {...p,[sym]:{...p[sym],candles,candles15,htf15,analysis,conds,cnt,loading:false,offline:false,stale:false}};
      });
      setFocusSym(f=>f??sym);
      addLog(`✅ ${sym}: ${candles.length} candles | Price: ${fmtPrice(analysis?.price)} | RSI:${fmt(analysis?.rsi,1)} | ${cnt}/${reqCnt}`,"success");
      if (htf15) addLog(`📊 ${sym} 15m: ${htf15?.trend?.toUpperCase()} | OB:${htf15?.ob?"✅":"❌"} FVG:${htf15?.fvg?"✅":"❌"} | ${cnt}/${reqCnt}`,"info");
      else addLog(`⚠️ ${sym} 15m fetch failed`,"warn");
      // Subscribe to live ticks
      if (!activeRequest()) return;
      const sub=await sendWS({ticks:apiSym,subscribe:1});
      if (!activeRequest()) { if (sub.subscription?.id && socket.readyState===WebSocket.OPEN) sendWS({forget:sub.subscription.id}).catch(()=>{}); return; }
      if (!sub.error&&sub.subscription?.id) {
        subMapRef.current[sym]=sub.subscription.id;
        addLog(`📊 ${sym} live tick stream active`,"info");
      }
    } catch(e) {
      if (!activeRequest()) return;
      addLog(`❌ ${sym}: ${e.message}`,"error");
      setSymData(p=>({...p,[sym]:{...p[sym],loading:false,error:e.message}}));
      if (wsRef.current?.readyState!==WebSocket.OPEN) loadOfflineCandles(sym);
    }
  },[addLog,sendWS,loadOfflineCandles]);

  useEffect(()=>{ subscribeRef.current=subscribeSymbol; },[subscribeSymbol]);

  // ── Chart-tab timeframe → Deriv granularity (seconds) ────────────────────
  const CHART_TF_MAP = {
    "1m":60, "2m":120, "3m":180, "5m":300, "10m":600, "15m":900,
    "30m":1800, "1h":3600, "2h":7200, "3h":10800, "4h":14400,
    "6h":21600, "8h":28800, "12h":43200, "1d":86400, "1w":604800,
  };
  const CHART_TIMEFRAMES = Object.keys(CHART_TF_MAP);
  const SCANNER_HTF_MAP = {
    "1m":"15m","2m":"15m","3m":"15m","5m":"1h","10m":"1h","15m":"4h",
    "30m":"4h","1h":"4h","2h":"1d","3h":"1d","4h":"1d","6h":"1d",
    "8h":"1d","12h":"1d","1d":"1w","1w":"1w"
  };

  const TF_DURATION_MAP = {
    "1m":{dur:"5",unit:"m"}, "2m":{dur:"5",unit:"m"},
    "3m":{dur:"10",unit:"m"},"5m":{dur:"15",unit:"m"},
    "10m":{dur:"30",unit:"m"},"15m":{dur:"30",unit:"m"},
    "30m":{dur:"60",unit:"m"},"1h":{dur:"60",unit:"m"},
    "2h":{dur:"120",unit:"m"},"3h":{dur:"180",unit:"m"},
    "4h":{dur:"240",unit:"m"},"6h":{dur:"360",unit:"m"},
    "8h":{dur:"480",unit:"m"},"12h":{dur:"720",unit:"m"},
    "1d":{dur:"1440",unit:"m"},"1w":{dur:"10080",unit:"m"},
  };
  

  // Fetch candles for a specific symbol+timeframe for Charts tab (uses HTTP — no WS competition)
  const fetchChartCandles = useCallback(async (sym, tf, force = false) => {
    const key = `${sym}_${tf}`;
    // Return cached data immediately — skip network request
    if (!force && chartCandleDataRef.current[key]?.candles?.length > 0) return;
    const gran = CHART_TF_MAP[tf] || 60;
    // More candles for higher timeframes so chart is populated
    const count = gran >= 86400 ? 365 : gran >= 14400 ? 500 : gran >= 3600 ? 500 : gran === 60 ? 1000 : 300;
    setChartCandleData(p => ({ ...p, [key]: { candles: p[key]?.candles || [], loading: true, error: null } }));
    try {
      const r = await apiFetch(`/api/chart-history?sym=${encodeURIComponent(sym)}&tf=${encodeURIComponent(tf)}&count=${count}`);
      const data = await r.json();
      if (data.error) throw new Error(data.error);
      setChartCandleData(p => ({ ...p, [key]: { candles: data.candles, loading: false, error: null, stale: data.stale, lastEpoch: data.lastEpoch } }));
    } catch(e) {
      setChartCandleData(p => ({ ...p, [key]: { candles: [], loading: false, error: e.message } }));
    }
  }, []);

  // Pre-fetch all key TFs for newly subscribed symbols — runs after fetchChartCandles is defined
  useEffect(() => {
    const newSyms = [...selectedSyms];
    if (!newSyms.length) return;
    const preFetchTfs = ["5m","15m","30m","1h","4h","1d"];
    let idx = 0;
    newSyms.forEach(sym => {
      preFetchTfs.forEach(tf => {
        setTimeout(() => fetchChartCandles(sym, tf), idx * 700);
        idx++;
      });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSyms]); // run whenever the set of subscribed symbols changes

  // Sync chartGridFullscreen state when user exits fullscreen via Escape key
  useEffect(() => {
    const handler = () => {
      const isGridFs = document.fullscreenElement === chartGridRef.current;
      setChartGridFullscreen(isGridFs);
      setFsHeight(window.innerHeight);
    };
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  // Auto-fetch when Charts tab slots change — each slot has its own sym+tf
  // ── Data Feeder progress polling ──────────────────────────────────────────
  useEffect(() => {
    if (tab !== "data") return;
    let cancelled = false;
    const load = async () => {
      setDataStatusLoading(true);
      try {
        const r = await fetch("/api/data/status");
        if (!cancelled) {
          if (r?.ok) {
            const data = await r.json();
            console.log("[DataStatus] Loaded:", data);
            setDataStatus(data);
          } else {
            console.warn("[DataStatus] Response not ok:", r?.status, r?.statusText);
            setDataStatus(null);
          }
        }
      } catch (e) {
        console.error("[DataStatus] Fetch error:", e.message);
      }
      setDataStatusLoading(false);
    };
    load();
    const id = setInterval(load, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [tab]);

  const chartTabActiveRef = useRef(false);
  useEffect(() => {
    if (tab !== "charts") { chartTabActiveRef.current = false; return; }
    chartTabActiveRef.current = true;
    const slotsToFetch = chartLayout === "single" ? chartSlots.slice(0,1) : chartLayout === "dual" ? chartSlots.slice(0,2) : chartSlots.slice(0,4);
    slotsToFetch.forEach(slot => { if (slot?.sym) fetchChartCandles(slot.sym, slot.tf); });
  }, [tab, chartSlots, chartLayout, fetchChartCandles]);

  // P9-15: Seed chartSlots + chartFocusKey + newSlotSym with the scanner's active
  // strategy symbols so the Charts tab does not always open on XAUUSD/Gold.
  // Additive — only fires when the chart state is still the XAUUSD default
  // AND the user just entered the charts tab. Subsequent visits/refreshes keep
  // the user's existing layout (Rule #1: no destructive reset).
  const chartDefaultsSeededRef = useRef(false);
  useEffect(() => {
    if (tab !== "charts") return;
    if (chartDefaultsSeededRef.current) return;
    const currentSym = chartSlots[0]?.sym;
    // Only seed if the slot is still the hardcoded XAUUSD default — i.e. the
    // user has not customised the chart layout yet on this device.
    if (currentSym && currentSym !== "XAUUSD") {
      chartDefaultsSeededRef.current = true;
      return;
    }
    let cancelled = false;
    const _seedToken = (() => { try { return localStorage.getItem("kara_auth_token"); } catch { return null; } })();
    fetch("/api/scanner/default-symbols", { headers: _seedToken ? { "Authorization": "Bearer " + _seedToken } : {} })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (cancelled) return;
        chartDefaultsSeededRef.current = true;
        if (!d || !Array.isArray(d.symbols) || d.symbols.length === 0) return;
        const syms = d.symbols.filter(Boolean);
        const primary = syms[0];
        const tf = chartSlots[0]?.tf || "1m";
        const newSlots = [{ sym: primary, tf }];
        setChartSlots(newSlots);
        setChartFocusKey(`${primary}_${tf}`);
        setNewSlotSym(primary);
        try { localStorage.setItem(sk("kara_chart_slots"), JSON.stringify(newSlots)); } catch {}
      })
      .catch(() => { chartDefaultsSeededRef.current = true; });
    return () => { cancelled = true; };
  }, [tab]);

  // Periodic refresh for non-live timeframes (5m, 30m, 1h, etc.) every 30s
  useEffect(() => {
    if (tab !== "charts") return;
    const id = setInterval(() => {
      const slotsToFetch = chartLayout === "single" ? chartSlots.slice(0,1) : chartLayout === "dual" ? chartSlots.slice(0,2) : chartSlots.slice(0,4);
      slotsToFetch.forEach(slot => {
        if (slot?.sym && slot.tf !== "1m" && slot.tf !== "15m") {
          fetchChartCandles(slot.sym, slot.tf, true); // force refresh
        }
      });
    }, 30000);
    return () => clearInterval(id);
  }, [tab, chartSlots, chartLayout, fetchChartCandles]);

  const unsubscribeSymbol = useCallback(async(sym)=>{
    subscribeGenerationRef.current[sym] = (subscribeGenerationRef.current[sym] || 0) + 1;
    const subId=subMapRef.current[sym];
    delete subMapRef.current[sym];
    setSymData(p=>{ const n={...p}; delete n[sym]; return n; });
    setFocusSym(f=>f===sym?null:f);
    if (subId&&wsRef.current?.readyState===WebSocket.OPEN) {
      try { await sendWS({forget:subId}); } catch{}
    }
    addLog(`⏹ ${sym} removed`,"warn");
  },[addLog,sendWS]);

  const toggleSymbol = useCallback((sym)=>{
    setSelectedSyms(prev=>{
      const next=new Set(prev);
      if (next.has(sym)) { next.delete(sym); selectedSymsRef.current=next; unsubscribeSymbol(sym); }
      else { next.add(sym); selectedSymsRef.current=next; subscribeRef.current?.(sym); }
      return next;
    });
  },[unsubscribeSymbol]);

  // ── WebSocket connect ─────────────────────────────────────────────────────
  const connectWS = useCallback(()=>{
    // A reconnect timer must not replace a socket still completing its handshake.
    if (wsRef.current && (wsRef.current.readyState===WebSocket.OPEN || wsRef.current.readyState===WebSocket.CONNECTING)) return;
    setWsStatus("connecting");
    addLog("🔌 Connecting to Deriv market feed...","scan");
    const token = localStorage.getItem("kara_auth_token");
    const wsProto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const url = token ? `${wsProto}//${window.location.host}/ws/chart-feed?token=${encodeURIComponent(token)}` : DERIV_WS;
    const ws=new WebSocket(url);
    wsRef.current=ws;
    const markReady=()=>{
      if (wsRef.current !== ws) return;
      setWsStatus("connected");
      wsRetriesRef.current = 0;
      addLog("🟢 Deriv market feed connected","success");
      Array.from(selectedSymsRef.current).forEach(sym=>subscribeRef.current?.(sym));
    };
    ws.onopen=()=>{ if (!token) markReady(); }; // proxied feed waits for upstream readiness
    ws.onmessage=(ev)=>{
      try {
        const data=JSON.parse(ev.data);
        if (data.type === "chart_feed_ready") { markReady(); return; }
        if (data.req_id&&pendingRef.current[data.req_id]) {
          pendingRef.current[data.req_id](data);
          delete pendingRef.current[data.req_id];
        }
        if (data.msg_type==="tick"&&data.tick) handleTick(data.tick);
      } catch(e){ console.error("WS parse",e); }
    };
    ws.onerror=()=>{ setWsStatus("error"); addLog("❌ WebSocket error","error"); };
    ws.onclose=()=>{
      if (wsRef.current !== ws) return;
      Object.keys(subscribeGenerationRef.current).forEach(sym=>{ subscribeGenerationRef.current[sym]++; });
      subMapRef.current = {};
      wsRef.current = null;
      setWsStatus("disconnected");
      // Immediately invalidate signal data; never wait for the historical HTTP response.
      setSymData(p=>Object.fromEntries(Object.entries(p).map(([sym,d])=>[sym,{...d,analysis:null,conds:null,cnt:0,offline:true}])));
      Array.from(selectedSymsRef.current).forEach(sym=>loadOfflineCandles(sym));
      wsRetriesRef.current++;
      if (wsRetriesRef.current > 15) {
        addLog("⚠️ WebSocket reconnect limit reached — retrying in 90s...","warn");
        wsRetriesRef.current = 0;
        wsReconnectRef.current = setTimeout(connectWS, 90000);
        return;
      }
      const delay = Math.min(5000 * wsRetriesRef.current, 60000);
      addLog(`🔌 WebSocket closed — reconnecting in ${delay/1000}s (attempt ${wsRetriesRef.current})...`,"warn");
      wsReconnectRef.current = setTimeout(connectWS, delay);
    };
  },[addLog,handleTick,loadOfflineCandles]);

  // ── AI Scan (manual, for FVG/OB detection) ───────────────────────────────
  const runAIScan = useCallback(async(sym)=>{
    const data=symDataRef.current[sym];
    if (!data?.candles?.length) { addLog(`⚠️ No data for ${sym}`,"warn"); return; }
    setAiScanning(sym);
    addLog(`🤖 AI scanning ${sym} for FVG/OB/TP levels...`,"scan");
    const last30=data.candles.slice(-30).map(c=>({o:fmt(c.open,5),h:fmt(c.high,5),l:fmt(c.low,5),c:fmt(c.close,5)}));
    const last15m=(data.candles15||[]).slice(-15).map(c=>({o:fmt(c.open,5),h:fmt(c.high,5),l:fmt(c.low,5),c:fmt(c.close,5)}));
    const htf15=data.htf15;
    const analysis=data.analysis;
    const defaultSLTP=computeSLTP(analysis,null);
    const direction=analysis?.direction||"NONE";
    const slInfo=defaultSLTP.sl?`Stop Loss: ${fmt(defaultSLTP.sl,5)} (prev ${direction==="CALL"?"swing low":"swing high"})`:"-";
    try {
      const scanSys = `Active strategy: ${activeStrategyRef.current}\n\n${URUS_SYS}`;
      const r=await apiFetch("/api/chat",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({
        model:"deepseek/deepseek-chat",max_tokens:700,
        messages:[{role:"system",content:scanSys},{role:"user",content:`Symbol: ${sym}\n15m HTF trend: ${htf15?.trend||"unknown"} | OB: ${htf15?.ob?"yes":"no"} | FVG: ${htf15?.fvg?"yes":"no"}\nLast 15 15-min candles:\n${JSON.stringify(last15m)}\n\n1m Direction: ${direction}\nEntry price: ${fmt(analysis?.price,5)}\n${slInfo}\n1m Trend: ${analysis?.trend||"unknown"}\nLast 30 1-min candles (OHLC):\n${JSON.stringify(last30)}\nProvide tp1, tp2, tp3 as actual price targets. Return ONLY the JSON.`}]
      })});
      const d=await r.json();
      if (!r.ok) throw new Error(d.error?.message||`HTTP ${r.status}`);
      const text=(d.choices?.[0]?.message?.content||"{}").replace(/```json|```/g,"").trim();
      const ai=JSON.parse(text);
      setSymData(p=>{
        const ex=p[sym]; if (!ex) return p;
        const conds=[...(ex.conds||Array(10).fill("fail"))];
        conds[7]=ai.fvg||ai.ob?"pass":"fail";
        // AI overrides: BOS/CHoCH (cond 3) and R:R (cond 9)
        if (ai.bos_choch&&ai.bos_choch!=="none") conds[2]="pass";
        if (ai.r_r>=2) conds[8]="pass"; else if (ai.r_r>=1.5) conds[8]="warn";
        const cnt=passedN(conds);
        return {...p,[sym]:{...ex,conds,cnt,aiData:ai}};
      });
      // Recalculate SL/TP with AI data and refresh popup + alerts if they match this symbol
      const sltp=computeSLTP(analysis,ai);
      setPopup(prev=>{
        if (!prev||prev.symbol!==sym) return prev;
        return {...prev,...sltp};
      });
      setAlerts(prev=>prev.map(al=>al.symbol===sym?{...al,...sltp}:al));
      addLog(`🤖 ${sym} AI: FVG:${ai.fvg?"✅":"❌"} OB:${ai.ob?"✅":"❌"} BOS:${ai.bos_choch||"none"} Bias:${ai.bias} R:R:${fmt(ai.r_r,1)} TP1:${ai.tp1?fmtPrice(ai.tp1):"-"} TP2:${ai.tp2?fmtPrice(ai.tp2):"-"} TP3:${ai.tp3?fmtPrice(ai.tp3):"-"}`,"success");
      if (ai.comment) addLog(`   → ${ai.comment}`,"info");
    } catch(e){ addLog(`❌ AI scan ${sym}: ${e.message}`,"error"); }
    finally { setAiScanning(null); }
  },[addLog]);

  // ── Trio-Agent Delegation Helpers ────────────────────────────────────────
  // T205: callStratos routes to /api/agents/stratos (server-side, reads strategy file)
  const callStratos = useCallback(async (symbol, userRequest) => {
    const d = symDataRef.current[symbol];
    if (!d) throw new Error(`Symbol ${symbol} not found`);
    const r = await apiFetch("/api/agents/stratos", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ analysis: d.analysis, cnt: d.cnt, conds: d.conds, htf15: d.htf15 || null, signalType: "URUS_10", userRequest })
    });
    if (!r.ok) throw new Error(`Stratos HTTP ${r.status}`);
    return r.json(); // returns { verdict, reason }
  }, []);

  const callToolsmith = useCallback(async (task, initialArgs = {}) => {
    addLog(`🔧 TOOLSMITH: "${task}"`, "scan");
    try {
      const r = await apiFetch("/api/agents/toolsmith", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ task, args: initialArgs, aiName }),
      });
      if (!r.ok) throw new Error(`Toolsmith HTTP ${r.status}`);
      const data = await r.json();
      if (data.steps) {
        data.steps.forEach(s => addLog(`🔧 TOOLSMITH → ${s.tool}: done`, "info"));
      } else {
        addLog(`🔧 TOOLSMITH → ${data.tool || task}: ${data.reason || "done"}`, "info");
      }
      return data;
    } catch (e) {
      addLog(`🔧 TOOLSMITH error: ${e.message}`, "error");
      throw e;
    }
  }, [addLog, aiName]);

  // ── Kara AI Chat ─────────────────────────────────────────────────────────
  // T201: buildKaraContext — compact snapshot, NO raw OHLCV arrays
  const buildKaraContext = () => {
    const wins = allPredsRef.current.filter(p=>p.outcome==="WIN").length;
    const losses = allPredsRef.current.filter(p=>p.outcome==="LOSS").length;
    // URUS regime context — injected when URUS TMA 88% strategy is active
    const urusCtx = {};
    const stratLower = (activeStrategy||"").toLowerCase();
    if (/urus.*tma|tma.*88|urus.*srm|srm.*tma/i.test(stratLower)) {
      const focusSym = focusSymRef.current || [...(selectedSymsRef.current||[])][0];
      const sd = focusSym ? symDataRef.current[focusSym] : null;
      if (sd?.analysis?.tma && sd?.analysis?.has) {
        const tma = sd.analysis.tma;
        const has = sd.analysis.has;
        const tmaMid = tma.mid || tma.value;
        const hasVal = typeof has === "number" ? has : has.close;
        urusCtx.urus = {
          strategy: "URUS TMA 88%",
          symbol: focusSym,
          regime: tmaMid && hasVal ? (tmaMid > hasVal ? "BEARISH" : "BULLISH") : "UNKNOWN",
          tma_mid: tmaMid, tma_upper: tma.upper, tma_lower: tma.lower, has: hasVal,
          expiry: "120s",
          rule: "Signal = 4X pattern fires in confirmed regime direction. NO trade without 4X.",
        };
      }
    }
    return {
      utcTime: new Date().toISOString(),
      jamaicaTime: formatJaFull(new Date()),
      session: sessionRef.current.name,
      account: {
        balance: accountInfo?.balance ?? 0,
        currency: accountInfo?.currency ?? "USD",
        dailyPnl: accountInfo?.dailyPnl ?? 0,
        weeklyPnl: accountInfo?.weeklyPnl ?? null,
        monthlyPnl: accountInfo?.monthlyPnl ?? null,
        currentProfit: accountInfo?.currentProfit ?? 0,
        openTrades: accountInfo?.openTradesCount ?? 0,
        totalProfit: (accountInfo?.currentProfit||0) + (accountInfo?.dailyPnl||0),
      },
      symbols: [...selectedSymsRef.current],
      conditions: Object.fromEntries(Object.entries(symDataRef.current).map(([sym,d])=>[sym,{
        cnt: d.cnt,
        dir: d.analysis?.direction,
        conds: d.conds,
        price: d.analysis?.price,
        ema13: d.analysis?.ema13,
        htf15: d.htf15 ? { trend: d.htf15.trend, ob: d.htf15.ob, fvg: d.htf15.fvg } : null,
      }])),
      alerts: alertsEnabledRef.current,
      alertVoice: alertVoiceRef.current,
      autoTrade: autoTradeRef.current,
      stake: tradeStakeRef.current,
      tab: tabRef.current,
      personality: personalityMode,
      activeStrategy,
      todaySignals: todayPreds.length,
      todayWR: `${wins}/${wins+losses}`,
      ...urusCtx,
    };
  };

  // T604: strategy upload handler
  const handleStrategyUpload = useCallback(async (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["md","txt"].includes(ext)) { addLog("❌ Only .md and .txt files allowed", "error"); return; }
    if (file.size > 100*1024) { addLog("❌ File too large (max 100KB)", "error"); return; }
    const fd = new FormData();
    fd.append("file", file); // must match multer stratUpload.single("file")
    try {
      const r = await apiFetch("/api/strategies/upload", { method: "POST", body: fd });
      const d = await r.json();
      if (d.ok) {
        addLog(`✅ Strategy uploaded: ${d.name}`, "success");
        const res = await apiFetch("/api/strategies");
        if (res.ok) setStrategies(await res.json());
      } else addLog(`❌ Upload failed: ${d.error||"unknown"}`, "error");
    } catch (e) { addLog(`❌ Upload error: ${e.message}`, "error"); }
  }, [addLog]);

  // Fetch and cache strategy schema from server (called on mount + strategy change)
  // TF string → granularity in seconds
  const TF_GRAN_MAP = {"1m":60,"2m":120,"3m":180,"5m":300,"10m":600,"15m":900,"30m":1800,"1h":3600,"2h":7200,"3h":10800,"4h":14400,"6h":21600,"8h":28800,"12h":43200,"1d":86400,"1w":604800};
  const fetchStrategySchema = useCallback(async () => {
    try {
      const r = await apiFetch("/api/strategies/schema");
      if (!r.ok) return;
      const schema = await r.json();
      setStrategySchema(schema);
      strategySchemaRef.current = schema;
      // Sync active strategy name from schema — avoids showing stale default on load
      if (schema?.name && !schema?.error) setActiveStrategy(schema.name);
      // Update dynamic timeframe refs from schema
      const entryTf = schema.timeframes?.entry;
      const htfTf   = schema.timeframes?.htf;
      if (entryTf && TF_GRAN_MAP[entryTf]) strategyEntryGranRef.current = TF_GRAN_MAP[entryTf];
      if (htfTf   && TF_GRAN_MAP[htfTf])   strategyHtfGranRef.current   = TF_GRAN_MAP[htfTf];
      const syms = (schema.symbols||["any"]).join(",");
      addLog(`📐 Schema: ${schema.conditionCount} conds, gate=${schema.signalGate?.requiredCount}, htfZone=${schema.signalGate?.requireHTFZone}, entry=${entryTf||"1m"} htf=${htfTf||"15m"} symbols=${syms}`, "info");
      // Warn if strategy is symbol-specific and active subscriptions don't match
      if (schema.symbols && !schema.symbols.includes("any")) {
        const active = Array.from(selectedSymsRef.current || []);
        const mismatched = active.filter(s => !schema.symbols.includes(s));
        if (mismatched.length) addLog(`⚠️ Strategy targets [${syms}] — active symbols [${mismatched.join(",")}] may not match`, "warn");
      }
    } catch (e) {
      console.warn("[Schema] Fetch failed:", e.message);
    }
  }, [addLog]);

  // T605: strategy activate handler
  const handleStrategyActivate = useCallback(async (name) => {
    try {
      const r = await apiFetch("/api/strategies/activate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name })
      });
      const d = await r.json();
      if (d.ok) {
        setActiveStrategy(name);
        addLog(`✅ Strategy activated: ${name}`, "success");
        const res = await apiFetch("/api/strategies");
        if (res.ok) setStrategies(await res.json());
        // Server awaits schema parse before responding, so fetch immediately
        await fetchStrategySchema();
        if (!strategySchemaRef.current?.conditionCount) setTimeout(fetchStrategySchema, 3000);
        return true;
      } else {
        addLog(`❌ Activate failed: ${d.error||"unknown"}`, "error");
        return false;
      }
    } catch (e) {
      addLog(`❌ Activate error: ${e.message}`, "error");
      return false;
    }
  }, [addLog, fetchStrategySchema]);

  // T605: strategy delete handler
  const handleStrategyDelete = useCallback(async (name) => {
    if (!window.confirm(`Delete strategy: ${name}?`)) return;
    try {
      const r = await apiFetch(`/api/strategies/${encodeURIComponent(name)}`, { method: "DELETE" });
      const d = await r.json();
      if (d.ok) {
        addLog(`✅ Strategy deleted: ${name}`, "success");
        setStrategies(p => p.filter(s => s.name !== name));
      } else addLog(`❌ Delete failed: ${d.error||"unknown"}`, "error");
    } catch (e) { addLog(`❌ Delete error: ${e.message}`, "error"); }
  }, [addLog]);

  // T606: strategy preview handler — fetches the specific strategy by name
  const handleStrategyPreview = useCallback(async (name) => {
    if (!name) return;
    try {
      const r = await apiFetch(`/api/strategies/${encodeURIComponent(name)}`);
      if (r.ok) {
        const d = await r.json();
        setStrategyPreviewContent(d.content || "");
        setStrategyPreviewName(d.name || name);
        setStrategyPreviewOpen(true);
      }
    } catch {}
  }, []);

  const sendToKara = useCallback(async(userText, options={})=>{
    // Recursion guard: asContext chains capped at depth 3
    const depth = options.depth ?? 0;
    if (depth > 3) {
      addLog("⚠️ sendToKara: max recursion depth reached, skipping", "warn");
      return;
    }
    const isVoiceRequest = !!(options.fromVoice || options.fromVoiceTab);
    let pendingAction = pendingChatActionRef.current;
    if (pendingAction && getVoiceGatePending()?.act !== pendingAction) {
      pendingChatActionRef.current = null;
      rejectVoiceAction(pendingAction);
      const expiredConfirm = !options.directAction && /^(confirm|confirm action|yes confirm|do it|proceed|cancel|cancel action|no|stop)$/i.test(String(userText || "").trim());
      pendingAction = null;
      if (expiredConfirm) {
        const message = "That confirmation expired. Please repeat the request if you still want it.";
        setChatMessages(p => [...p, { role: "assistant", content: message, time: formatJaTime(new Date()) }].slice(-200));
        speak(message, 1);
        return;
      }
    }
    if (pendingAction && !options.directAction) {
      const pendingText = String(userText || "").trim();
      if (/^(confirm|confirm action|yes confirm|do it|proceed|cancel|cancel action|no|stop)$/i.test(pendingText) && getVoiceGatePending()?.act !== pendingAction) {
        pendingChatActionRef.current = null;
        rejectVoiceAction(pendingAction);
        setChatMessages(p => [...p, { role: "assistant", content: "That confirmation expired. Please repeat the request if you still want it.", time: formatJaTime(new Date()) }].slice(-200));
        speak("That confirmation expired. Please repeat the request if you still want it.", 1);
        return;
      }
      if (/^(confirm|confirm action|yes confirm|do it|proceed)$/i.test(String(userText || "").trim())) {
        pendingChatActionRef.current = null;
        const approval = approveVoiceAction(pendingAction);
        if (!approval.ok) {
          rejectVoiceAction(pendingAction);
          setChatMessages(p => [...p, { role: "assistant", content: "That confirmation expired. Please repeat the request if you still want it.", time: formatJaTime(new Date()) }].slice(-200));
          speak("That confirmation expired. Please repeat the request if you still want it.", 1);
          return;
        }
        return sendToKara("", { directAction: { ...pendingAction, __confirmed: true }, asContext: true, fromVoice: isVoiceRequest });
      }
      if (/^(cancel|cancel action|no|stop)$/i.test(String(userText || "").trim())) {
        pendingChatActionRef.current = null;
        rejectVoiceAction(pendingAction);
        const cancelledAt = formatJaTime(new Date());
        setChatMessages(p => [...p, { role: "assistant", content: "Cancelled. No action was executed.", time: cancelledAt }].slice(-200));
        speak("Cancelled. No action was executed.", 1);
        return;
      }
      const pendingAt = formatJaTime(new Date());
      setChatMessages(p => [...p, { role: "assistant", content: "There is a pending confirmation. Say ‘confirm’ to proceed or ‘cancel’ to stop.", time: pendingAt }].slice(-200));
      speak("Please say confirm or cancel.", 1);
      return;
    }
    // 2026-10-09 agent-ui: typed/voiced "confirm"/"cancel" answers the latest agent confirm card.
    const pendingAgent = pendingAgentConfirmRef.current;
    if (pendingAgent && !options.directAction && !options.asContext) {
      const t = String(userText || "").trim();
      const yes = /^(yes|yes confirm|confirm|confirm action|do it|proceed|approve|go ahead)[.!]?$/i.test(t);
      const no = /^(no|cancel|cancel action|stop|abort|don'?t)[.!]?$/i.test(t);
      if (yes || no) {
        setChatMessages(p=>[...p,{role:"user",content:userText,time:formatJaTime(new Date())}].slice(-200));
        await agentConfirmRef.current?.(pendingAgent.call_id, yes);
        return yes ? "confirmed" : "cancelled";
      }
    }
    const time=formatJaTime(new Date());
    // asContext=true: don't show as user message in UI (used for sub-agent result injection)
    if (!options.asContext && !options.skipUserEcho) {
      setChatMessages(p=>[...p,{role:"user",content:userText,time}].slice(-200));
    }
    // 2026-10-09 agent-ui: normal user turns go through the agent runtime (/api/agent/chat, real tools +
    // UI control). Legacy JSON-actions path below stays as the fallback (404 → retry legacy for 5 min).
    if (!options.asContext && !options.directAction && !options.legacy && agentTurnRef.current
        && Date.now() - agentRouteMissingRef.current > 5 * 60 * 1000) {
      return agentTurnRef.current(userText, options);
    }
    setChatThinking(true);
    const ctx = buildKaraContext();
    // Token-budget sliding window: keep as many recent messages as fit in 12K total tokens
    const TOKEN_BUDGET = 12000;
    const usedBySystem = estimateTokens(buildAmberSys(_karaUserName, aiName, personalityMode === "execution" ? "execution" : personalityMode)) + estimateTokens(JSON.stringify(ctx)) + 600;
    let remaining = TOKEN_BUDGET - usedBySystem;
    // Keep voice turns lean: large transcript history adds avoidable model latency.
    const all = chatMsgsRef.current.slice(-(isVoiceRequest ? 8 : 25));
    const recentMsgs = [];
    for (let i = all.length - 1; i >= 0 && remaining > 0; i--) {
      // Trim long individual messages to prevent one huge response eating the entire budget
      const historyCharLimit = isVoiceRequest ? 360 : 800;
      const content = all[i].content.length > historyCharLimit
        ? all[i].content.slice(0, historyCharLimit) + "…[truncated]"
        : all[i].content;
      const t = estimateTokens(content);
      if (t > remaining) break;
      recentMsgs.unshift({ role: all[i].role, content });
      remaining -= t;
    }
    const payload = {
      model:"moonshotai/kimi-k2",max_tokens:800,
      agent: aiName === "Lala" ? "lala" : "kara",  // route to correct persona on server
      context: {                                              // server-side persona also gets a richer ctx block
        user: _karaUserName,
        balance: accountInfo?.balance,
        loginid: accountInfo?.loginid,
        is_virtual: accountInfo?.is_virtual,
        active_strategy: activeStrategy,
        active_tab: tabRef.current,                            // <-- ContextBroker (2026-10-02); ref = never stale (agent-ui 2026-10-09)
        active_symbol: chartFocusKey ? String(chartFocusKey).split("_")[0] : null,
        stake: typeof stake === "number" ? stake : null,
        today_pnl: ctx?.todayPnl,
        today_trades: ctx?.todayTrades,
        today_wr: ctx?.todayWR,
        open_trades: ctx?.openTradeCount,
        session: sessionLabel,
        utc_time: new Date().toISOString(),
        personalityMode: personalityMode === "execution" ? "execution" : personalityMode,
      },
      messages:[
        {role:"system",content:buildAmberSys(_karaUserName, aiName, personalityMode === "execution" ? "execution" : personalityMode)},
        ...recentMsgs,
        options.asContext
          ? {role:"user",content:`[CONTEXT]: ${userText}`}
          : isVoiceRequest
            ? {role:"user",content:`[VOICE ${_karaUserName}]: ${userText}`}
            : {role:"user",content:`[DASHBOARD]\n${JSON.stringify(ctx)}\n\n[${_karaUserName}]: ${userText}`},
      ],
    };
    console.log(`[AMBER] estimated tokens: ${estimateTokens(JSON.stringify(payload))}`);
    const streamTime=formatJaTime(new Date());
    // Show thinking indicator
    // Instant ack, computed client-side in the same frame as the send (no network wait).
    setChatMessages(p=>[...p,{role:"assistant",content:"⏳ "+instantAckText(userText, aiName),time:streamTime,_streaming:true}].slice(-200));
    let rawText = null; // 2026-10-09 agent-ui: hoisted — was block-scoped, so server tool badges below threw ReferenceError
    // Sentence-by-sentence TTS (2026-10-11): speak each finished sentence while the
    // reply is still streaming, instead of waiting for the whole reply.
    let _spokenText = "";
    const _speakStreamed = (fullText) => {
      const raw = String(fullText || "").trimStart();
      // Before the "message" field appears, visibleChatText returns raw JSON — don't speak it.
      if (/^[{`]/.test(raw) && !/"message"\s*:\s*"/.test(raw)) return;
      const vis = visibleChatText(fullText);
      if (!vis.startsWith(_spokenText)) return;
      const rest = vis.slice(_spokenText.length);
      let cut = -1;
      for (const m of rest.matchAll(/[.!?…](?=\s)|\n/g)) cut = m.index + m[0].length;
      if (cut <= 0) return;
      const chunk = rest.slice(0, cut).trim();
      _spokenText += rest.slice(0, cut);
      if (chunk.length > 1) speak(chunk, 5, { keep: true });
    };
    try {
      let resp;
      if (options.directAction) {
        resp = { message: "", actions: [options.directAction] };
      } else {
        const updateStreamingBubble = (content) => setChatMessages(p=>{
          const m=[...p];
          for(let i=m.length-1;i>=0;i--){
            if(m[i]._streaming){m[i]={...m[i],content,_streaming:true};return m;}
          }
          return [...p,{role:"assistant",content,time:streamTime,_streaming:true}].slice(-200);
        });
        rawText = await streamChatResponse(apiFetch, payload, {
          onAck: ({ ack }) => updateStreamingBubble(`⚡ ${ack}`),
          onDelta: (_chunk, fullText) => { updateStreamingBubble(visibleChatText(fullText) || "⏳ Thinking..."); _speakStreamed(fullText); },
        });
        try {
          resp = parseAIJSON(rawText);
        } catch (e) {
          resp = { message: rawText };
        }
      }

      const msg=options.directAction ? "" : (resp.message||`Done, ${_karaUserName}.`);
      // Replace thinking indicator with final message
      setChatMessages(p=>{const m=[...p];for(let i=m.length-1;i>=0;i--){if(m[i]._streaming){m[i]={...m[i],content:msg,_streaming:false};return m;}}return[...p,{role:"assistant",content:msg,time:streamTime}].slice(-200);});

      // 2026-10-03 (Hermes) — surface server-side tool execution in the chat UI.
      // The server ran the LLM's emitted actions[] (pnl_report, search_second_brain,
      // run_backtest, etc.) and returned a structured actions_executed[] envelope.
      // Render an inline badge so the user can see what actually happened, and prompt
      // for confirmation on any GATED tool (set_stake / execute_trade / activate_strategy).
      try {
        const meta = (rawText && typeof rawText === "object" && rawText.chatMeta) ? rawText.chatMeta() : null;
        const serverActions = meta?.actions_executed || [];
        const serverPending = meta?.pending_confirmations || [];
        if (serverActions.length || serverPending.length) {
          const okCount = serverActions.filter(a => a?.ok !== false && !a?.pending_confirmation).length;
          const failCount = serverActions.length - okCount - serverPending.length;
          const toolLines = [];
          if (okCount)   toolLines.push(`✅ ${okCount} tool${okCount === 1 ? "" : "s"} ran`);
          if (failCount > 0) toolLines.push(`❌ ${failCount} failed`);
          if (serverPending.length) toolLines.push(`🔒 ${serverPending.length} awaiting your confirmation`);
          if (toolLines.length) {
            setChatMessages(p=>[...p,{role:"assistant",content:`🔧 ${toolLines.join(" · ")}`,time:formatJaTime(new Date()),_isToolBadge:true}].slice(-200));
          }
          // If the server already created paused_confirmation tasks, link them in chat
          for (const pend of serverPending) {
            const taskId = pend?.task?.task?.id || pend?.task_id;
            const label = pend?.tool || "action";
            const prompt = taskId
              ? `🔒 \`${label}\` is awaiting your confirmation. Tap **Confirm** in the Agent panel, or reply "confirm" / "cancel".`
              : `🔒 \`${label}\` requires your confirmation before it can run.`;
            setChatMessages(p=>[...p,{role:"assistant",content:prompt,time:formatJaTime(new Date()),_isToolBadge:true,_pendingTaskId:taskId}].slice(-200));
            speak(`A gated action is awaiting your confirmation.`, 1);
          }
        }
      } catch (e) { console.warn(`[chatbot-tools] UI render failed: ${e.message}`); }

      // Execute actions with Trio-Agent delegation
      let spokeViaAction = false;
      for (const act of (resp.actions||[])) {
        try {
          const permission = checkVoiceAction(act);
          if (!permission.execute) {
            if (permission.needsConfirm) {
              pendingChatActionRef.current = act;
              const confirmPrompt = `Confirmation required: ${permission.label}. Say “confirm” to proceed, or “cancel” to abort.`;
              setChatMessages(p=>[...p,{role:"assistant",content:confirmPrompt,time:formatJaTime(new Date())}].slice(-200));
              speak(confirmPrompt, 1);
            } else {
            const blocked = `Action blocked: ${permission.reason||"not approved"}`;
              setChatMessages(p=>[...p,{role:"assistant",content:blocked,time:formatJaTime(new Date())}].slice(-200));
               if (isVoiceRequest) speak(blocked, 1);
             }
            continue;
          }
          if (act.type === "delegate_to_strat") {
            spokeViaAction = true; // strat speaks on verdict — suppress speak(msg)
            // T206: sanity guard — cnt must meet requiredCount for active strategy
            const sym = act.symbol;
            const cnt = symDataRef.current[sym]?.cnt ?? 0;
            const reqCntGuard = getStrategyRequiredCount(strategySchemaRef.current);
            if (cnt < reqCntGuard) {
              const reason = `Algorithmic score is ${cnt}/${reqCntGuard}. Stratos requires ${reqCntGuard}/${reqCntGuard} to evaluate. Skipping validation.`;
              addLog(`🛡️ Sanity guard: ${reason}`, "warn");
              setChatMessages(p => [...p, { role: "assistant", content: `🛡️ ${reason}`, time: formatJaTime(new Date()) }]);
              continue;
            }
            addLog(`🤖 Delegating ${sym} to STRATOS...`, "scan");
            callStratos(sym, userText).then(verdict => {
              const vCol = verdict.verdict === "APPROVE" ? C.green : C.amber;
              const stratContent = `🛡️ <strong>STRATOS VERDICT</strong>: <span style="color:${vCol}">${verdict.verdict}</span>\n${verdict.reason}`;
              setChatMessages(p => [...p, { role: "assistant", content: stratContent, time: formatJaTime(new Date()) }]);
              if (verdict.verdict === "APPROVE" && verdict.action === "buy_contract") {
                addLog(`🛠️ STRATOS approved! Calling TOOLSMITH to execute...`, "success");
                speak("Stratos approved the trade. Executing now.");
                callToolsmith(verdict.action, verdict.args).then(res => {
                  setChatMessages(p => [...p, { role: "assistant", content: `✅ <strong>TOOLSMITH EXECUTION</strong>\n${JSON.stringify(res).slice(0, 200)}`, time: formatJaTime(new Date()) }]);
                });
              } else {
                speak(`Stratos returned a ${verdict.verdict} verdict.`);
              }
            }).catch(e => addLog(`❌ Stratos error: ${e.message}`, "error"));
          }
          // T207: delegate_to_scout — result fed back to Kara for natural synthesis
          else if (act.type === "delegate_to_scout") {
            const r2 = await apiFetch("/api/agents/scout", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ task: act.task || "economic_calendar", params: act.params || {}, context: userText })
            });
            const result = await r2.json();
            if (result.success) {
              const summary = result.summary || JSON.stringify(result.data).slice(0, 600);
              await sendToKaraRef.current(
                `Research complete for "${act.params?.query || act.task}": ${summary}. Respond to the user naturally using this information. Never mention where you got it from.`,
                { asContext: true, depth: depth + 1 }
              );
            } else {
              setChatMessages(p => [...p, { role: "assistant", content: "Couldn't pull that info right now.", time: formatJaTime(new Date()) }]);
            }
          }
          // T208: delegate_to_cortex
          else if (act.type === "delegate_to_cortex") {
            const sym = act.symbol || focusSym;
            const d = symDataRef.current[sym];
            if (d) {
              // Lean summary: key levels + indicators + last 20/10 candles (not raw 50)
              const cortexPayload = {
                symbol: sym,
                activeStrategy: activeStrategyRef.current,
                price: d.analysis?.price, ema13: d.analysis?.ema13, ema200: d.analysis?.ema200,
                rsi: d.analysis?.rsi, atr: d.analysis?.atr,
                trend1m: d.analysis?.trend, bos_choch: d.analysis?.bos_choch,
                swing_high: d.analysis?.swing_high, swing_low: d.analysis?.swing_low,
                liq_sweep: d.analysis?.liq_sweep, liq_sweep_dir: d.analysis?.liq_sweep_dir,
                htf15: d.htf15,
                conds: d.conds, cnt: d.cnt,
                candles1m: d.candles?.slice(-20).map(c=>({o:c.open,h:c.high,l:c.low,c:c.close})),
                candles15m: d.candles15?.slice(-10).map(c=>({o:c.open,h:c.high,l:c.low,c:c.close})),
              };
              console.log(`[CORTEX] estimated tokens: ${estimateTokens(JSON.stringify(cortexPayload))}`);
              const r2 = await apiFetch("/api/chat", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  model: "deepseek/deepseek-chat",
                  messages: [
                    { role: "system", content: `You are CORTEX, a deep market analysis engine. Analyze the provided market data across timeframes. Return ONLY JSON: {"bias":"CALL|PUT|NONE","score":0-10,"recommendation":"short text","confluences":[]}` },
                    { role: "user", content: JSON.stringify(cortexPayload) }
                  ]
                })
              });
              const cd = await r2.json();
              const rawC = cd.choices?.[0]?.message?.content || "{}";
              try {
                const result = parseAIJSON(rawC);
                setChatMessages(p => [...p, { role: "assistant", content: `🧠 CORTEX: bias=${result.bias} score=${result.score}/10 — ${result.recommendation}`, time: formatJaTime(new Date()) }]);
              } catch { setChatMessages(p => [...p, { role: "assistant", content: `🧠 CORTEX: ${rawC.slice(0, 200)}`, time: formatJaTime(new Date()) }]); }
            }
          }
          else if (act.type === "delegate_to_toolsmith") {
            addLog(`🛠️ Calling ${act.tool}...`, "scan");
            // Special handling for n8n telegram tool arguments if they are flat
            const args = (act.tool.startsWith("send_text_message_to_") && act.args && !act.args.message)
              ? { message: act.args.text || act.args.msg || JSON.stringify(act.args) }
              : (act.args || {});
              
            callToolsmith(act.tool, args).then(async res => {
              // Extract readable result text
              let resultText;
              if (res?.steps) {
                resultText = res.steps.map(s => {
                  const content = s.result?.content;
                  return Array.isArray(content) ? content.map(c=>c.text||JSON.stringify(c)).join("\n") : JSON.stringify(s.result).slice(0,1000);
                }).join("\n---\n");
              } else {
                const content = res?.result?.content;
                resultText = Array.isArray(content) ? content.map(c=>c.text||JSON.stringify(c)).join("\n") : JSON.stringify(res).slice(0,1000);
              }
              await sendToKaraRef.current(
                `MCP tool "${act.tool}" returned: ${resultText}. Respond to the user naturally using this result.`,
                { asContext: true, depth: depth + 1 }
              );
            }).catch(e => addLog(`❌ Toolsmith error: ${e.message}`, "error"));
          }
          else if (act.type==="add_symbol"&&SYMBOLS.includes(act.symbol)&&!selectedSymsRef.current.has(act.symbol)) uiHelpersRef.current.watchlist("add",[act.symbol]);
          else if (act.type==="remove_symbol"&&selectedSymsRef.current.has(act.symbol)) uiHelpersRef.current.watchlist("remove",[act.symbol]);
          else if (act.type==="add_all_symbols") uiHelpersRef.current.watchlist("add_all");
          else if (act.type==="clear_symbols") uiHelpersRef.current.watchlist("clear");
          else if (act.type==="ai_scan"&&symDataRef.current[act.symbol]) setTimeout(()=>runAIScan(act.symbol),400);
          else if (act.type==="focus_symbol"&&SYMBOLS.includes(act.symbol)) setFocusSym(act.symbol);
          else if (act.type==="set_tab") { const nt = normalizeTab(act.tab); if (nt) setTab(nt); }
          else if (act.type==="open_chat") {/* KENYON 2026-09-25: never auto-open — user clicks the bubble */}
          else if (act.type==="close_chat") setChatOpen(false);
          else if (act.type==="toggle_alerts") setAlertsEnabled(act.value!==undefined ? !!act.value : (v=>!v));
          else if (act.type==="toggle_alert_voice") setAlertVoice(act.value!==undefined ? !!act.value : (v=>!v));
          else if (act.type==="speak") { speak(act.text||msg); spokeViaAction = true; }
          else if (act.type==="send_telegram") {
            const tgMsg = act.message || msg;
            const username = authUser?.username || "ken72";
            const n8nToolName = `send_text_message_to_${username}`;
            
            // Check if user-specific n8n tool exists
            const hasN8nTool = [...dockerToolsRef.current, ...n8nToolsRef.current].some(t => t.name === n8nToolName);
            
            if (hasN8nTool) {
              addLog(`🛠️ Sending Telegram via n8n: ${n8nToolName}...`, "scan");
              callToolsmith(n8nToolName, { message: tgMsg }).then(async res => {
                setChatMessages(p=>[...p,{role:"assistant",content:`✅ Telegram sent via n8n, ${_karaUserName}.`,time:formatJaTime(new Date())}]);
              }).catch(e => {
                addLog(`❌ n8n Telegram failed: ${e.message} — falling back`, "warn");
                sendTelegram(tgMsg).then(ok => {
                  const status = ok ? `✅ Telegram sent (legacy), ${_karaUserName}.` : "❌ Telegram failed — check bot token.";
                  setChatMessages(p=>[...p,{role:"assistant",content:status,time:formatJaTime(new Date())}]);
                });
              });
            } else {
              sendTelegram(tgMsg).then(ok=>{
                const status = ok ? `✅ Telegram sent, ${_karaUserName}.` : "❌ Telegram failed — check bot token.";
                setChatMessages(p=>[...p,{role:"assistant",content:status,time:formatJaTime(new Date())}]);
                if (!ok) speak("Telegram failed. Check the bot token.", 1);
              });
            }
          }
          else if (act.type==="execute_trade") {
            spokeViaAction = true; // trade speaks for itself — suppress speak(msg)
            const esSym=act.symbol||"", esDir=act.direction||"CALL", esStake=act.stake||tradeStakeRef.current;
            if (!esSym) { setChatMessages(p=>[...p,{role:"assistant",content:"❌ No symbol specified for trade.",time:formatJaTime(new Date())}]); break; }
            addLog(`🤖 Kara executing: ${esDir} ${esSym} $${esStake}`, "scan");
            speak(`Executing ${esDir==="CALL"?"buy":"sell"} on ${esSym}, ${_karaUserName}.`);
            executeTrade({ sym:esSym, direction:esDir, stake:esStake }).then(result=>{
              const status = result
                ? `✅ <strong>Trade placed</strong> — ${esDir==="CALL"?"📈 BUY":"📉 SELL"} <strong>${esSym}</strong> @ $${esStake} stake.`
                : `❌ Trade failed — check DERIV_API_TOKEN in .env and logs.`;
              setChatMessages(p=>[...p,{role:"assistant",content:status,time:formatJaTime(new Date())}]);
              if (result) speak(`Trade confirmed, ${_karaUserName}. ${esDir==="CALL"?"Buy":"Sell"} ${esSym} is live.`);
            });
          }
          else if (act.type==="toggle_auto_trade") {
            const newVal = act.value !== undefined ? !!act.value : !autoTradeRef.current;
            setAutoTrade(newVal);
            setChatMessages(p=>[...p,{role:"assistant",content:`🤖 Auto-trade <strong>${newVal?"ENABLED ✅":"DISABLED ❌"}</strong>. ${newVal?"I'll auto-execute on every signal, ${_karaUserName}.":"Standing down — signals will alert only."}`,time:formatJaTime(new Date())}]);
          }
          else if (act.type==="set_stake") {
            const newStake = parseFloat(act.value)||1;
            setTradeStake(newStake);
            setChatMessages(p=>[...p,{role:"assistant",content:`💰 Stake set to <strong>$${newStake}</strong> per trade.`,time:formatJaTime(new Date())}]);
          }
          // T503: personality mode
          else if (act.type==="set_personality") { setPersonalityMode(act.mode); speak(act.mode==="formal"?"Formal mode activated.":`Back to normal, ${_karaUserName}.`); }
          // T902: strategy actions
          else if (act.type==="activate_strategy") {
            const activated = await handleStrategyActivate(act.name);
            if (activated) speak(`Strategy switched to ${(act.name||"").replace(".md","")}, ${_karaUserName}.`);
          }
          else if (act.type==="show_strategy_tab") { setTab("strategy"); speak("Strategy manager."); }
          else if (act.type==="show_stratlab_tab"||act.type==="open_strategy_lab") { setTab(normalizeTab("stratlab")); speak("TradingView AI Optimizer."); }
          // T910: create_strategy — Kara composes and saves a new strategy file
          else if (act.type === "create_strategy") {
            const { name, content, activate: doActivate } = act;
            if (!name || !content) { addLog("❌ create_strategy: missing name or content", "error"); }
            else {
              try {
                const r2 = await apiFetch("/api/strategies/create", {
                  method: "POST", headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ name, content })
                });
                const d2 = await r2.json();
                if (d2.ok) {
                  const res = await apiFetch("/api/strategies");
                  if (res.ok) setStrategies(await res.json());
                  setChatMessages(p => [...p, { role: "assistant", content: `✅ Strategy <strong>${d2.name}</strong> created.`, time: formatJaTime(new Date()) }]);
                  if (doActivate) {
                    await handleStrategyActivate(d2.name);
                    speak(`Strategy ${d2.name.replace(".md","")} created and activated, ${_karaUserName}.`);
                  } else {
                    speak(`Strategy ${d2.name.replace(".md","")} created and added to the library, ${_karaUserName}.`);
                  }
                } else { addLog(`❌ Strategy create failed: ${d2.error}`, "error"); }
              } catch (e) { addLog(`❌ Strategy create error: ${e.message}`, "error"); }
            }
          }
          // Full system control actions
          else if (act.type==="toggle_wake_word") { toggleWakeWordRef.current?.(); speak(wakeWordRunRef.current?"Wake word disabled.":`Always-on wake word activated, ${_karaUserName}.`); }
          else if (act.type==="toggle_voice") { toggleChatVRef.current?.(); }
          else if (act.type==="clear_history") { setAllPreds([]); setTodayPreds([]); localStorage.removeItem(PREDS_KEY); localStorage.removeItem(HISTORY_KEY); speak(`Trade history cleared, ${_karaUserName}.`); addLog("🗑 History cleared via Kara","warn"); }
          else if (act.type==="clear_alerts") { setAlertMessages([]); speak("Alerts cleared."); }
          else if (act.type==="toggle_alert_voice") { setAlertVoice(v=>{ const nv=!v; speak(nv?"Alert voice on.":"Alert voice off."); return nv; }); }
          else if (act.type==="toggle_alerts") { setAlertsEnabled(v=>{ const nv=act.value!==undefined?!!act.value:!v; speak(nv?"Alerts enabled.":"Alerts muted."); return nv; }); }
          else if (act.type==="set_tab") { const nt = normalizeTab(act.tab); if (nt) setTab(nt); }
          // ── 📊 Inline Charts Tab control actions ──────────────────────────
          else if (act.type==="set_chart_symbol") {
            const syms = Array.isArray(act.symbols) ? act.symbols.slice(0,4) : [act.symbol||"XAUUSD"];
            uiHelpersRef.current.setChartSymbols(syms, act.timeframe || "1m", { replaceAll: true });
            speak(`Charts showing ${syms.join(", ")}.`);
          }
          else if (act.type==="set_chart_timeframe") {
            // Apply new TF to all current slots
            const tf = act.timeframe||"1m";
            uiHelpersRef.current.setChartTimeframe(tf, { all: true });
            speak(`Chart timeframe set to ${tf}.`);
          }
          else if (act.type==="set_chart_layout") {
            uiHelpersRef.current.setChartLayout(["single","dual","quad"].includes(act.layout) ? act.layout : "single");
            speak(`Chart layout set to ${act.layout}.`);
          }
          else if (act.type==="set_chart_focus") {
            const sym = act.symbol||"XAUUSD";
            const existing = chartSlots.find(s=>s.sym===sym);
            if (existing) setChartFocusKey(`${existing.sym}_${existing.tf}`);
            setTab("charts");
          }
          else if (act.type==="open_charts"||act.type==="open_chart_dashboard"||act.type==="goto_charts") {
            setTab("charts"); speak("Opening charts.");
          }
          else if (act.type==="run_backtest") {
            const btSym = act.symbol || "R_100";
            setChatMessages(p=>[...p,{role:"assistant",content:`Running advanced backtest on ${btSym}...`,time:formatJaTime(new Date())}]);
            try {
              const rb = await apiFetch("/api/advanced-backtest/run",{method:"POST",headers:{"Content-Type":"application/json"},
                body:JSON.stringify({strategy: activeStrategyRef.current || "VIX_ULTRA_88", symbol:btSym, granularity:act.granularity||60, db_count:act.count||20160})});
              const rd = await rb.json();
              if (rd.error) {
                setChatMessages(p=>[...p,{role:"assistant",content:`Backtest failed: ${rd.error}`,time:formatJaTime(new Date())}]);
              } else {
                await sendToKaraRef.current(
                  `Advanced backtest complete for ${btSym}. Results: ${JSON.stringify(rd)}. Summarize win rate, total trades, profit factor, max drawdown, and whether the strategy is viable. Only use the numbers provided.`,
                  { asContext: true, depth: depth + 1 }
                );
              }
            } catch(e) { setChatMessages(p=>[...p,{role:"assistant",content:`Backtest error: ${e.message}`,time:formatJaTime(new Date())}]); }
          }
          else if (act.type==="sis_improve") {
            // Start Strategy Improvement Service loop from Kara
            const payload = {
              pine_code: act.pine_code || act.code || "",
              symbol: act.symbol || "XAUUSD",
              granularity: act.granularity || 60,
              target_win_rate: act.target_win_rate || 0.7,
            };
            if (!payload.pine_code) {
              setChatMessages(p=>[...p,{role:"assistant",content:"I need the Pine Script strategy code to start SIS improvement.",time:formatJaTime(new Date())}]);
            } else {
              setChatMessages(p=>[...p,{role:"assistant",content:`Starting SIS improvement loop for ${payload.symbol} @ ${payload.granularity}s aiming for ${(payload.target_win_rate*100).toFixed(0)}% win rate...`,time:formatJaTime(new Date())}]);
              try {
                const r = await apiFetch("/api/sis/improve",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(payload)});
                const d = await r.json();
                if (d.task_id) {
                  setChatMessages(p=>[...p,{role:"assistant",content:`SIS task started. Task ID: ${d.task_id}. Ask me to check \"SIS status\" any time.`,time:formatJaTime(new Date())}]);
                } else {
                  setChatMessages(p=>[...p,{role:"assistant",content:`SIS improve failed: ${d.error||"unknown error"}`,time:formatJaTime(new Date())}]);
                }
              } catch(e) {
                setChatMessages(p=>[...p,{role:"assistant",content:`SIS improve error: ${e.message}`,time:formatJaTime(new Date())}]);
              }
            }
          }
          else if (act.type==="sis_status") {
            const taskId = act.task_id || act.id;
            if (!taskId) {
              setChatMessages(p=>[...p,{role:"assistant",content:"I need a SIS task_id to check status.",time:formatJaTime(new Date())}]);
            } else {
              try {
                const r = await apiFetch(`/api/sis/status/${encodeURIComponent(taskId)}`);
                const d = await r.json();
                if (d.state) {
                  setChatMessages(p=>[...p,{role:"assistant",content:`SIS task ${taskId}: state=${d.state}, best win rate=${(d.best_win_rate*100||0).toFixed(2)}%, iterations=${d.iterations}.`,time:formatJaTime(new Date())}]);
                } else {
                  setChatMessages(p=>[...p,{role:"assistant",content:`SIS status error: ${d.detail||d.error||"unknown error"}`,time:formatJaTime(new Date())}]);
                }
              } catch(e) {
                setChatMessages(p=>[...p,{role:"assistant",content:`SIS status error: ${e.message}`,time:formatJaTime(new Date())}]);
              }
            }
          }
          else if (act.type==="sis_deploy") {
            const taskId = act.task_id || act.id;
            if (!taskId) {
              setChatMessages(p=>[...p,{role:"assistant",content:"I need a SIS task_id to deploy.",time:formatJaTime(new Date())}]);
            } else {
              try {
                const r = await apiFetch("/api/sis/deploy",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({task_id:taskId})});
                const d = await r.json();
                if (d.state) {
                  setChatMessages(p=>[...p,{role:"assistant",content:`SIS deploy for task ${taskId}: ${d.message||"ok"}`,time:formatJaTime(new Date())}]);
                } else {
                  setChatMessages(p=>[...p,{role:"assistant",content:`SIS deploy error: ${d.detail||d.error||"unknown error"}`,time:formatJaTime(new Date())}]);
                }
              } catch(e) {
                setChatMessages(p=>[...p,{role:"assistant",content:`SIS deploy error: ${e.message}`,time:formatJaTime(new Date())}]);
              }
            }
          }
          else if (act.type==="ml_predict") {
            spokeViaAction = true;
            const sym = act.symbol || focusSym || SYMBOLS[0];
            const d = symDataRef.current[sym];
            const features = d?.analysis ? {
              rsi: d.analysis.rsi,
              ema13: d.analysis.ema13,
              atr: d.analysis.atr,
              cnt: d.cnt,
              trend: d.analysis.trend,
            } : {};
            setChatMessages(p => [...p, { role: "assistant", content: `🤖 Running ML prediction on ${sym}...`, time: formatJaTime(new Date()) }]);
            apiFetch("/api/ml/predict", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ symbol: sym, features }),
            }).then(r => r.json()).then(d => {
              const resultText = `🤖 <strong>ML Prediction — ${sym}</strong>\n${JSON.stringify(d, null, 2).slice(0, 400)}`;
              setChatMessages(p => [...p, { role: "assistant", content: resultText, time: formatJaTime(new Date()) }]);
              speak(`ML prediction for ${sym} complete, ${_karaUserName}.`, 1);
            }).catch(e => {
              setChatMessages(p => [...p, { role: "assistant", content: `❌ ML predict failed: ${e.message}`, time: formatJaTime(new Date()) }]);
            });
          }
          else if (act.type==="ml_status") {
            setTab("ml");
            spokeViaAction = true;
            Promise.all([apiFetch("/api/ml/status"), apiFetch("/api/ml/metrics")]).then(async ([sr, mr]) => {
              const status = sr.ok ? await sr.json() : {};
              const metrics = mr.ok ? await mr.json() : {};
              const msg = `🧠 ML Status: Model ${status.model_ready?"ready":"not ready"} | Accuracy: ${metrics.accuracy!=null?Math.round(metrics.accuracy*100)+"%" : "N/A"} | F1: ${metrics.f1!=null?metrics.f1.toFixed(3):"N/A"} | Samples: ${status.total_signals_logged??0} | Auto-train: ${status.auto_train_enabled?"on":"off"}`;
              setChatMessages(p => [...p, { role: "assistant", content: msg, time: formatJaTime(new Date()) }]);
              speak(`ML model is ${status.model_ready?"ready":"not ready"}. Accuracy ${metrics.accuracy!=null?Math.round(metrics.accuracy*100)+"percent":"unknown"}.`, 1);
            }).catch(() => speak("Showing ML status on the dashboard.", 1));
          }
          else if (act.type==="ml_train") {
            spokeViaAction = true;
            apiFetch("/api/ml/train", { method: "POST" }).then(d => {
              const msg = d?.message || `ML model retraining started.`;
              speak(msg, 1);
              setChatMessages(p => [...p, { role: "assistant", content: `🧠 ${msg}`, time: formatJaTime(new Date()) }]);
            }).catch(e => {
              setChatMessages(p => [...p, { role: "assistant", content: `❌ ML train failed: ${e.message}`, time: formatJaTime(new Date()) }]);
            });
          }
          else if (act.type==="ml_improve") {
            spokeViaAction = true;
            const payload = { target_win_rate: act.target_win_rate || 0.65, max_iterations: act.max_iterations || 10, stop_on_plateau: act.stop_on_plateau || 3 };
            apiFetch("/api/ml/improve", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) }).then(r => r.json()).then(d => {
              const msg = d.status === "improvement_loop_started" ? `ML improvement loop started, targeting ${Math.round((payload.target_win_rate||0.65)*100)}% win rate.` : (d.status === "already_running" ? "Improvement loop is already running." : `ML improve: ${d.status || d.error}`);
              speak(msg, 1);
              setChatMessages(p => [...p, { role: "assistant", content: msg, time: formatJaTime(new Date()) }]);
            }).catch(e => setChatMessages(p => [...p, { role: "assistant", content: `ML improve error: ${e.message}`, time: formatJaTime(new Date()) }]));
          }
          else if (act.type==="ml_improve_stop") {
            spokeViaAction = true;
            apiFetch("/api/ml/improve/stop", { method: "POST" }).then(r => r.json()).then(d => {
              const msg = `ML improvement loop stopped.`;
              speak(msg, 1);
              setChatMessages(p => [...p, { role: "assistant", content: msg, time: formatJaTime(new Date()) }]);
            }).catch(e => setChatMessages(p => [...p, { role: "assistant", content: `Stop error: ${e.message}`, time: formatJaTime(new Date()) }]));
          }
          else if (act.type==="list_strategies") {
            apiFetch("/api/strategies").then(r => r.json()).then(d => {
              const strats = Array.isArray(d) ? d : (d.strategies || []);
              sendToKaraRef.current(`Available strategies: ${JSON.stringify(strats)}. Tell the user what strategies are available in a natural, readable way. Mention the active one if known.`, { asContext: true, depth: depth + 1 });
            }).catch(e => setChatMessages(p => [...p, { role: "assistant", content: `Strategy list error: ${e.message}`, time: formatJaTime(new Date()) }]));
          }
          else if (act.type==="get_risk_config") {
            apiFetch("/api/risk-config").then(r => r.json()).then(d => {
              sendToKaraRef.current(`Current risk config: ${JSON.stringify(d)}. Explain each setting to the user in plain English.`, { asContext: true, depth: depth + 1 });
            }).catch(e => setChatMessages(p => [...p, { role: "assistant", content: `Risk config error: ${e.message}`, time: formatJaTime(new Date()) }]));
          }
          else if (act.type==="set_risk_config") {
            const { type: _t, ...riskPayload } = act;
            apiFetch("/api/risk-config", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(riskPayload) }).then(r => r.json()).then(d => {
              const msg = d.ok ? `Risk settings updated.` : `Risk config update failed: ${d.error}`;
              speak(msg, 1);
              setChatMessages(p => [...p, { role: "assistant", content: msg, time: formatJaTime(new Date()) }]);
            }).catch(e => setChatMessages(p => [...p, { role: "assistant", content: `Risk config error: ${e.message}`, time: formatJaTime(new Date()) }]));
          }
          else if (act.type==="run_backtest_walkforward") {
            const wfSym = act.symbol || "R_100";
            setChatMessages(p => [...p, { role: "assistant", content: `Running walk-forward analysis on ${wfSym}...`, time: formatJaTime(new Date()) }]);
            apiFetch("/api/advanced-backtest/wfa", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ strategy: activeStrategyRef.current || "VIX_ULTRA_88", symbol: wfSym, granularity: act.granularity || 60, db_count: act.count || 20160, n_splits: 5, train_pct: 0.7, n_trials: 50 }) }).then(r => r.json()).then(d => {
              sendToKaraRef.current(`Walk-forward analysis results for ${wfSym}: ${JSON.stringify(d)}. Summarize robustness score, fold results, and whether the strategy is robust (robustness >= 0.60).`, { asContext: true, depth: depth + 1 });
            }).catch(e => setChatMessages(p => [...p, { role: "assistant", content: `Walk-forward error: ${e.message}`, time: formatJaTime(new Date()) }]));
          }
          else if (act.type==="data_status") {
            apiFetch("/api/data/status").then(r => r.json()).then(d => {
              sendToKaraRef.current(`Market data DB status: ${JSON.stringify(d)}. Tell the user what data is available, which symbols have good coverage, and which are missing.`, { asContext: true, depth: depth + 1 });
            }).catch(e => setChatMessages(p => [...p, { role: "assistant", content: `Data status error: ${e.message}`, time: formatJaTime(new Date()) }]));
          }
          else if (act.type==="ml_metrics") {
            apiFetch("/api/ml/metrics").then(r => r.json()).then(d => {
              sendToKaraRef.current(`ML model metrics: ${JSON.stringify(d)}. Explain the accuracy, precision, recall, and F1 score to the user in plain language.`, { asContext: true, depth: depth + 1 });
            }).catch(e => setChatMessages(p => [...p, { role: "assistant", content: `ML metrics error: ${e.message}`, time: formatJaTime(new Date()) }]));
          }
          else if (act.type==="ml_performance") {
            apiFetch("/api/ml/performance_history").then(r => r.json()).then(d => {
              sendToKaraRef.current(`ML performance history: ${JSON.stringify(d)}. Summarize the trend — is accuracy improving or declining over time?`, { asContext: true, depth: depth + 1 });
            }).catch(e => setChatMessages(p => [...p, { role: "assistant", content: `ML performance error: ${e.message}`, time: formatJaTime(new Date()) }]));
          }
          else if (act.type==="ml_auto_train") {
            spokeViaAction = true;
            apiFetch("/api/ml/auto_train/toggle", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ enabled: act.enabled !== false }) }).then(r => r.json()).then(d => {
              const msg = `Auto-training ${d.auto_train_enabled ? "enabled" : "disabled"}.`;
              speak(msg, 1);
              setChatMessages(p => [...p, { role: "assistant", content: `🧠 ${msg}`, time: formatJaTime(new Date()) }]);
            }).catch(e => setChatMessages(p => [...p, { role: "assistant", content: `Auto-train error: ${e.message}`, time: formatJaTime(new Date()) }]));
          }
          else if (act.type==="ml_set_confidence") {
            spokeViaAction = true;
            apiFetch("/api/ml/set_confidence", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ threshold: act.threshold ?? 0.55 }) }).then(r => r.json()).then(d => {
              const msg = d.ok ? `ML confidence gate set to ${Math.round((d.min_confidence??act.threshold)*100)}%.` : `Error: ${d.error}`;
              speak(msg, 1);
              setChatMessages(p => [...p, { role: "assistant", content: `🎯 ${msg}`, time: formatJaTime(new Date()) }]);
            }).catch(e => setChatMessages(p => [...p, { role: "assistant", content: `Set confidence error: ${e.message}`, time: formatJaTime(new Date()) }]));
          }
          else if (act.type==="memory_stats") {
            apiFetch("/api/memory/stats").then(r => r.json()).then(d => {
              sendToKaraRef.current(`Vector memory stats: ${JSON.stringify(d)}. Tell the user how many signal vectors are stored and what the win rate looks like in memory.`, { asContext: true, depth: depth + 1 });
            }).catch(e => setChatMessages(p => [...p, { role: "assistant", content: `Memory stats error: ${e.message}`, time: formatJaTime(new Date()) }]));
          }
          else if (act.type==="get_scheduler_status") {
            apiFetch("/api/scheduler/status").then(r => r.json()).then(d => {
              sendToKaraRef.current(`Scheduler jobs: ${JSON.stringify(d)}. List the cron jobs and their next run times in a readable format.`, { asContext: true, depth: depth + 1 });
            }).catch(e => setChatMessages(p => [...p, { role: "assistant", content: `Scheduler error: ${e.message}`, time: formatJaTime(new Date()) }]));
          }
          else if (act.type==="get_trade_history") {
            apiFetch("/api/trades/history").then(r => r.json()).then(d => {
              const trades = Array.isArray(d) ? d : (d.trades || []);
              sendToKaraRef.current(`Recent trade history (${trades.length} trades): ${JSON.stringify(trades.slice(-20))}. Summarize the recent performance — wins, losses, symbols traded, and P&L.`, { asContext: true, depth: depth + 1 });
            }).catch(e => setChatMessages(p => [...p, { role: "assistant", content: `Trade history error: ${e.message}`, time: formatJaTime(new Date()) }]));
          }

          else if (act.type==="get_weather") {
            spokeViaAction = true;
            const weatherCity = act.city || "London";
            setChatMessages(p=>[...p,{role:"assistant",content:`☁️ Checking weather for ${weatherCity}...`,time:formatJaTime(new Date())}]);
            apiFetch(`/api/weather?city=${encodeURIComponent(weatherCity)}`).then(r=>r.json()).then(wd=>{
              if (wd.error) {
                setChatMessages(p=>[...p,{role:"assistant",content:`❌ Weather error: ${wd.error}`,time:formatJaTime(new Date())}]);
              } else {
                const wMsg = `☁️ <strong>Weather — ${wd.city}${wd.country?", "+wd.country:""}</strong>\n${wd.description?wd.description.charAt(0).toUpperCase()+wd.description.slice(1):""}\n??️ <strong>${wd.temp}°C</strong> (feels like ${wd.feels_like}°C) · ?? ${wd.humidity}% humidity${wd.wind_speed?" · ?? "+wd.wind_speed+" m/s":""}`;
                setChatMessages(p=>[...p,{role:"assistant",content:wMsg,time:formatJaTime(new Date())}]);
                speak(`Weather in ${wd.city}: ${wd.description}, ${Math.round(wd.temp)} degrees Celsius.`, 1);
              }
            }).catch(e=>setChatMessages(p=>[...p,{role:"assistant",content:`❌ Weather unavailable: ${e.message}`,time:formatJaTime(new Date())}]));
          }
          else if (act.type==="store_historical_data") {
            const sym2 = act.symbol||"XAUUSD";
            setChatMessages(p=>[...p,{role:"assistant",content:`Downloading ${sym2} candles from Deriv...`,time:formatJaTime(new Date())}]);
            try {
              const rs = await apiFetch("/api/historical-data/store",{method:"POST",headers:{"Content-Type":"application/json"},
                body:JSON.stringify({symbol:sym2,granularity:act.granularity||60,count:act.count||1000})});
              const sd = await rs.json();
              await sendToKaraRef.current(
                `Historical data storage result: ${JSON.stringify(sd)}. Confirm to the user naturally.`,
                { asContext: true, depth: depth + 1 }
              );
            } catch(e) { setChatMessages(p=>[...p,{role:"assistant",content:`Storage error: ${e.message}`,time:formatJaTime(new Date())}]); }
          }
          else if (act.type==="list_historical_data") {
            try {
              const rl = await apiFetch("/api/historical-data/list");
              const ld = await rl.json();
              await sendToKaraRef.current(
                `Stored historical data files: ${JSON.stringify(ld)}. Tell the user what data is available naturally.`,
                { asContext: true, depth: depth + 1 }
              );
            } catch(e) { setChatMessages(p=>[...p,{role:"assistant",content:`Could not list historical data: ${e.message}`,time:formatJaTime(new Date())}]); }
          }
          else if (act.type==="list_mcp_tools") {
            try {
              const r2 = await apiFetch("/api/mcp/tools");
              const d2 = await r2.json();
              const tools = d2.tools || [];
              if (!tools.length) {
                setChatMessages(p=>[...p,{role:"assistant",content:"No MCP tools available — Docker gateway may still be starting.",time:formatJaTime(new Date())}]);
              } else {
                const toolList = tools.map(t=>`${t.name}: ${t.description||"(no description)"}`).join("\n");
                await sendToKaraRef.current(
                  `Available MCP tools (${tools.length} total from all connected servers):\n${toolList}\n\nTell the user what tools are available and offer to use any of them.`,
                  { asContext: true, depth: depth + 1 }
                );
              }
            } catch(e) { setChatMessages(p=>[...p,{role:"assistant",content:`MCP tools unavailable: ${e.message}`,time:formatJaTime(new Date())}]); }
          }
          else if (act.type==="call_mcp_tool") {
            const toolName = act.name || act.tool;
            if (!toolName) { setChatMessages(p=>[...p,{role:"assistant",content:"call_mcp_tool requires a name.",time:formatJaTime(new Date())}]); }
            else {
              setChatMessages(p=>[...p,{role:"assistant",content:`Calling ${toolName}...`,time:formatJaTime(new Date())}]);
              try {
                const r2 = await apiFetch("/api/mcp/call", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ name: toolName, arguments: act.args || act.arguments || {} })
                });
                const d2 = await r2.json();
                if (d2.error) throw new Error(d2.error);
                const resultText = Array.isArray(d2.content)
                  ? d2.content.map(c=>c.text||JSON.stringify(c)).join("\n")
                  : JSON.stringify(d2).slice(0, 2000);
                await sendToKaraRef.current(
                  `MCP tool "${toolName}" returned: ${resultText}. Respond to the user naturally using this result.`,
                  { asContext: true, depth: depth + 1 }
                );
              } catch(e) { setChatMessages(p=>[...p,{role:"assistant",content:`${toolName} failed: ${e.message}`,time:formatJaTime(new Date())}]); }
            }
          }
          else if (act.type==="show_health") {
            spokeViaAction = true; // suppress speak(msg) — health handler speaks once below
            apiFetch("/api/health").then(r=>r.json()).then(d=>{
              const issues=Object.entries(d).filter(([k,v])=>v===false).map(([k])=>k);
              const statusText = issues.length===0
                ? `All systems green, ${_karaUserName}. Strategy: ${d.activeStrategy?.replace(".md","")}.`
                : `Issues detected: ${issues.join(", ")}.`;
              speak(statusText, 1);
              setChatMessages(p=>[...p,{role:"assistant",content:statusText,time:formatJaTime(new Date())}]);
            }).catch(()=>{ speak("Health check failed."); });
          }
          else if (act.type==="get_trading_schedule") {
            apiFetch("/api/schedule").then(r=>r.json()).then(s=>{
              sendToKaraRef.current(`Current trading schedule: ${JSON.stringify(s)}. Tell the user what it means in plain English.`, { asContext: true, depth: depth+1 });
            }).catch(e=>{ setChatMessages(p=>[...p,{role:"assistant",content:`Schedule error: ${e.message}`,time:formatJaTime(new Date())}]); });
          }
          else if (act.type==="set_trading_schedule") {
            apiFetch("/api/schedule", {
              method: "POST", headers: {"Content-Type":"application/json"},
              body: JSON.stringify(act.schedule || { alwaysOn: true, enabled: true, windows: [] }),
            }).then(r=>r.json()).then(s=>{
              addLog(`📅 Trading schedule saved`, "scan");
              sendToKaraRef.current(`Schedule saved: ${JSON.stringify(s.schedule)}. Confirm to the user in plain English what was set.`, { asContext: true, depth: depth+1 });
            }).catch(e=>{ setChatMessages(p=>[...p,{role:"assistant",content:`Failed to save schedule: ${e.message}`,time:formatJaTime(new Date())}]); });
          }
          // ── MT5 actions ───────────────────────────────────────────
          else if (act.type==="po_health" || act.type==="mt5_health") {
            spokeViaAction = true;
            setTab("mt5");
            apiFetch("/api/mt5-mcp/health", {silent:true}).then(r=>r.json()).then(d=>{
              const ok = d.data?.worker_ok === true;
              const msg2 = ok ? `✅ MT5 terminal is connected. MCP version: ${d.data?.version}.` : `❌ MT5 terminal is not connected. Start the MT5 terminal on the Hostinger VPS.`;
              setChatMessages(p=>[...p,{role:"assistant",content:msg2,time:formatJaTime(new Date())}]);
              speak(ok ? "MT5 is connected." : "MT5 is not connected.", 1);
            }).catch(e=>{ setChatMessages(p=>[...p,{role:"assistant",content:`MT5 health error: ${e.message}`,time:formatJaTime(new Date())}]); });
          }
          else if (act.type==="po_signals" || act.type==="mt5_rates") {
            spokeViaAction = true;
            setTab("mt5");
            const sym = act.symbol || "XAUUSD";
            apiFetch(`/api/mt5-mcp/rates?symbol=${sym}&period=M5&count=5`, {silent:true}).then(r=>r.json()).then(d=>{
              const bars = d.data?.bars || [];
              const msg2 = bars.length > 0
                ? `MT5 ${sym} last ${bars.length} M5 bars: ${bars.map(b=>`${new Date(b[0]*1000).toISOString().replace("T"," ").slice(0,16)} O=${b[1].toFixed(2)} H=${b[2].toFixed(2)} L=${b[3].toFixed(2)} C=${b[4].toFixed(2)}`).join(" | ")}`
                : `No rate data for ${sym}.`;
              setChatMessages(p=>[...p,{role:"assistant",content:msg2,time:formatJaTime(new Date())}]);
            }).catch(e=>{ setChatMessages(p=>[...p,{role:"assistant",content:`MT5 rates error: ${e.message}`,time:formatJaTime(new Date())}]); });
          }
          else if (act.type==="po_recommend" || act.type==="mt5_recommend") {
            spokeViaAction = true;
            setTab("mt5");
            setChatMessages(p=>[...p,{role:"assistant",content:"ℹ️ MT5 signals and recommendations are viewed directly in the MetaTrader 5 terminal. Open the terminal on the Hostinger VPS to see analyst recommendations.",time:formatJaTime(new Date())}]);
          }
          else if (act.type==="po_trades" || act.type==="mt5_history") {
            spokeViaAction = true;
            setTab("mt5");
            setChatMessages(p=>[...p,{role:"assistant",content:"ℹ️ MT5 trade history is available in the MetaTrader 5 terminal on the Hostinger VPS. Open the terminal to view your trading history.",time:formatJaTime(new Date())}]);
          }
          else if (act.type==="po_pnl" || act.type==="mt5_pnl") {
            spokeViaAction = true;
            setTab("mt5");
            setChatMessages(p=>[...p,{role:"assistant",content:"ℹ️ MT5 P&L is tracked in the MetaTrader 5 terminal on the Hostinger VPS. Your account equity and balance are shown in the MT5 tab.",time:formatJaTime(new Date())}]);
          }
          else if (act.type==="po_prices" || act.type==="mt5_prices") {
            spokeViaAction = true;
            setTab("mt5");
            setChatMessages(p=>[...p,{role:"assistant",content:"ℹ️ MT5 live prices are shown in the MT5 tab above. Select a symbol and timeframe, then click Load to see live rates.",time:formatJaTime(new Date())}]);
          }
          else if (act.type === "spawn_agent_task") {
            spokeViaAction = true;
            const r2 = await apiFetch("/api/tasks", {
              method: "POST", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                task_type: act.task_type || "general_analysis",
                title: act.title || userText.slice(0, 140) || "Kara task",
                target: act.target || null,
                payload: act.payload || { request: userText },
              }),
            });
            const data = await r2.json();
            if (!r2.ok || !data.task) throw new Error(data.error || `Task create failed (${r2.status})`);
            const task = data.task;
            const initial = task.status === "paused_confirmation"
              ? `⚠️ Agent task ${task.id} needs confirmation. Say “confirm task ${task.id}”.`
              : `🤖 Agent task ${task.id} started. I’ll report its independently checked result here.`;
            setChatMessages(p=>[...p,{role:"assistant",content:initial,time:formatJaTime(new Date())}].slice(-200));
            if (task.status !== "paused_confirmation") {
              // 30s safety-net poll (primary delivery is via /ws/signals agent_task_done)
              (async () => {
                for (let attempt=0; attempt<60; attempt++) {
                  await new Promise(resolve => setTimeout(resolve, 30000));
                  const check = await apiFetch(`/api/tasks/${encodeURIComponent(task.id)}`, { silent: true });
                  if (!check.ok) continue;
                  const currentData = await check.json();
                  const current = currentData.task;
                  if (current && ["completed","failed","cancelled"].includes(current.status)) {
                    const result = current.result?.output || current.error || "No result text returned.";
                    const verdict = current.critic_verdict ? `\nCritic: ${current.critic_verdict}` : "";
                    setChatMessages(p=>[...p,{role:"assistant",content:`${current.status === "completed" ? "✅" : "❌"} Agent task ${current.id}: ${result}${verdict}`,time:formatJaTime(new Date())}].slice(-200));
                    break;
                  }
                }
              })().catch(e => addLog(`Agent task poll error: ${e.message}`, "warn"));
            }
          }
          else if (act.type === "task_status") {
            spokeViaAction = true;
            const r2 = await apiFetch(`/api/tasks/${encodeURIComponent(act.task_id || "")}`, { silent: true });
            const data = await r2.json();
            if (!r2.ok || !data.task) throw new Error(data.error || `Task lookup failed (${r2.status})`);
            const task = data.task;
            const result = task.result?.output ? `\n${task.result.output}` : "";
            setChatMessages(p=>[...p,{role:"assistant",content:`🤖 ${task.id}: ${task.status} — ${task.progress_msg || ""}${result}`,time:formatJaTime(new Date())}].slice(-200));
          }
          else if (act.type === "steer_agent_task") {
            spokeViaAction = true;
            const r2 = await apiFetch(`/api/tasks/${encodeURIComponent(act.task_id || "")}/steer`, { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ instruction: act.instruction || userText }) });
            const data = await r2.json();
            if (!r2.ok || !data.task) throw new Error(data.error || `Task steer failed (${r2.status})`);
            setChatMessages(p=>[...p,{role:"assistant",content:`↪️ Task ${data.task.id} redirected: ${data.task.progress_msg}`,time:formatJaTime(new Date())}].slice(-200));
          }
          else if (act.type === "cancel_agent_task") {
            spokeViaAction = true;
            const r2 = await apiFetch(`/api/tasks/${encodeURIComponent(act.task_id || "")}/cancel`, { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ reason:"User cancelled from Kara/Lala chat" }) });
            const data = await r2.json();
            if (!r2.ok || !data.task) throw new Error(data.error || `Task cancel failed (${r2.status})`);
            setChatMessages(p=>[...p,{role:"assistant",content:`🛑 Task ${data.task.id} cancelled.`,time:formatJaTime(new Date())}].slice(-200));
          }
          else if (act.type === "confirm_agent_task") {
            spokeViaAction = true;
            const r2 = await apiFetch(`/api/tasks/${encodeURIComponent(act.task_id || "")}/confirm`, { method:"POST", headers:{"Content-Type":"application/json"}, body:"{}" });
            const data = await r2.json();
            if (!r2.ok || !data.task) throw new Error(data.error || `Task confirmation failed (${r2.status})`);
            setChatMessages(p=>[...p,{role:"assistant",content:`✅ Task ${data.task.id} confirmed and queued.`,time:formatJaTime(new Date())}].slice(-200));
          }
          else if (act.type==="po_set_account_mode" || act.type==="mt5_switch_account") {
            spokeViaAction = true;
            setTab("mt5");
            setChatMessages(p=>[...p,{role:"assistant",content:"ℹ️ MT5 account switching is done directly in the MetaTrader 5 terminal on the Hostinger VPS.",time:formatJaTime(new Date())}]);
          }
          else if (act.type==="po_trade" || act.type==="mt5_trade") {
            spokeViaAction = true;
            setTab("mt5");
            setChatMessages(p=>[...p,{role:"assistant",content:"ℹ️ MT5 trades are placed directly in the MetaTrader 5 terminal on the Hostinger VPS.",time:formatJaTime(new Date())}]);
          }
        } catch (actErr) {
          addLog(`❌ Action error: ${actErr.message}`, "error");
        }
      }
      // Voice requests must hear the answer, not just see a chat bubble. Keep
      // action-owned speech exclusive and never speak an empty direct-action reply.
      if (_spokenText) {
        // Part of the reply was already spoken while streaming — speak only the tail.
        if (!spokeViaAction && msg && msg.startsWith(_spokenText)) {
          const tail = msg.slice(_spokenText.length).trim();
          if (tail.length > 1) speak(tail, 5, { keep: true });
        }
      } else if (!spokeViaAction && msg && !options.directAction) speak(msg);
      return msg;
    } catch(e) {
      // Clear streaming placeholder with error text
      setChatMessages(p=>{const m=[...p];for(let i=m.length-1;i>=0;i--){if(m[i]._streaming){m[i]={...m[i],content:`❌ ${e.message}`,_streaming:false};return m;}}return p;});
      addLog(`❌ Kara: ${e.message}`,"error");
      if (options.fromVoiceTab) throw e;
    } finally { setChatThinking(false); }
  },[toggleSymbol,unsubscribeSymbol,runAIScan,speak,addLog,dockerOk,callStratos,callToolsmith,executeTrade,personalityMode,activeStrategy,todayPreds,focusSym,handleStrategyActivate]);

  useEffect(() => { sendToKaraRef.current = sendToKara; }, [sendToKara]);
  // 2026-10-31: other tabs (TradingView tab "Ask Kara/Lala") can hand the chatbot a prompt: window.dispatchEvent(new CustomEvent("kara:ask",{detail:{text}}))
  useEffect(() => {
    const onAsk = (e) => { const t = String(e?.detail?.text || "").trim(); if (!t) return; setChatOpen(true); setChatUnread(0); sendToKaraRef.current?.(t); };
    window.addEventListener("kara:ask", onAsk);
    return () => window.removeEventListener("kara:ask", onAsk);
  }, []);


  const toggleChatVoice = useCallback(()=>{
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    // ── STOP ─────────────────────────────────────────────────────────────────
    if (chatVoice==="recording") {
      chatActiveRef.current=false;
      startChatSRRef.current=null;
      try { chatSRRef.current?.abort(); } catch(_){}
      chatSRRef.current=null;
      setChatVoice("idle");
      addLog("🎤 Kara voice off","scan");
      // Resume wake word if it was running
      if (wakeWordRunRef.current) setTimeout(()=>startWakeWordRef.current?.(), 600);
      return;
    }
    if (!SR) { addLog("🎤 Web Speech API not supported","error"); return; }
    // Stop scanner voice (one SR at a time in Chrome)
    if (voiceState==="recording") { try { srRef.current?.stop(); } catch(_){} setVoiceState("idle"); }
    // Pause wake word — chat voice takes over
    if (wakeWordSRRef.current) { try { wakeWordSRRef.current.abort(); } catch(_){} wakeWordSRRef.current = null; }
    // Speech and a live mic cannot coexist — stop TTS before acquiring the mic.
    interruptTtsForMic();

    // ── startSR: creates a new SR session, defers if TTS is playing ──────────
    // Annyang pattern: track start time to prevent Chrome rate-limiting on rapid restarts
    let lastStartAt = 0;
    const startSR = ()=>{
      if (!chatActiveRef.current) return;
      if (document.visibilityState !== "visible") return; // hidden tab must not hold the mic
      // Mic arbiter: bail out instead of stealing/duplicating the microphone.
      if (micOwnerRef.current && micOwnerRef.current !== "chat") return;
      if (isSpeakingRef.current) { setTimeout(startSR, 300); return; } // wait for TTS to finish
      // A live chat recognizer already owns the mic. Never abort/recreate it from
      // a duplicate watchdog or delayed TTS callback — that creates restart storms.
      if (chatSRRef.current) return;
      const sr = new SR();
      chatSRRef.current = sr;
      micOwnerRef.current = "chat";
      sr.continuous      = true;
      sr.interimResults  = false;
      sr.lang            = "en-US";   // en-US has more Google training data → better accuracy
      sr.maxAlternatives = 3;
      sr.onresult = e=>{
        for (let i=e.resultIndex; i<e.results.length; i++){
          if (e.results[i].isFinal){
            const t=e.results[i][0].transcript.trim();
            if (!t || t.length < 2) continue; // T512: discard noise
            if (isEcho(t, speakingNow())) continue; // Jarvis acoustic echo filter (ECHO_TAIL_MS=1800ms)
            // ── Voice stop/wait/listen commands ──────────────────────────
            const tl=t.toLowerCase().replace(/[^a-z\s]/g,"").trim();
            // "kara/lala/scanner listen" — resume after wait
            if (/\b(kara|cara|lala|scanner)\s*(listen|go|continue|ready|im\s+ready)/i.test(tl) && karaWaitingRef.current) {
              karaWaitingRef.current = false;
              speak("I'm listening.", 1);
              return;
            }
            // "kara/lala/scanner wait" — pause without ending session
            if (/\b(kara|cara|lala|scanner)\s*(wait|pause|hold\s*on)/i.test(tl) && !karaWaitingRef.current) {
              karaWaitingRef.current = true;
              speak("Okay, standing by.", 1);
              try { chatSRRef.current?.abort(); } catch(_){}
              chatSRRef.current = null;
              return;
            }
            // If waiting and user speaks without "kara/lala/scanner listen", ignore
            if (karaWaitingRef.current) return;
            if (/\b(kara|cara|lala|scanner)\s*(stop\s+listening|stop|shut\s*up|go\s+quiet|be\s+quiet|wait|pause)/i.test(tl) || /^(stop\s+listening|shut\s+up|kara\s+stop|kara\s+wait|lala\s+stop|lala\s+wait|scanner\s+stop|scanner\s+wait)$/.test(tl)){
              addLog(`🎤 Stop command heard: "${t}"`, "scan");
              karaWaitingRef.current = false;
              speak("Okay, going quiet.");
              // small delay so TTS starts before we kill the SR
              setTimeout(()=>{
                chatActiveRef.current=false;
                startChatSRRef.current=null;
                try { chatSRRef.current?.abort(); } catch(_){}
                chatSRRef.current=null;
                setChatVoice("idle");
                if (wakeWordRunRef.current) setTimeout(()=>startWakeWordRef.current?.(), 800);
              }, 120);
              return;
            }
            // ── Normal message → send to Kara ────────────────────────────
            addLog(`🎤 Kara heard: "${t}"`, "scan");
            /* KENYON 2026-09-25: no auto-open — user clicks the bubble */
            sendToKara(t, { fromVoice: true });
          }
        }
      };
      sr.onerror = e=>{
        if (e.error==="no-speech"||e.error==="aborted") return;
        addLog(`🎤 Kara mic: ${e.error}`,"error");
        // Only terminate on permission errors — everything else lets onend restart
        if (micOwnerRef.current === "chat") micOwnerRef.current = null;
        if (e.error==="not-allowed"||e.error==="service-not-allowed"){
          chatActiveRef.current=false;
          setChatVoice("idle");
          if (wakeWordRunRef.current) setTimeout(()=>startWakeWordRef.current?.(), 600);
        }
      };
      sr.onend = ()=>{
        // Ignore an onend event from an older recognizer after a newer one took
        // ownership. Stale callbacks used to clear the live ref and spawn rivals.
        if (chatSRRef.current && chatSRRef.current !== sr) return;
        if (chatSRRef.current === sr) chatSRRef.current = null;
        if (micOwnerRef.current === "chat") micOwnerRef.current = null;
        if (!chatActiveRef.current) return;
        if (document.visibilityState !== "visible") return; // reacquire on 'visible' instead
        const elapsed = Date.now() - lastStartAt;
        const base = elapsed < 300 ? 300 - elapsed : 0;
        // If TTS is playing, done() will restart — 3s safety net in case it doesn't fire
        const delay = isSpeakingRef.current ? Math.max(base, 3000) : base;
        setTimeout(()=>{
          if (chatActiveRef.current && !isSpeakingRef.current && !chatSRRef.current) startSR();
        }, delay);
      };
      try { lastStartAt = Date.now(); sr.start(); } catch(e){ addLog(`🎤 ${e.message}`,"error"); chatActiveRef.current=false; setChatVoice("idle"); }
    };

    startChatSRRef.current = startSR;
    chatActiveRef.current  = true;
    setChatVoice("recording");
    /* KENYON 2026-09-25: no auto-open — user clicks the bubble */
    addLog("🎤 Kara voice on — speak anytime","scan");
    startSR();
  },[chatVoice,voiceState,sendToKara,addLog,setVoiceState]);


  // ── Wake Word ("amber") — single en-US SR, auto-restarts, watchdog via useEffect ─
  const startWakeWordSR = useCallback(()=>{
    if (!wakeWordRunRef.current || chatActiveRef.current || isSpeakingRef.current || document.visibilityState !== "visible") {
      wakeWordPendingRef.current = false;
      wakeWordPendingSinceRef.current = 0;
      return;
    }
    // Single-owner invariant: delayed timers/watchdogs must not replace a live
    // wake recognizer. Its own onend callback is responsible for releasing it.
    if (wakeWordSRRef.current) return;
    // Another recognizer (scanner one-shot or chat conversation) owns the mic.
    if (micOwnerRef.current && micOwnerRef.current !== "wake") return;
    wakeWordPendingRef.current = false;
    wakeWordPendingSinceRef.current = 0;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;

    const sr = new SR();
    sr.continuous      = true;
    sr.interimResults  = true;   // accept an exact wake phrase before Chrome finalizes; never trigger on partial sentences
    sr.lang            = "en-US";
    sr.maxAlternatives = 5;      // alternate transcripts can recover a misheard Kara/Lala

    let triggered  = false;
    let startedAt  = 0;

    sr.onresult = e=>{
      for (let i=e.resultIndex; i<e.results.length; i++){
        // No confidence filter — Chrome returns low confidence (~0.2–0.4) for non-dictionary
        // words like "amber", so filtering would cause missed detections
        const raw = e.results[i][0].transcript;
        if (raw.trim().length < 2) continue;
        // Final results may contain a command; interim results must be only the
        // short wake phrase to avoid waking on partial unrelated speech.
        let wakeDetected = false;
        for (let a=0; a<e.results[i].length; a++){
          const alt = e.results[i][a].transcript.toLowerCase().replace(/[^a-z\s]/g,"").replace(/\s+/g," ").trim();
          if (getActiveWakeRegex().test(alt) &&
              (e.results[i].isFinal || /^(?:hey |hi )?(?:kara|cara|karr|ka ra|lala|la la|nala|lara|laiah)$/.test(alt))) {
            wakeDetected = true; break;
          }
        }
        const txt = raw.toLowerCase().replace(/[^a-z\s]/g,"").replace(/\s+/g," ").trim();
        if (e.results[i].isFinal) addLog(`🎙️ Heard: "${raw.trim()}"`, "scan");
        if (!wakeDetected) continue;
        const hasStop = (/\bstop\s*listening\b/i.test(txt))
          || (getWakeWordPrefixRegex().test(txt) && /\b(stop\s+listening|stop|shut\s*up|go\s+quiet|be\s+quiet)/i.test(txt))
          || /^(stop\s+listening|shut\s*up)$/i.test(txt);
        if (hasStop){ addLog(`🎙️ Wake+stop suppressed: "${raw.trim()}"`, "scan"); continue; }
        if (triggered) continue;
        triggered = true;
        addLog(`🎙️ Wake word: "${raw.trim()}"`, "scan");
        try { sr.abort(); } catch(_){}
        wakeWordSRRef.current = null;
        // The aborted recognizer may never emit onend on Android. Release its
         // arbiter ownership now, before the conversation listener is scheduled.
         if (micOwnerRef.current === "wake") micOwnerRef.current = null;
         if (chatActiveRef.current) break;
        // ── Activate continuous conversation ──────────────────────────────────
        chatActiveRef.current = true;
        setChatVoice("recording");
        /* KENYON 2026-09-25: no auto-open — user clicks the bubble */
        speak(`Yes, ${_karaUserName}?`, 1);
        addLog(`🎙️ ${aiName} activated — say "${_karaUserName === "Lando" ? "lala stop" : "kara stop"}" to stop.`, "scan");
        let lastConvoStartAt = 0;
        const wakeConvoSR = ()=>{
          if (!chatActiveRef.current) return;
          if (document.visibilityState !== "visible") return;
          if (isSpeakingRef.current) { setTimeout(wakeConvoSR, 300); return; }
          if (micOwnerRef.current && micOwnerRef.current !== "chat") return;
          // A conversation recognizer may already have been started by a TTS
          // completion callback. Do not replace a live owner.
          if (chatSRRef.current) return;
          const wsr = new SR();
          chatSRRef.current = wsr;
          micOwnerRef.current = "chat";
          wsr.continuous      = true;
          wsr.interimResults  = false;
          wsr.lang            = "en-US";
          wsr.maxAlternatives = 3;
          wsr.onresult = evt=>{
            for (let j=evt.resultIndex; j<evt.results.length; j++){
              if (!evt.results[j].isFinal) continue;
              const t = evt.results[j][0].transcript.trim();
              if (!t) continue;
              if (isEcho(t, speakingNow())) continue; // Jarvis acoustic echo filter (ECHO_TAIL_MS=1800ms)
              const tl = t.toLowerCase().replace(/[^a-z\s]/g,"").trim();
              // "kara/lala listen" — resume after wait
              if (getWakeWordPrefixRegex().test(tl) && /\b(listen|go|continue|ready|im\s+ready)/i.test(tl) && karaWaitingRef.current) {
                karaWaitingRef.current = false;
                speak("I'm listening.", 1);
                return;
              }
              // "kara/lala wait" — pause without ending session
              if (getWakeWordPrefixRegex().test(tl) && /\b(wait|pause|hold\s*on)/i.test(tl) && !karaWaitingRef.current) {
                karaWaitingRef.current = true;
                speak("Okay, standing by.", 1);
                try { chatSRRef.current?.abort(); } catch(_){}
                chatSRRef.current = null;
                return;
              }
              // If waiting and user speaks without "kara/lala listen", ignore
              if (karaWaitingRef.current) return;
              // "stop listening" is universal (Hermes 2026-10-11) — no wake prefix needed
              const isStopCmd = (/\bstop\s*listening\b/i.test(tl))
                || (getWakeWordPrefixRegex().test(tl) && /\b(stop\s+listening|stop|shut\s*up|go\s+quiet|be\s+quiet|wait|pause)/i.test(tl))
                || /^(stop\s+listening|shut\s*up)$/i.test(tl);
              if (isStopCmd){
                addLog(`🎤 Stop: "${t}"`, "scan");
                karaWaitingRef.current = false;
                speak("Going quiet.", 1);
                setTimeout(()=>{
                  chatActiveRef.current = false;
                  startChatSRRef.current = null;
                  try { chatSRRef.current?.abort(); } catch(_){}
                  chatSRRef.current = null;
                  setChatVoice("idle");
                  if (wakeWordRunRef.current) setTimeout(()=>startWakeWordRef.current?.(), 250);
                }, 120);
                return;
              }
              /* KENYON 2026-09-25: no auto-open — user clicks the bubble */
              handleCmdRef.current?.(t);
            }
          };
          wsr.onerror = e2=>{
            if (e2.error==="no-speech"||e2.error==="aborted") return;
            addLog(`🎤 Voice error: ${e2.error}`, "error");
            if (e2.error==="not-allowed"||e2.error==="service-not-allowed"){
              chatActiveRef.current = false;
              startChatSRRef.current = null;
              setChatVoice("idle");
              if (wakeWordRunRef.current) setTimeout(()=>startWakeWordRef.current?.(), 600);
            }
          };
          wsr.onend = ()=>{
            if (chatSRRef.current && chatSRRef.current !== wsr) return;
            if (chatSRRef.current === wsr) chatSRRef.current = null;
            if (micOwnerRef.current === "chat") micOwnerRef.current = null;
            if (!chatActiveRef.current || document.visibilityState !== "visible") return;
            const elapsed = Date.now() - lastConvoStartAt;
            const base = elapsed < 300 ? 300 - elapsed : 0;
            const delay = isSpeakingRef.current ? Math.max(base, 3000) : base;
            setTimeout(()=>{
              if (chatActiveRef.current && !isSpeakingRef.current && !chatSRRef.current)
                startChatSRRef.current?.();
            }, delay);
          };
          try { lastConvoStartAt = Date.now(); wsr.start(); } catch(e2){
            if (chatSRRef.current === wsr) chatSRRef.current = null;
            if (micOwnerRef.current === "chat") micOwnerRef.current = null;
            addLog(`🎤 ${e2.message}`, "error");
            // Transient mic-busy errors recover via the existing watchdog.
            if (/permission|denied|not.allowed/i.test(e2.message)) {
              chatActiveRef.current = false;
              setChatVoice("idle");
            }
          }
        };
        startChatSRRef.current = wakeConvoSR;
        break;
      }
    };

    sr.onerror = e=>{
      if (e.error==="no-speech"||e.error==="aborted") return;
      if (e.error==="not-allowed"||e.error==="service-not-allowed") {
        addLog("🎙️ Mic permission denied — click '🎤 ENABLE VOICE' to grant access","error");
        wakeWordRunRef.current = false;
        setWakeActive(false);
        setMicDenied(true);
        return;
      }
      addLog(`🎙️ Wake word error: ${e.error}`,"error");
    };

    sr.onend = ()=>{
      // An aborted older SR can end after a replacement started. Never let that
      // stale event erase the replacement or schedule another mic contender.
      if (wakeWordSRRef.current && wakeWordSRRef.current !== sr) return;
      if (wakeWordSRRef.current === sr) wakeWordSRRef.current = null;
      if (micOwnerRef.current === "wake") micOwnerRef.current = null;
      if (!triggered && wakeWordRunRef.current && !chatActiveRef.current && document.visibilityState === "visible"){
        const elapsed = Date.now() - startedAt;
        const delay = elapsed < 2000 ? 5000 : elapsed < 5000 ? 2000 : 800;
        wakeWordPendingRef.current = true; // tell watchdog a restart is already scheduled
        wakeWordPendingSinceRef.current = Date.now();
        if (wakeWordRestartTimerRef.current) clearTimeout(wakeWordRestartTimerRef.current);
        wakeWordRestartTimerRef.current = setTimeout(()=>{
          wakeWordRestartTimerRef.current = null;
          // Clear first: even if Android delayed this callback, the watchdog is
          // free to recover if starting fails or the page state changed.
          wakeWordPendingRef.current = false;
          wakeWordPendingSinceRef.current = 0;
          if (wakeWordRunRef.current && !chatActiveRef.current && !isSpeakingRef.current && document.visibilityState === "visible")
            startWakeWordRef.current?.();
        }, delay);
      }
    };

    try {
      startedAt = Date.now();
      sr.start();
      wakeWordSRRef.current = sr;
      micOwnerRef.current = "wake";
      setMicDenied(false);
      if (wakeWordFirstStartRef.current) {
        addLog("🎙️ Wake word listening — say \"kara\" anytime", "scan");
        wakeWordFirstStartRef.current = false;
      }
    } catch(e){
    addLog(`🎙️ Wake word failed: ${e.message}`,"error");
    wakeWordSRRef.current = null;
      if (micOwnerRef.current === "wake") micOwnerRef.current = null;
       }
  },[addLog, setChatVoice, setChatOpen]);

  const toggleWakeWord = useCallback(()=>{
    if (wakeWordRunRef.current){
      // Turn off
      wakeWordRunRef.current = false;
      wakeWordFirstStartRef.current = true; // reset so next enable logs again
      setWakeActive(false);
      if (wakeWordSRRef.current) { try { wakeWordSRRef.current.abort(); } catch(_){} wakeWordSRRef.current = null; }
      addLog("🎙️ Wake word listener disabled","scan");
    } else {
      // Turn on
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SR){ addLog("🎙️ Wake word: browser Speech API not supported","error"); return; }
      wakeWordRunRef.current = true;
      setWakeActive(true);
      addLog("🎙️ Wake word ON — say \"amber\" to activate","scan");
      startWakeWordSR();
    }
  },[addLog, startWakeWordSR]);

  // ── Mic permission helper: plain function (no useCallback) to avoid any TDZ ──
  // All dependencies accessed via refs — safe to define anywhere after refs are created
  const requestMicAndStart = async () => {
    try {
      addLog('🎤 Requesting mic access...', 'scan');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(t => t.stop());
      setMicDenied(false);
      addLog('🎤 Mic granted! Starting voice...', 'scan');
      setTimeout(() => {
        if (!wakeWordRunRef.current) {
          wakeWordRunRef.current = true;
          setWakeActive(true);
          startWakeWordRef.current?.();
        } else {
          toggleChatVRef.current?.();
        }
      }, 300);
    } catch (err) {
      setMicDenied(true);
      // Show exact error in the log so user knows what to fix
      const hint =
        err.name === 'NotAllowedError'   ? '→ Check Windows Settings › Privacy › Microphone — enable for Chrome' :
        err.name === 'NotFoundError'     ? '→ No microphone detected — plug one in' :
        err.name === 'NotReadableError'  ? '→ Mic is in use by another app — close it' :
        err.name === 'SecurityError'     ? '→ Page must be served over HTTPS or localhost' : '';
      addLog(`🚫 Mic blocked: ${err.name} ${hint}`, 'error');
    }
  };
  // ── Alias for `<VoiceHomeTab onRequestMic={...}>` (additive, Rule #1) ──
  //     Same function pointer as `requestMicAndStart`. Both widgets share it.
  const onRequestMic = requestMicAndStart;

  // Voice tab equity points: same settled-real-trades rule as HistoryPanel,
  // derived in this component's scope so the prop never silently stays empty.
  const voiceEquityPoints = useMemo(() => [...realTrades]
    .filter(h => h.executed === true && h.outcome && h.outcome !== 'pending')
    .sort((a, b) => {
      const closeMs = h => {
        const raw = h.sell_time || h.exit_time || h.timestamp || 0;
        return typeof raw === 'number' ? (raw < 1e12 ? raw * 1000 : raw) : new Date(raw).getTime() || 0;
      };
      return closeMs(a) - closeMs(b);
    })
    .reduce((points, h) => {
      points.push((points[points.length-1] || 0) + (typeof h.pnl === 'number' ? h.pnl : 0));
      return points;
    }, []), [realTrades]);

  // ── Derived booleans / timestamps for <VoiceHomeTab> (additive, Rule #1) ──
  //     Each is a useMemo derived from already-existing state. No new fetches,
  //     no new timers, no broken pre-existing UI. These existed in the parent
  //     component already in some form but were never wired into VoiceHomeTab.
  const serverOk   = useMemo(() => !!(serverHealth && (serverHealth.openrouter || serverHealth.openai || serverHealth.tts || serverHealth.n8n || serverHealth.telegram)), [serverHealth]);
  const scannerOk  = useMemo(() => !!(serverHealth && (serverHealth.deriv_mcp || serverHealth.mt5_mcp)), [serverHealth]);
  const mlOk       = useMemo(() => !!(serverHealth && (serverHealth.tradingview_screener_mcp || serverHealth.minimax)), [serverHealth]);
  const serverTime = useMemo(() => (serverHealth && serverHealth.serverTime) || Date.now(), [serverHealth]);
  const onVoice = useCallback((text) => sendToKaraRef.current?.(text, { fromVoiceTab: true }), []);  // speech uses chat action/sub-agent pipeline

  // ── 🗠 Chart Sub-Agent (Charts tab) ──────────────────────────────────────
  const runChartAgent = useCallback(async (task, opts = {}) => {
    if (!task?.trim() || chartAgentBusy) return;
    setChartAgentBusy(true);
    const userMsg = { role: "user", content: task };
    setChartAgentHistory(h => [...h, userMsg]);
    setChartAgentLog(l => [...l, { role:"user", text:task, time:formatJaTime(new Date()) }]);
    try {
      const context = {
        symbols:     [...selectedSymsRef.current],
        strategy:    activeStrategyRef.current,
        session:     sessionRef.current || {},
        focusSym,
        ticks:       Object.fromEntries(
          Object.entries(symData).map(([s,d])=>[s,d?.analysis?.price]).filter(([,v])=>v)
        ),
        alerts:      alertMessages?.slice(0,10)||[],
        predictions: allPreds?.slice(0,5)||[]
      };
      const r = await apiFetch("/api/chart-agent/run", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ task, context, history: chartAgentHistory.slice(-10) })
      });
      const d = await r.json();
      const assistantText = d.summary || d.error || "No response";
      setChartAgentHistory(h => [...h, d.assistant_message||{role:"assistant",content:assistantText}]);
      setChartAgentLog(l => [...l, {
        role:"agent", text:assistantText,
        observations:d.observations||"", ok:d.ok, time:formatJaTime(new Date())
      }]);
      if (opts.voice) speak(cleanForSpeech(assistantText).slice(0, 300));
      if (!d.ok) addLog(`❌ Chart agent: ${d.error}`,"error");
      else       addLog(`🗠 Chart agent: ${assistantText.slice(0,80)}`,"info");
    } catch(e) {
      addLog(`❌ Chart agent: ${e.message}`,"error");
      setChartAgentLog(l=>[...l,{role:"error",text:e.message,time:formatJaTime(new Date())}]);
    } finally { setChartAgentBusy(false); setChartAgentTask(""); }
  }, [chartAgentBusy, chartAgentHistory, selectedSymsRef, activeStrategyRef, sessionRef,
      focusSym, symData, alertMessages, allPreds, addLog, speak]);

  // ── Docker MCP ───────────────────────────────────────────────────────────
  const dockerLog_ = useCallback(msg=>setDockerLog(p=>[`[${formatJaTime(new Date())}] ${msg}`,...p.slice(0,99)]),[]);
  const fetchDockerTools = useCallback(async()=>{
    setDockerFetching(true); dockerLog_("Loading all MCP tools (Brave + Deriv)...");
    try {
      const r=await apiFetch("/api/mcp/tools");
      const d=await r.json(); if (!r.ok) throw new Error(d.error||`HTTP ${r.status}`);
      const tools=d.tools||[];
      setDockerTools(tools); setDockerOk(true);
      dockerLog_(`✅ ${tools.length} tool${tools.length!==1?"s":""} loaded: ${tools.slice(0,8).map(t=>t.name).join(", ")}${tools.length>8?"...":""}`);
    } catch(e){ setDockerOk(false); dockerLog_(`❌ ${e.message}`); }
    finally { setDockerFetching(false); }
  },[dockerLog_]);

  const callDockerTool = useCallback(async(name,args={})=>{
    dockerLog_(`▶ ${name}(${JSON.stringify(args).slice(0,120)})`);
    try {
      const r=await apiFetch("/api/mcp/call",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({name,args})});
      const d=await r.json(); if (!r.ok) throw new Error(d.error||`HTTP ${r.status}`);
      const result=JSON.stringify(d.result||d).slice(0,500);
      dockerLog_(`✅ ${name} → ${result}`);
      return d.result;
    } catch(e){ dockerLog_(`❌ ${name}: ${e.message}`); throw e; }
  },[dockerLog_]);
  useEffect(()=>{ callDockerToolRef.current=callDockerTool; },[callDockerTool]);

  // ── N8N MCP ───────────────────────────────────────────────────────────────
  const n8nLog_ = useCallback(msg=>setN8nLog(p=>[`[${formatJaTime(new Date())}] ${msg}`,...p.slice(0,99)]),[]);
  const fetchN8NTools = useCallback(async()=>{
    setN8nFetching(true); n8nLog_("Connecting to N8N MCP...");
    try {
      const r=await apiFetch("/api/n8n/mcp",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({jsonrpc:"2.0",method:"tools/list",id:Date.now()})});
      const d=await r.json(); if (!r.ok) throw new Error(d.error||`HTTP ${r.status}`);
      const tools=d.result?.tools||[];
      setN8nTools(tools); setN8nOk(true);
      n8nLog_(`✅ Connected — ${tools.length} tool${tools.length!==1?"s":""}: ${tools.slice(0,8).map(t=>t.name).join(", ")}${tools.length>8?"...":""}`);
    } catch(e){ setN8nOk(false); n8nLog_(`❌ ${e.message}`); }
    finally { setN8nFetching(false); }
  },[n8nLog_]);
  const execN8NTool = useCallback(async(name,args={})=>{
    n8nLog_(`▶ ${name}(${JSON.stringify(args).slice(0,120)})`);
    try {
      const r=await apiFetch("/api/n8n/mcp",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({jsonrpc:"2.0",method:"tools/call",params:{name,arguments:args},id:Date.now()})});
      const d=await r.json(); if (!r.ok) throw new Error(d.error||`HTTP ${r.status}`);
      const result=JSON.stringify(d.result||d).slice(0,500);
      n8nLog_(`✅ ${name} → ${result}`);
      return d.result;
    } catch(e){ n8nLog_(`❌ ${name}: ${e.message}`); throw e; }
  },[n8nLog_]);

  // ── Voice ─────────────────────────────────────────────────────────────────
  const handleCmd = useCallback(transcript=>{
    addLog(`🎤 "${transcript}"`,"scan");
    // Strip assistant address prefixes ("scanner", "kara", "lala", "hey kara", etc.)
    const cleanText = transcript.replace(/^(?:(?:hey|ok|okay|hi|hello)\s+)?(?:scanner|kara|lala|cara)\b[,\s:]*/i, "").trim();
    const t = (cleanText || transcript).toLowerCase().trim();

    // If user literally called out the assistant's name or said "scanner" with nothing else, acknowledge attentively
    if (!cleanText && /^(?:(?:hey|ok|okay|hi|hello)\s+)?(?:scanner|kara|lala|cara)[.?!]*$/i.test(transcript.trim())) {
      const ack = _karaUserName === "Lando" ? "I'm listening, Lando. What do you need?" : "I'm listening, Kenyon. What do you need?";
      speak(ack, 1);
      return;
    }

    let pendingVoiceAction = pendingChatActionRef.current;
    if (pendingVoiceAction && getVoiceGatePending()?.act !== pendingVoiceAction) {
      pendingChatActionRef.current = null;
      rejectVoiceAction(pendingVoiceAction);
      const expiredConfirmation = /^(confirm|confirm action|yes confirm|do it|proceed|cancel|cancel action|no|stop)$/i.test(t);
      pendingVoiceAction = null;
      if (expiredConfirmation) {
        const message = "That confirmation expired. Please repeat the request if you still want it.";
        setChatMessages(p=>[...p,{role:"assistant",content:message,time:formatJaTime(new Date())}].slice(-200));
        speak(message, 1);
        return;
      }
    }
    if (pendingVoiceAction) {
      const isConfirmation = /^(confirm|confirm action|yes confirm|do it|proceed)$/i.test(t);
      const isCancellation = /^(cancel|cancel action|no|stop)$/i.test(t);
      if ((isConfirmation || isCancellation) && getVoiceGatePending()?.act !== pendingVoiceAction) {
        pendingChatActionRef.current = null;
        rejectVoiceAction(pendingVoiceAction);
        setChatMessages(p=>[...p,{role:"assistant",content:"That confirmation expired. Please repeat the request if you still want it.",time:formatJaTime(new Date())}].slice(-200));
        speak("That confirmation expired. Please repeat the request if you still want it.", 1);
        return;
      }
      if (isConfirmation) {
        pendingChatActionRef.current = null;
        const approval = approveVoiceAction(pendingVoiceAction);
        if (!approval.ok) {
          rejectVoiceAction(pendingVoiceAction);
          setChatMessages(p=>[...p,{role:"assistant",content:"That confirmation expired. Please repeat the request if you still want it.",time:formatJaTime(new Date())}].slice(-200));
          speak("That confirmation expired. Please repeat the request if you still want it.", 1);
          return;
        }
        sendToKara("", { directAction: { ...pendingVoiceAction, __confirmed: true }, asContext: true, fromVoice: true });
      } else if (isCancellation) {
        pendingChatActionRef.current = null;
        rejectVoiceAction(pendingVoiceAction);
        setChatMessages(p=>[...p,{role:"assistant",content:"Cancelled. No action was executed.",time:formatJaTime(new Date())}].slice(-200));
        speak("Cancelled. No action was executed.", 1);
      } else {
        speak("A confirmation is waiting. Say confirm to proceed or cancel to stop.", 1);
      }
      return;
    }
    if (/\b(add|select|watch)\b/.test(t)) {
      for (const s of SYMBOLS) { if (t.includes(s.toLowerCase().replace("_"," "))||t.includes(s.toLowerCase())) { if (!selectedSymsRef.current.has(s)) toggleSymbol(s); speak(`Added ${s}`); return; } }
    }
    if (/\b(remove|stop|cancel)\b/.test(t)) {
      for (const s of SYMBOLS) { if (t.includes(s.toLowerCase().replace("_"," "))||t.includes(s.toLowerCase())) { if (selectedSymsRef.current.has(s)) toggleSymbol(s); speak(`Removed ${s}`); return; } }
    }
    const fastChartAction = parseVoiceFastChartIntent(cleanText || transcript, SYMBOLS);
    if (fastChartAction) {
      sendToKara("", { directAction: fastChartAction, asContext: true, fromVoice: true });
      return;
    }
    const navMatch = t.match(/^(?:please )?(?:open|show|go to|view|switch to|take me to|navigate to) (?:the )?(scanner|strategy|strategy manager|ml signals|docker|backtests?|history|settings|charts?|info)(?: tab| panel)?$/i);
    if (navMatch) {
      const key = navMatch[1].toLowerCase();
      const tabByVoiceName = { scanner: "scanner", strategy: "strategy", "strategy manager": "strategy", "ml signals": "ml", docker: "docker", backtest: "advanced_backtest", backtests: "advanced_backtest", history: "history", settings: "settings", chart: "charts", charts: "charts", info: "info" };
      setTab(tabByVoiceName[key]);
      speak(`Opening ${key} tab, ${_karaUserName}.`, 1);
      return;
    }
    const autoTradeRequest = t.match(/^(?:turn|switch|set) (?:auto[- ]?trade|auto trading) (on|off)$|^(?:turn|switch) (on|off) (?:auto[- ]?trade|auto trading)$|^(enable|disable) auto[- ]?trade$/i);
    if (autoTradeRequest) {
      const requested = /\b(off|disable)\b/i.test(t) ? false : true;
      sendToKara("", { directAction: { type: "toggle_auto_trade", value: requested }, asContext: true, fromVoice: true });
      return;
    }
    const directStrategySwitch = t.match(/^switch (?:to|the) (.+) strategy$/i);
    if (directStrategySwitch) {
      const rawName = directStrategySwitch[1].trim().replace(/\.md$/i, "").replace(/\s+/g, "_");
      const name = `${rawName}.md`;
      sendToKara("", { directAction: { type: "activate_strategy", name }, asContext: true, fromVoice: true });
      return;
    }
    if (/all symbols/.test(t)) { SYMBOLS.forEach(s=>{ if (!selectedSymsRef.current.has(s)) toggleSymbol(s); }); speak("All symbols added"); return; }
    if (/clear|none|remove all/.test(t)) { sendToKara("", { directAction: { type: "clear_symbols" }, asContext: true, fromVoice: true }); return; }
    if (/status|conditions/.test(t)) { const sym=focusSym; if (sym&&symDataRef.current[sym]) { const d=symDataRef.current[sym]; const reqCnt = getStrategyRequiredCount(strategySchemaRef.current); speak(`${sym}: ${d.cnt} of ${reqCnt} conditions. ${d.analysis?.direction}. RSI ${d.analysis?.rsi?.toFixed(0)}.`); } else speak("No symbol focused"); return; }
    if (/price|quote/.test(t)) { const sym=focusSym; if (sym&&symDataRef.current[sym]) { speak(`${sym} at ${symDataRef.current[sym].analysis?.price?.toFixed(5)}`); } return; }
    if (/session|time|clock/.test(t)) { const si=sessionRef.current; const ja=`${formatJaTime(new Date())} Jamaica time`; if(si.active) speak(`${ja}. ${si.overlap?"Overlap — ":""}${si.sessions?.map(s=>s.name).join(" and ")||si.name} ${si.sessions?.length>1?"sessions":"session"} active.`); else speak(`${ja}. ${si.weekend?"Weekend.":"Pre-market."} ${si.next} opens in ${Math.floor(si.minsTo/60)} hours ${si.minsTo%60} minutes.`); return; }
    const directBacktest = t.match(/^(?:please )?(?:run|start) (?:an? )?(?:advanced )?backtest(?: on ([a-z0-9_ ]+))?$/i);
    if (directBacktest) {
      const requestedSymbol = (directBacktest[1] || focusSym || "R_100").trim().toUpperCase().replace(/\s+/g, "_");
      sendToKara("", { directAction: { type: "run_backtest", symbol: requestedSymbol }, asContext: true, fromVoice: true });
      return;
    }
    if (/^(?:open|show|go to|view)?\s*(?:the\s+)?(?:n.?8.?n|workflow|backtest)\s*(?:panel|tab)?$/i.test(t)) { setTab("advanced_backtest"); speak("Backtests panel"); return; }
    if (/^(?:open|show|go to|view)?\s*(?:the\s+)?(?:history|trade log|log)\s*(?:panel|tab)?$/i.test(t)) { setTab("history"); speak("History"); return; }
    // ── Chart dashboard voice commands ────────────────────────────────────
    if (/open chart|show chart|go to chart|launch chart|chart dash/i.test(t)) {
      setTab("charts"); speak(`Opening charts, ${_karaUserName}. `); return;
    }
    if (/chart agent|control chart|chart bot/i.test(t)) {
      setTab("charts"); setChartAgentOpen(true); speak("Chart agent panel is open. What should I do?"); return;
    }
    // T608: strategy voice commands
    if (/strategy lab|improve strat|tradingview optimizer|asia agent/i.test(t)) { setTab(normalizeTab("stratlab")); speak("TradingView AI Optimizer."); return; }
    if (/show strat|strategy tab/i.test(t)) { setTab("strategy"); speak("Strategy manager."); return; }
    if (/what strategy/i.test(t)) { speak(`We're running ${activeStrategy.replace(/\.(md|txt)$/i,"")}, ${_karaUserName}.`); return; }
    const strategySwitchMatch = t.match(/switch (?:to|the) (.+?) strategy/i);
    if (strategySwitchMatch) {
      const rawName = strategySwitchMatch[1].trim().replace(/\.md$/i, "").replace(/\s+/g, "_");
      const name = `${rawName}.md`;
      if (name.length > 3) sendToKara("", { directAction: { type: "activate_strategy", name }, asContext: true, fromVoice: true });
      return;
    }
    // T503: personality commands in handleCmd
    if (/formal mode|be professional|be serious/i.test(t)) { setPersonalityMode("formal"); speak("Formal mode activated."); return; }
    if (/\b(relax|chill|casual|normal mode)\b/i.test(t)) { setPersonalityMode("casual"); speak(`Back to normal, ${_karaUserName}.`); return; }
    // T1002: win rate voice command
    if (/win rate|how.*doing|stats|performance/i.test(t)) {
      const wins2=allPredsRef.current.filter(p=>p.outcome==="WIN").length;
      const losses2=allPredsRef.current.filter(p=>p.outcome==="LOSS").length;
      const pending2=allPredsRef.current.filter(p=>p.outcome==="pending").length;
      const wr=wins2+losses2>0?Math.round(wins2/(wins2+losses2)*100):null;
      speak(wr!=null?`Win rate is ${wr} percent, ${_karaUserName}. ${wins2} wins, ${losses2} losses, ${pending2} pending.`:`No resolved trades yet, ${_karaUserName}. ${pending2} signal${pending2!==1?"s":""} pending outcome.`);
      return;
    }
    // ── Fallback: send to Kara with cleaned text ──────────────────────────────
    /* KENYON 2026-09-25: no auto-open — user clicks the bubble */
    sendToKara(cleanText || transcript, { fromVoice: true });
  },[addLog,speak,toggleSymbol,unsubscribeSymbol,focusSym,sendToKara,activeStrategy,handleStrategyActivate,callToolsmith]);

  const toggleVoice = useCallback(async()=>{
    // ── Stop any active recording ──────────────────────────────────────────
    if (voiceState==="recording") {
      srRef.current?.stop();
      recRef.current?.stop();
      return;
    }

    // Exactly one recognizer may own the Android microphone. Temporarily pause
    // chat/wake while this one-shot scanner command listens, then hand ownership
    // back to whichever passive listener was active before the button press.
    interruptTtsForMic();
    const resumeChatAfterVoice = chatActiveRef.current;
    const resumeWakeAfterVoice = !resumeChatAfterVoice && wakeWordRunRef.current;
    if (resumeChatAfterVoice) {
      chatActiveRef.current = false;
      setChatVoice("idle");
      const activeChatSr = chatSRRef.current;
      chatSRRef.current = null;
      try { activeChatSr?.abort(); } catch(_){}
    }
    if (resumeWakeAfterVoice) {
      wakeWordRunRef.current = false;
      if (wakeWordRestartTimerRef.current) clearTimeout(wakeWordRestartTimerRef.current);
      wakeWordRestartTimerRef.current = null;
      wakeWordPendingRef.current = false;
      wakeWordPendingSinceRef.current = 0;
      const activeWakeSr = wakeWordSRRef.current;
      wakeWordSRRef.current = null;
      try { activeWakeSr?.abort(); } catch(_){}
    }
    const restorePassiveVoiceOwner = ()=>{
      if (scannerVoiceRestoreRef.current !== restorePassiveVoiceOwner) return;
      scannerVoiceRestoreRef.current = null;
      if (voiceOwnerResumeTimerRef.current) clearTimeout(voiceOwnerResumeTimerRef.current);
      voiceOwnerResumeTimerRef.current = setTimeout(()=>{
        voiceOwnerResumeTimerRef.current = null;
        if (resumeChatAfterVoice) {
          chatActiveRef.current = true;
          setChatVoice("listening");
          startChatSRRef.current?.();
        } else if (resumeWakeAfterVoice) {
          wakeWordRunRef.current = true;
          setWakeActive(true);
          startWakeWordRef.current?.();
        }
      }, 350);
    };
    scannerVoiceRestoreRef.current = restorePassiveVoiceOwner;

    // ── Fallback (hoisted): MediaRecorder → server Whisper ────────────────
    // Named so the SpeechRecognition error handler can fall through to it
    // (rule F7) instead of leaving the mic button apparently dead.
    let mediaFallbackUsed = false;
    const startMediaFallback = async ()=>{
      if (mediaFallbackUsed) return;
      mediaFallbackUsed = true;
      try {
        const stream=await navigator.mediaDevices.getUserMedia({audio:true});
        const rec=new MediaRecorder(stream,{mimeType:"audio/webm"});
        chunksRef.current=[];
        rec.ondataavailable=e=>{ if (e.data.size>0) chunksRef.current.push(e.data); };
        rec.onstop=async()=>{
          stream.getTracks().forEach(t=>t.stop()); setVoiceState("processing");
          const blob=new Blob(chunksRef.current,{type:"audio/webm"});
          try {
            const r=await apiFetch("/api/stt",{method:"POST",headers:{"Content-Type":"audio/webm"},body:blob});
            const d=await r.json();
            if (d.transcript) handleCmd(d.transcript);
            else addLog(`🎤 ${d.error||"No transcript — add OPENAI_API_KEY or GROQ_API_KEY to .env"}`,"warn");
          } catch(e){ addLog(`🎤 STT: ${e.message}`,"error"); }
          finally {
            setVoiceState("idle");
            recRef.current = null;
            if (micOwnerRef.current === "scan") micOwnerRef.current = null;
            restorePassiveVoiceOwner();
          }
        };
        rec.start(); recRef.current=rec; setVoiceState("recording");
        micOwnerRef.current = "scan";
        addLog("🎤 Listening (Whisper fallback)...","scan");
      } catch(e){
        addLog(`🎤 Mic: ${e.message}`,"error");
        setVoiceState("idle");
        if (micOwnerRef.current === "scan") micOwnerRef.current = null;
        restorePassiveVoiceOwner();
      }
    };

    // ── Primary: Web Speech API (Chrome/Edge, free, no API key needed) ─────
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      const sr = new SpeechRecognition();
      sr.continuous    = false;
      sr.interimResults= false;
      sr.lang          = "en-US";
      sr.onresult = e => {
        const transcript = e.results[0][0].transcript;
        setVoiceState("idle");
        handleCmd(transcript);
      };
      sr.onerror = e => {
        if (e.error!=="no-speech"&&e.error!=="aborted")
          addLog(`🎤 Speech error: ${e.error}`,"error");
        srRef.current = null;
        if (micOwnerRef.current === "scan") micOwnerRef.current = null;
        // F7: Chrome answers 'service-not-allowed' when the speech service is
        // temporarily unavailable (not a real permission denial). Don't kill the
        // button — drop straight into the MediaRecorder/Whisper path instead.
        if (e.error==="service-not-allowed" && !mediaFallbackUsed) {
          mediaFallbackUsed = true;
          addLog("🎤 Web Speech unavailable — switching to Whisper fallback","warn");
          startMediaFallback();
          return;
        }
        setVoiceState("idle");
        restorePassiveVoiceOwner();
      };
      sr.onend = () => {
        srRef.current = null;
        if (micOwnerRef.current === "scan") micOwnerRef.current = null;
        setVoiceState("idle");
        restorePassiveVoiceOwner();
      };
      srRef.current = sr;
      try {
        micOwnerRef.current = "scan";
        sr.start();
        setVoiceState("recording");
        addLog("🎤 Listening...","scan");
        return;
      } catch(e){
        // Android Web Speech can throw synchronously when another audio owner
        // has just released the mic. Release the stale ref and continue into
        // MediaRecorder/Whisper rather than leaving the button apparently dead.
        srRef.current = null;
        addLog(`🎤 Browser speech unavailable (${e.message}) — using audio fallback`,"warn");
      }
    }

    // ── Fallback: MediaRecorder → server Whisper ───────────────────────────
    await startMediaFallback();
  },[voiceState,handleCmd,addLog]);

  // ── Sync latest function versions into refs (avoids stale-closure issues) ─
  useEffect(()=>{ startWakeWordRef.current  = startWakeWordSR;  },[startWakeWordSR]);
  useEffect(()=>{ toggleChatVRef.current    = toggleChatVoice;  },[toggleChatVoice]);
  useEffect(()=>{ toggleWakeWordRef.current = toggleWakeWord;   },[toggleWakeWord]);
  useEffect(()=>{ sendToKaraRef.current     = sendToKara;       },[sendToKara]);
  useEffect(()=>{ handleCmdRef.current     = handleCmd;        },[handleCmd]);

  // ── On mount: silently check if mic already granted (no popup, no gesture needed) ──
  useEffect(()=>{
    let permStatus = null;
    let cancelled = false;
    const check = async () => {
      try {
        const perm = await navigator.permissions.query({ name: 'microphone' });
        if (cancelled) return;
        permStatus = perm;
        if (perm.state === 'denied') {
          setMicDenied(true);
        } else if (perm.state === 'granted') {
          // Already allowed — safe to start SR without a user gesture
          setMicDenied(false);
          if (micAutoStartTimerRef.current) clearTimeout(micAutoStartTimerRef.current);
          micAutoStartTimerRef.current = setTimeout(()=>{
            micAutoStartTimerRef.current = null;
            if (!wakeWordRunRef.current) {
              wakeWordRunRef.current = true;
              setWakeActive(true);
              startWakeWordRef.current?.();
            }
          }, 800);
        } else {
          // 'prompt' state — must wait for a real user click before calling getUserMedia
          setMicDenied(true); // show the enable button so user knows to click
        }
        perm.onchange = () => {
          if (perm.state === 'denied') setMicDenied(true);
          if (perm.state === 'granted') setMicDenied(false);
        };
      } catch(_) {
        // permissions API not available — show enable button
        if (!cancelled) setMicDenied(true);
      }
    };
    check();
    return ()=>{
      cancelled = true;
      if (permStatus) { try { permStatus.onchange = null; } catch(_){} }
      if (micAutoStartTimerRef.current) clearTimeout(micAutoStartTimerRef.current);
      micAutoStartTimerRef.current = null;
      if (chatVisibilityTimerRef.current) clearTimeout(chatVisibilityTimerRef.current);
      chatVisibilityTimerRef.current = null;
      // Release every microphone owner so an unmount can never leave a live SR
      // or an open MediaRecorder stream behind (that was the 'beep' source).
      chatActiveRef.current = false;
      try { chatSRRef.current?.abort(); } catch(_){}
      chatSRRef.current = null;
      try { srRef.current?.stop(); } catch(_){}
      srRef.current = null;
      try { recRef.current?.stop(); } catch(_){}
      recRef.current = null;
      if (micOwnerRef.current) micOwnerRef.current = null;
      wakeWordRunRef.current = false;
      if (wakeWordRestartTimerRef.current) clearTimeout(wakeWordRestartTimerRef.current);
      if (wakeWordVisibilityTimerRef.current) clearTimeout(wakeWordVisibilityTimerRef.current);
      if (voiceOwnerResumeTimerRef.current) clearTimeout(voiceOwnerResumeTimerRef.current);
      wakeWordRestartTimerRef.current = null;
      wakeWordVisibilityTimerRef.current = null;
      voiceOwnerResumeTimerRef.current = null;
      scannerVoiceRestoreRef.current = null;
      wakeWordPendingRef.current = false;
      wakeWordPendingSinceRef.current = 0;
      try { wakeWordSRRef.current?.abort(); } catch(_){}
    };
  },[]);

  // ── Effects ───────────────────────────────────────────────────────────────
  // SR watchdog: if voice mode is active but SR silently died, revive it every 2.5 s
  useEffect(()=>{
    const id = setInterval(()=>{
      // If isSpeakingRef stuck > 60s with no audio, force-reset it
      if (isSpeakingRef.current && speakingStartRef.current > 0) {
        if (Date.now() - speakingStartRef.current > 60_000) {
          isSpeakingRef.current = false;
          speakingStartRef.current = 0;
          addLog("⚠️ TTS watchdog: isSpeaking stuck — force reset", "warn");
          processQueueRef.current?.();
        }
      }
      if (document.visibilityState !== "visible") return;
      if (chatActiveRef.current && !isSpeakingRef.current && !chatSRRef.current) {
        addLog("🎤 SR watchdog: reviving listener...","warn");
        startChatSRRef.current?.();
      }
      // Android may suspend a scheduled timeout while Chrome is backgrounded.
      // A pending marker older than 12s is stale; release it so the watchdog can
      // reacquire the microphone instead of leaving wake word dead forever.
      if (wakeWordPendingRef.current && wakeWordPendingSinceRef.current > 0 && Date.now() - wakeWordPendingSinceRef.current > 12000) {
        if (wakeWordRestartTimerRef.current) clearTimeout(wakeWordRestartTimerRef.current);
        wakeWordRestartTimerRef.current = null;
        wakeWordPendingRef.current = false;
        wakeWordPendingSinceRef.current = 0;
      }
      // Wake word watchdog: if wake word enabled but SR is not running AND no restart is already pending
      if (wakeWordRunRef.current && !chatActiveRef.current && !wakeWordSRRef.current && !isSpeakingRef.current && !wakeWordPendingRef.current) {
        startWakeWordRef.current?.(); // silent restart
      }
    }, 1500);
    return ()=>clearInterval(id);
  },[addLog]);

  // Chrome/Android can revoke or stall speech recognition while the page is
  // backgrounded. Release the mic while hidden and reacquire exactly once when
  // visible, instead of letting watchdog/onend timers fight and beep.
  useEffect(()=>{
    const onVisibility = ()=>{
      if (document.visibilityState !== "visible") {
        if (wakeWordRestartTimerRef.current) clearTimeout(wakeWordRestartTimerRef.current);
        if (wakeWordVisibilityTimerRef.current) clearTimeout(wakeWordVisibilityTimerRef.current);
        wakeWordRestartTimerRef.current = null;
        wakeWordVisibilityTimerRef.current = null;
        wakeWordPendingRef.current = false;
        wakeWordPendingSinceRef.current = 0;
        if (wakeWordSRRef.current) {
          const sr = wakeWordSRRef.current;
          wakeWordSRRef.current = null;
          if (micOwnerRef.current === "wake") micOwnerRef.current = null;
          try { sr.abort(); } catch(_){}
        }
        // Chat conversation recognizer must release the mic too — leaving it
        // running while hidden is what made Android stack two recognizers.
        if (chatSRRef.current) {
          const csr = chatSRRef.current;
          chatSRRef.current = null;
          if (micOwnerRef.current === "chat") micOwnerRef.current = null;
          try { csr.abort(); } catch(_){}
        }
        if (chatVisibilityTimerRef.current) { clearTimeout(chatVisibilityTimerRef.current); chatVisibilityTimerRef.current = null; }
        return;
      }
      if (wakeWordRunRef.current && !chatActiveRef.current && !isSpeakingRef.current && !wakeWordSRRef.current) {
        if (wakeWordVisibilityTimerRef.current) clearTimeout(wakeWordVisibilityTimerRef.current);
        wakeWordVisibilityTimerRef.current = setTimeout(()=>{
          wakeWordVisibilityTimerRef.current = null;
          startWakeWordRef.current?.();
        }, 350);
      }
      // Foreground again: reacquire the chat conversation mic exactly once —
      // but never while the one-shot scanner recognizer holds the arbiter.
      if (chatActiveRef.current && !isSpeakingRef.current && !chatSRRef.current && micOwnerRef.current !== "scan") {
        if (chatVisibilityTimerRef.current) clearTimeout(chatVisibilityTimerRef.current);
        chatVisibilityTimerRef.current = setTimeout(()=>{
          chatVisibilityTimerRef.current = null;
          if (chatActiveRef.current && !isSpeakingRef.current && !chatSRRef.current) startChatSRRef.current?.();
        }, 350);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return ()=>{
      document.removeEventListener("visibilitychange", onVisibility);
      if (wakeWordVisibilityTimerRef.current) clearTimeout(wakeWordVisibilityTimerRef.current);
      wakeWordVisibilityTimerRef.current = null;
      if (chatVisibilityTimerRef.current) clearTimeout(chatVisibilityTimerRef.current);
      chatVisibilityTimerRef.current = null;
    };
  },[]);

  const symsHydratedRef = useRef(false); // set once the server's selectedSymbols are applied
  // Sync user settings from server on mount + populate selectedSymbols
  useEffect(()=>{
    apiFetch("/api/user/settings").then(r=>r.ok?r.json():null).then(srv=>{
      if (!srv) return;
      setUserSettings(srv);
      localStorage.setItem(sk("kara_user_settings"), JSON.stringify(srv));
      if (srv.selectedSymbols?.length > 0) setSelectedSyms(new Set(srv.selectedSymbols));
      symsHydratedRef.current = true; // 2026-10-11: only now may selectedSymbols be auto-saved
      if (srv.alertsEnabled !== undefined) {
        setAlertsEnabled(!!srv.alertsEnabled);
        localStorage.setItem(sk("amber_alerts"), String(!!srv.alertsEnabled));
      }
      if (srv.alertVoice !== undefined) {
        setAlertVoice(!!srv.alertVoice);
        localStorage.setItem(sk("amber_alert_voice"), String(!!srv.alertVoice));
      }
      if (srv.autoTrade !== undefined) {
        setAutoTrade(!!srv.autoTrade);
        localStorage.setItem(sk("amber_auto_trade"), String(!!srv.autoTrade));
      }
      if (srv.deadZonesEnabled !== undefined) {
        setDeadZonesEnabled(!!srv.deadZonesEnabled);
        localStorage.setItem(sk("amber_dead_zones"), String(!!srv.deadZonesEnabled));
      }
      if (srv.mlAutoTrade !== undefined) {
        setMlAutoTrade(!!srv.mlAutoTrade);
        localStorage.setItem(sk("amber_ml_auto_trade"), String(!!srv.mlAutoTrade));
      }
      if (srv.mlAutoConfidence !== undefined) {
        const pct = Math.min(99, Math.max(55, Math.round((srv.mlAutoConfidence || 0) * 100)));
        setMlAutoConfidence(pct);
        localStorage.setItem(sk("amber_ml_auto_confidence"), String(pct));
      }
      if (srv.stake !== undefined) {
        const stakeValue = Number(srv.stake) || 1;
        setTradeStake(stakeValue);
        localStorage.setItem(sk("amber_trade_stake"), String(stakeValue));
      }
      if (srv.personalityMode !== undefined) {
        setPersonalityMode(srv.personalityMode || "casual");
        localStorage.setItem(sk("amber_personality_mode"), srv.personalityMode || "casual");
      }
      if (srv.tradingTf !== undefined) {
        setTradingTf(srv.tradingTf || null);
        if (srv.tradingTf) localStorage.setItem(sk("amber_trading_tf"), srv.tradingTf);
        else localStorage.removeItem(sk("amber_trading_tf"));
      }
    }).catch(()=>{});
  },[]);

  // Auto-save selectedSymbols to server whenever they change (triggers server scanner reload)
  // 2026-10-11 claude: never save before the server's list has loaded. selectedSyms starts as an
  // empty Set, so on a slow (mobile) load the 800ms save posted [] and wiped the user's trading
  // symbols (seen 19:39 UTC: ken72 ["JD50","JD25"] -> [] on page load). Re-applied 2026-10-12
  // after a parallel worker's overwrite dropped it.
  const _selectedSymsSaveTimer = useRef(null);
  useEffect(()=>{
    if (!selectedSyms || !symsHydratedRef.current) return;
    clearTimeout(_selectedSymsSaveTimer.current);
    _selectedSymsSaveTimer.current = setTimeout(()=>{
      apiFetch("/api/user/settings", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ selectedSymbols: [...selectedSyms] })
      }).catch(()=>{});
    }, 800);
  },[selectedSyms]);

  // T401: keep allPredsRef in sync for handleTick (which has empty deps)
  useEffect(()=>{ allPredsRef.current=allPreds; },[allPreds]);
  useEffect(()=>{ sendTelegramRef.current=sendTelegram; },[sendTelegram]);
  useEffect(()=>{ addTradeHistoryRef.current=addTradeHistory; },[addTradeHistory]);
  useEffect(()=>{ speakRef.current=speak; },[speak]);
  // T502: persist personality mode
  useEffect(()=>{ try{localStorage.setItem(sk("amber_personality_mode"),personalityMode);}catch{} if(!settingsHydratedRef.current) return; apiFetch("/api/user/settings",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({personalityMode}),}).catch(()=>{}); },[personalityMode]);
  // T603: fetch strategies on mount
  useEffect(()=>{
    apiFetch("/api/strategies").then(r=>r.ok?r.json():null).then(d=>{ if(d) setStrategies(d); }).catch(()=>{});
    apiFetch("/api/strategies/active").then(r=>r.ok?r.json():null).then(d=>{ if(d?.name) setActiveStrategy(d.name); }).catch(()=>{});
    fetchStrategySchema();
  },[fetchStrategySchema]);
  // Trade history fetch helpers — used by both the polling effect and the sync button
  const fetchHistory = useCallback(()=>{
    apiFetch("/api/trades/history",{silent:true}).then(r=>r.ok?r.json():[]).then(d=>{ if(Array.isArray(d)) setHistory(d); }).catch(()=>{});
  },[]);
  const fetchPredictions = useCallback(()=>{
    apiFetch("/api/trades/predictions",{silent:true}).then(r=>r.ok?r.json():[]).then(d=>{
      if (!Array.isArray(d)) return;
      const today=new Date().toDateString();
      const cutoff=Date.now()-60*24*60*60*1000;
      const filtered=d.filter(p=>{ try{ return new Date(p.date).getTime()>cutoff; }catch{ return false; } });
      setAllPreds(filtered);
      setTodayPreds(filtered.filter(p=>p.date===today));
    }).catch(()=>{});
  },[]);
  // Load trade history on mount and poll every 15s for real-time updates
  useEffect(()=>{
    fetchHistory(); fetchPredictions();
    const id=setInterval(()=>{ fetchHistory(); fetchPredictions(); },15_000);
    return ()=>clearInterval(id);
  },[fetchHistory,fetchPredictions]);

  // Mission status + symbol WR — poll every 60s (data changes slowly)
  const fetchMissionStatus = useCallback(()=>{
    apiFetch("/api/mission/status",{silent:true}).then(r=>r.ok?r.json():null).then(d=>{ if(d) setMissionStatus(d); }).catch(()=>{});
  },[]);
  const fetchSymbolWr = useCallback((strat)=>{
    const s = strat || activeStrategy;
    if (!s) return;
    apiFetch(`/api/analytics/symbol-wr?strategy=${encodeURIComponent(s)}&min_trades=10`,{silent:true})
      .then(r=>r.ok?r.json():null)
      .then(d=>{ if(d?.symbol_wr) { const m={}; d.symbol_wr.forEach(r=>{m[r.symbol]=r;}); setSymbolWr(m); } })
      .catch(()=>{});
  },[activeStrategy]);
  useEffect(()=>{
    fetchMissionStatus(); fetchSymbolWr();
    const id=setInterval(()=>{ fetchMissionStatus(); fetchSymbolWr(); },60_000);
    return ()=>clearInterval(id);
  },[fetchMissionStatus,fetchSymbolWr]);
  // Poll active trades every 5s; check settled contracts every 8s (short contracts settle in ~5s)
  useEffect(()=>{
    pollActiveTrades();
    const id=setInterval(pollActiveTrades,5_000);
    const id2=setInterval(checkSettledContracts,8_000);
    return ()=>{ clearInterval(id); clearInterval(id2); };
  },[pollActiveTrades,checkSettledContracts]);
  useEffect(()=>{ selectedSymsRef.current=selectedSyms; },[selectedSyms]);
  useEffect(()=>{ symDataRef.current=symData; },[symData]);
  useEffect(()=>{ focusSymRef.current=focusSym; },[focusSym]);
  useEffect(()=>{
    if (!userSettings) return;
    const current = {
      alertsEnabled: userSettings.alertsEnabled,
      alertVoice: userSettings.alertVoice,
      autoTrade: userSettings.autoTrade,
      mlAutoTrade: userSettings.mlAutoTrade,
      mlAutoConfidence: userSettings.mlAutoConfidence,
      stake: userSettings.stake,
      tradingTf: userSettings.tradingTf,
      personalityMode: userSettings.personalityMode,
    };
    const desired = {
      alertsEnabled,
      alertVoice,
      autoTrade,
      mlAutoTrade,
      mlAutoConfidence: mlAutoConfidence / 100,
      stake: tradeStake,
      tradingTf,
      personalityMode,
    };
    if (JSON.stringify(current) !== JSON.stringify(desired)) {
      setUserSettings(prev => ({ ...prev, ...desired }));
    }
  }, [alertsEnabled, alertVoice, autoTrade, mlAutoTrade, mlAutoConfidence, tradeStake, tradingTf, personalityMode, userSettings]);
  useEffect(()=>{ chartCandleDataRef.current=chartCandleData; },[chartCandleData]);
  useEffect(()=>{ sessionRef.current=session; },[session]);
  useEffect(()=>{ chatMsgsRef.current=chatMessages; },[chatMessages]);
  useEffect(()=>{ alertsRef.current=alerts; },[alerts]);
  useEffect(()=>{ todayPredsRef.current=todayPreds; },[todayPreds]);
  useEffect(()=>{ dockerToolsRef.current=dockerTools; },[dockerTools]);
  useEffect(()=>{ if(!settingsHydratedRef.current) return; alertsEnabledRef.current=alertsEnabled; try{localStorage.setItem(sk("amber_alerts"),String(alertsEnabled));}catch{} apiFetch("/api/user/settings",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({alertsEnabled}),}).catch(()=>{}); },[alertsEnabled]);
  useEffect(()=>{ if(!settingsHydratedRef.current) return; alertVoiceRef.current=alertVoice; try{localStorage.setItem(sk("amber_alert_voice"),String(alertVoice));}catch{} apiFetch("/api/user/settings",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({alertVoice}),}).catch(()=>{}); },[alertVoice]);
  useEffect(()=>{ if(!settingsHydratedRef.current) return; autoTradeRef.current=autoTrade; try{localStorage.setItem(sk("amber_auto_trade"),String(autoTrade));}catch{} apiFetch("/api/user/settings",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({autoTrade}),}).catch(()=>{}); },[autoTrade]);
  useEffect(()=>{ if(!settingsHydratedRef.current) return; try{localStorage.setItem(sk("amber_dead_zones"),String(deadZonesEnabled));}catch{} apiFetch("/api/user/settings",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({deadZonesEnabled}),}).catch(()=>{}); },[deadZonesEnabled]);
  useEffect(()=>{ if(!settingsHydratedRef.current) return; mlAutoTradeRef.current=mlAutoTrade; try{localStorage.setItem(sk("amber_ml_auto_trade"),String(mlAutoTrade));}catch{} apiFetch("/api/user/settings",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({mlAutoTrade}),}).catch(()=>{}); },[mlAutoTrade]);
  useEffect(()=>{ if(!settingsHydratedRef.current) return; try{localStorage.setItem(sk("amber_ml_auto_confidence"),String(mlAutoConfidence));}catch{} apiFetch("/api/user/settings",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({mlAutoConfidence:mlAutoConfidence/100}),}).catch(()=>{}); },[mlAutoConfidence]);
  useEffect(()=>{ try{localStorage.setItem(sk("kara_chart_slots"),JSON.stringify(chartSlots));}catch{} },[chartSlots]);
  useEffect(()=>{ if(!settingsHydratedRef.current) return; tradeStakeRef.current=tradeStake; try{localStorage.setItem(sk("amber_trade_stake"),String(tradeStake));}catch{} apiFetch("/api/user/settings",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({stake:tradeStake}),}).catch(()=>{}); },[tradeStake]);
  useEffect(()=>{ if(!settingsHydratedRef.current) return; try{ if(tradingTf) localStorage.setItem(sk("amber_trading_tf"),tradingTf); else localStorage.removeItem(sk("amber_trading_tf")); }catch{} apiFetch("/api/user/settings",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({tradingTf}),}).catch(()=>{}); },[tradingTf]);
  useEffect(()=>{ tabRef.current=tab; },[tab]);
  // Wake-word restart on tab change (kept intact after voice-tab removal 2026-09-25)
  useEffect(()=>{
    if (wakeWordRunRef.current) setTimeout(()=>startWakeWordRef.current?.(), 400);
  },[tab]);
  // Feed the DualChatPanel with live dashboard state (Kara ⇆ Lala side-by-side chat)
  useEffect(() => {
    try {
      window.__chatCtx = {
        user: _karaUserName,
        balance: accountInfo?.balance,
        loginid: accountInfo?.loginid,
        is_virtual: accountInfo?.is_virtual,
        active_strategy: activeStrategy,
        today_pnl: realTrades ? realTrades.reduce((s,t)=>{
          try {
            const ts = t.closed_at || t.close_time || t.created_at || t.time;
            const d = typeof ts === 'number' ? new Date(ts*1000) : new Date(ts);
            if (d.toISOString().slice(0,10) !== new Date().toISOString().slice(0,10)) return s;
            return s + (typeof t.pnl === 'number' ? t.pnl : 0);
          } catch { return s; }
        }, 0) : 0,
        open_trades: Object.values(symData || {}).reduce((s,sd)=> s + ((sd && sd.openTrades) ? sd.openTrades.length : 0), 0),
        session: sessionLabel,
        utc_time: new Date().toISOString(),
        personalityMode: personalityMode === "execution" ? "execution" : personalityMode,
      };
    } catch {}
  }, [accountInfo, activeStrategy, realTrades, symData, sessionLabel]);

  useEffect(()=>{ activeStrategyRef.current=activeStrategy; },[activeStrategy]);
  // Strategy tab equity curve — daily PnL trend for the active strategy
  useEffect(()=>{
    if (tab!=="strategy" || !activeStrategy) return;
    const stratName = activeStrategy.replace(/\.(md|txt)$/i,"");
    apiFetch(`/api/analytics/strategy-timeline?strategy=${encodeURIComponent(stratName)}`)
      .then(r=>r.ok?r.json():null)
      .then(d=>{ if (d?.timeline) setStrategyTimeline(d.timeline); })
      .catch(()=>{});
  },[tab, activeStrategy]);
  useEffect(()=>{ strategySchemaRef.current=strategySchema; },[strategySchema]);
  // When todayPreds changes, sync allPreds and POST new predictions to server
  useEffect(()=>{
    if (!todayPreds.length) return;
    const today=new Date().toDateString();
    const cutoff=Date.now()-60*24*60*60*1000;
    setAllPreds(prev=>{
      const withoutToday=prev.filter(p=>p.date!==today);
      return [...todayPreds,...withoutToday].filter(p=>{ try{ return new Date(p.date).getTime()>cutoff; }catch{ return false; } });
    });
    // POST only the latest pred (the one just added)
    const latest=todayPreds[0];
    if (latest) {
      apiFetch("/api/trades/predictions",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(latest)}).catch(()=>{});
    }
  },[todayPreds]);

  useEffect(()=>{
    if ("speechSynthesis" in window) {
      window.speechSynthesis.getVoices(); 
      window.speechSynthesis.addEventListener("voiceschanged", ()=>window.speechSynthesis.getVoices(), {once:true});
    }
    // Auto-fetch tools on startup so Toolsmith is ready
    fetchDockerTools();
  },[fetchDockerTools]);

  useEffect(()=>{ connectWS(); },[connectWS]);
  useEffect(()=>{
    const t=setInterval(()=>setSession(getSessions()),30000);
    const pollHealth=()=>{
      apiFetch("/api/health").then(r=>r.json()).then(d=>{ setServerHealth(d); if(d.docker_mcp) setDockerOk(true); }).catch(()=>{});
      apiFetch("/api/mcp/docker/status").then(r=>r.json()).then(d=>setDockerOk(!!d.ready)).catch(()=>{});
    };
    pollHealth();
    const h=setInterval(pollHealth,10000);
    return ()=>{ clearInterval(t); clearInterval(h); };
  },[]);
  useEffect(()=>()=>{
    clearTimeout(wsReconnectRef.current);
    const socket=wsRef.current;
    wsRef.current=null; // prevents an unmounted/logged-out scanner from reconnecting
    socket?.close();
  },[]);

  // ── Signal push WebSocket (/ws/signals) — zero-latency server-pushed alerts ──
  useEffect(()=>{
    const token = localStorage.getItem("kara_auth_token");
    if (!token) return;
    const wsProto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsHost  = window.location.host;
    let ws, reconnTimer;
    let reconnDelay = 2000; // exponential backoff: starts at 2s, doubles up to 60s
    const connect = () => {
      // Always re-read token from localStorage so stale tokens are not reused after re-login
      const currentToken = localStorage.getItem("kara_auth_token");
      if (!currentToken) return;
      try {
        ws = new window.WebSocket(`${wsProto}//${wsHost}/ws/signals?token=${encodeURIComponent(currentToken)}`);
        ws.onopen = () => { reconnDelay = 2000; }; // reset backoff on successful connection
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (msg.type === "analysis") {
              // P1-07: Authoritative real-time analysis from server (authoritative split-brain fix)
              if (msg.sym) {
                setSymData(prev => {
                  const existing = prev[msg.sym];
                  if (!existing) return prev;
                  // If we already have a newer or same timestamp from the server, update
                  if (existing.serverTime && existing.serverTime > msg.serverTime) return prev;
                  return {
                    ...prev,
                    [msg.sym]: { 
                      ...existing, 
                      analysis: msg.analysis, 
                      conds: msg.conds, 
                      cnt: msg.cnt, 
                      htf15: msg.htf15,
                      serverTime: msg.serverTime 
                    },
                  };
                });
              }
            } else if (msg.type === "signal") {
              // Store server signalId for monitored trade linking (used in prediction entry id)
              if (msg.signalId) serverSignalIdRef.current[msg.sym] = { id: msg.signalId, ts: Date.now() };
              // P1-07: Apply server-computed indicator values to symData to eliminate split-brain.
              // The server is the authoritative source; override the client-recomputed analysis
              // so the dashboard shows the same values the backend acted on when firing the signal.
              if (msg.indicators && msg.sym) {
                setSymData(prev => {
                  const existing = prev[msg.sym];
                  if (!existing) return prev;
                  // Merge server indicators into existing analysis (price already up-to-date from ticks)
                  const mergedAnalysis = existing.analysis
                    ? { ...existing.analysis, ...msg.indicators, price: msg.price, direction: msg.direction }
                    : { ...msg.indicators, price: msg.price, direction: msg.direction };
                  // If server also pushed conds, use them; otherwise keep client-computed conds
                  const mergedConds = msg.conds || existing.conds;
                  // If server pushed htf15, use it; otherwise keep client-computed htf15
                  const mergedHtf15 = msg.htf15 || existing.htf15;
                  return {
                    ...prev,
                    [msg.sym]: { ...existing, analysis: mergedAnalysis, conds: mergedConds, cnt: msg.cnt, htf15: mergedHtf15 },
                  };
                });
              }
              // P7-01: Paint live signal marker on chart if this symbol is currently focused
              if (msg.sym === focusSymRef.current && msg.price && typeof signalMarkerRef.current === "function") {
                signalMarkerRef.current(msg.price, msg.direction, msg.timestamp || null);
              }
              if (msg.source === "urus") setUrusStatus(prev => { const n = {...prev}; delete n[msg.sym]; return n; });
              if (alertsEnabledRef.current) {
                const dir = msg.direction === "CALL" ? "📈 BUY" : "📉 SELL";
                const srcLabel = msg.source === "urus" ? "URUS TMA 88%" : "SIGNAL PUSH";
                const reasonSnip = msg.reason ? ` — ${msg.reason.replace(/\|.*$/,"").trim()}` : "";
                setAlertMessages(p=>[...p,{role:"assistant",content:`⚡ <strong>${srcLabel}</strong> — ${dir} <strong>${msg.sym}</strong> @ ${fmtPrice(msg.price)}${msg.sl?` SL:${fmtPrice(msg.sl)}`:""}${reasonSnip} <em>(live push)</em>`,time:formatJaTime(new Date())}].slice(-50));
                if (alertVoiceRef.current) {
                  const symName = {R_100:"Volatility 100",R_75:"Volatility 75",R_50:"Volatility 50",R_25:"Volatility 25",R_10:"Volatility 10",JD50:"Jump 50",BOOM500:"Boom 500",CRASH500:"Crash 500",BOOM1000:"Boom 1000",CRASH1000:"Crash 1000",XAUUSD:"Gold",EURUSD:"Euro Dollar",GBPUSD:"Cable"}[msg.sym]||(msg.sym||"market");
                  if (msg.source === "urus") {
                    const regime = msg.reason?.includes("BEARISH")?"bearish":"bullish";
                    const pattern = msg.reason?.includes("4X Sell")?"four X sell pattern fired":"four X buy pattern fired";
                    speak(`${_karaUserName}! ${symName} URUS signal. ${regime} regime. ${pattern}. ${msg.direction==="CALL"?"Entering buy.":"Entering sell."} Expiry 2 minutes.`, 1);
                  } else {
                    speakAlert(`Signal. ${msg.direction === "CALL" ? "Buy" : "Sell"} ${msg.sym}`);
                  }
                }
              }
            } else if (msg.type === "urus_no_trade") {
              setUrusStatus(prev => ({ ...prev, [msg.sym]: { reason: msg.reason, price: msg.price, time: Date.now() } }));
            } else if (msg.type === "signal_blocked") {
              addLog(`🚫 Signal blocked (${msg.symbol}): ${msg.reason}`, "warn");
              setAlertMessages(p => [...p, {
                role: "assistant",
                content: `🚫 <strong>SIGNAL BLOCKED</strong> — <strong>${msg.symbol}</strong> ${msg.direction} @ ${msg.price.toFixed(5)} rejected: ${msg.reason}`,
                time: formatJaTime(new Date()),
              }].slice(-50));
            } else if (msg.type === "trade_error") {
              addLog(`❌ Trade error (${msg.symbol}): ${msg.reason}`, "error");
              setAlertMessages(p => [...p, {
                role: "assistant",
                content: `❌ <strong>TRADE ERROR</strong> — <strong>${msg.symbol}</strong> ${msg.direction} execution failed: ${msg.reason}`,
                time: formatJaTime(new Date()),
              }].slice(-50));
            } else if (msg.type === "trade_closed") {
              const pnlStr = msg.pnl >= 0 ? `+$${msg.pnl.toFixed(2)}` : `-$${Math.abs(msg.pnl).toFixed(2)}`;
              const tradeOutcome = msg.pnl >= 0 ? "WIN" : "LOSS";
              const outcome = msg.pnl >= 0 ? "✅ WIN" : "❌ LOSS";
              setAlertMessages(p=>[...p,{role:"assistant",content:`${msg.pnl>=0?"🟢":"🔴"} <strong>${outcome}</strong> — ${msg.sym} ${msg.direction==="CALL"?"📈":"📉"} | P&L: <strong>${pnlStr}</strong>${msg.reason?` | ${msg.reason}`:""}`,time:formatJaTime(new Date())}].slice(-50));
              // Patch history immediately so PnL stats update without waiting for the 15s poll
              const nowSec = Math.floor(Date.now() / 1000);
              setHistory(prev => prev.map(t =>
                String(t.id) === String(msg.contractId)
                  ? { ...t, outcome: tradeOutcome, pnl: msg.pnl, sell_time: nowSec, exit_time: nowSec, _settled: true }
                  : t
              ));
              if (msg.signalId) {
                const updater = p => p.id === msg.signalId ? { ...p, outcome: msg.outcome ?? tradeOutcome, theoreticalPnl: msg.pnl, outcomeTime: formatJaTime(new Date()) } : p;
                setTodayPreds(prev => prev.map(updater));
                setAllPreds(prev => prev.map(updater));
              }
            } else if (msg.type === "monitored_closed") {
              const oc = msg.outcome;
              const dir = msg.direction === "CALL" ? "📈 BUY" : "📉 SELL";
              const pnlStr = (msg.theoreticalPnl ?? 0) >= 0
                ? `+$${(msg.theoreticalPnl ?? 0).toFixed(2)}` : `-$${Math.abs(msg.theoreticalPnl ?? 0).toFixed(2)}`;
              const updater = p => p.id === msg.signalId
                ? { ...p, outcome: oc, outcomePrice: msg.exitPrice, outcomeTime: formatJaTime(new Date()), theoreticalPnl: msg.theoreticalPnl }
                : p;
              setTodayPreds(prev => prev.map(updater));
              setAllPreds(prev => prev.map(updater));
              if (alertsEnabledRef.current) {
                setAlertMessages(p=>[...p,{role:"assistant",content:`👁 <strong>MONITORED ${oc === "WIN" ? "WIN ✅" : "LOSS ❌"}</strong> — ${dir} <strong>${msg.sym}</strong> @ ${fmtPrice(msg.exitPrice)} | ${msg.reason} | Theoretical: ${pnlStr} <em>(not executed)</em>\n🤖 ML Engine logged outcome for training`,time:formatJaTime(new Date())}].slice(-50));
              }
              if (alertVoiceRef.current) speakAlert(`Monitored ${oc === "WIN" ? "win" : "loss"}. ${msg.sym}.`);
            } else if (msg.type === "agent_task_progress") {
              setRunningTasks(prev => {
                const existing = prev.find(t => t.task_id === msg.task_id);
                if (existing) return prev.map(t => t.task_id === msg.task_id ? { ...t, progress: msg.progress, message: msg.message, ts: msg.ts } : t);
                return [...prev, { task_id: msg.task_id, progress: msg.progress, message: msg.message, ts: msg.ts }];
              });
            } else if (msg.type === "agent_task_done") {
              setRunningTasks(prev => prev.filter(t => t.task_id !== msg.task_id));
              const icon = msg.status === "completed" ? "✅" : "❌";
              const verdict = msg.critic_verdict ? `\nCritic: ${msg.critic_verdict}` : "";
              const summary = msg.result_summary || msg.error || "Task finished.";
              setChatMessages(p => [...p, { role: "assistant", content: `${icon} Agent task ${msg.task_id}: ${summary}${verdict}`, time: formatJaTime(new Date()) }].slice(-200));
            } else if (msg.type === "account-info") {
              // Live account balance stream from server every 5s
              if (msg.username === _karaUserName || !msg.username) {
                setAccountInfo(prev => ({ ...prev, ...msg }));
              }
            }
          } catch {}
        };
        ws.onclose = () => { reconnTimer = setTimeout(connect, reconnDelay); reconnDelay = Math.min(reconnDelay * 2, 60000); };
        ws.onerror = () => { try { ws.close(); } catch {} };
      } catch {}
    };
    connect();
    return () => { clearTimeout(reconnTimer); try { ws?.close(); } catch {} };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Derived ───────────────────────────────────────────────────────────────
  const focused = focusSym ? symData[focusSym] : null;
  const cC = c=>c==="pass"?C.green:c==="warn"?C.amber:c==="fail"?C.red:c==="pending"?C.blue:C.dim;
  const cI = c=>c==="pass"?"✅":c==="warn"?"⚠️":c==="fail"?"❌":"⏳";
  const micC = voiceState==="recording"?C.purple:voiceState==="processing"?C.blue:C.muted;
  const tabS = t=>({ padding:"9px 22px",borderRadius:"4px 4px 0 0",cursor:"pointer",fontSize:13,fontFamily:"inherit",fontWeight:700,letterSpacing:1,border:"none",background:tab===t?C.panel:"transparent",color:tab===t?C.text:C.muted,borderBottom:`2px solid ${tab===t?C.amber:"transparent"}`,transition:"all 0.15s" });
  const wsCol = wsStatus==="connected"?C.green:wsStatus==="connecting"?C.amber:wsStatus==="error"?C.red:C.muted;
  const wsIcon = wsStatus==="connected"?"🟢":wsStatus==="connecting"?"🟡":"🔴";

  // Sort tick feed: most recent first, group by symbol for display
  const recentTicks = [...tickFeed].reverse().slice(0,50);

  // ══════════════════════════════════════════════════════════════════════════
  // 2026-10-09 agent-ui: chat agent ↔ dashboard bridge (Phase 1)
  // Everything below is assigned to refs on every render so async agent callbacks
  // always see fresh state. Contract: src/agent/uiTools.js.
  // ══════════════════════════════════════════════════════════════════════════
  agentLiveRef.current = {
    tab, chartSlots, chartLayout, chartFocusKey, focusSym, scannerTf, history, allPreds, todayPreds,
    strategies, activeStrategy, accountInfo, mlStatus, mlPrediction, mlPredictSym, mlPredictResult,
    userSettings, symData, selectedSyms, strategySchema, alertsEnabled, alertVoice, autoTrade, tradeStake,
    dockerTools, dockerOk, activeTrades, alertMessages, missionStatus, scanStatus, dataStatus, wsStatus,
  };
  {
    const _sleep = (ms) => new Promise(r => setTimeout(r, ms));
    const _layoutCount = (l) => l === "quad" ? 4 : l === "dual" ? 2 : 1;
    const _normSym = (raw) => {
      let s = String(raw || "").trim();
      if (!s) return null;
      const up = s.toUpperCase();
      if (SYMBOLS.includes(up)) return up;
      if (SYMBOLS.includes(s)) return s;
      const viaApi = Object.keys(SYMBOL_API).find(k => String(SYMBOL_API[k]).toUpperCase() === up);
      if (viaApi) return viaApi;
      if (/^FRX/i.test(s) && SYMBOLS.includes(up.slice(3))) return up.slice(3);
      if (/^R\d+$/.test(up) && SYMBOLS.includes("R_" + up.slice(1))) return "R_" + up.slice(1);
      return up; // unknown symbol: let the chart try (strategy symbols may be outside the static list)
    };
    const _normTf = (tf) => {
      if (tf == null || tf === "") return null;
      const t = String(tf).trim().toLowerCase().replace(/^(\d+)\s*(min|mins|minute|minutes)$/, "$1m").replace(/^(\d+)\s*(hr|hrs|hour|hours)$/, "$1h");
      return CHART_TF_MAP[t] ? t : null;
    };
    const _slotKey = (s) => `${s.sym}_${s.tf}`;

    const helpers = {
      normSym: _normSym, normTf: _normTf,
      navigate: (t) => {
        const n = normalizeTab(t);
        if (!n) throw new Error(`unknown tab "${t}". Valid: ${TAB_IDS.join(", ")}`);
        setTab(n);
        return n;
      },
      // Legacy semantics when opts.replaceAll: slots := symbols (max 4).
      setChartSymbols: (symsIn, tfIn, opts = {}) => {
        const L = agentLiveRef.current;
        const syms = (Array.isArray(symsIn) ? symsIn : [symsIn]).map(_normSym).filter(Boolean).slice(0, 4);
        if (!syms.length) throw new Error("no valid symbol");
        const curSlots = L.chartSlots || [];
        let next;
        let focusIdx = 0;
        if (opts.replaceAll) {
          const tf = _normTf(tfIn) || "1m";
          next = syms.map(s => ({ sym: s, tf }));
        } else if (Number.isInteger(opts.slot)) {
          const i = Math.max(0, Math.min(3, opts.slot));
          next = [...curSlots];
          while (next.length <= i) next.push({ sym: syms[0], tf: _normTf(tfIn) || "1m" });
          next[i] = { sym: syms[0], tf: _normTf(tfIn) || next[i]?.tf || "1m" };
          focusIdx = i;
        } else {
          // single symbol → replace the focused slot (keeps the rest of a dual/quad grid)
          let i = curSlots.findIndex(s => _slotKey(s) === L.chartFocusKey);
          if (i < 0 || i >= _layoutCount(L.chartLayout)) i = 0;
          next = curSlots.length ? [...curSlots] : [{ sym: syms[0], tf: "1m" }];
          syms.forEach((s, k) => {
            const idx = k === 0 ? i : Math.min(3, k);
            while (next.length <= idx) next.push({ sym: s, tf: _normTf(tfIn) || "1m" });
            next[idx] = { sym: s, tf: _normTf(tfIn) || next[idx]?.tf || "1m" };
          });
          focusIdx = i;
        }
        setChartSlots(next);
        setChartFocusKey(_slotKey(next[focusIdx] || next[0]));
        const need = Math.max(next.length, focusIdx + 1);
        if (!opts.replaceAll && need > _layoutCount(L.chartLayout)) setChartLayout(need > 2 ? "quad" : "dual");
        next.forEach(s => { try { fetchChartCandles(s.sym, s.tf); } catch {} });
        setTab("charts");
        return { slots: next, focusIdx };
      },
      setChartTimeframe: (tfIn, opts = {}) => {
        const tf = _normTf(tfIn);
        if (!tf) throw new Error(`invalid timeframe "${tfIn}"`);
        const L = agentLiveRef.current;
        if (opts.all) {
          setChartSlots(p => { const n = p.map(s => ({ ...s, tf })); n.forEach(s => fetchChartCandles(s.sym, s.tf)); return n; });
          setTab("charts");
          return { tf, target: "all" };
        }
        if (!Number.isInteger(opts.slot) && L.tab === "scanner" && chartVoiceRef.current) {
          setScannerTf(tf);
          return { tf, target: "scanner" };
        }
        const slots = L.chartSlots || [];
        let i = Number.isInteger(opts.slot) ? opts.slot : slots.findIndex(s => _slotKey(s) === L.chartFocusKey);
        if (i < 0) i = 0;
        const next = [...slots];
        while (next.length <= i) next.push({ sym: next[0]?.sym || "R_50", tf });
        next[i] = { ...next[i], tf };
        setChartSlots(next);
        setChartFocusKey(_slotKey(next[i]));
        fetchChartCandles(next[i].sym, tf);
        setTab("charts");
        return { tf, target: `slot ${i}`, slot: i };
      },
      setChartLayout: (layout, symbols, tfIn) => {
        if (!["single", "dual", "quad"].includes(layout)) throw new Error(`invalid layout "${layout}"`);
        setChartLayout(layout);
        const L = agentLiveRef.current;
        // Defense-in-depth: if the model emitted a partial payload (no symbols for a multi-slot layout),
        // fill the remaining slots from selectedSyms, then from a known-good R-synthetics pool.
        // This prevents the bug where setChartLayout("quad", undefined) only updates the layout
        // state but leaves chartSlots with 1 entry, so the chart grid keeps rendering JD50 1m.
        const want = _layoutCount(layout);
        const fallbackPool = ["R_50", "R_25", "R_75", "R_10", "R_100", "JD10", "JD25", "JD50", "JD75", "JD100"];
        let fillSyms = [];
        if (Array.isArray(symbols) && symbols.length) {
          fillSyms = symbols.slice();
        }
        // pad from selectedSyms (user's watchlist)
        const fromSelected = [...(L.selectedSyms || [])].map(String);
        for (const s of fromSelected) {
          if (fillSyms.length >= want) break;
          if (!fillSyms.includes(s) && _normSym(s)) fillSyms.push(s);
        }
        // pad from fallback pool
        for (const s of fallbackPool) {
          if (fillSyms.length >= want) break;
          if (!fillSyms.includes(s)) fillSyms.push(s);
        }
        if (Array.isArray(fillSyms) && fillSyms.length) {
          const tf = _normTf(tfIn);
          const next = [...(L.chartSlots || [])];
          fillSyms.slice(0, want).forEach((s, i) => {
            const sym = _normSym(s); if (!sym) return;
            next[i] = { sym, tf: tf || next[i]?.tf || "1m" };
          });
          setChartSlots(next);
          setChartFocusKey(_slotKey(next[0]));
          next.forEach(s => { try { fetchChartCandles(s.sym, s.tf); } catch {} });
        } else if (_normTf(tfIn)) {
          const tf = _normTf(tfIn);
          setChartSlots(p => { const n = p.map(s => ({ ...s, tf })); n.forEach(s => fetchChartCandles(s.sym, s.tf)); return n; });
        }
        setTab("charts");
        return { layout, filled: fillSyms.slice(0, want) };
      },
      focusSlot: (slot) => {
        const L = agentLiveRef.current;
        const s = (L.chartSlots || [])[slot];
        if (!s) throw new Error(`slot ${slot} is empty (have ${(L.chartSlots || []).length} slots)`);
        setChartFocusKey(_slotKey(s));
        if (slot >= _layoutCount(L.chartLayout)) setChartLayout(slot > 1 ? "quad" : "dual");
        setTab("charts");
        return { slot, symbol: s.sym, timeframe: s.tf };
      },
      watchlist: (op, symsIn = []) => {
        const syms = (symsIn || []).map(_normSym).filter(Boolean);
        const has = (s) => selectedSymsRef.current.has(s);
        if (op === "add") syms.forEach(s => { if (!has(s)) toggleSymbol(s); });
        else if (op === "remove") syms.forEach(s => { if (has(s)) toggleSymbol(s); });
        else if (op === "add_all") SYMBOLS.forEach(s => { if (!has(s)) toggleSymbol(s); });
        else if (op === "clear") { SYMBOLS.forEach(s => { if (has(s)) unsubscribeSymbol(s); }); setSelectedSyms(new Set()); }
        else if (op === "focus") { if (!syms[0]) throw new Error("focus needs a symbol"); if (!has(syms[0])) toggleSymbol(syms[0]); setFocusSym(syms[0]); }
        else throw new Error(`unknown watchlist op "${op}"`);
        return { op, symbols: syms };
      },
      alerts: (op) => {
        if (op === "enable" || op === "disable") { const v = op === "enable"; setAlertsEnabled(v); try { localStorage.setItem(sk("amber_alerts"), String(v)); } catch {} }
        else if (op === "voice_on" || op === "voice_off") { const v = op === "voice_on"; setAlertVoice(v); try { localStorage.setItem(sk("amber_alert_voice"), String(v)); } catch {} }
        else if (op === "clear") setAlertMessages([]);
        else throw new Error(`unknown alerts op "${op}"`);
        return { op };
      },
    };
    uiHelpersRef.current = helpers;

    // ── chart registry ──
    const getTargetChart = (slot) => {
      const L = agentLiveRef.current;
      if (Number.isInteger(slot)) return { chart: chartRefs.current[slot] || null, where: `slot ${slot}`, slot };
      if (L.tab === "charts") {
        let i = (L.chartSlots || []).findIndex(s => _slotKey(s) === L.chartFocusKey);
        if (i < 0 || i >= _layoutCount(L.chartLayout)) i = 0;
        return { chart: chartRefs.current[i] || null, where: `slot ${i}`, slot: i };
      }
      if (chartVoiceRef.current) return { chart: chartVoiceRef.current, where: "scanner", slot: null };
      return { chart: null, where: "none", slot: null };
    };
    // Wait (≤1.5s) for the target chart's ref to mount / reflect an expected symbol+tf.
    const waitForChart = async (slot, expect = {}, timeoutMs = 1500) => {
      const t0 = Date.now();
      let tgt = getTargetChart(slot);
      if (!tgt.chart && tgt.where === "none") { setTab("charts"); }
      while (Date.now() - t0 < timeoutMs) {
        tgt = getTargetChart(slot);
        const st = tgt.chart?.getState?.();
        if (tgt.chart && (!expect.symbol || st?.symbol === expect.symbol) && (!expect.timeframe || st?.timeframe === expect.timeframe)) return tgt;
        await _sleep(100);
      }
      tgt = getTargetChart(slot);
      if (!tgt.chart) throw new Error(`chart not available (${tgt.where}) — open the Charts tab or pick a symbol first`);
      return tgt;
    };
    const chartStateAll = () => {
      const L = agentLiveRef.current;
      const visible = (L.chartSlots || []).slice(0, _layoutCount(L.chartLayout));
      return {
        active_tab: L.tab, layout: L.chartLayout, focus_key: L.chartFocusKey,
        scanner: chartVoiceRef.current?.getState?.() || (L.focusSym ? { symbol: L.focusSym, timeframe: L.scannerTf, mounted: false } : null),
        slots: visible.map((s, i) => {
          const st = chartRefs.current[i]?.getState?.();
          return st ? { slot: i, focused: _slotKey(s) === L.chartFocusKey, ...st } : { slot: i, symbol: s.sym, timeframe: s.tf, mounted: false };
        }),
      };
    };
    const _strip = (obj, depth = 0) => {
      if (obj == null || typeof obj !== "object") return obj;
      if (Array.isArray(obj)) return obj.slice(0, 20).map(v => _strip(v, depth + 1));
      if (depth > 3) return "…";
      const out = {};
      for (const [k, v] of Object.entries(obj)) {
        if (/token|secret|password|passwd|api_?key|ssid|credential|auth/i.test(k)) continue;
        out[k] = typeof v === "string" && v.length > 200 ? v.slice(0, 200) + "…" : _strip(v, depth + 1);
      }
      return out;
    };
    const tabSnapshot = (tabId) => {
      const L = agentLiveRef.current;
      const meta = TAB_REGISTRY.find(t => t.id === tabId) || {};
      const base = { tab: tabId, label: meta.label, desc: meta.desc, is_active_tab: L.tab === tabId };
      const acct = L.accountInfo || {};
      switch (tabId) {
        case "scanner": {
          const syms = [...(L.selectedSyms || [])].slice(0, 15);
          return { ...base, watchlist: syms, focusSym: L.focusSym, scannerTf: L.scannerTf, ws: L.wsStatus,
            symbols: syms.map(s => { const d = L.symData?.[s] || {}; const a = d.analysis || {};
              return { sym: s, price: a.price, dir: a.direction, cnt: d.cnt, rsi: a.rsi != null ? Math.round(a.rsi) : undefined, htf: d.htf15?.trend, offline: d.offline || undefined }; }),
            open_trades: (L.activeTrades || []).length, auto_trade: L.autoTrade, stake: L.tradeStake,
            recent_alerts: (L.alertMessages || []).slice(-5).map(m => String(m?.content || m || "").replace(/<[^>]+>/g, "").slice(0, 140)) };
        }
        case "strategy": {
          const sch = L.strategySchema || {};
          return { ...base, active: L.activeStrategy,
            strategies: (L.strategies || []).slice(0, 30).map(s => ({ name: s.name, win_rate: s.win_rate ?? s.winRate, status: s.status })),
            schema: { name: sch.name, conditions: sch.conditionCount, gate: sch.signalGate?.requiredCount, symbols: sch.symbols, timeframes: sch.timeframes },
            best_session: acct.bestSession, best_hours: _strip(acct.sessionStats?.bestHours ?? acct.sessionStats) };
        }
        case "ml":
          return { ...base, status: _strip(L.mlStatus), last_prediction: _strip(L.mlPredictResult), predict_symbol: L.mlPredictSym, scanner_prediction: _strip(L.mlPrediction) };
        case "history": {
          const rows = [...(L.history || [])].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
          const done = rows.filter(r => /win|loss/i.test(String(r.outcome || "")));
          const wins = done.filter(r => /win/i.test(r.outcome)).length;
          const pnl = rows.reduce((t, r) => t + (Number(r.pnl) || 0), 0);
          return { ...base, stats: { trades: rows.length, settled: done.length, wins, losses: done.length - wins,
              win_rate: done.length ? +(wins / done.length * 100).toFixed(1) : null, total_pnl: +pnl.toFixed(2),
              daily_pnl: acct.dailyPnl, weekly_pnl: acct.weeklyPnl, monthly_pnl: acct.monthlyPnl, today_signals: (L.todayPreds || []).length },
            last_trades: rows.slice(0, 20).map(r => ({ t: r.date || r.time || (r.timestamp ? new Date(r.timestamp).toISOString().slice(0, 16) : undefined),
              sym: r.symbol || r.sym, dir: r.direction, out: r.outcome, pnl: r.pnl != null ? +Number(r.pnl).toFixed(2) : undefined, stake: r.stake, strat: r.strategy ? String(r.strategy).slice(0, 24) : undefined })) };
        }
        case "settings":
          return { ...base, auto_trade: L.autoTrade, stake: L.tradeStake, alerts: L.alertsEnabled, alert_voice: L.alertVoice, settings: _strip(L.userSettings) };
        case "charts":
          return { ...base, ...chartStateAll() };
        case "docker":
          return { ...base, connected: L.dockerOk, tools: (L.dockerTools || []).slice(0, 40).map(t => t.name) };
        case "advanced_backtest":
          return { ...base, active_strategy: L.activeStrategy, strategies: (L.strategies || []).slice(0, 20).map(s => s.name), hint: "Backtest results live inside the Backtests tab component; run backtests via the backtest tools to get numbers." };
        case "forge":
          return { ...base, mission: _strip(L.missionStatus), hint: "Forge queue/results are loaded inside the Forge tab; use improver/forge tools for details." };
        case "info":
          return { ...base, account: { balance: acct.balance, currency: acct.currency, loginid: acct.loginid, is_virtual: acct.is_virtual, strategy: acct.strategyName || L.activeStrategy }, scanner_status: _strip(L.scanStatus), data_status: _strip(L.dataStatus) };
        case "mt5":
          return { ...base, hint: "MT5 terminal — live rates, positions, symbols on the Hostinger VPS MetaTrader 5 account." };
        default:
          return base;
      }
    };
    const _fit = (obj) => {
      let s = JSON.stringify(obj);
      if (s.length <= 3900) return obj;
      const o = { ...obj };
      for (const k of ["last_trades", "symbols", "strategies", "tools", "slots"]) {
        if (Array.isArray(o[k]) && o[k].length > 5) { o[k] = o[k].slice(0, Math.ceil(o[k].length / 2)); if (JSON.stringify(o).length <= 3900) return o; }
      }
      return capResult(o, 3900);
    };
    const _settle = () => _sleep(160);
    const _bool = (v) => (typeof v === "boolean" ? v : undefined);

    runUiActionRef.current = async (name, inputRaw) => {
      const input = inputRaw && typeof inputRaw === "object" ? inputRaw : {};
      const slot = Number.isInteger(input.slot) ? input.slot : (input.slot != null && !isNaN(Number(input.slot)) ? Number(input.slot) : undefined);
      switch (name) {
        case "ui_set_tab": {
          const n = helpers.navigate(input.tab);
          // Defense-in-depth: wait for React to re-render so agentLiveRef.current.tab
          // reflects the new tab before we return. Without this, e2e tests that poll
          // getState() immediately after the SSE returns see the stale tab.
          await _settle();
          const confirmed = agentLiveRef.current.tab;
          return { tab: confirmed, requested: n, label: TAB_REGISTRY.find(t => t.id === confirmed)?.label };
        }
        case "ui_view_tab": {
          const n = input.tab ? normalizeTab(input.tab) : agentLiveRef.current.tab;
          if (!n) throw new Error(`unknown tab "${input.tab}"`);
          return _fit(tabSnapshot(n));
        }
        case "ui_chart_set_symbol": {
          const r = helpers.setChartSymbols([input.symbol], input.timeframe, { slot });
          const s = r.slots[r.focusIdx];
          const tgt = await waitForChart(r.focusIdx, { symbol: s.sym, timeframe: s.tf });
          return { tab: "charts", slot: r.focusIdx, ...(tgt.chart.getState?.() || { symbol: s.sym, timeframe: s.tf }) };
        }
        case "ui_chart_set_timeframe": {
          const r = helpers.setChartTimeframe(input.timeframe, { slot });
          if (r.target === "scanner") { await _settle(); return { ...r, state: chartVoiceRef.current?.getState?.() }; }
          const tgt = await waitForChart(r.slot, { timeframe: r.tf });
          return { ...r, state: tgt.chart.getState?.() };
        }
        case "ui_chart_set_layout": {
          helpers.setChartLayout(input.layout, input.symbols, input.timeframe);
          const n = _layoutCount(input.layout);
          const t0 = Date.now();
          while (Date.now() - t0 < 1500) {
            const L = agentLiveRef.current;
            const vis = (L.chartSlots || []).slice(0, n);
            if (L.tab === "charts" && L.chartLayout === input.layout && vis.every((s, i) => chartRefs.current[i]?.getState?.()?.symbol === s.sym)) break;
            await _sleep(100);
          }
          return chartStateAll();
        }
        case "ui_chart_focus_slot": {
          const r = helpers.focusSlot(slot ?? 0);
          await waitForChart(r.slot).catch(() => null);
          return r;
        }
        case "ui_chart_indicators": {
          // Defense-in-depth: ensure the charts tab is open and at least one chart slot is mounted
          // before calling addIndicator. Without this, if the user is on a different tab the chart
          // widget isn't mounted and the indicator call silently no-ops or throws.
          setTab("charts");
          await _settle();
          const { chart, where } = await waitForChart(slot, {}, 3000);
          const op = input.op;
          const ids = (Array.isArray(input.ids) ? input.ids : input.id ? [input.id] : []).map(x => String(x).toLowerCase());
          if (op === "add" && !ids.length) {
            throw new Error("ui_chart_indicators add requires ids (e.g. [\"rsi\", \"bb\"]) — model emitted empty list");
          }
          const unknown = ids.filter(id => !INDICATOR_IDS.includes(id));
          const valid = ids.filter(id => INDICATOR_IDS.includes(id));
          if (op === "clear") chart.clearIndicators();
          else if (op === "add") { try { console.log(`[agent-runtime] ui_chart_indicators add: ${valid.join(",")}, unknown: ${unknown.join(",")}, chart: ${where}`); } catch {} if (!valid.length) throw new Error(`no valid indicator ids (unknown: ${unknown.join(",")})`); valid.forEach(id => { chart.addIndicator(id); if (input.params && Object.keys(input.params).length) chart.setIndicatorParams?.(id, input.params); }); }
          else if (op === "remove") valid.forEach(id => chart.removeIndicator(id));
          else if (op === "set_params") { if (!valid.length) throw new Error("set_params needs ids"); valid.forEach(id => chart.setIndicatorParams?.(id, input.params || {})); }
          else throw new Error(`unknown op "${op}"`);
          await _settle();
          return { chart: where, op, indicators: chart.getState?.()?.indicators, ...(unknown.length ? { unknown_ids: unknown } : {}) };
        }
        case "ui_chart_draw": {
          // Defense-in-depth: switch to charts tab + wait for chart mount (3s) before drawing.
          setTab("charts");
          await _settle();
          const { chart, where } = await waitForChart(slot, {}, 3000);
          if (input.op === "clear") { chart.clearDrawings?.(); return { chart: where, drawings: 0 }; }
          if (input.op === "mode") { chart.setDrawingTool(input.type || "horizontal"); return { chart: where, mode: input.type || "horizontal", note: "user clicks the chart to place points" }; }
          if (input.op !== "draw") throw new Error(`unknown op "${input.op}"`);
          const type = input.type || "horizontal";
          let p1 = input.p1, p2 = input.p2;
          if (type !== "horizontal" && !p1 && !p2) throw new Error(`${type} needs p1 and p2 ({time, price})`);
          const d = chart.addDrawing({ type, price: input.price, p1: p1 || (input.price != null ? { price: input.price } : undefined), p2 });
          return { chart: where, drawn: d, total: (chart.getState?.()?.drawings || []).length };
        }
        case "ui_chart_toggle": {
          setTab("charts");
          await _settle();
          const { chart, where } = await waitForChart(slot, {}, 3000);
          const f = input.feature;
          const cur = chart.getState?.()?.overlays || {};
          const on = _bool(input.on) ?? !cur[f];
          const map = {
            sessions: () => chart.toggleSessionOverlay(on),
            hour_heatmap: () => chart.toggleHourHeatmap(on),
            equity: () => chart.toggleEquity(on),
            strategy_overlay: () => chart.toggleStrategyOverlay(on),
            history_markers: () => chart.toggleHistoryMarkers(on),
            active_markers: () => chart.toggleActiveMarkers(on),
            link: () => chart.setLinkMode(on),
            mtf: () => chart.openMtf?.(on),
            risk: () => chart.openRisk?.(on),
            fullscreen: () => chart.toggleFullscreen?.(on),
          };
          if (!map[f]) throw new Error(`unknown feature "${f}"`);
          const extra = await map[f]();
          await _settle();
          const out = { chart: where, feature: f, on };
          if (f === "mtf" && extra) out.mtf = _strip(extra);
          if (f === "risk" && extra) out.risk = _fit(_strip(extra));
          if (f === "fullscreen") out.note = "Browsers may block fullscreen unless triggered by a click; the user can press the ⛶ button.";
          return out;
        }
        case "ui_chart_view": {
          const { chart, where } = await waitForChart(slot);
          if (input.op === "zoom_in") chart.zoomIn(); else if (input.op === "zoom_out") chart.zoomOut(); else if (input.op === "fit") chart.fitContent();
          else throw new Error(`unknown op "${input.op}"`);
          return { chart: where, op: input.op };
        }
        case "ui_chart_price_alert": {
          const { chart, where } = await waitForChart(slot);
          const st = chart.getState?.() || {};
          const price = Number(input.price);
          if (!isFinite(price)) throw new Error("price must be a number");
          const sym = input.symbol ? _normSym(input.symbol) : st.symbol;
          const condition = input.direction || (st.lastPrice != null && price < st.lastPrice ? "below" : "above");
          chart.setPriceAlert({ symbol: sym, price, condition });
          return { chart: where, symbol: sym, price, condition, note: sym !== st.symbol ? "alert fires only while this chart shows that symbol" : undefined };
        }
        case "ui_chart_journal": {
          const { chart, where } = await waitForChart(slot);
          if (input.op === "add") { if (!input.text) throw new Error("text required"); chart.addJournalEntry(String(input.text).slice(0, 500)); await _settle(); }
          const list = (chart.getJournalEntries?.() || []).slice(-10).map(e => ({ ts: e.ts, text: e.text, symbol: e.symbol, timeframe: e.timeframe }));
          return { chart: where, op: input.op, entries: list };
        }
        case "ui_chart_preset": {
          const { chart, where } = await waitForChart(slot);
          if (input.op === "list") return { chart: where, presets: chart.listPresets() };
          if (!input.name) throw new Error("name required");
          if (input.op === "save") return { chart: where, saved: chart.savePreset(input.name) };
          if (input.op === "load") { const ok = chart.loadPreset(input.name); if (!ok) throw new Error(`preset "${input.name}" not found (have: ${chart.listPresets().join(", ") || "none"})`); await _settle(); return { chart: where, loaded: input.name, state: chart.getState?.() }; }
          throw new Error(`unknown op "${input.op}"`);
        }
        case "ui_chart_screenshot": {
          const { chart, where } = await waitForChart(slot);
          const ok = chart.screenshot();
          if (!ok) throw new Error("screenshot failed (chart not rendered yet?)");
          return { chart: where, downloaded: true };
        }
        case "ui_chart_state":
          return _fit(chartStateAll());
        case "ui_watchlist":
          return helpers.watchlist(input.op, input.symbols || (input.symbol ? [input.symbol] : []));
        case "ui_alerts":
          return helpers.alerts(input.op);
        case "ui_speak": {
          if (!input.text) throw new Error("text required");
          speak(String(input.text).slice(0, 600));
          return { spoken: true };
        }
        default:
          throw new Error(`unknown UI tool "${name}"`);
      }
    };

    // ── agent chat context (sent with every /api/agent/chat turn) ──
    const buildAgentContext = () => {
      const L = agentLiveRef.current;
      const acct = L.accountInfo || {};
      const visible = (L.chartSlots || []).slice(0, _layoutCount(L.chartLayout));
      let ctx = {};
      try { ctx = buildKaraContext() || {}; } catch {}
      return {
        user: _karaUserName, username: authUser?.username,
        active_tab: L.tab, tabs: TAB_IDS,
        chart_layout: L.chartLayout,
        chart_slots: visible.map((s, i) => ({ slot: i, symbol: s.sym, timeframe: s.tf, focused: _slotKey(s) === L.chartFocusKey })),
        focused_slot: Math.max(0, visible.findIndex(s => _slotKey(s) === L.chartFocusKey)),
        focus_symbol: L.focusSym, scanner_tf: L.scannerTf,
        active_symbol: L.tab === "charts" ? (String(L.chartFocusKey || "").split("_").slice(0, -1).join("_") || null) : L.focusSym,
        active_strategy: L.activeStrategy, stake: L.tradeStake, auto_trade: L.autoTrade,
        balance: acct.balance, currency: acct.currency, loginid: acct.loginid, is_virtual: acct.is_virtual,
        today_pnl: ctx.todayPnl ?? acct.dailyPnl, today_wr: ctx.todayWR, open_trades: (L.activeTrades || []).length,
        watchlist: [...(L.selectedSyms || [])].slice(0, 20),
        session: sessionLabel, personalityMode, utc_time: new Date().toISOString(),
      };
    };

    helpers.buildAgentContext = buildAgentContext;
    const _stripAck = (c) => String(c || "");
    agentConfirmRef.current = async (callId, approve) => {
      if (!callId) return;
      const patchAll = (status, extra) => setChatMessages(p => p.map(m => m?._confirms ? setConfirmStatus(m, callId, status, extra) : m));
      patchAll("sending");
      if (pendingAgentConfirmRef.current?.call_id === callId) pendingAgentConfirmRef.current = null;
      try {
        const res = await confirmAgentCall(apiFetch, callId, approve);
        const summary = res?.summary || res?.message || res?.text || (res?.result ? JSON.stringify(res.result).slice(0, 240) : (approve ? "Done." : "Cancelled."));
        patchAll(approve ? "approved" : "cancelled", { result: String(summary).slice(0, 300) });
        if (approve) speak(String(res?.text || res?.summary || "Done.").slice(0, 300), 1);
      } catch (e) {
        patchAll("error", { result: e.message });
        speak(`That action failed: ${e.message}`, 1);
      }
    };

    agentTurnRef.current = async (userText, options = {}) => {
      const isVoice = !!(options.fromVoice || options.fromVoiceTab);
      const turnId = `agent_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
      const time = formatJaTime(new Date());
      const history = (chatMsgsRef.current || [])
        .filter(m => (m.role === "user" || m.role === "assistant") && !m._isToolBadge && !m._streaming && typeof m.content === "string" && m.content.trim())
        .slice(-(isVoice ? 8 : 16))
        .map(m => ({ role: m.role, content: _stripAck(m.content).slice(0, isVoice ? 400 : 1200) }));
      if (history.length && history[history.length - 1].role === "user" && history[history.length - 1].content === String(userText).slice(0, 1200)) history.pop();
      const messages = [...history, { role: "user", content: String(userText) }];
      setChatMessages(p => [...p, { role: "assistant", content: "⏳ " + instantAckText(userText, aiName), time, _streaming: true, _agent: true, _agentTurn: turnId }].slice(-200));
      const patch = (fn) => setChatMessages(p => { const i = p.findIndex(m => m._agentTurn === turnId); if (i < 0) return p; const n = [...p]; n[i] = fn(n[i]); return n; });
      setChatThinking(true);
      let spoke = false;
      let voiceBuf = "";
      const flushVoiceSentences = () => {
        if (!isVoice) return;
        const re = /[.!?]\s+/g;
        let lastEnd = 0, m;
        while ((m = re.exec(voiceBuf)) !== null) {
          const sentence = voiceBuf.slice(lastEnd, m.index + 1).trim();
          lastEnd = m.index + m[0].length;
          if (sentence.length >= 8) { speak(sentence); spoke = true; }
        }
        if (lastEnd > 0) voiceBuf = voiceBuf.slice(lastEnd);
      };
      try {
        const res = await streamAgentChat({
          apiFetch,
          agent: aiName === "Lala" ? "lala" : "kara",
          messages,
          context: { ...buildAgentContext(), voice_mode: isVoice },
          runUiAction: async (n, i) => { if (n === "ui_speak") spoke = true; return runUiActionRef.current(n, i); },
          onEvent: (ev, p) => {
            patch(m => applyAgentEvent(m, ev, p));
            if (ev === "delta" && isVoice && p?.text) {
              voiceBuf += p.text;
              flushVoiceSentences();
            }
            if (ev === "confirm_required") {
              pendingAgentConfirmRef.current = { call_id: p?.call_id, name: p?.name };
              speak(`Confirmation needed${p?.summary ? ": " + String(p.summary).slice(0, 140) : ""}. Say confirm or cancel.`, 1);
              spoke = true;
            }
          },
          onFallback: async () => {
            agentRouteMissingRef.current = Date.now();
            setChatMessages(p => p.filter(m => m._agentTurn !== turnId));
            setChatThinking(false);
            const text = await sendToKaraRef.current(userText, { ...options, legacy: true, skipUserEcho: true });
            return { text: text || "" };
          },
        });
        if (res?.fallback) return res.text;
        patch(m => ({ ...m, _streaming: false }));
        const finalText = String(res?.text || "").trim();
        if (isVoice && voiceBuf.trim()) { speak(voiceBuf.trim()); spoke = true; }
        if (finalText && !spoke) speak(finalText);
        return finalText;
      } catch (e) {
        patch(m => applyAgentEvent(m, "error", { message: e?.message || String(e) }));
        addLog(`❌ ${aiName} agent: ${e?.message}`, "error");
        if (options.fromVoiceTab) throw e;
        return "";
      } finally {
        setChatThinking(false);
      }
    };

    // Playwright / debugging hook — read-only snapshot, always fresh via refs.
    if (typeof window !== "undefined") {
      window.__karaDebug = {
        getState: () => {
          const L = agentLiveRef.current;
          return {
            tab: L.tab, chartLayout: L.chartLayout, chartSlots: L.chartSlots, chartFocusKey: L.chartFocusKey, focusSym: L.focusSym,
            charts: { scanner: chartVoiceRef.current?.getState?.() || null, slots: [0, 1, 2, 3].map(i => chartRefs.current[i]?.getState?.() || null) },
          };
        },
        runUiAction: (n, i) => runUiActionRef.current?.(n, i),
      };
    }
  }

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div className="kara-root" data-active-tab={tab} style={{background:C.bg,minHeight:"100vh",fontFamily:"'Courier New',monospace",color:C.text,padding:isMobile?"10px":"18px",boxSizing:"border-box",maxWidth:"100vw",overflowX:"hidden"}}>
      <style>{`
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.25}}
        @keyframes blink{0%,100%{opacity:1}50%{opacity:0.4}}
        input:focus,select:focus{outline:1px solid ${C.blue}44}
        ::-webkit-scrollbar{width:4px} ::-webkit-scrollbar-track{background:${C.panel}} ::-webkit-scrollbar-thumb{background:${C.border};border-radius:2px}
      `}</style>

      {popup&&<TradePopup a={popup} onClose={()=>setPopup(null)}/>}
      <AlertsWidget messages={alertMessages} alertVoice={alertVoice} onToggleVoice={()=>setAlertVoice(v=>!v)}/>
      <ElevenLabsLiveWidget
        onLog={addLog}
        apiFetch={apiFetch}
        runUiAction={(n, i) => runUiActionRef.current ? runUiActionRef.current(n, i) : Promise.reject(new Error("UI bridge not ready"))}
        username={authUser?.username || ""}
        recentAlerts={alertMessages.slice(-10).map(m=>`${m.time||""} ${String(m.content||"").replace(/\s+/g," ").slice(0,200)}`)}
        latestEvent={(()=>{ for(let i=alertMessages.length-1;i>=Math.max(0,alertMessages.length-5);i--){ const c=String(alertMessages[i]?.content||""); if(/SIGNAL|AUTO-TRADE|TRADE|WON|LOST|WIN|LOSS/i.test(c)) return `${alertMessages[i].time||""} ${c.replace(/\s+/g," ").slice(0,200)}`; } return ""; })()}
        contextText={`Kara dashboard user: ${authUser?.username || "unknown"}. Active strategy: ${activeStrategy || "none"}. Auto-trade: ${autoTrade ? "ON" : "OFF"}.${accountInfo ? ` Balance: ${Number(accountInfo.balance||0).toFixed(2)} ${accountInfo.currency||""}. Daily PnL: ${Number(accountInfo.dailyPnl||0).toFixed(2)}.` : ""}`}
        onActiveChange={(on)=>{
          // Pause the browser wake-word SR while ElevenLabs holds the mic; restore after.
          if (on && wakeWordRunRef.current) { elevenPausedWakeRef.current = true; toggleWakeWordRef.current?.(); }
          else if (!on && elevenPausedWakeRef.current) { elevenPausedWakeRef.current = false; if (!wakeWordRunRef.current) toggleWakeWordRef.current?.(); }
        }}
      />

      {/* ── DEAD ZONE ALERT ── */}
      {missionStatus?.hour_is_dead_zone && deadZonesEnabled && (
        <div className="kara-deadzone-alert" style={{
          margin: "0 0 16px 0", padding: "14px 22px", borderRadius: 10,
          background: "linear-gradient(90deg, #450a0a, #1a0606)", border: `2px solid #ef4444`, color: "#fecaca",
          display: "flex", alignItems: "center", gap: 18, boxShadow: "0 8px 32px rgba(239, 68, 68, 0.2)"
        }}>
          <div style={{ fontSize: 34, filter: "drop-shadow(0 0 10px #ef4444)" }}>⛔</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 900, fontSize: 17, color: "#fff", textTransform: "uppercase", letterSpacing: 2 }}>
              Scanner Idle: Dead Zone Detected
            </div>
            <div style={{ fontSize: 14, marginTop: 4, opacity: 0.9, fontWeight: 500 }}>
              Session filter is ACTIVE for <b>{activeStrategy?.replace(/\.(md|txt)$/i,"").replace(/_/g," ")}</b>.
              No trades will fire until <b>{String((missionStatus.next_active_hour - 5 + 24) % 24).padStart(2,"0")}:00 (Jamaica)</b>.
            </div>
          </div>
          <div style={{ textAlign: "right", background: "rgba(0,0,0,0.3)", padding: "8px 15px", borderRadius: 8, border: "1px solid rgba(239, 68, 68, 0.3)" }}>
            <div style={{ fontSize: 24, fontWeight: 900, color: "#fff", fontFamily: "monospace" }}>
              {Math.ceil(missionStatus.mins_to_next_active)}m
            </div>
            <div style={{ fontSize: 10, textTransform: "uppercase", opacity: 0.7, fontWeight: 700, letterSpacing: 1 }}>Until Active</div>
          </div>
        </div>
      )}

      {/* ── DAILY PROFIT TARGET REACHED & SHADOW DATA COLLECTION BANNER ── */}
      {scanStatus?.dailyProfitTargetHit && (() => {
        const banked   = scanStatus.accountDailyPnl ?? scanStatus.dailyProfitTarget ?? 200;
        const shadow   = scanStatus.shadowStats?.dailyPnl ?? 0;
        const combined = banked + shadow;
        const sWins    = scanStatus.shadowStats?.wins ?? 0;
        const sLosses  = scanStatus.shadowStats?.losses ?? 0;
        const sTotal   = sWins + sLosses;
        const sWR      = scanStatus.shadowStats?.winRate ?? 0;
        return (
          <div className="kara-profit-target-alert" style={{
            margin: "0 0 16px 0", padding: "14px 22px", borderRadius: 10,
            background: "linear-gradient(90deg, #064e3b, #022c22)", border: "2px solid #10b981", color: "#a7f3d0",
            display: "flex", alignItems: "center", gap: 18, boxShadow: "0 8px 32px rgba(16,185,129,0.25)",
            flexWrap: "wrap"
          }}>
            <div style={{ fontSize: 34, filter: "drop-shadow(0 0 10px #10b981)", flexShrink: 0 }}>🎯</div>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontWeight: 900, fontSize: 17, color: "#fff", textTransform: "uppercase", letterSpacing: 2 }}>
                Daily Target Hit — +${banked.toFixed(2)} Banked — Auto-Trade Paused
              </div>
              <div style={{ fontSize: 13, marginTop: 4, opacity: 0.9, fontWeight: 500 }}>
                Live orders secured. <b>Shadow Trading ACTIVE</b> — edge data collection &amp; ML training until Jamaica midnight reset.
              </div>
            </div>
            {/* ── PnL breakdown chips ── */}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", flexShrink: 0 }}>
              {/* Live banked */}
              <div style={{ textAlign: "center", background: "rgba(0,0,0,0.45)", padding: "8px 14px", borderRadius: 8, border: "1px solid #10b98166" }}>
                <div style={{ fontSize: 20, fontWeight: 900, color: "#10b981", fontFamily: "monospace" }}>
                  +${banked.toFixed(2)}
                </div>
                <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, opacity: 0.8, marginTop: 2 }}>
                  💰 Live Banked
                </div>
              </div>
              {/* Shadow PnL */}
              <div style={{ textAlign: "center", background: "rgba(0,0,0,0.45)", padding: "8px 14px", borderRadius: 8, border: "1px solid #6366f166" }}>
                <div style={{ fontSize: 20, fontWeight: 900, color: shadow >= 0 ? "#818cf8" : "#f87171", fontFamily: "monospace" }}>
                  {shadow >= 0 ? "+" : ""}${shadow.toFixed(2)}
                </div>
                <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, opacity: 0.8, marginTop: 2 }}>
                  👁 Shadow{sTotal > 0 ? ` (${sWins}W/${sLosses}L · ${sWR}%)` : " (0 trades)"}
                </div>
              </div>
              {/* Combined */}
              <div style={{ textAlign: "center", background: "rgba(0,0,0,0.55)", padding: "8px 14px", borderRadius: 8, border: `2px solid ${combined >= 0 ? "#fbbf24" : "#f87171"}` }}>
                <div style={{ fontSize: 22, fontWeight: 900, color: combined >= 0 ? "#fbbf24" : "#f87171", fontFamily: "monospace" }}>
                  {combined >= 0 ? "+" : ""}${combined.toFixed(2)}
                </div>
                <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, opacity: 0.9, marginTop: 2, color: "#fbbf24" }}>
                  ⚡ Combined Day
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── 💰 DAILY $35 CHALLENGE CARD ── */}
      {(() => {
        const ch = challengeData;
        if (!ch) return null;
        const bal = ch.current_balance || 100;
        const todayPnl = ch.today?.pnl ?? ch.today?.daily_pnl ?? 0;
        const dailyTarget = ch.config?.daily_target ?? ch.today?.daily_profit_target ?? 35;
        const dailyStop = ch.config?.daily_stop_loss ?? ch.today?.stop_loss ?? 50;
        const targetProg = Math.min(100, Math.max(0, todayPnl > 0 ? (todayPnl / dailyTarget) * 100 : 0));
        const isTargetHit = ch.today?.target_hit || todayPnl >= dailyTarget;
        const isStopped = ch.today?.stopped || ch.today?.loss_limit_hit || (todayPnl <= -dailyStop);
        const mode = ch.mode || "sim";
        const profitDays = ch.profitable_days ?? (ch.daily_history || []).filter(d => d.target_hit || (d.pnl||d.daily_pnl||0) >= dailyTarget).length;
        const totalDays = ch.total_days ?? ch.current_day ?? 0;
        const streak = (ch.daily_history || []).slice().reverse().reduce((s, d) => {
          if (s.done) return s;
          if ((d.pnl||d.daily_pnl||0) > 0) { s.count++; return s; }
          s.done = true; return s;
        }, { count: 0, done: false }).count;
        const wr = ch.overall?.win_rate ?? (ch.today?.trades > 0 ? ((ch.today?.wins||0)/(ch.today?.trades)*100).toFixed(1) : (ch.db_overall?.trades > 0 ? ((ch.db_overall.wins / ch.db_overall.trades) * 100).toFixed(1) : "60.9"));
        const currentStake = ch.current_stake || Math.min(ch.config?.max_stake || 15, Math.max(ch.config?.min_stake || 2, Math.round(bal * (ch.config?.stake_pct || 0.15) * 100) / 100));
        const isLegacy = ch.legacy;

        const badgeColor = isTargetHit ? "#10b981" : isStopped ? "#ef4444" : mode === "live" ? "#f59e0b" : "#818cf8";
        const badgeBg = isTargetHit ? "#065f46" : isStopped ? "#450a0a" : mode === "live" ? "rgba(245,158,11,0.2)" : "rgba(129,140,248,0.15)";
        const badgeText = isTargetHit ? "TARGET HIT" : isStopped ? "DAILY STOP" : mode === "sim" ? "SIMULATED" : mode === "live" ? "LIVE" : "DEMO";

        return (
          <div className="kara-challenge-card" style={{
            margin: "0 0 16px 0", padding: "16px 22px", borderRadius: 12,
            background: "linear-gradient(135deg, rgba(24, 24, 37, 0.95), rgba(15, 23, 42, 0.98))",
            border: isTargetHit ? "2px solid #10b981" : `1px solid ${mode === "live" ? "rgba(245,158,11,0.4)" : "rgba(129,140,248,0.35)"}`,
            boxShadow: isTargetHit ? "0 8px 32px rgba(16,185,129,0.25)" : "0 8px 32px rgba(0,0,0,0.4)",
            display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 16
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0, flex: "1 1 220px" }}>
              <div style={{
                width: 42, height: 42, flexShrink: 0, borderRadius: "50%",
                background: isTargetHit ? "rgba(16,185,129,0.2)" : "rgba(99,102,241,0.15)",
                border: `2px solid ${isTargetHit ? "#10b981" : badgeColor}`,
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22
              }}>
                {isTargetHit ? "🎯" : isStopped ? "🛑" : "💰"}
              </div>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontWeight: 900, fontSize: 15, color: "#fff", letterSpacing: 1.5, textTransform: "uppercase" }}>
                    {isLegacy ? "$100 → $1K Challenge" : "Daily $35 Challenge"}
                  </span>
                  <span style={{
                    fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 10,
                    background: badgeBg, color: badgeColor, border: `1px solid ${badgeColor}`
                  }}>
                    {badgeText}
                  </span>
                  {streak >= 2 && <span style={{ fontSize: 10, fontWeight: 800, padding: "2px 6px", borderRadius: 10, background: "rgba(16,185,129,0.15)", color: "#10b981", border: "1px solid rgba(16,185,129,0.3)" }}>{streak}d streak</span>}
                </div>
                <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 3 }}>
                  Balance: <b style={{ color: "#f8fafc" }}>${bal.toFixed(2)}</b> • Stake: <b style={{ color: "#f8fafc" }}>${currentStake.toFixed(2)}</b> • <b style={{ color: "#38bdf8" }}>JD50, JD25</b> • {profitDays}/{totalDays} target days
                </div>
              </div>
            </div>

            <div style={{ flex: "1 1 180px", minWidth: 0, maxWidth: "100%" }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, fontWeight: 700, marginBottom: 6, gap: 6, flexWrap: "wrap" }}>
                <span style={{ color: "#94a3b8" }}>Today: <b style={{ color: todayPnl >= 0 ? "#10b981" : "#ef4444", fontSize: 14 }}>{todayPnl >= 0 ? "+" : ""}${todayPnl.toFixed(2)}</b></span>
                <span style={{ color: isTargetHit ? "#10b981" : "#f59e0b" }}>{isTargetHit ? "TARGET HIT" : `${targetProg.toFixed(0)}% to +$${dailyTarget}`}</span>
              </div>
              <div style={{ height: 8, borderRadius: 4, background: "rgba(255,255,255,0.1)", overflow: "hidden", position: "relative" }}>
                <div style={{
                  width: `${Math.max(3, Math.min(100, Math.abs(todayPnl / dailyTarget) * 100))}%`, height: "100%",
                  background: todayPnl >= 0
                    ? (isTargetHit ? "linear-gradient(90deg, #10b981, #34d399)" : "linear-gradient(90deg, #6366f1, #10b981)")
                    : "linear-gradient(90deg, #ef4444, #f87171)",
                  borderRadius: 4, transition: "width 0.5s ease"
                }} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#64748b", marginTop: 3 }}>
                <span>-${dailyStop} stop</span>
                <span>+${dailyTarget} target</span>
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ textAlign: "right", background: "rgba(0,0,0,0.35)", padding: "6px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.08)" }}>
                <div style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", fontWeight: 700 }}>Trades Today</div>
                <div style={{ fontSize: 15, fontWeight: 900, color: "#f8fafc", fontFamily: "monospace" }}>
                  {ch.today?.trades ?? ch.db_today?.trades ?? 0}
                </div>
              </div>
              <div style={{ textAlign: "right", background: "rgba(0,0,0,0.35)", padding: "6px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.08)" }}>
                <div style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", fontWeight: 700 }}>Win Rate</div>
                <div style={{ fontSize: 15, fontWeight: 900, color: "#38bdf8", fontFamily: "monospace" }}>
                  {wr}% <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 500 }}>({ch.today?.wins ?? ch.db_today?.wins ?? 0}W/{ch.today?.losses ?? 0}L)</span>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      {tab!=="voice"&&(() => {
        const chatProvider = userSettings?.agentProviders?.["chat"] || "auto";
        const isGemini = chatProvider === "gemini" || chatProvider.startsWith("gemini_");
        return (
          <KaraWidget 
            open={chatOpen} 
            onToggle={()=>{ setChatOpen(o=>{ if(!o) setChatUnread(0); return !o; }); }} 
            messages={chatMessages} 
            thinking={chatThinking} 
            onSend={sendToKara} 
            voiceState={chatVoice} 
            isSpeaking={isSpeaking}
            onVoice={toggleChatVoice} 
            wakeActive={wakeActive} 
            onToggleWake={toggleWakeWord} 
            unread={chatUnread} 
            micDenied={micDenied} 
            onRequestMic={requestMicAndStart} 
            aiName={aiName}
            isGemini={isGemini}
            liveActive={geminiLive.active}
            onToggleLive={() => geminiLive.active ? geminiLive.stop() : geminiLive.start()}
            personalityMode={personalityMode}
            onPersonalityModeChange={(m)=>{ setPersonalityMode(m); try{ localStorage.setItem(sk("amber_personality_mode"), m); }catch{}; }}
            onAgentConfirm={(callId, approve)=>agentConfirmRef.current?.(callId, approve)}
            runningTasks={runningTasks}
            onTaskCancel={async (taskId)=>{
              try {
                const r = await apiFetch(`/api/tasks/${encodeURIComponent(taskId)}/cancel`, { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ reason:"User cancelled from chat widget" }) });
                const d = await r.json();
                if (r.ok) setChatMessages(p=>[...p,{role:"assistant",content:`🛑 Task ${taskId} cancelled.`,time:formatJaTime(new Date())}].slice(-200));
              } catch(e) { setChatMessages(p=>[...p,{role:"assistant",content:`Cancel failed: ${e.message}`,time:formatJaTime(new Date())}].slice(-200)); }
            }}
          />
        );
      })()}
      {/* 2026-10-02 — DualChatPanel (Hermes) — additive Rule #1, no KaraWidget path touched */}
      <DualChatPanel
        C={C}
        userName={accountInfo?.username || userSettings?.username || (authUser?.username) || "Kenyon"}
        token={typeof window !== "undefined" ? window.localStorage.getItem("kara_auth_token") : null}
        open={chatDualOpen}
        onClose={()=>setChatDualOpen(false)}
        displayName={displayNameFor(authUser)}
        apiFetch={apiFetch}
        runUiAction={(n, i) => runUiActionRef.current ? runUiActionRef.current(n, i) : Promise.reject(new Error("UI bridge not ready"))}
        getContext={() => uiHelpersRef.current.buildAgentContext?.() || {}}
      />
      {/* ── HEADER ── */}
      <div className="kara-header" style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",paddingBottom:14,borderBottom:`1px solid ${C.border}`,flexWrap:"wrap",gap:10,marginBottom:14}}>
        <div style={{minWidth:0,flex:"1 1 220px",maxWidth:"100%"}}>
          <div style={{fontSize:26,fontWeight:900,color:C.amber,letterSpacing:4,lineHeight:1}}>{`⚡ ${isLando ? "LALA SCANNER" : "KARA AI"}`}</div>
          <div style={{fontSize:12,color:C.muted,letterSpacing:2,marginTop:3}}>15M HTF SMC EMA13 • 24/7 REAL-TIME DERIV DATA</div>
          <div style={{fontSize:11, color:C.green, fontWeight:700, letterSpacing:1, marginTop:4, display:"flex", alignItems:"center", gap:6}}>
            <span style={{color:C.muted}}>STRATEGY:</span>
            <span style={{background:`${C.green}15`, padding:"2px 8px", borderRadius:4, border:`1px solid ${C.green}33`}}>
              {(accountInfo?.strategyName || activeStrategy).replace(/\.(md|txt)$/i,"").replace(/_/g," ").toUpperCase()}
            </span>
          </div>
          <div className="kara-header-badges" style={{display:"flex",gap:8,marginTop:8,flexWrap:"wrap",alignItems:"center"}}>
            {/* WS status */}
            <span style={{fontSize:12,padding:"3px 8px",borderRadius:3,background:wsStatus==="connected"?"#022c22":"#1a0a00",color:wsCol,border:`1px solid ${wsCol}44`,fontWeight:700}}>
              {wsIcon} WS {wsStatus.toUpperCase()}
            </span>
            {/* Countdown to auto-trading resume (loss/profit limit or outside hours) — 2026-10-11 */}
            <TradeResumeTimer apiFetch={apiFetch} />
            {/* Server badges */}
            {[["OR",serverHealth.openrouter],["OAI",serverHealth.openai],["N8N",serverHealth.n8n],["TG",serverHealth.telegram],["TTS",serverHealth.piper||serverHealth.openai||serverHealth.groq],["🐋",dockerOk]].map(([l,ok])=>(
              <span key={l} style={{fontSize:10,padding:"2px 6px",borderRadius:3,background:ok?"#022c22":"#1a1a2e",color:ok?C.green:"#334155",border:`1px solid ${ok?C.green+"44":C.border}`}}>{l} {ok?"✓":"○"}</span>
            ))}
            {/* MCP server badges */}
            {[["MT5",serverHealth.mt5_mcp],["TV",serverHealth.tradingview_screener_mcp],["BRAVE",serverHealth.brave_mcp],["DERIV",serverHealth.deriv_mcp]].map(([l,ok])=>(
              <span key={l} title={`${l} MCP Server`} style={{fontSize:10,padding:"2px 6px",borderRadius:3,background:ok?"#0a1628":"#1a1a2e",color:ok?"#38bdf8":"#334155",border:`1px solid ${ok?"#38bdf844":C.border}`}}>{l} {ok?"✓":"○"}</span>
            ))}
            <span style={{fontSize:11,color:C.muted}}>|</span>
            <span style={{fontSize:12,color:C.muted}}>Active: <strong style={{color:C.green}}>{selectedSyms.size}</strong> symbol{selectedSyms.size!==1?"s":""}</span>
          </div>
        </div>

        {/* Market Sessions — all active sessions shown simultaneously */}
        <div className="kara-session-widget" style={{textAlign:"center",minWidth:0,flex:"1 1 220px",maxWidth:"100%"}}>
          <div style={{fontSize:11,color:C.green,letterSpacing:3,fontWeight:700,marginBottom:4}}>⚡ 24/7 SCANNER</div>
          
          {/* Best Trading Hours by Session — strategy-specific, last 30 days */}
          {accountInfo?.hourlyStats && Object.keys(accountInfo.hourlyStats).length > 0 && (()=>{
            const SESSION_RANGES = {LONDON:[7,8,9,10],NY:[13,14,15,16],ASIA:[0,1,2,3,23]};
            const allHours = Object.entries(accountInfo.hourlyStats)
              .filter(([,s])=>s.trades>=2)
              .sort((a,b)=>b[1].winRate-a[1].winRate);
            const top4Overall = allHours.slice(0,4);
            const sessionHours = {};
            Object.entries(SESSION_RANGES).forEach(([sess,hrs])=>{
              const sh = allHours.filter(([h])=>hrs.includes(parseInt(h))).slice(0,4);
              if(sh.length>0) sessionHours[sess]=sh;
            });
            const hasSessions = Object.keys(sessionHours).length>0;
            return (
              <div style={{background:`${C.panel}`,border:`1px solid ${C.border}`,borderRadius:4,padding:"5px 8px",marginBottom:6}}>
                <div style={{fontSize:8,color:C.amber,letterSpacing:1,fontWeight:700,marginBottom:3}}>BEST HOURS (UTC) — {activeStrategy?.replace(/\.(md|txt)$/i,"").toUpperCase()||"STRATEGY"}</div>
                {hasSessions ? Object.entries(sessionHours).map(([sess,hrs])=>(
                  <div key={sess} style={{display:"flex",alignItems:"center",gap:4,marginBottom:2}}>
                    <span style={{fontSize:8,color:C.muted,minWidth:36,fontWeight:700}}>{sess}:</span>
                    <div style={{display:"flex",gap:3,flexWrap:"wrap"}}>
                      {hrs.map(([h,s])=>{
                        const wr=Math.round((s.wins/s.trades)*100);
                        const col=wr>=70?C.green:wr>=55?C.amber:"#94a3b8";
                        return <span key={h} style={{fontSize:9,fontWeight:700,color:col,background:col+"18",borderRadius:3,padding:"1px 4px"}}>{parseInt(h).toString().padStart(2,"0")}:00 {wr}%</span>;
                      })}
                    </div>
                  </div>
                )) : (
                  <div style={{display:"flex",gap:3,flexWrap:"wrap"}}>
                    {top4Overall.map(([h,s])=>{
                      const wr=Math.round((s.wins/s.trades)*100);
                      const col=wr>=70?C.green:wr>=55?C.amber:"#94a3b8";
                      return <span key={h} style={{fontSize:9,fontWeight:700,color:col,background:col+"18",borderRadius:3,padding:"1px 4px"}}>{parseInt(h).toString().padStart(2,"0")}:00 {wr}%</span>;
                    })}
                    <span style={{fontSize:9,color:C.dim}}>({allHours.length} hours tracked)</span>
                  </div>
                )}
                {accountInfo.bestSession&&<div style={{fontSize:8,color:C.dim,marginTop:2}}>Top session: <span style={{color:C.blue,fontWeight:700}}>{accountInfo.bestSession.name}</span> {Math.round(accountInfo.bestSession.winRate)}% win</div>}
              </div>
            );
          })()}

          {session.active ? (
            <>
              <div style={{display:"flex",gap:4,flexWrap:"wrap",justifyContent:"center",marginBottom:3}}>
                {session.sessions.map(s=>(
                  <span key={s.name} style={{fontSize:11,padding:"2px 7px",borderRadius:3,background:s.color+"22",color:s.color,border:`1px solid ${s.color}55`,fontWeight:700,letterSpacing:0.5}}>
                    {s.flag} {s.name}
                  </span>
                ))}
              </div>
              {session.overlap&&<div style={{fontSize:10,color:C.amber,fontWeight:700,letterSpacing:1,marginBottom:2}}>⚡ OVERLAP — HIGH LIQUIDITY</div>}
            </>
          ) : (
            <div style={{fontSize:12,color:C.muted,fontWeight:700}}>
              {session.weekend?"🗓 WEEKEND":"🕐 PRE-MARKET"}
              <div style={{fontSize:11,color:C.dim,marginTop:2,fontWeight:400}}>
                → <span style={{color:C.amber}}>{session.next}</span> in {Math.floor(session.minsTo/60)}h {session.minsTo%60}m
              </div>
            </div>
          )}
          <div style={{fontSize:10,color:"#334155",marginTop:2}}>{formatJaFull(new Date())}</div>

          {/* Current Hour Status — dead zone vs active */}
          {missionStatus && (()=>{
            const h = missionStatus.current_hour_utc;
            const dead = missionStatus.hour_is_dead_zone;
            const col = dead ? C.red : C.green;
            const bg  = dead ? "#1a0a00" : "#022c22";
            return (
              <div style={{marginTop:5,padding:"4px 8px",borderRadius:4,background:bg,border:`1px solid ${col}44`,textAlign:"center"}}>
                <div style={{fontSize:10,fontWeight:700,color:col,letterSpacing:1}}>
                  {dead ? "🔴 DEAD ZONE" : "🟢 ACTIVE HOUR"}
                </div>
                <div style={{fontSize:11,fontWeight:900,color:col}}>
                  H{String(h).padStart(2,"0")}:00 UTC
                </div>
                {dead && <div style={{fontSize:9,color:C.muted,marginTop:1}}>
                  Next active: H{String(missionStatus.next_active_hour).padStart(2,"0")}:00 ({Math.ceil(missionStatus.mins_to_next_active/60)}h)
                </div>}
              </div>
            );
          })()}
        </div>

        {/* Voice + Alerts controls */}
        <div className="kara-header-controls" style={{display:"flex",gap:10,alignItems:"flex-start",flexWrap:"wrap",minWidth:0,flex:"1 1 220px",maxWidth:"100%"}}>
          {/* Alerts toggle */}
          <div style={{textAlign:"center"}}>
            <button
              onClick={()=>setAlertsEnabled(v=>!v)}
              style={{
                padding:"7px 14px",borderRadius:4,cursor:"pointer",fontSize:12,
                fontFamily:"inherit",fontWeight:700,letterSpacing:1,
                background:alertsEnabled?"#022c22":"#1a0a00",
                color:alertsEnabled?C.green:C.red,
                border:`1px solid ${alertsEnabled?C.green+"55":C.red+"55"}`,
                transition:"all 0.2s",
              }}
            >
              {alertsEnabled?"🔔 ALERTS ON":"🔕 ALERTS OFF"}
            </button>
            <div style={{fontSize:11,color:C.dim,marginTop:3}}>
              {alertsEnabled?"Kara notifying":"Silent mode"}
            </div>
          </div>
          {/* Dead Zones toggle */}
          <div style={{textAlign:"center"}}>
            <button
              onClick={()=>setDeadZonesEnabled(true)} /* 2026-10-13: pinned ON by Kenyon (server enforces via settingsLocks.mjs) */
              style={{
                padding:"7px 14px",borderRadius:4,cursor:"default",fontSize:12,
                fontFamily:"inherit",fontWeight:700,letterSpacing:1,
                background:deadZonesEnabled?"#1a0a00":"#0d1f35",
                color:deadZonesEnabled?"#ef4444":C.muted,
                border:`1px solid ${deadZonesEnabled?"#ef444455":C.border}`,
                transition:"all 0.2s",
              }}
              title="Dead zones locked ON — auto-trade only fires in the strategy's proven hours (pinned 2026-10-13; change via invariants.json on Kenyon's directive)"
            >
              {deadZonesEnabled?"⛔ DEAD ZONES ON 🔒":"⛔ DEAD ZONES OFF"}
            </button>
            <div style={{fontSize:11,color:C.dim,marginTop:3}}>
              {deadZonesEnabled?"Blocking low-WR hours":"Trading all hours"}
            </div>
          </div>
          {/* Auto-Trade toggle */}
          <div style={{textAlign:"center"}}>
            <button
              onClick={()=>setAutoTrade(v=>!v)}
              style={{
                padding:"7px 14px",borderRadius:4,cursor:"pointer",fontSize:12,
                fontFamily:"inherit",fontWeight:700,letterSpacing:1,
                background:autoTrade?"#1a0a00":"#0d1f35",
                color:autoTrade?C.amber:C.muted,
                border:`2px solid ${autoTrade?C.amber+"88":C.border}`,
                transition:"all 0.2s",
              }}
              title={autoTrade?`Auto-trade ON — will place real trades on ${getStrategyRequiredCount(strategySchema)}/${getStrategyRequiredCount(strategySchema)} signals`:"Auto-trade OFF — signals only"}
            >
              {autoTrade?"🤖 AUTO ON":"🤖 AUTO OFF"}
            </button>
            <div style={{display:"flex",alignItems:"center",gap:6,marginTop:4,justifyContent:"center"}}>
              <span style={{fontSize:12,color:C.dim,fontWeight:700}}>$</span>
              <input
                type="number" min="0.35" max="50000" step="0.5"
                value={tradeStake}
                onChange={e=>setTradeStake(Math.max(0.35,parseFloat(e.target.value)||1))}
                style={{width:64,fontSize:16,fontWeight:900,background:C.panel,border:`2px solid ${C.amber}`,color:C.text,borderRadius:4,padding:"4px 2px",textAlign:"center",fontFamily:"inherit",outline:"none"}}
                title="Stake per trade (USD)"
              />
              <span style={{fontSize:11,color:C.amber,fontWeight:700,textTransform:"uppercase"}}>stake</span>
            </div>
            {accountInfo&&!accountInfo.error&&(
              <div style={{fontSize:9,fontWeight:700,marginTop:2,color:accountInfo.is_virtual?"#fbbf24":C.green}}>
                {accountInfo.is_virtual?"🎮 DEMO ACCOUNT":"💰 REAL ACCOUNT"}
              </div>
            )}
            {autoTrade && strategySchema?.strategy?.toLowerCase().includes("urus") && Object.keys(urusStatus).length > 0 && (
              <div style={{marginTop:5,fontSize:9,color:"#92400e",lineHeight:1.5,textAlign:"left",background:"#1a0e00",border:"1px solid #78350f55",borderRadius:3,padding:"3px 6px"}}>
                <div style={{color:"#f59e0b",fontWeight:700,marginBottom:2}}>Gates blocking:</div>
                {Object.entries(urusStatus).map(([sym,s])=>{
                  const shortReason = s.reason?.includes("ranging") ? "ranging" : s.reason?.includes("slope") ? "slope↕" : s.reason?.split(":")[0]||"block";
                  return <div key={sym} title={s.reason} style={{whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}><strong style={{color:"#d97706"}}>{sym}:</strong> ⛔ {shortReason}</div>;
                })}
              </div>
            )}
          </div>
          {/* ML Auto-Trade toggle + confidence threshold */}
          <div style={{textAlign:"center"}}>
            <button
              onClick={()=>setMlAutoTrade(v=>!v)}
              style={{
                padding:"7px 14px",borderRadius:4,cursor:"pointer",fontSize:12,
                fontFamily:"inherit",fontWeight:700,letterSpacing:1,
                background:mlAutoTrade?"#0a1a30":"#0d1f35",
                color:mlAutoTrade?"#60a5fa":C.muted,
                border:`2px solid ${mlAutoTrade?"#3b82f6":""+C.border}`,
                transition:"all 0.2s",
              }}
              title={mlAutoTrade?`ML Auto-Trade ON — ML brain trades independently at ≥${mlAutoConfidence}% confidence (no strategy conditions required)`:"ML Auto-Trade OFF — enable to let the ML brain trade independently"}
            >
              {mlAutoTrade?"🧠 ML AUTO ON":"🧠 ML AUTO OFF"}
            </button>
            {/* Confidence threshold input — always visible so user can set before enabling */}
            <div style={{display:"flex",alignItems:"center",gap:5,marginTop:5,justifyContent:"center"}}>
              <span style={{fontSize:11,color:C.dim,fontWeight:700}}>MIN</span>
              <input
                type="number"
                min="55" max="99" step="1"
                value={mlAutoConfidence}
                onChange={e=>{
                  const v=Math.min(99,Math.max(55,parseInt(e.target.value,10)||75));
                  setMlAutoConfidence(v);
                }}
                style={{
                  width:48,fontSize:14,fontWeight:900,textAlign:"center",
                  background:mlAutoTrade?"#0a1a30":C.panel,
                  border:`2px solid ${mlAutoTrade?"#3b82f6":C.border}`,
                  color:mlAutoTrade?"#60a5fa":C.muted,
                  borderRadius:4,padding:"3px 2px",fontFamily:"inherit",outline:"none",
                }}
                title="Minimum ML confidence % to trigger a trade (55–99)"
              />
              <span style={{fontSize:11,color:mlAutoTrade?"#60a5fa":C.dim,fontWeight:700}}>%</span>
            </div>
            <div style={{fontSize:10,color:mlAutoTrade?"#60a5fa":C.dim,marginTop:2,lineHeight:"1.3"}}>
              {mlAutoTrade?`ML brain: ≥${mlAutoConfidence}% confidence`:"ML brain: independent trades"}
            </div>
          </div>
          {/* Trading Timeframe selector */}
          <div style={{textAlign:"center"}}>
            {(()=>{
              const effectiveTf = tradingTf || strategySchema?.timeframes?.entry || "1m";
              const isOverride = !!tradingTf;
              return (<>
                <select
                  value={tradingTf||""}
                  onChange={e=>setTradingTf(e.target.value||null)}
                  style={{padding:"6px 8px",borderRadius:4,fontSize:13,fontWeight:700,fontFamily:"inherit",
                    background:isOverride?"#0a1f0a":"#0d1f35",
                    color:isOverride?C.green:C.muted,
                    border:`2px solid ${isOverride?C.green+"88":C.border}`,cursor:"pointer",outline:"none"}}
                  title="Trading timeframe for signal detection. 'Strategy' uses the active strategy's built-in entry timeframe."
                >
                  <option value="">📐 Strategy TF</option>
                  {["1m","2m","3m","5m","15m","30m","1h","4h"].map(tf=>(
                    <option key={tf} value={tf}>{tf.toUpperCase()}</option>
                  ))}
                </select>
                <div style={{fontSize:9,color:isOverride?C.green:C.dim,marginTop:3,fontWeight:isOverride?700:400}}>
                  {isOverride?`▶ ${effectiveTf.toUpperCase()} override`:`▶ ${effectiveTf.toUpperCase()} (strategy)`}
                </div>
              </>);
            })()}
          </div>
          {/* Voice */}
          <div style={{textAlign:"center"}}>
            <button onClick={toggleVoice} disabled={voiceState==="processing"} style={{...btn(voiceState!=="idle",micC),background:voiceState==="recording"?"#2d1060":voiceState==="processing"?"#0f1f3d":C.panel}}>
              {voiceState==="recording"?"🔴 STOP":voiceState==="processing"?"⏳":"🎤 VOICE"}
            </button>
            <div style={{fontSize:11,color:C.dim,marginTop:3}}>{voiceState==="recording"?"Listening…":voiceState==="processing"?"Processing…":"Say a command"}</div>
          </div>
        </div>
      </div>

      {/* ── User info + logout bar ── */}
      <div style={{display:"flex",alignItems:"center",gap:10,padding:"6px 16px",background:C.panel,borderBottom:`1px solid ${C.border}`,flexWrap:"wrap"}}>
        <span style={{fontSize:11,color:C.dim}}>Signed in as</span>
        <span style={{fontSize:12,fontWeight:700,color:C.amber}}>{displayNameFor(authUser)}</span>
        {/* Live Deriv account info */}
        {accountInfo && !accountInfo.error && (<>
          <span style={{fontSize:11,color:C.border}}>│</span>
          <span style={{fontSize:11,color:C.muted,fontFamily:"'Courier New',monospace"}}>{accountInfo.loginid}</span>
          <span style={{fontSize:12,fontWeight:900,color:C.text}}>{accountInfo.balance.toFixed(2)} {accountInfo.currency}</span>
          <span style={{fontSize:10,padding:"2px 7px",borderRadius:3,fontWeight:700,
            background:accountInfo.is_virtual?"#78350f22":"#022c22",
            color:accountInfo.is_virtual?"#fbbf24":C.green,
            border:`1px solid ${accountInfo.is_virtual?"#fbbf2444":C.green+"44"}`
          }}>{accountInfo.is_virtual?"🎮 DEMO":"💰 REAL"}</span>
          {accountInfo.currentProfit!==undefined&&accountInfo.currentProfit!==0&&<span style={{fontSize:11,fontWeight:700,color:accountInfo.currentProfit>=0?C.green:C.red}}>Open: {accountInfo.currentProfit>=0?"+":""}{accountInfo.currentProfit.toFixed(2)}</span>}
          {(()=>{
            // Show only real-account PnL from Deriv profit_table (D/W/M only, no Tot).
            // Demo accounts always use SQLite (local) PnL and a DEMO badge.
            const isDemo = !!accountInfo.is_virtual;
            const useReal = !isDemo && accountInfo.realDailyPnl !== null && accountInfo.realDailyPnl !== undefined;
            const d = useReal ? accountInfo.realDailyPnl   : accountInfo.dailyPnl;
            const w = useReal ? accountInfo.realWeeklyPnl  : accountInfo.weeklyPnl;
            const m = useReal ? accountInfo.realMonthlyPnl : accountInfo.monthlyPnl;
            const badge = useReal
              ? <span style={{fontSize:9,padding:"1px 4px",borderRadius:2,background:"#022c2288",color:"#34d399",border:"1px solid #34d39944",fontWeight:700,marginLeft:3}}>REAL</span>
              : isDemo
                ? <span style={{fontSize:9,padding:"1px 4px",borderRadius:2,background:"#78350f22",color:"#fbbf24",border:"1px solid #fbbf2444",fontWeight:700,marginLeft:3}}>DEMO</span>
                : null;
            return (<>
              <span style={{fontSize:11,fontWeight:700,color:d>=0?C.green:C.red,display:"flex",alignItems:"center",gap:1}}>D: {d>=0?"+":""}{d!=null?d.toFixed(2):"—"}{badge}</span>
              {w!=null&&<span style={{fontSize:11,fontWeight:700,color:w>=0?C.green:C.red,display:"flex",alignItems:"center",gap:1}}>W: {w>=0?"+":""}{w.toFixed(2)}{badge}</span>}
              {m!=null&&<span style={{fontSize:11,fontWeight:700,color:m>=0?"#a78bfa":C.red,display:"flex",alignItems:"center",gap:1}}>M: {m>=0?"+":""}{m.toFixed(2)}{badge}</span>}
            </>);
          })()}
          {accountInfo.openTradesCount>0&&<span style={{fontSize:11,color:C.amber}}>📊 {accountInfo.openTradesCount} open {accountInfo.openTrades&&accountInfo.openTrades.length>0?`(${accountInfo.openTrades.map(t=>t.symbol).join(", ")})`:""}</span>}
        </>)}
        {accountInfo?.error&&<span style={{fontSize:11,color:C.red}}>⚠ {accountInfo.error}</span>}
        <button onClick={onLogout} style={{marginLeft:"auto",padding:"4px 12px",borderRadius:3,background:"transparent",border:`1px solid #ef444444`,color:"#ef4444",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Logout</button>
      </div>

      {/* ── TV Alert Flash Banner ── */}

      {/* ── TABS ── */}
      <div className="kara-tabs" style={{display:"flex",borderBottom:`1px solid ${C.border}`,marginBottom:14,overflowX:"auto",scrollbarWidth:"none",alignItems:"stretch"}}>
        <button style={tabS("scanner")} onClick={()=>setTab("scanner")}>📊 SCANNER</button>
        <button style={tabS("strategy")} onClick={()=>setTab("strategy")}>🗂 STRATEGY</button>
            <button
              onClick={() => setTab('ml')}
              className={`tab-btn ${tab === 'ml' ? 'active' : ''}`}
              style={tabS("ml")}
            >
              🧠 ML SIGNALS
            </button>
        <button style={tabS("docker")} onClick={()=>setTab("docker")}>🐋 DOCKER{dockerTools.length>0?` (${dockerTools.length})`:""}
          {dockerOk&&<span style={{marginLeft:4,fontSize:9,color:C.green}}>●</span>}
        </button>
        <button style={tabS("advanced_backtest")} onClick={()=>setTab("advanced_backtest")}>🧪 BACKTESTS</button>
        <button style={{...tabS("forge"), color: tab==="forge"?C.amber:"#fbbf24"}} onClick={()=>setTab("forge")}>⚡ FORGE (50+ T/Wk @ 80%)</button>
        <button style={tabS("history")} onClick={()=>setTab("history")}>📋 HISTORY{allPreds.length>0?` (${allPreds.length})`:""}</button>
        <button style={tabS("settings")} onClick={()=>setTab("settings")}>⚙️ SETTINGS</button>
        <button style={tabS("charts")} onClick={()=>setTab("charts")}>📈 CHARTS</button>
        <button style={tabS("info")} onClick={()=>setTab("info")}>📖 INFO</button>
        <button style={tabS("mt5")} onClick={()=>setTab("mt5")}>📊 MT5</button>
        {(authUser?.username==="ken72"||authUser?.username==="lando")&&<button style={tabS("tvdesktop")} onClick={()=>setTab("tvdesktop")}>📺 TRADINGVIEW</button>}
        <button style={tabS("voice")} onClick={()=>setTab("voice")}>🎙️ VOICE</button>
        {/* VOICE tab button removed 2026-09-25 per Kenyon */}
        {/* ── Strategy quick-switcher — always visible in tab bar ── */}
        {strategies.length>0&&(
          <div className="kara-strategy-picker" style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:6,padding:"0 10px",flexShrink:0,borderLeft:`1px solid ${C.border}`}}>
            <span style={{fontSize:10,color:C.muted,letterSpacing:1,whiteSpace:"nowrap"}}>STRATEGY</span>
            <select
              value={activeStrategy}
              onChange={e=>{ if(e.target.value&&e.target.value!==activeStrategy) handleStrategyActivate(e.target.value); }}
              style={{background:C.panel,border:`1px solid ${C.green}55`,color:C.green,fontSize:11,padding:"3px 8px",borderRadius:4,cursor:"pointer",fontFamily:"inherit",fontWeight:700,maxWidth:200,outline:"none"}}
              title="Switch active strategy"
            >
              {strategies.map(s=>(
                <option key={s.name} value={s.name} style={{background:C.bg,color:s.name===activeStrategy?C.green:C.text}}>
                  {s.name===activeStrategy?"✓ ":""}{s.name.replace(/\.md$/i,"").replace(/_/g," ")}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* ════════════ SCANNER TAB ════════════ */}
      {tab==="scanner"&&(
        <>
          {/* ── Continuous Forge Live Status Banner ── */}
          <div
            onClick={() => setTab("forge")}
            style={{
              background: "linear-gradient(90deg, #131b2a 0%, #1e293b 100%)",
              border: `1px solid #f59e0b55`,
              borderRadius: 8,
              padding: "10px 16px",
              marginBottom: 14,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              cursor: "pointer",
              transition: "all 0.15s"
            }}
            title="Click to view Continuous Strategy Forge & Basket Evolution"
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ fontSize: 16 }}>⚡</span>
              <span style={{ fontSize: 12, fontWeight: 900, color: "#f59e0b", letterSpacing: 1 }}>
                CONTINUOUS FORGE
              </span>
              <span style={{ fontSize: 11, color: "#94a3b8" }}>
                Target: <b style={{ color: "#f8fafc" }}>≥50 T/Wk @ 80% WR</b>
              </span>
              <span style={{ fontSize: 10, background: "#022c22", color: "#10b981", border: "1px solid #10b98144", padding: "1px 6px", borderRadius: 3, fontWeight: 700 }}>
                CRON ACTIVE (*/20M)
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11 }}>
              <span style={{ color: "#38bdf8", fontWeight: 700 }}>Synthetics & Forex Baskets Evolving</span>
              <span style={{ color: "#f59e0b", fontWeight: 800 }}>OPEN FORGE ➔</span>
            </div>
          </div>

          {/* ── Symbol Picker ── */}
          <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:"14px 16px",marginBottom:14}}>
            {/* Header row */}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10,flexWrap:"wrap",gap:8}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <div style={{fontSize:12,color:C.muted,letterSpacing:2}}>
                  MARKET SYMBOLS
                  <span style={{color:C.dim,fontWeight:400,letterSpacing:0,fontSize:11,marginLeft:6}}>
                    — {selectedSyms.size}/{SYMBOLS.length} active
                  </span>
                </div>
              </div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                <button onClick={()=>SYMBOLS.forEach(s=>{ if (!selectedSyms.has(s)) toggleSymbol(s); })} style={{...btn(false),padding:"5px 12px",fontSize:11}}>ADD ALL</button>
                <button onClick={()=>{ SYMBOLS.forEach(s=>{ if (selectedSyms.has(s)) unsubscribeSymbol(s); }); setSelectedSyms(new Set()); }} style={{...btn(false,C.red),padding:"5px 12px",fontSize:11}}>CLEAR ALL</button>
              </div>
            </div>
            {/* Search filter */}
            <div style={{marginBottom:12}}>
              <input
                id="symbol-search"
                type="text"
                placeholder="🔍  Filter symbols... (e.g. EUR, GOLD, BTC, BOOM)"
                onChange={e=>{
                  const v=e.target.value.toUpperCase().trim();
                  // store in a dataset attribute so the groups can read it without adding state
                  document.getElementById("symbol-picker-groups").dataset.filter=v;
                  // trigger re-render of visibility via class
                  document.querySelectorAll("[data-sym-btn]").forEach(el=>{
                    const sym=el.dataset.symBtn||"";
                    const name=(SYM_NAMES[sym]||"").toUpperCase();
                    el.style.display=(!v||sym.includes(v)||name.includes(v))?"":"none";
                  });
                  document.querySelectorAll("[data-sym-grp]").forEach(el=>{
                    const btns=el.querySelectorAll("[data-sym-btn]");
                    const anyVisible=[...btns].some(b=>b.style.display!=="none");
                    el.style.display=anyVisible?"":"none";
                  });
                }}
                style={{
                  width:"100%",background:C.bg,border:`1px solid ${C.border}`,
                  borderRadius:5,padding:"7px 12px",color:C.text,fontSize:12,
                  fontFamily:"inherit",outline:"none",boxSizing:"border-box",
                }}
              />
            </div>
            {/* Groups */}
            <div id="symbol-picker-groups" data-filter="">
            {displayGroups.map(grp=>{
              const isCollapsed = !grp.primary && !!collapsedGroups[grp.label];
              const activeInGrp = grp.syms.filter(s=>selectedSyms.has(s)).length;
              return (
              <div key={grp.label} data-sym-grp={grp.label} style={{marginBottom: isCollapsed ? 4 : 12}}>
                {/* Group header row */}
                <div
                  onClick={!grp.primary ? ()=>toggleGroupCollapse(grp.label) : undefined}
                  style={{display:"flex",alignItems:"center",gap:8,marginBottom: isCollapsed ? 0 : 6,
                    cursor: grp.primary ? "default" : "pointer",
                    padding: grp.primary ? "0" : "4px 6px",
                    borderRadius: grp.primary ? 0 : 4,
                    background: (!grp.primary && isCollapsed) ? C.panel : "transparent",
                    border: (!grp.primary && isCollapsed) ? `1px solid ${C.border}` : "none",
                    transition:"background 0.15s",
                  }}
                >
                  {/* Collapse toggle arrow for non-primary */}
                  {!grp.primary && (
                    <div style={{fontSize:9,color:C.dim,width:10,flexShrink:0,transition:"transform 0.2s",
                      transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)"}}>▼</div>
                  )}
                  <div style={{fontSize:10,color:grp.color,letterSpacing:2,opacity:0.85,flex:1,
                    fontWeight: grp.primary ? 600 : 500}}>{grp.label}</div>
                  {/* Active count badge when collapsed */}
                  {isCollapsed && activeInGrp > 0 && (
                    <div style={{fontSize:9,background:grp.color+"22",color:grp.color,padding:"1px 6px",
                      borderRadius:10,border:`1px solid ${grp.color}44`,fontWeight:700}}>
                      {activeInGrp} active
                    </div>
                  )}
                  {/* Count of all syms when collapsed */}
                  {isCollapsed && (
                    <div style={{fontSize:9,color:C.dim}}>{grp.syms.length} syms</div>
                  )}
                  {/* + all / ✕ none buttons (only when expanded) */}
                  {!isCollapsed && <>
                    <button
                      onClick={e=>{e.stopPropagation();grp.syms.forEach(s=>{ if (!selectedSyms.has(s)) toggleSymbol(s); });}}
                      style={{fontSize:9,padding:"2px 7px",border:`1px solid ${grp.color}44`,borderRadius:3,
                        background:"transparent",color:grp.color,cursor:"pointer",fontFamily:"inherit",opacity:0.7}}
                    >+ all</button>
                    <button
                      onClick={e=>{e.stopPropagation();grp.syms.forEach(s=>{ if (selectedSyms.has(s)) unsubscribeSymbol(s); });}}
                      style={{fontSize:9,padding:"2px 7px",border:`1px solid ${C.red}44`,borderRadius:3,
                        background:"transparent",color:C.red,cursor:"pointer",fontFamily:"inherit",opacity:0.7}}
                    >✕ none</button>
                  </>}
                </div>
                {/* Symbol buttons — hidden when collapsed */}
                {!isCollapsed && (
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {grp.syms.map(s=>{
                    const active=selectedSyms.has(s);
                    const d=symData[s];
                    const price=d?.analysis?.price;
                    const change=d?.analysis?.change;
                    const acc=grp.color;
                    return (
                      <button key={s} data-sym-btn={s} onClick={()=>toggleSymbol(s)} title={SYM_NAMES[s]||s} style={{
                        background:active?(focusSym===s?acc+"33":acc+"15"):C.bg,
                        border:`1px solid ${active?(focusSym===s?acc:acc+"55"):C.border}`,
                        color:active?acc:C.muted, padding:"7px 12px", borderRadius:5, cursor:"pointer",
                        fontFamily:"inherit", fontWeight:700, fontSize:12, transition:"all 0.15s",
                        minWidth:82, textAlign:"center",
                      }}>
                        <div>{s}</div>
                        {d?.loading&&<div style={{fontSize:9,color:C.amber,marginTop:1}}>⏳</div>}
                        {price&&!d.loading&&(
                          <div style={{fontSize:9,color:change>=0?C.green:C.red,marginTop:1}}>
                            {fmt(price,price>500?1:price>10?3:5)} {change>=0?"▲":"▼"}
                          </div>
                        )}
                        {!active&&!d?.loading&&!price&&<div style={{fontSize:8,color:C.dim,marginTop:1}}>+ add</div>}
                        {d?.error&&<div style={{fontSize:8,color:C.red,marginTop:1}}>✕ err</div>}
                      </button>
                    );
                  })}
                </div>
                )}
              </div>
              );
            })}
            </div>

          </div>

          {/* ── Active Trades Panel ── */}
          <ActiveTradesPanel trades={activeTrades} onClose={closeTrade} onRefresh={pollActiveTrades} />

          {selectedSyms.size===0&&(
            <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:"48px 0",textAlign:"center",marginBottom:14}}>
              <div style={{fontSize:32,marginBottom:12}}>📊</div>
              <div style={{fontSize:18,color:C.muted,fontWeight:700}}>No symbols selected</div>
              <div style={{fontSize:14,color:C.dim,marginTop:8}}>Click symbols above to start monitoring real-time price data</div>
              <div style={{fontSize:13,color:C.blue,marginTop:6}}>24/7 Scanning • 15m HTF Filter • 1m Entry • Real OHLC data</div>
            </div>
          )}

          {/* ── Symbol Cards Grid ── */}
          {selectedSyms.size>0&&(()=>{
            const cardReqCnt=getStrategyRequiredCount(strategySchema);
            // KENYON 2026-10-11: viewport-aware card grid — on mobile always stack to 1 column so
            //   the cards don't get squeezed into 1/3-width unreadable ribbons.
            const cardCols = isMobile ? 1 : Math.min(selectedSyms.size, 3);
            return (
            <div className="kara-symbol-cards" style={{display:"grid",gridTemplateColumns:`repeat(${cardCols},1fr)`,gap:isMobile?8:12,marginBottom:14}}>
              {displaySymbols.filter(s=>selectedSyms.has(s)).map(s=>{
                const d=symData[s]||{};
                const a=d.analysis;
                const conds=d.conds||Array(10).fill("pending");
                const cnt=d.cnt??0;
                const isFocused=focusSym===s;
                const isAIing=aiScanning===s;
                const acc=a?.direction==="CALL"?C.green:a?.direction==="PUT"?C.red:C.muted;
                return (
                  <div key={s} onClick={()=>setFocusSym(s)} style={{background:C.panel,border:`2px solid ${isFocused?C.amber:cnt>=cardReqCnt?C.green+"88":cnt>=cardReqCnt-2?C.amber+"66":cnt>=Math.floor(cardReqCnt*0.6)?C.border:C.border}`,borderRadius:10,padding:16,cursor:"pointer",transition:"border 0.2s"}}>
                    {/* Card header */}
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                      <div>
                        <div style={{display:"flex",alignItems:"center",gap:6}}>
                          <div style={{fontSize:16,fontWeight:900,color:isFocused?C.amber:"#94a3b8",letterSpacing:2}}>{s}</div>
                          {symbolWr[s] && (()=>{
                            const wr = symbolWr[s].win_rate_pct;
                            const trades = symbolWr[s].trades;
                            const col = wr>=70?C.green:wr>=55?C.amber:C.red;
                            return <span title={`Live WR from ${trades} trades`} style={{fontSize:10,fontWeight:700,padding:"1px 5px",borderRadius:3,background:col+"18",color:col,border:`1px solid ${col}44`}}>{wr.toFixed(1)}%</span>;
                          })()}
                        </div>
                        {d.loading&&<div style={{fontSize:12,color:C.amber,marginTop:2}}>⏳ Loading candles...</div>}
                        {d.error&&<div style={{fontSize:12,color:C.red,marginTop:2}}>❌ {d.error}</div>}
                      </div>
                      <div style={{textAlign:"right"}}>
                        <div style={{fontSize:11,color:C.muted,marginBottom:2}}>{cnt}/{cardReqCnt} conditions</div>
                        <div style={{height:4,width:80,background:C.border,borderRadius:2,overflow:"hidden"}}>
                          <div style={{width:`${Math.min(100,cnt/cardReqCnt*100)}%`,height:"100%",background:cnt>=cardReqCnt?C.green:cnt>=cardReqCnt-2?C.amber:cnt>=Math.floor(cardReqCnt*0.6)?"#f97316":C.red,borderRadius:2,transition:"width 0.4s"}}/>
                        </div>
                      </div>
                    </div>

                    {a&&(
                      <>
                        {/* Price */}
                        <div style={{display:"flex",alignItems:"baseline",gap:8,marginBottom:8}}>
                          <div style={{fontSize:28,fontWeight:900,color:acc,lineHeight:1}}>{fmtPrice(a.price)}</div>
                          <div style={{fontSize:13,color:a.change>=0?C.green:C.red,fontWeight:700}}>
                            {a.change>=0?"▲":"▼"} {fmtPrice(Math.abs(a.change))}
                            <span style={{fontSize:11,color:C.muted,fontWeight:400,marginLeft:4}}>({fmt(Math.abs(a.changePct),3)}%)</span>
                          </div>
                        </div>

                        {/* Direction badge */}
                        <div style={{display:"inline-block",padding:"3px 10px",borderRadius:4,background:acc+"22",border:`1px solid ${acc}44`,fontSize:13,fontWeight:700,color:acc,marginBottom:10}}>
                          {a.direction==="CALL"?"📈 CALL — BUY":a.direction==="PUT"?"📉 PUT — SELL":"⚖️ NO BIAS"} &nbsp;•&nbsp; {a.trend?.toUpperCase()||"—"}
                        </div>

                        {/* 15m HTF bar */}
                        {(()=>{
                          const h=d.htf15;
                          if (!h) return <div style={{fontSize:11,color:C.muted,marginBottom:8,padding:"4px 8px",background:C.bg,borderRadius:4,border:`1px solid ${C.border}`}}>📐 15m HTF: loading...</div>;
                          const tCol=h.trend==="bullish"?C.green:h.trend==="bearish"?C.red:C.muted;
                          const gated=h.ob&&h.fvg;
                          const htfDir=h.trend==="bullish"?"CALL":h.trend==="bearish"?"PUT":"NONE";
                          const aligned=htfDir===a.direction;
                          return (
                            <div style={{fontSize:11,marginBottom:8,padding:"5px 8px",background:"#050d1a",borderRadius:4,border:`1px solid ${gated&&aligned?C.green+"55":C.amber+"44"}`,display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                              <span style={{color:C.amber,letterSpacing:1,fontWeight:700}}>📐 C1·15m:</span>
                              <span style={{color:tCol,fontWeight:700,letterSpacing:1}}>{h.trend?.toUpperCase()||"—"}</span>
                              <span style={{color:C.border}}>│</span>
                              <span style={{color:h.ob?C.green:C.red}}>OB {h.ob?"✅":"❌"}</span>
                              <span style={{color:h.fvg?C.green:C.red}}>FVG {h.fvg?"✅":"❌"}</span>
                              <span style={{color:gated&&aligned?C.green:C.amber,fontWeight:700}}>{gated&&aligned?"✅ ENTRY VALID":!aligned?"⛔ WRONG TREND":"⚠️ OB/FVG NEEDED"}</span>
                            </div>
                          );
                        })()}

                        {/* Indicators */}
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"4px 12px",fontSize:12,marginBottom:10}}>
                          {[
                            ["EMA13", fmtPrice(a.ema13)],
                            ["EMA200", a.ema200 ? fmtPrice(a.ema200) : "< 200 bars"],
                            ["TMA Mid", a.tma ? fmtPrice(a.tma.mid) : "—"],
                            ["TMA Up/Lo", a.tma ? `${fmtPrice(a.tma.upper)} / ${fmtPrice(a.tma.lower)}` : "—"],
                            ["HAS", a.has ? (a.has.color === "#38bdf8" ? "BULL 🟦" : "BEAR 🟪") : "—"],
                            ["Aroon U/D", a.aroon ? `${Math.round(a.aroon.up)} / ${Math.round(a.aroon.down)}` : "—"],
                            ["Pattern", a.bheurekso || "none"],
                            ["ATR", fmt(a.atr, a.atr > 1 ? 4 : 6)]
                          ].map(([l,v])=>(
                            <div key={l} style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={`${l}: ${v}`}>
                              <span style={{color:"#475569"}}>{l}: </span>
                              <span style={{color:"#94a3b8"}}>{v}</span>
                            </div>
                          ))}
                        </div>

                        {/* Swing high/low */}
                        <div style={{display:"flex",gap:12,fontSize:12,marginBottom:10}}>
                          <span style={{color:C.red}}>▲ H: {fmtPrice(a.swing_high)}</span>
                          <span style={{color:C.green}}>▼ L: {fmtPrice(a.swing_low)}</span>
                        </div>

                        {/* Events */}
                        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:10}}>
                          {a.liq_sweep&&<span style={{fontSize:11,color:C.amber,background:"#1a1200",border:`1px solid ${C.amber}44`,padding:"2px 7px",borderRadius:3}}>💧 LIQ SWEEP ({a.liq_sweep_dir})</span>}
                          <span style={{fontSize:11,color:a.direction==="CALL"?C.green:C.red,background:C.bg,border:`1px solid ${C.border}`,padding:"2px 7px",borderRadius:3}}>{a.in_discount?"📉 DISCOUNT ZONE":"📈 PREMIUM ZONE"}</span>
                        </div>

                        {/* Conditions mini-grid */}
                        <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:3,marginBottom:10}}>
                          {conds.map((c,i)=>(
                            <div key={i} title={strategySchema?.conditions?.[i]?.label??CONDITIONS[i]} style={{height:6,background:cC(c),borderRadius:2,opacity:c==="pending"?0.5:1}}/>
                          ))}
                        </div>

                        {/* AI data if available */}
                        {d.aiData&&(
                          <div style={{background:"#0a1628",border:`1px solid ${C.blue}44`,borderRadius:4,padding:"6px 10px",fontSize:12,marginBottom:8}}>
                            <span style={{color:C.blue}}>🤖 AI:</span> <span style={{color:C.muted}}>FVG:{d.aiData.fvg?"✅":"❌"} OB:{d.aiData.ob?"✅":"❌"} {d.aiData.bos_choch} R:R={fmt(d.aiData.r_r,1)}</span>
                            <div style={{color:"#475569",marginTop:2}}>{d.aiData.comment}</div>
                          </div>
                        )}

                        {/* AI scan button */}
                        <button onClick={e=>{e.stopPropagation();runAIScan(s);}} disabled={isAIing} style={{...btn(false,C.blue),padding:"5px 12px",fontSize:12,width:"100%"}}>
                          {isAIing?"⏳ AI scanning...":"🤖 AI SCAN (FVG/OB)"}
                        </button>
                      </>
                    )}
                    {!a&&!d.loading&&!d.error&&<div style={{fontSize:13,color:C.dim,textAlign:"center",padding:"20px 0"}}>Waiting for data...</div>}
                    </div>
                    );
                    })}
                    </div>
                    );
                    })()}

          {/* KENYON 2026-10-11: mobile empty-state panel — fills the void between the header pills and the
              Telegram toggles when no chart is rendering and no conditions panel exists. Honest, dense,
              shows live numbers so the trader knows the scanner is alive even when offline. */}
          {isMobile && selectedSyms.size > 0 && !focused?.candles?.length && (
            <div className="kara-mobile-empty" style={{
              background: "#0a1628",
              border: `1px solid ${wsStatus === "connected" ? C.green : C.amber}55`,
              borderRadius: 10,
              padding: "14px 14px",
              marginBottom: 14,
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <div style={{width:8,height:8,borderRadius:"50%",background:wsStatus==="connected"?C.green:C.amber,boxShadow:`0 0 8px ${wsStatus==="connected"?C.green:C.amber}66`}} />
                <div style={{fontSize:12,letterSpacing:2,color:C.muted,fontWeight:700}}>
                  {wsStatus==="connected" ? "SCANNER LIVE — OFFLINE CHART" : "WS DISCONNECTED — RECONNECTING"}
                </div>
              </div>
              <div style={{fontSize:13,color:C.dim,lineHeight:1.5}}>
                {wsStatus === "connected"
                  ? <>Chart needs <b style={{color:C.amber}}>Deriv live candles</b> to render. Scanner is computing signals — tap any symbol card below for live WR / conditions.</>
                  : <>Live chart hidden during WS reconnect (5–60s). The scanner is still watching your {selectedSyms.size} symbol{selectedSyms.size!==1?"s":""} and will fire signals when conditions align.</>}
              </div>
              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                {Array.from(selectedSyms).slice(0, 6).map(s => {
                  const d = symData[s] || {};
                  const cnt = d.cnt ?? 0;
                  const dir = d.analysis?.direction;
                  const dirCol = dir === "CALL" ? C.green : dir === "PUT" ? C.red : C.muted;
                  const focusOnClick = () => { setFocusSym(s); };
                  return (
                    <button key={s} onClick={focusOnClick} style={{
                      padding: "5px 9px", borderRadius: 6, fontSize: 11, fontFamily: "inherit",
                      fontWeight: 700, cursor: "pointer", background: focusSym===s?`${C.amber}22`:`${C.border}22`,
                      border: `1px solid ${focusSym===s?C.amber:C.border}`,
                      color: focusSym===s?C.amber:C.dim, display:"flex",gap:5,alignItems:"center",
                    }}>
                      <span style={{color:C.text}}>{s}</span>
                      <span style={{color: dirCol}}>{dir==="CALL"?"▲":dir==="PUT"?"▼":"·"}</span>
                      <span style={{color:cnt>=8?C.green:cnt>=6?C.amber:C.red}}>{cnt}/10</span>
                    </button>
                  );
                })}
              </div>
              {focusSym && (
                <button onClick={() => setTab("charts")} style={{
                  padding:"10px 14px",borderRadius:6,fontSize:12,fontFamily:"inherit",fontWeight:700,
                  cursor:"pointer",background:`${C.blue}22`,border:`1px solid ${C.blue}55`,color:C.blue,
                  letterSpacing:1,alignSelf:"flex-start",
                }}>📈 OPEN CHART VIEW →</button>
              )}
            </div>
          )}

          {/* ── Chart Widget ── */}
          {focused?.candles?.length > 0 && (
            <div className="kara-chart-area" style={{position:"relative"}}>
              {focused.offline && <div style={{color:C.amber,padding:8,fontWeight:700}}>⚠️ OFFLINE HISTORICAL CHART — last candle {focused.lastEpoch ? new Date(focused.lastEpoch*1000).toLocaleString("en-JM",{timeZone:"America/Jamaica"}) : "unknown"} Jamaica time. No live signals or chart trades.</div>}
              <ChartWidget
                ref={chartVoiceRef}
                symbol={focusSym}
                timeframe={scannerTf || "1m"}
                data={symData[focusSym]}
                height={400}
                stake={tradeStake}
                lastPrice={symData[focusSym]?.analysis?.price ?? null}
                signalMarkerRef={signalMarkerRef}
                onBuy={payload => executeTrade(payload)}
                onSell={payload => executeTrade(payload)}
                activeTrades={activeTrades.filter(t => {
                  const tSym = Object.keys(SYMBOL_API).find(k => SYMBOL_API[k] === t.symbol) || t.symbol;
                  return tSym === focusSym;
                })}
                onCloseTrade={closeTrade}
                onMlPredict={async (sym) => {
                  const d = symDataRef.current[sym];
                  const a = d?.analysis;
                  const sltp = a ? computeSLTP(a, d?.aiData) : {};
                  const si = sessionRef.current;
                  const payload = a ? { symbol: sym, price: a.price, ema13: a.ema13, ema200: a.ema200, atr: a.atr, atr_sma: a.atr_sma, rsi: a.rsi, trend: a.trend, bos_choch: a.bos_choch || "none", liq_sweep: a.liq_sweep || false, direction: a.direction || "NONE", session: si?.name || "other", htf_ob: d?.htf15?.ob || false, htf_fvg: d?.htf15?.fvg || false, rr_ratio: sltp.rr || 0, conditions_passed: d?.cnt || 0, ema13_pullback: a.ema13_pullback_buy || a.ema13_pullback_sell || false, zone: a.zone || "neutral", candle_open: a.open, candle_high: a.high, candle_low: a.low, candle_close: a.close } : { symbol: sym };
                  try {
                    const r = await apiFetch("/api/ml/predict", { method: "POST", headers: {"Content-Type":"application/json"}, body: JSON.stringify(payload) });
                    const result = await r.json();
                    // ML engine returns prediction: 0|1 — ChartWidget needs "CALL"|"PUT"
                    const enriched = {
                      ...result,
                      prediction: typeof result.prediction === 'number'
                        ? (a?.direction || "CALL")
                        : result.prediction,
                      atr: result.atr || a?.atr,
                    };
                    setMlPredictSym(sym);
                    setMlPredictResult(enriched);
                    return enriched;
                  } catch(e) { console.error("ML predict:", e); return {error: e.message}; }
                }}
                // ── kara-chart-voice: callbacks for voice orchestrator ──
                onRunAnalyze={async (sym) => {
                  try { const r = await runAIScan(sym); return r?.analysisText || "Analysis complete."; }
                  catch (e) { return "Analysis failed: " + e.message; }
                }}
                onRunFindTrade={async (sym) => {
                  try {
                    const ml = await apiFetch("/api/ml/predict", { method: "POST", headers: {"Content-Type":"application/json"}, body: JSON.stringify({ symbol: sym }) }).then(r => r.json()).catch(() => null);
                    const d = symDataRef.current[sym];
                    const reqCnt = getStrategyRequiredCount(strategySchemaRef.current);
                    const cnt = d?.cnt ?? 0;
                    const dir = d?.analysis?.direction || "NONE";
                    return { direction: dir, confidence: ml?.confidence ?? null, conditions: { met: cnt, required: reqCnt } };
                  } catch (e) { return { text: "Scan failed: " + e.message }; }
                }}
                onTrade={async (sym, direction) => {
                  if (!sym) return;
                  try { await executeTrade({ symbol: sym, direction }); }
                  catch (e) { addLog("🎤 Voice trade error: " + e.message, "error"); }
                }}
                onCloseSymbol={async (sym) => {
                  if (!sym) return;
                  const symTrades = activeTrades.filter(t => {
                    const ts = Object.keys(SYMBOL_API).find(k => SYMBOL_API[k] === t.symbol) || t.symbol;
                    return ts === sym;
                  });
                  for (const t of symTrades) { try { await closeTrade(t); } catch (e) {} }
                }}
                onCloseAll={async () => {
                  for (const t of activeTrades) { try { await closeTrade(t); } catch (e) {} }
                }}
                onClearIndicators={() => {
                  try {
                    const ev = new CustomEvent("kara-chart-clear-indicators");
                    window.dispatchEvent(ev);
                  } catch (e) {}
                }}
                onSpeak={(txt, pri) => speak(txt, pri || 1)}
                onSetSymbol={(sym) => { if (sym && SYMBOLS.includes(sym)) { setFocusSym(sym); setSelectedSyms(prev => new Set([...prev, sym])); } }}
                onSetTimeframe={(tf) => { if (tf && CHART_TIMEFRAMES.includes(tf)) setScannerTf(tf); }}
                                strategyIndicators={{
                  cnt: focused.cnt ?? 0,
                  direction: focused.analysis?.direction || "NONE",
                  signal: (focused.cnt ?? 0) >= getStrategyRequiredCount(strategySchemaRef.current),
                  reqCnt: getStrategyRequiredCount(strategySchemaRef.current),
                }}
                onEvalConditions={handleEvalConditions}
                condEvalResult={condEvalSym === focusSym ? condEvalResult : null}
              />
              {condEvalSym === focusSym && condEvalResult && (
                <ConditionEvaluationPopup
                  symbol={condEvalSym}
                  conditions={condEvalResult.conditions}
                  count={condEvalResult.count}
                  required={condEvalResult.required}
                  signalReady={condEvalResult.signalReady}
                  direction={condEvalResult.direction}
                  price={condEvalResult.price}
                  strategyName={condEvalResult.strategyName}
                  autoTrade={autoTrade}
                  lastUpdated={condEvalUpdatedAt}
                  onClose={() => { setCondEvalSym(null); setCondEvalResult(null); }}
                  onExecute={async (sym, direction) => {
                    const r = await apiFetch("/api/trades/manual-execute", {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ symbol: sym, direction, stake: tradeStake }),
                    });
                    return r.json();
                  }}
                />
              )}
            </div>
          )}

          {/* ── 2-col: Conditions + Alerts ── */}
          {focused?.analysis&&(
            <div className="kara-conditions-row" style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:isMobile?10:12,marginBottom:14}}>
              {/* Conditions detail */}
              {(()=>{
                const condReqCnt = getStrategyRequiredCount(strategySchema);
                const condLabels = strategySchema?.conditions?.map(c=>c.label) ?? CONDITIONS;
                return (
              <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:16}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <div style={{fontSize:12,color:C.muted,letterSpacing:2}}>CONDITIONS — <span style={{color:C.amber}}>{focusSym}</span></div>
                  <div style={{fontSize:20,fontWeight:900,color:focused.cnt>=condReqCnt?C.green:focused.cnt>=condReqCnt-2?C.amber:C.red}}>{focused.cnt}/{condReqCnt}</div>
                </div>
                <div style={{height:6,background:C.border,borderRadius:3,overflow:"hidden",marginBottom:12}}>
                  <div style={{width:`${Math.min(100,(focused.cnt||0)/condReqCnt*100)}%`,height:"100%",background:focused.cnt>=condReqCnt?C.green:focused.cnt>=condReqCnt-2?C.amber:focused.cnt>=Math.floor(condReqCnt*0.6)?"#f97316":C.red,transition:"width 0.6s ease",borderRadius:3}}/>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {condLabels.map((cname,i)=>{
                    const c=focused.conds?.[i]??"pending";
                    return (
                      <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 8px",background:c==="pass"?C.green+"0a":c==="warn"?C.amber+"0a":C.bg,borderRadius:4}}>
                        <span style={{fontSize:14}}>{cI(c)}</span>
                        <span style={{fontSize:13,color:cC(c),flex:1}}>{cname}</span>
                        {c==="pending"&&<span style={{fontSize:10,color:C.blue}}>{i===0?"Loading…":"Pending"}</span>}
                      </div>
                    );
                  })}
                </div>
                {/* 15m HTF detail */}
                {(()=>{
                  const h=symData[focusSym]?.htf15;
                  if (!h) return null;
                  const tCol=h.trend==="bullish"?C.green:h.trend==="bearish"?C.red:C.muted;
                  const gated=h.ob&&h.fvg;
                  const htfDir=h.trend==="bullish"?"CALL":h.trend==="bearish"?"PUT":"NONE";
                  const focusDir=focused.analysis?.direction;
                  const aligned=htfDir===focusDir;
                  return (
                    <div style={{marginTop:10,padding:"8px 12px",background:"#050d1a",border:`1px solid ${gated&&aligned?C.green+"55":C.amber+"44"}`,borderRadius:6}}>
                      <div style={{fontSize:10,color:C.amber,letterSpacing:2,marginBottom:6,fontWeight:700}}>📐 C1 · 15M HTF CONFIRMATION GATE</div>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6}}>
                        {[
                          ["TREND", h.trend.toUpperCase(), tCol],
                          ["OB",    h.ob?"DETECTED":"MISSING", h.ob?C.green:C.red],
                          ["FVG",   h.fvg?"DETECTED":"MISSING", h.fvg?C.green:C.red],
                        ].map(([l,v,col])=>(
                          <div key={l} style={{background:C.panel,borderRadius:4,padding:"5px 8px",border:`1px solid ${col}33`}}>
                            <div style={{fontSize:9,color:C.muted,letterSpacing:1}}>{l}</div>
                            <div style={{fontSize:12,fontWeight:700,color:col,marginTop:1}}>{v}</div>
                          </div>
                        ))}
                      </div>
                      {h.ob&&h.obZone&&<div style={{fontSize:10,color:C.muted,marginTop:5}}>OB zone: {fmtPrice(h.obZone.low)} – {fmtPrice(h.obZone.high)} ({h.obType})</div>}
                      {h.fvg&&h.fvgZone&&<div style={{fontSize:10,color:C.muted}}>FVG zone: {fmtPrice(h.fvgZone.low)} – {fmtPrice(h.fvgZone.high)} ({h.fvgType})</div>}
                      <div style={{marginTop:6,fontSize:12,fontWeight:700,color:gated&&aligned?C.green:C.amber}}>
                        {gated&&aligned?"✅ GATE OPEN — 1m entry valid with trend":"⛔ "+(!aligned?"Trend mismatch: 15m "+h.trend+" vs 1m "+focusDir?.toLowerCase():"Missing "+(h.ob?"FVG":"OB")+" on 15m — signal blocked")}
                      </div>
                    </div>
                  );
                })()}

                {focused.cnt>=condReqCnt-2&&focused.cnt<condReqCnt&&(
                  <div style={{marginTop:12,padding:"8px 12px",background:"#1a1200",border:`1px solid ${C.amber}`,borderRadius:4,fontSize:13,color:C.amber,fontWeight:700,textAlign:"center"}}>
                    ⚠️ {focused.cnt}/{condReqCnt} — NEEDS {condReqCnt-focused.cnt} MORE CONDITION{condReqCnt-focused.cnt!==1?"S":""}
                  </div>
                )}
                {focused.cnt>=condReqCnt&&(
                  <div onClick={()=>alerts[0]&&setPopup(alerts[0])} style={{marginTop:12,padding:"8px 12px",background:"#022c22",border:`1px solid ${C.green}`,borderRadius:4,fontSize:14,color:C.green,fontWeight:700,textAlign:"center",cursor:"pointer"}}>
                    🔥 ALL {condReqCnt}/{condReqCnt} — TAP TO VIEW SIGNAL
                  </div>
                )}
              </div>
                );})()}

              {/* Alerts */}
              <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:16,display:"flex",flexDirection:"column",gap:8}}>
                <div style={{fontSize:12,color:C.muted,letterSpacing:2}}>ACTIVE ALERTS</div>
                {alerts.length===0?(
                  <div style={{fontSize:14,color:C.dim,textAlign:"center",marginTop:20,flex:1}}>
                    <div style={{fontSize:28,marginBottom:8}}>🔔</div>
                    Monitoring {selectedSyms.size} symbol{selectedSyms.size!==1?"s":""}...<br/>
                    <span style={{fontSize:12}}>Alert fires when ≥ {Math.max(1, getStrategyRequiredCount(strategySchema)-2)}/{getStrategyRequiredCount(strategySchema)} conditions met</span>
                  </div>
                ):alerts.map((a,i)=>(
                  <div key={i} onClick={()=>setPopup(a)} style={{background:"#022c22",border:`1px solid ${C.green}33`,borderRadius:4,padding:"10px 12px",cursor:"pointer"}}>
                    <div style={{fontSize:14,color:C.green,fontWeight:700}}>🚨 {a.direction==="CALL"?"📈 BUY":"📉 SELL"} {a.symbol}</div>
                    <div style={{fontSize:12,color:C.muted}}>@ {fmtPrice(a.price)} • {a.passed}/{getStrategyRequiredCount(strategySchema)} • {a.time}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Today's Predictions ── */}
          {(todayPreds.length>0||selectedSyms.size>0)&&(()=>{
            const wins=todayPreds.filter(p=>p.outcome==="WIN").length;
            const losses=todayPreds.filter(p=>p.outcome==="LOSS").length;
            const pending=todayPreds.filter(p=>p.outcome==="pending").length;
            const expired=todayPreds.filter(p=>p.outcome==="EXPIRED").length;
            const wr=wins+losses>0?Math.round(wins/(wins+losses)*100):null;
            return (
              <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:14,marginBottom:14}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10,flexWrap:"wrap",gap:8}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                    <div style={{fontSize:12,color:C.muted,letterSpacing:2}}>📅 TODAY'S SIGNALS <span style={{color:C.dim,fontSize:10,fontWeight:400,letterSpacing:0}}>— {new Date().toDateString()}</span></div>
                    {wr!==null&&<span style={{fontSize:15,fontWeight:900,color:wr>=60?C.green:wr>=40?C.amber:C.red,background:wr>=60?"#022c22":wr>=40?"#1a1200":"#1a0000",border:`1px solid ${wr>=60?C.green:wr>=40?C.amber:C.red}`,padding:"3px 14px",borderRadius:4}}>WR {wr}%</span>}
                  </div>
                  <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                    {[["WIN",wins,C.green],["LOSS",losses,C.red],["PENDING",pending,C.blue],["EXPIRED",expired,C.amber]].map(([l,n,c])=>n>0&&(
                      <span key={l} style={{fontSize:11,color:c,background:c+"15",border:`1px solid ${c}44`,padding:"2px 8px",borderRadius:3}}>{n} {l}</span>
                    ))}
                    {todayPreds.length>0&&<button onClick={()=>setTodayPreds([])} style={{background:"none",border:`1px solid ${C.border}`,color:C.dim,padding:"2px 8px",borderRadius:3,cursor:"pointer",fontSize:11,fontFamily:"inherit"}}>CLEAR</button>}
                  </div>
                </div>

                {todayPreds.length===0?(
                  <div style={{textAlign:"center",color:C.dim,padding:"28px 0",fontSize:13}}>
                    <div style={{fontSize:24,marginBottom:8}}>🎯</div>
                    No signals yet today<br/>
                    <span style={{fontSize:12}}>All signals that meet the required condition threshold appear here automatically</span>
                  </div>
                ):(
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    {todayPreds.map((p)=>{
                      const buy=p.direction==="CALL";
                      const oc=p.outcome;
                      const isStale=oc==="pending"&&p.ts&&(Date.now()-p.ts)>15*60*1000;
                      const displayOc=isStale?"EXPIRED":oc;
                      const ocColor=displayOc==="WIN"?C.green:displayOc==="LOSS"?C.red:displayOc==="EXPIRED"?C.amber:C.blue;
                      const ocBg=displayOc==="WIN"?"#022c22":displayOc==="LOSS"?"#1a0000":displayOc==="EXPIRED"?"#1a1200":"#001830";
                      const ocIcon=displayOc==="WIN"?"✅ WIN":displayOc==="LOSS"?"❌ LOSS":displayOc==="EXPIRED"?"⏰ EXPIRED":"⏳ ACTIVE";
                      return (
                        <div key={p.id} style={{background:ocBg,border:`1px solid ${ocColor}33`,borderRadius:6,padding:"10px 14px"}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:6}}>
                            {/* Left: direction + symbol */}
                            <div style={{display:"flex",alignItems:"center",gap:10}}>
                              <span style={{fontSize:18,fontWeight:900,color:buy?C.green:C.red}}>{buy?"📈":"📉"}</span>
                              <div>
                                <div style={{fontSize:14,fontWeight:700,color:buy?C.green:C.red}}>{buy?"BUY":"SELL"} <span style={{color:C.text}}>{p.symbol}</span></div>
                                <div style={{fontSize:11,color:C.muted,marginTop:1}}>{p.time} &nbsp;•&nbsp; 15m: <span style={{color:p.htf15?.trend==="bullish"?C.green:p.htf15?.trend==="bearish"?C.red:C.muted}}>{p.htf15?.trend?.toUpperCase()||p.session||"—"}</span> &nbsp;•&nbsp; {p.passed}/10 conds</div>
                              </div>
                            </div>
                            {/* Right: outcome badge */}
                            <div style={{textAlign:"right"}}>
                              <div style={{fontSize:13,fontWeight:900,color:ocColor}}>{ocIcon}</div>
                              {p.outcomeTime&&<div style={{fontSize:10,color:C.muted,marginTop:1}}>@ {fmtPrice(p.outcomePrice)} · {p.outcomeTime}</div>}
                            </div>
                          </div>
                          {/* Price levels row */}
                          <div style={{display:"flex",gap:10,marginTop:8,flexWrap:"wrap",fontSize:12}}>
                            <span style={{color:"#94a3b8"}}>ENTRY: <strong style={{color:C.text}}>{fmtPrice(p.entry)}</strong></span>
                            {p.sl&&<span style={{color:C.red}}>SL: <strong>{fmtPrice(p.sl)}</strong></span>}
                            {p.tp1&&<span style={{color:C.green}}>TP1: <strong>{fmtPrice(p.tp1)}</strong></span>}
                            {p.tp2&&<span style={{color:C.green,opacity:0.8}}>TP2: <strong>{fmtPrice(p.tp2)}</strong></span>}
                            {p.tp3&&<span style={{color:C.green,opacity:0.6}}>TP3: <strong>{fmtPrice(p.tp3)}</strong></span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── Live Tick Feed ── */}
          {selectedSyms.size>0&&(
            <div style={{background:"#020912",border:`1px solid ${C.border}`,borderRadius:8,padding:14,marginBottom:14}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <div style={{fontSize:12,color:C.muted,letterSpacing:2}}>⚡ LIVE PRICE FEED — REAL DERIV TICKS</div>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <span style={{display:"inline-block",width:7,height:7,borderRadius:"50%",background:wsStatus==="connected"?C.green:C.red,animation:"pulse 1.2s infinite"}}/>
                  <span style={{fontSize:11,color:wsStatus==="connected"?C.green:C.red}}>{wsStatus==="connected"?"STREAMING":"DISCONNECTED"}</span>
                </div>
              </div>
              <div style={{height:180,overflowY:"auto",fontFamily:"'Courier New',monospace",fontSize:13}}>
                {recentTicks.length===0&&<div style={{color:C.dim,textAlign:"center",marginTop:40}}>Waiting for ticks... Add symbols above to begin</div>}
                {recentTicks.map((t,i)=>{
                  const sym=t.sym; const d=symData[sym];
                  const cnt=d?.cnt??0;
                  const up=t.change>=0;
                  return (
                    <div key={i} className="kara-tick-row" style={{display:"flex",gap:10,alignItems:"center",padding:"2px 0",borderBottom:`1px solid ${C.bg}`,opacity:1-i*0.015}}>
                      <span style={{color:C.border,minWidth:70}}>[{t.time}]</span>
                      <span style={{color:"#94a3b8",minWidth:60,fontWeight:700}}>{sym}</span>
                      <span style={{color:up?C.green:C.red,minWidth:100,fontWeight:700}}>{fmtPrice(t.price)}</span>
                      <span style={{color:up?C.green:C.red,minWidth:80}}>{up?"▲":"▼"} {fmtPrice(Math.abs(t.change))}</span>
                      {d?.analysis&&(
                        <>
                          <span style={{color:"#475569",fontSize:12}}>RSI:{fmt(d.analysis.rsi,0)}</span>
                          <span style={{color:"#475569",fontSize:12,marginLeft:8}}>ATR:{fmt(d.analysis.atr,d.analysis.atr>1?3:5)}</span>
                          <span style={{color:cnt>=getStrategyRequiredCount(strategySchema)?C.green:cnt>=(getStrategyRequiredCount(strategySchema)-2)?C.amber:C.muted,fontSize:12,marginLeft:8,fontWeight:700}}>{cnt}/{getStrategyRequiredCount(strategySchema)}{cnt>=getStrategyRequiredCount(strategySchema)?"🔥":"✓"}</span>
                          {d.analysis.liq_sweep&&<span style={{color:C.amber,fontSize:11,marginLeft:4}}>💧SWEEP</span>}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Event Log ── */}
          <div style={{background:"#020912",border:`1px solid ${C.border}`,borderRadius:8,padding:14}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <div style={{fontSize:12,color:C.muted,letterSpacing:2}}>EVENT LOG <span style={{color:C.dim,fontWeight:400,fontSize:10,letterSpacing:0}}>— newest first</span></div>
              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                <span style={{fontSize:11,color:C.dim}}>{logs.length} entries</span>
                <button onClick={()=>setLogs([])} style={{background:"none",border:`1px solid ${C.border}`,color:C.dim,padding:"2px 8px",borderRadius:3,cursor:"pointer",fontSize:11,fontFamily:"inherit"}}>CLEAR</button>
              </div>
            </div>
            <div style={{height:220,overflowY:"auto",fontSize:12,lineHeight:1.9}}>
              {logs.length===0&&<div style={{color:C.dim,textAlign:"center",marginTop:60}}>Select symbols above to begin monitoring…</div>}
              {[...logs].reverse().map((l,i)=>(
                <div key={i} style={{color:l.type==="alert"?"#fbbf24":l.type==="success"?C.green:l.type==="error"?C.red:l.type==="warn"?C.amber:l.type==="scan"?C.blue:l.type==="price"?"#a78bfa":"#475569",fontFamily:"'Courier New',monospace",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                  <span style={{color:C.border,userSelect:"none"}}>[{l.time}]</span> {l.msg}
                </div>
              ))}
            </div>
          </div>

          {/* Voice hints */}
          <div style={{marginTop:10,display:"flex",gap:6,flexWrap:"wrap",justifyContent:"center"}}>
            {["add R_100","remove R_75","all symbols","status","price","session","backtest","history"].map(h=>(
              <span key={h} style={{fontSize:11,color:C.border,background:C.panel,border:`1px solid ${C.border}`,borderRadius:3,padding:"2px 8px"}}>🎤 "{h}"</span>
            ))}
          </div>
          </>
          )}


          {/* ════════════ STRATEGY TAB ════════════ */}
      {(tab==="strategy"||tab==="stratlab")&&(
        <StrategyPanel
          strategies={strategies}
          activeStrategy={activeStrategy}
          onUpload={handleStrategyUpload}
          onActivate={handleStrategyActivate}
          onDelete={handleStrategyDelete}
          onPreview={handleStrategyPreview}
          hourlyStats={accountInfo?.hourlyStats}
          sessionStats={accountInfo?.sessionStats}
          bestSession={accountInfo?.bestSession}
          timeline={strategyTimeline}
          apiFetch={apiFetch}
        />
      )}
      {/* T606: Strategy preview modal */}
      {strategyPreviewOpen&&(
        <div onClick={()=>setStrategyPreviewOpen(false)} style={{position:"fixed",inset:0,background:"#000b",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div onClick={e=>e.stopPropagation()} style={{background:"#0a1628",border:`2px solid ${C.border}`,borderRadius:14,padding:28,width:"90%",maxWidth:700,maxHeight:"85vh",display:"flex",flexDirection:"column",fontFamily:"'Courier New',monospace"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div style={{fontSize:15,fontWeight:700,color:C.amber}}>📄 {strategyPreviewName}</div>
              <button onClick={()=>setStrategyPreviewOpen(false)} style={{background:"none",border:"none",color:C.muted,cursor:"pointer",fontSize:20}}>✕</button>
            </div>
            <pre style={{flex:1,overflow:"auto",fontSize:12,color:C.text,whiteSpace:"pre-wrap",wordBreak:"break-word",lineHeight:1.6,margin:0}}>{strategyPreviewContent}</pre>
            <div style={{marginTop:14,display:"flex",gap:8,justifyContent:"flex-end"}}>
              {strategyPreviewName!==activeStrategy&&<button onClick={()=>{ handleStrategyActivate(strategyPreviewName); setStrategyPreviewOpen(false); }} style={{padding:"7px 16px",borderRadius:4,background:`${C.green}22`,border:`1px solid ${C.green}55`,color:C.green,cursor:"pointer",fontFamily:"inherit",fontWeight:700}}>✅ Activate This Strategy</button>}
              <button onClick={()=>setStrategyPreviewOpen(false)} style={{padding:"7px 16px",borderRadius:4,background:C.dim,border:`1px solid ${C.border}`,color:C.text,cursor:"pointer",fontFamily:"inherit"}}>Close</button>
            </div>
          </div>
        </div>
      )}
      {/* ════════════ ML SIGNALS TAB ════════════ */}
      {tab === "ml" && (
        <div style={{padding:"20px",color:C.text,background:"#070d1a",minHeight:"100vh"}}>
          <div style={{marginBottom:40}}>
            <div style={{display:"flex",flexDirection:"column",alignItems:"flex-start",gap:16,marginBottom:16}}>
              <div>
                <div style={{fontSize:"clamp(20px, 5vw, 28px)",fontWeight:900,color:C.blue,letterSpacing:1,marginBottom:4}}>🤖 ML SIGNAL ENGINE</div>
                <div style={{fontSize:"clamp(12px, 3vw, 13px)",color:C.muted}}>AI-powered prediction system that learns from your trading results</div>
              </div>
              <div style={{padding:"8px 16px",borderRadius:8,fontSize:12,fontWeight:700,background:mlStatus?.is_trained?`${C.green}22`:`${C.amber}22`,color:mlStatus?.is_trained?C.green:C.amber,border:`2px solid ${mlStatus?.is_trained?C.green:C.amber}`,whiteSpace:"nowrap"}}>
                {mlStatus?.is_trained?"✅ READY":"⏳ INITIALIZING"}
              </div>
            </div>
          </div>

          {/* KEY METRICS */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(150px, 1fr))",gap:"12px",marginBottom:32}}>
            {[
              {label:"Win Rate",value:mlStatus?.accuracy?(mlStatus.accuracy*100).toFixed(1)+"%":"—",color:mlStatus?.accuracy>=0.65?C.green:mlStatus?.accuracy>=0.55?C.amber:C.red},
              {label:"Samples",value:mlStatus?.train_samples?.toLocaleString?mlStatus.train_samples.toLocaleString():"—",color:C.blue},
              {label:"Confidence",value:mlStatus?.min_confidence?`${(mlStatus.min_confidence*100).toFixed(0)}%`:"55%",color:C.amber},
              {label:"Auto-Trains",value:mlStatus?.auto_train_count??0,color:C.purple},
            ].map(m=>(
              <div key={m.label} style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:10,padding:16,textAlign:"center"}}>
                <div style={{fontSize:"11px",color:C.muted,letterSpacing:2,marginBottom:8,textTransform:"uppercase"}}>{m.label}</div>
                <div style={{fontSize:"clamp(20px, 4vw, 32px)",fontWeight:900,color:m.color}}>{m.value}</div>
              </div>
            ))}
          </div>

          {/* MAIN 2-COLUMN */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(280px, 1fr))",gap:20,marginBottom:32}}>
            <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:12,padding:24}}>
              <div style={{fontSize:16,fontWeight:700,color:C.blue,marginBottom:20}}>🔮 Predict a Setup</div>
              <select value={mlPredictSym} onChange={e=>setMlPredictSym(e.target.value)}
                style={{width:"100%",background:C.bg,color:C.text,border:`1px solid ${C.border}`,borderRadius:8,padding:12,fontSize:13,fontFamily:"inherit",marginBottom:16}}>
                {["XAUUSD","EURUSD","GBPUSD","USDJPY","AUDJPY","R_100","R_75"].map(s=><option key={s} value={s}>{s}</option>)}
              </select>
              <button onClick={runMlPredict} disabled={!mlStatus}
                style={{width:"100%",background:C.blue,color:"#fff",border:"none",borderRadius:8,padding:12,fontSize:13,fontWeight:700,cursor:"pointer"}}>
                🔮 GET PREDICTION
              </button>
              {mlPredictResult && !mlPredictResult.error && (()=>{
                const res = mlPredictResult;
                const ctx = mlPredictCtx || {};
                const isWin = res.prediction === 1;
                const isBlocked = res.ml_blocked;
                const winPct = ((res.win_probability||res.confidence||0)*100).toFixed(0);
                const rawPct = res.raw_confidence != null ? (res.raw_confidence*100).toFixed(0) : null;
                const dir = ctx.direction || "NONE";
                const isCall = dir === "CALL" || dir === "BUY" || dir === "UP";
                const entryPx = ctx.entry_price;
                const sl = ctx.sl;
                const tp1 = ctx.tp1;
                const tp2 = ctx.tp2;
                const atr = ctx.atr;
                const rr = ctx.rr_ratio;
                const conds = ctx.conditions_passed;
                const regime = res.regime && res.regime !== "Unknown" ? res.regime : null;
                const accentColor = isBlocked ? C.amber : isWin ? C.green : C.red;
                const tradeColor = isCall ? C.green : C.red;
                return (
                  <div style={{marginTop:20,borderRadius:10,border:`1px solid ${accentColor}44`,overflow:"hidden"}}>
                    {/* Header banner */}
                    <div style={{background:`${accentColor}22`,padding:"12px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
                      <div style={{display:"flex",alignItems:"center",gap:10}}>
                        <span style={{fontSize:22,lineHeight:1}}>{isBlocked?"⚠️":isWin?"✅":"❌"}</span>
                        <div>
                          <div style={{fontSize:13,fontWeight:800,color:accentColor,letterSpacing:1}}>
                            {isBlocked?"BLOCKED — LOW CONFIDENCE":isWin?"LIKELY WIN":"LIKELY LOSS"}
                          </div>
                          <div style={{fontSize:11,color:C.muted,marginTop:1}}>
                            {res.source==="heuristic_fallback"?"Heuristic fallback (model not trained)":"ML Model prediction"}
                          </div>
                        </div>
                      </div>
                      <div style={{textAlign:"right"}}>
                        <div style={{fontSize:28,fontWeight:900,color:accentColor,lineHeight:1}}>{winPct}%</div>
                        <div style={{fontSize:10,color:C.muted}}>Win probability</div>
                      </div>
                    </div>
                    {/* Trade direction + regime row */}
                    <div style={{padding:"10px 16px",background:C.bg,display:"flex",gap:10,alignItems:"center",flexWrap:"wrap",borderBottom:`1px solid ${C.border}22`}}>
                      {dir!=="NONE"&&dir!==""&&(
                        <div style={{display:"flex",alignItems:"center",gap:6,padding:"5px 12px",borderRadius:20,background:`${tradeColor}18`,border:`1px solid ${tradeColor}44`}}>
                          <span style={{fontSize:14,lineHeight:1}}>{isCall?"↑":"↓"}</span>
                          <span style={{fontSize:12,fontWeight:800,color:tradeColor,letterSpacing:1}}>{dir}</span>
                        </div>
                      )}
                      {regime&&(
                        <div style={{display:"flex",alignItems:"center",gap:5,padding:"5px 10px",borderRadius:20,background:`${C.purple}18`,border:`1px solid ${C.purple}44`}}>
                          <span style={{fontSize:11,color:C.purple,fontWeight:700}}>{regime==="Ranging"?"〜":regime==="Trending"?"→":"⚡"} {regime}</span>
                        </div>
                      )}
                      {rawPct&&rawPct!==winPct&&(
                        <div style={{marginLeft:"auto",fontSize:10,color:C.muted}}>Raw: {rawPct}%{regime==="Ranging"?" (−15% regime adj)":""}</div>
                      )}
                    </div>
                    {/* Levels grid */}
                    {(entryPx||sl||tp1||tp2||atr||rr!=null||conds!=null)&&(
                      <div style={{padding:"12px 16px",display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(90px,1fr))",gap:8}}>
                        {entryPx&&(<div style={{background:C.panel,borderRadius:6,padding:"8px 10px",textAlign:"center"}}>
                          <div style={{fontSize:10,color:C.muted,marginBottom:3}}>Entry</div>
                          <div style={{fontSize:12,fontWeight:700,color:C.text}}>{fmtPrice(entryPx,mlPredictSym)}</div>
                        </div>)}
                        {sl&&(<div style={{background:C.panel,borderRadius:6,padding:"8px 10px",textAlign:"center"}}>
                          <div style={{fontSize:10,color:C.muted,marginBottom:3}}>Stop Loss</div>
                          <div style={{fontSize:12,fontWeight:700,color:C.red}}>{fmtPrice(sl,mlPredictSym)}</div>
                        </div>)}
                        {tp1&&(<div style={{background:C.panel,borderRadius:6,padding:"8px 10px",textAlign:"center"}}>
                          <div style={{fontSize:10,color:C.muted,marginBottom:3}}>TP 1</div>
                          <div style={{fontSize:12,fontWeight:700,color:C.green}}>{fmtPrice(tp1,mlPredictSym)}</div>
                        </div>)}
                        {tp2&&(<div style={{background:C.panel,borderRadius:6,padding:"8px 10px",textAlign:"center"}}>
                          <div style={{fontSize:10,color:C.muted,marginBottom:3}}>TP 2</div>
                          <div style={{fontSize:12,fontWeight:700,color:C.green}}>{fmtPrice(tp2,mlPredictSym)}</div>
                        </div>)}
                        {rr!=null&&rr>0&&(<div style={{background:C.panel,borderRadius:6,padding:"8px 10px",textAlign:"center"}}>
                          <div style={{fontSize:10,color:C.muted,marginBottom:3}}>R:R</div>
                          <div style={{fontSize:12,fontWeight:700,color:rr>=2?C.green:rr>=1?C.amber:C.red}}>{parseFloat(rr).toFixed(1)}</div>
                        </div>)}
                        {atr&&(<div style={{background:C.panel,borderRadius:6,padding:"8px 10px",textAlign:"center"}}>
                          <div style={{fontSize:10,color:C.muted,marginBottom:3}}>ATR</div>
                          <div style={{fontSize:12,fontWeight:700,color:C.blue}}>{fmtPrice(atr,mlPredictSym)}</div>
                        </div>)}
                        {conds!=null&&(<div style={{background:C.panel,borderRadius:6,padding:"8px 10px",textAlign:"center"}}>
                          <div style={{fontSize:10,color:C.muted,marginBottom:3}}>Conditions</div>
                          <div style={{fontSize:12,fontWeight:700,color:conds>=8?C.green:conds>=6?C.amber:C.red}}>{conds}/10</div>
                        </div>)}
                      </div>
                    )}
                    {/* Confidence bar */}
                    <div style={{padding:"8px 16px 14px"}}>
                      <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:C.muted,marginBottom:4}}>
                        <span>Win probability</span>
                        <span style={{fontWeight:700,color:accentColor}}>{winPct}%</span>
                      </div>
                      <div style={{height:5,background:`${C.border}44`,borderRadius:3,overflow:"hidden"}}>
                        <div style={{height:"100%",width:`${winPct}%`,background:accentColor,borderRadius:3,transition:"width 0.4s ease"}}/>
                      </div>
                      {isBlocked&&(
                        <div style={{fontSize:10,color:C.amber,marginTop:6}}>Below confidence gate ({(mlStatus?.min_confidence||0.55)*100|0}%) — signal suppressed by ML gate</div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>

            <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:12,padding:24}}>
              <div style={{fontSize:16,fontWeight:700,color:C.amber,marginBottom:20}}>⚙️ Settings</div>
              <div style={{marginBottom:24}}>
                <div style={{fontSize:12,color:C.muted,marginBottom:8,display:"flex",justifyContent:"space-between"}}>
                  <span>Confidence Gate</span>
                  <span style={{fontWeight:700,color:C.amber}}>{mlStatus?.min_confidence?(mlStatus.min_confidence*100).toFixed(0):55}%</span>
                </div>
                <input type="range" min="40" max="85" defaultValue={mlStatus?.min_confidence?(mlStatus.min_confidence*100):55}
                  onChange={e=>apiFetch("/api/ml/confidence_gate",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({gate:parseInt(e.target.value)/100})})}
                  style={{width:"100%",cursor:"pointer",marginBottom:12}}/>
                <div style={{display:"flex",gap:8}}>
                  {[40,60,80].map(v=>(
                    <button key={v} onClick={()=>apiFetch("/api/ml/confidence_gate",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({gate:v/100})})}
                      style={{flex:1,padding:8,borderRadius:6,background:C.bg,border:`1px solid ${C.border}`,color:C.muted,cursor:"pointer",fontSize:11,fontWeight:700}}>
                      {v}%
                    </button>
                  ))}
                </div>
              </div>
              <div style={{borderTop:`1px solid ${C.border}`,paddingTop:20}}>
                <button onClick={async()=>await apiFetch("/api/ml/auto_train/toggle",{method:"POST"})}
                  style={{width:"100%",padding:12,borderRadius:8,background:mlStatus?.auto_train_enabled!==false?`${C.green}22`:`${C.amber}22`,color:mlStatus?.auto_train_enabled!==false?C.green:C.amber,border:`1px solid ${mlStatus?.auto_train_enabled!==false?C.green:C.amber}55`,cursor:"pointer",fontWeight:700,fontSize:12}}>
                  {mlStatus?.auto_train_enabled!==false?"⏸ PAUSE":"▶️ RESUME"}
                </button>
              </div>
            </div>
          </div>

          {/* FEATURE IMPORTANCE */}
          <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:12,padding:"16px"}}>
            <div style={{fontSize:"clamp(14px, 3vw, 16px)",fontWeight:700,color:C.purple,marginBottom:16}}>📊 What Matters Most</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:"12px"}}>
              {mlStatus?.feature_importance && Object.entries(mlStatus.feature_importance).slice(0,10).map(([name,pct])=>(
                <div key={name} style={{background:C.bg,borderRadius:8,padding:12}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:6,gap:8}}>
                    <div style={{fontSize:"10px",color:C.muted,textTransform:"capitalize",minWidth:0}}>{name.replace(/_/g," ")}</div>
                    <div style={{fontSize:"10px",fontWeight:700,color:C.blue,whiteSpace:"nowrap"}}>{(pct*100).toFixed(1)}%</div>
                  </div>
                  <div style={{height:4,background:`${C.border}44`,borderRadius:2,overflow:"hidden"}}>
                    <div style={{height:"100%",background:C.blue,width:`${pct*100}%`}}/>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* DATA FEEDER PROGRESS */}
          <div style={{marginTop:32,background:C.panel,border:`1px solid ${C.border}`,borderRadius:12,padding:"16px"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
              <div style={{fontSize:"clamp(14px, 3vw, 16px)",fontWeight:700,color:C.green}}>💾 Data Feeder Progress</div>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <div style={{fontSize:"12px",fontWeight:700,display:"flex",alignItems:"center",gap:6}}>
                  {dataFeederStatus?.feederRunning ? (
                    <span style={{color:C.green}}>🟢 RUNNING</span>
                  ) : (
                    <span style={{color:C.red}}>🔴 STOPPED</span>
                  )}
                </div>
                {dataFeederStatus?.growthRate && (
                  <div style={{fontSize:"11px",color:C.blue,padding:"4px 8px",background:`${C.blue}22`,borderRadius:4}}>
                    ⬆ {dataFeederStatus.growthRate}
                  </div>
                )}
              </div>
            </div>
            {dataFeederStatus ? (
              <div style={{display:"flex",flexDirection:"column",gap:20}}>
                {dataFeederStatus.coverage?.reduce((acc, item) => {
                  const symbolData = acc[item.symbol] || [];
                  acc[item.symbol] = [...symbolData, item];
                  return acc;
                }, {}) && Object.entries(dataFeederStatus.coverage?.reduce((acc, item) => {
                  const symbolData = acc[item.symbol] || [];
                  acc[item.symbol] = [...symbolData, item];
                  return acc;
                }, {}) || {}).map(([symbol, items]) => {
                  const GRAN_LABELS = {60:"1m",120:"2m",300:"5m",900:"15m",1800:"30m",3600:"1h",14400:"4h",86400:"1d",604800:"1w",2592000:"1M"};
                  const sorted = [...items].sort((a,b) => a.granularity - b.granularity);
                  return (
                    <div key={symbol} style={{background:C.bg,borderRadius:8,padding:12,borderLeft:`3px solid ${C.blue}`}}>
                      <div style={{fontSize:"12px",fontWeight:700,color:C.text,marginBottom:12}}>{symbol}</div>
                      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))",gap:10}}>
                        {sorted.map(item => {
                          const target = 1200000;
                          const progress = Math.min(100, (item.candleCount / target) * 100);
                          const yearsEquiv = (item.candleCount / 525600).toFixed(2);
                          const label = GRAN_LABELS[item.granularity] || `${item.granularity}s`;
                          const is1m = item.granularity === 60;
                          return (
                            <div key={`${symbol}_${item.granularity}`} style={{
                              background:is1m?`${C.green}11`:`${C.border}44`,
                              borderRadius:6,
                              padding:10,
                              border:is1m?`1px solid ${C.green}`:"none",
                              position:"relative"
                            }}>
                              {is1m && <div style={{position:"absolute",top:6,right:8,fontSize:"10px",color:C.green,fontWeight:700}}>⭐ PRIORITY</div>}
                              <div style={{display:"flex",justifyContent:"space-between",marginBottom:6,gap:8}}>
                                <div style={{fontSize:"11px",fontWeight:700,color:is1m?C.green:C.text}}>{label}</div>
                                <div style={{fontSize:"10px",fontWeight:700,color:C.blue}}>{progress.toFixed(0)}%</div>
                              </div>
                              <div style={{fontSize:"9px",color:C.muted,marginBottom:6}}>{item.candleCount?.toLocaleString?item.candleCount.toLocaleString():item.candleCount} candles</div>
                              <div style={{height:4,background:`${C.border}44`,borderRadius:2,overflow:"hidden",marginBottom:4}}>
                                <div style={{height:"100%",background:is1m?C.green:progress>=100?C.green:progress>=75?C.amber:C.blue,width:`${progress}%`,transition:"width 0.3s ease"}}/>
                              </div>
                              <div style={{fontSize:"8px",color:C.muted}}>{yearsEquiv}y | {item.from?.slice(0,10)??'?'} to {item.to?.slice(0,10)??'?'}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{color:C.muted,fontSize:"13px"}}>⏳ Loading data status...</div>
            )}
            <div style={{marginTop:16,fontSize:"11px",color:C.muted,padding:12,background:`${C.border}22`,borderRadius:8}}>
              📌 Target: ~2 years of 1-minute candles per symbol ({(1200000).toLocaleString()} candles). Data collection is continuous — updates every 5 seconds.
            </div>
          </div>
        </div>
      )}


      {/* ════════════ DOCKER TAB ════════════ */}
      {tab==="docker"&&(
        <DockerMCPPanel tools={dockerTools} log={dockerLog} onFetch={fetchDockerTools} onCall={callDockerTool} fetching={dockerFetching} connected={dockerOk}/>
      )}

      {/* ════════════ BACKTESTS TAB (Advanced Backtester) ════════════ */}
      {tab==="advanced_backtest"&&(
        <AdvancedBacktestTab activeStrategy={activeStrategy} strategies={strategies} />
      )}

      {/* ════════════ CONTINUOUS FORGE TAB (50+ T/Wk @ 80% WR) ════════════ */}
      {tab==="forge"&&(
        <ContinuousForgeTab apiFetch={apiFetch} />
      )}

      {/* ════════════ HISTORY TAB ════════════ */}
      {tab==="history"&&(
        <MemoHistoryPanel
          history={[...history].sort((a,b)=>(b.timestamp||0)-(a.timestamp||0))}
          apiFetch={apiFetch}
          onClear={()=>{
            setAllPreds([]);
            setTodayPreds([]);
            setHistory([]);
            apiFetch("/api/trades/history",{method:"DELETE"}).catch(()=>{});
            apiFetch("/api/trades/predictions",{method:"DELETE"}).catch(()=>{});
            addLog("🗑 History cleared","warn");
          }}
          onSync={async () => {
            addLog("🔄 Syncing pending trades...", "info");
            try {
              const res = await apiFetch("/api/trades/sync", { method: "POST" });
              const data = await res.json();
              if (data.ok) {
                if (data.synced > 0) {
                  addLog(`✅ Sync complete: ${data.synced} of ${data.total_pending} pending trades resolved`, "success");
                } else {
                  addLog(`✅ Sync complete: no pending trades to resolve`, "info");
                }
                fetchHistory();
                fetchPredictions();
              } else {
                addLog(`❌ Sync failed: ${data.error}`, "error");
              }
            } catch (err) {
              addLog(`❌ Sync error: ${err.message}`, "error");
            }
          }}
        />
      )}

      {/* ════════════ TV ALERTS TAB ════════════ */}

      {/* ════════════ SETTINGS TAB ════════════ */}
      {tab==="settings"&&(
        <TelegramAlertsWidget
          settings={userSettings}
          accountInfo={accountInfo}
          setAlertsEnabled={setAlertsEnabled}
          ollamaModels={ollamaModels}
          onSave={async (newSettings) => {            await apiFetch("/api/user/settings", {
              method:"POST", headers:{"Content-Type":"application/json"},
              body: JSON.stringify(newSettings)
            });
            setUserSettings(newSettings);
            localStorage.setItem(sk("kara_user_settings"), JSON.stringify(newSettings));
            if (newSettings.alertsEnabled !== undefined) {
              setAlertsEnabled(!!newSettings.alertsEnabled);
              localStorage.setItem(sk("amber_alerts"), String(!!newSettings.alertsEnabled));
            }
          }}
          onTestAlert={()=>apiFetch("/api/scanner/test-alert",{method:"POST"})}
          onRestartScanner={()=>apiFetch("/api/scanner/restart",{method:"POST"})}
          missionStatus={missionStatus}
        />
      )}




      {/* VOICE TAB render removed 2026-09-25 per Kenyon — disk cleanup */}



{/* ════════════ INFO TAB ════════════ */}
      {tab==="info"&&(
        <InfoTab
          accountInfo={accountInfo}
          token={localStorage.getItem("kara_auth_token")}
          username={authUser?.username}
          displayName={displayNameFor(authUser)}
        />
      )}

      {/* ════════════ MT5 TAB ════════════ */}
      {tab==="mt5"&&(
        <MT5Tab />
      )}

      {/* ════════════ TRADINGVIEW LIVE TAB (server TradingView Desktop via noVNC, ken72 only) ════════════ */}
      {tab==="tvdesktop"&&(authUser?.username==="ken72"||authUser?.username==="lando")&&(
        <TradingViewDesktopTab onExit={()=>setTab("scanner")} />
      )}

      {/* VOICE TAB — removed 2026-10-11 (Kenyon: voice widgets cover it). File kept for recovery. */}

      {/* ════════════ CHARTS TAB ════════════ */}
      {tab==="charts"&&(()=>{
        // Derived: focused slot + sym
        const focusedSlot = chartSlots.find(s=>`${s.sym}_${s.tf}`===chartFocusKey) || chartSlots[0] || {sym:"XAUUSD",tf:"1m"};
        const focusedSym = focusedSlot.sym;
        const reqCnt = getStrategyRequiredCount(strategySchemaRef.current);
        return (
        <div style={{display:"flex",flexDirection:"column",gap:12}}>

          {/* ── Toolbar ── */}
          <div className="kara-charts-toolbar" style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:"12px 16px",display:"flex",gap:10,flexWrap:"wrap",alignItems:"center"}}>

            {/* Add Chart */}
            <div style={{display:"flex",gap:5,flexWrap:"wrap",alignItems:"center"}}>
              <span style={{fontSize:11,color:C.muted,letterSpacing:2,whiteSpace:"nowrap"}}>ADD CHART</span>
              <select value={newSlotSym} onChange={e=>setNewSlotSym(e.target.value)}
                style={{background:C.panel,border:`1px solid ${C.border}`,color:C.text,padding:"4px 7px",borderRadius:4,fontSize:12,fontFamily:"inherit"}}>
                {displaySymbols.map(s=><option key={s} value={s}>{s}</option>)}
              </select>
              <select value={newSlotTf} onChange={e=>setNewSlotTf(e.target.value)}
                style={{background:C.panel,border:`1px solid ${C.border}`,color:C.blue,padding:"4px 7px",borderRadius:4,fontSize:12,fontFamily:"inherit"}}>
                {CHART_TIMEFRAMES.map(tf=><option key={tf} value={tf}>{tf}</option>)}
              </select>
              <button onClick={()=>{
                if (chartSlots.length >= 4) return;
                const key=`${newSlotSym}_${newSlotTf}`;
                if (chartSlots.some(s=>s.sym===newSlotSym&&s.tf===newSlotTf)) { setChartFocusKey(key); return; }
                const newSlots=[...chartSlots,{sym:newSlotSym,tf:newSlotTf}];
                setChartSlots(newSlots); setChartFocusKey(key); fetchChartCandles(newSlotSym,newSlotTf);
                if (newSlots.length>=4&&chartLayout==="single") setChartLayout("quad");
                else if (newSlots.length>=2&&chartLayout==="single") setChartLayout("dual");
              }} style={{padding:"4px 12px",borderRadius:4,fontSize:12,fontFamily:"inherit",fontWeight:700,cursor:"pointer",background:`${C.green}22`,border:`1px solid ${C.green}55`,color:C.green}}>＋ Add</button>
              <button onClick={()=>{ setChartSlots([{sym:newSlotSym,tf:newSlotTf}]); setChartFocusKey(`${newSlotSym}_${newSlotTf}`); setChartLayout("single"); fetchChartCandles(newSlotSym,newSlotTf); }}
                style={{padding:"4px 10px",borderRadius:4,fontSize:11,fontFamily:"inherit",cursor:"pointer",background:"transparent",border:`1px solid ${C.border}`,color:C.dim}}>Clear</button>
            </div>
            <div style={{width:1,height:24,background:C.border,flexShrink:0}}/>

            {/* Layout Picker */}
            <div style={{display:"flex",gap:5,alignItems:"center"}}>
              <span style={{fontSize:11,color:C.muted,letterSpacing:2,whiteSpace:"nowrap"}}>LAYOUT</span>
              {[["single","▣ 1"],["dual","▦ 2"],["quad","⊞ 4"]].map(([layout,label])=>(
                <button key={layout} onClick={()=>setChartLayout(layout)}
                  style={{padding:"3px 11px",borderRadius:4,fontSize:12,fontFamily:"inherit",fontWeight:700,cursor:"pointer",
                    background:chartLayout===layout?`${C.purple}22`:"transparent",
                    border:`1px solid ${chartLayout===layout?C.purple:C.border}`,
                    color:chartLayout===layout?C.purple:C.dim,transition:"all 0.15s"}}>
                  {label}
                </button>
              ))}
            </div>

            <div style={{marginLeft:"auto",display:"flex",gap:6,alignItems:"center"}}>
              {(chartLayout === "dual" || chartLayout === "quad") && (
                <button onClick={() => {
                  if (!chartGridRef.current) return;
                  if (!document.fullscreenElement) {
                    chartGridRef.current.requestFullscreen().catch(() => {});
                    setChartGridFullscreen(true);
                  } else {
                    document.exitFullscreen();
                    setChartGridFullscreen(false);
                  }
                }} style={{padding:"5px 12px",borderRadius:4,fontSize:11,fontFamily:"inherit",fontWeight:700,cursor:"pointer",
                  background:chartGridFullscreen?`${C.blue}33`:`${C.blue}11`,border:`1px solid ${chartGridFullscreen?C.blue:C.border}`,color:chartGridFullscreen?C.blue:C.dim}}>
                  {chartGridFullscreen ? "⛶ Exit Full" : "⛶ Full"}
                </button>
              )}
              <button onClick={()=>{ setChatOpen(true); setChatUnread(0); }}
                style={{padding:"5px 12px",borderRadius:4,fontSize:11,fontFamily:"inherit",fontWeight:700,cursor:"pointer",
                  background:`${C.amber}22`,border:`1px solid ${C.amber}55`,color:C.amber}}>
                ⚡ Ask Kara
              </button>
              {/* 2026-10-02 — DualChatPanel trigger (Hermes) — additive Rule #1 */}
              <button onClick={()=>setChatDualOpen(true)}
                title="Open Kara + Lala side-by-side chat"
                style={{padding:"5px 12px",borderRadius:4,fontSize:11,fontFamily:"inherit",fontWeight:700,cursor:"pointer",
                  background:`${C.purple}22`,border:`1px solid ${C.purple}55`,color:C.purple}}>
                💬💬 Dual Chat
              </button>
            </div>
          </div>

          {/* ── Quick Symbol Chips — one-click to open a chart ── */}
          <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:"8px 12px"}}>
            <div style={{fontSize:10,color:C.muted,letterSpacing:2,marginBottom:6}}>QUICK ADD SYMBOL</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
              {displaySymbols.map(s=>{
                const isOpen = chartSlots.some(sl=>sl.sym===s);
                const isLive = selectedSyms.has(s);
                const sData = symData[s];
                const sCnt = sData?.cnt||0;
                const sDir = sData?.analysis?.direction;
                const sDirCol = sDir==="CALL"?C.green:sDir==="PUT"?C.red:C.muted;
                return (
                  <button key={s} onClick={()=>{
                    if (isOpen) { setChartFocusKey(`${s}_${newSlotTf}`); setTab("charts"); return; }
                    if (chartSlots.length>=4) {
                      // Replace oldest non-focused slot
                      const toReplace = chartSlots.find(sl=>`${sl.sym}_${sl.tf}`!==chartFocusKey) || chartSlots[0];
                      const newSlots = chartSlots.map(sl=>sl===toReplace?{sym:s,tf:newSlotTf}:sl);
                      setChartSlots(newSlots); setChartFocusKey(`${s}_${newSlotTf}`); fetchChartCandles(s,newSlotTf); return;
                    }
                    const newSlots=[...chartSlots,{sym:s,tf:newSlotTf}];
                    setChartSlots(newSlots); setChartFocusKey(`${s}_${newSlotTf}`);
                    fetchChartCandles(s,newSlotTf);
                    if (newSlots.length>=4&&chartLayout==="single") setChartLayout("quad");
                    else if (newSlots.length>=2&&chartLayout==="single") setChartLayout("dual");
                  }}
                  style={{padding:"3px 9px",borderRadius:4,fontSize:11,fontFamily:"inherit",fontWeight:700,cursor:"pointer",
                    background:isOpen?`${C.amber}22`:isLive?`${sDirCol}11`:`${C.border}22`,
                    border:`1px solid ${isOpen?C.amber:isLive?sDirCol:C.border}`,
                    color:isOpen?C.amber:isLive?sDirCol:C.dim,
                    transition:"all 0.15s",position:"relative"}}>
                    {s}
                    {isLive&&<span style={{position:"absolute",top:-3,right:-3,width:6,height:6,borderRadius:"50%",background:sCnt>=reqCnt?C.green:C.blue}}/>}
                  </button>
                );
              })}
              <span style={{fontSize:10,color:C.border,alignSelf:"center",marginLeft:4}}>● = live scanning &nbsp; ● = signal ready</span>
            </div>
          </div>

          {/* ── Active Trades Panel ── */}
          <ActiveTradesPanel trades={activeTrades} onClose={closeTrade} onRefresh={pollActiveTrades} />

          {/* ── Chart Grid — each slot independent sym+tf ── */}
          {(()=>{
            const cols = chartLayout==="quad"?2:chartLayout==="dual"?2:1;
            const slotsToShow = chartLayout==="single"?chartSlots.slice(0,1):chartLayout==="dual"?chartSlots.slice(0,2):chartSlots.slice(0,4);
            const emptySlots = chartLayout==="quad"?4-slotsToShow.length:chartLayout==="dual"?2-slotsToShow.length:0;
            const vw = window.innerWidth;
            const CARD_HDR = 50;
            const gridH = chartGridFullscreen
              ? (chartLayout==="quad" ? Math.floor((fsHeight - CARD_HDR) / 2) - CARD_HDR : fsHeight - CARD_HDR)
              : chartLayout==="quad"  ? (vw<=768?220:vw<=1024?300:360)
              : chartLayout==="dual"  ? (vw<=768?280:vw<=1024?400:460)
              :                         (vw<=768?340:vw<=1024?500:580);
            return (
              <div ref={chartGridRef} className="kara-chart-grid" style={{display:"grid",gridTemplateColumns:`repeat(${cols},1fr)`,gap:chartGridFullscreen?0:10,background:chartGridFullscreen?"#0d1f35":"transparent",height:chartGridFullscreen?"100vh":"auto",overflowY:chartGridFullscreen?"hidden":"visible"}}>
                {slotsToShow.map((slot, slotIdx)=>{
                  const {sym, tf} = slot;
                  const slotKey = `${sym}_${tf}`;
                  const d = symData[sym]||{};
                  const chartD = chartCandleData[slotKey];
                  const a = d.analysis;
                  const isFocus = chartFocusKey===slotKey;
                  const isLive = selectedSyms.has(sym);
                  const dir = a?.direction;
                  const dirCol = dir==="CALL"?C.green:dir==="PUT"?C.red:C.muted;
                  const condCnt = d.cnt||0;
                  // Live candles: 1m uses scanner's realtime 1m candles, 15m uses realtime 15m candles
                  const liveCandles = !d.offline&&wsStatus==="connected"&&(tf==="1m"&&d.candles?.length>0?d.candles : tf==="15m"&&d.candles15?.length>0?d.candles15:null);
                  const _lc = Array.isArray(liveCandles) ? liveCandles : []; const liveFresh = Boolean(_lc.length && _lc[_lc.length-1]?.time && Date.now()/1000-_lc[_lc.length-1].time<=Math.max(180,(CHART_TF_MAP[tf]||60)*3));
                   const chartDataForWidget = liveFresh ? {candles:liveCandles,loading:false,error:null} : (chartD||d);
                  return (
                    <div key={`slot_${slotIdx}_${sym}_${tf}`} onClick={()=>setChartFocusKey(slotKey)}
                      style={{background:C.panel,border:`2px solid ${isFocus?C.amber:C.border}`,borderRadius:10,overflow:"hidden",cursor:"pointer",transition:"border 0.2s"}}>

                      {/* Card header */}
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 10px",borderBottom:`1px solid ${C.border}`,background:"#0a1628"}}>
                        <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
                          <select value={sym} onClick={e=>e.stopPropagation()} onChange={e=>{
                            const newSym=e.target.value;
                            const newKey=`${newSym}_${tf}`;
                            if (chartSlots.some((s,i)=>i!==slotIdx&&s.sym===newSym&&s.tf===tf)) return;
                            setChartSlots(p=>{ const r=[...p]; if(r[slotIdx]) r[slotIdx]={...r[slotIdx],sym:newSym}; return r; });
                            if (chartFocusKey===slotKey) setChartFocusKey(newKey);
                            fetchChartCandles(newSym,tf,true);
                          }} style={{background:"#0a1628",border:`1px solid ${isFocus?C.amber:C.border}`,color:isFocus?C.amber:C.text,padding:"2px 5px",borderRadius:3,fontSize:13,fontWeight:900,fontFamily:"inherit",letterSpacing:1,cursor:"pointer"}}>{displaySymbols.map(s=><option key={s} value={s}>{s}</option>)}</select>
                          {isLive&&liveFresh&&<span style={{fontSize:9,fontWeight:700,padding:"1px 5px",borderRadius:3,background:`${C.green}22`,border:`1px solid ${C.green}44`,color:C.green,letterSpacing:1}}>● LIVE</span>}
                          {!liveFresh&&<span style={{fontSize:9,fontWeight:700,color:C.amber}} title={chartD?.lastEpoch ? new Date(chartD.lastEpoch*1000).toLocaleString("en-JM",{timeZone:"America/Jamaica"}) : "Historical data"}>⚠ HISTORICAL — NOT LIVE</span>}
                          {a&&<span style={{fontSize:10,fontWeight:700,padding:"1px 5px",borderRadius:3,background:`${dirCol}22`,border:`1px solid ${dirCol}44`,color:dirCol}}>
                            {dir==="CALL"?"📈 CALL":dir==="PUT"?"📉 PUT":"⚖ NONE"}
                          </span>}
                          {a&&<span style={{fontSize:12,fontWeight:900,color:dirCol,fontFamily:"'Courier New',monospace"}}>{fmtPrice(a.price)}</span>}
                          {a&&<span style={{fontSize:10,padding:"1px 5px",borderRadius:3,background:condCnt>=reqCnt?`${C.green}22`:condCnt>=reqCnt-2?`${C.amber}22`:`${C.red}11`,color:condCnt>=reqCnt?C.green:condCnt>=reqCnt-2?C.amber:C.red,fontWeight:700}}>{condCnt}/{reqCnt}</span>}
                        </div>
                        <div style={{display:"flex",gap:5,alignItems:"center"}}>
                          <select value={tf} onClick={e=>e.stopPropagation()} onChange={e=>{
                            const newTf=e.target.value;
                            const newKey=`${sym}_${newTf}`;
                            // Prevent duplicate sym+tf — if another slot already has this exact combo, do nothing
                            if (chartSlots.some((s,i)=>i!==slotIdx&&s.sym===sym&&s.tf===newTf)) return;
                            setChartSlots(p=>{ const r=[...p]; if(r[slotIdx]) r[slotIdx]={...r[slotIdx],tf:newTf}; return r; });
                            if (chartFocusKey===slotKey) setChartFocusKey(newKey);
                            fetchChartCandles(sym,newTf,true);
                          }} style={{background:"#0a1628",border:"1px solid #1e3a5f",color:C.blue,padding:"2px 5px",borderRadius:3,fontSize:11,fontFamily:"inherit",cursor:"pointer"}}>
                            {CHART_TIMEFRAMES.map(t=><option key={t} value={t}>{t}</option>)}
                          </select>
                          {chartD?.loading&&<span style={{fontSize:10,color:C.blue}}>⏳</span>}
                          <button onClick={e=>{
                            e.stopPropagation();
                            const newSlots=chartSlots.filter((_,i)=>i!==slotIdx);
                            const fallback=newSlots.length?newSlots:[{sym:"XAUUSD",tf:"1m"}];
                            setChartSlots(fallback);
                            if (chartFocusKey===slotKey) setChartFocusKey(`${fallback[0].sym}_${fallback[0].tf}`);
                          }} style={{background:"transparent",border:"none",color:"#475569",cursor:"pointer",fontSize:15,padding:"0 3px",lineHeight:1}} title="Remove chart">×</button>
                        </div>
                      </div>

                      {/* Chart — live for 1m/15m, historical for others */}
                      <MemoChartWidget
                        ref={el => { chartRefs.current[slotIdx] = el; }}
                        symbol={sym}
                        timeframe={tf}
                        data={chartDataForWidget}
                        height={gridH}
                        stake={tradeStake}
                        lastPrice={symData[sym]?.analysis?.price ?? null}
                        onBuy={payload=> { if (liveFresh || (!chartD?.stale && wsStatus==="connected")) executeTrade(payload); else addLog("⚠️ Historical chart: trading disabled","warn"); }}
                        onSell={payload=> { if (liveFresh || (!chartD?.stale && wsStatus==="connected")) executeTrade(payload); else addLog("⚠️ Historical chart: trading disabled","warn"); }}
                        activeTrades={activeTrades.filter(t=>{
                          const tSym=Object.keys(SYMBOL_API).find(k=>SYMBOL_API[k]===t.symbol)||t.symbol;
                          return tSym===sym||t.symbol===SYMBOL_API[sym];
                        })}
                        onCloseTrade={closeTrade}
                        strategyIndicators={{ cnt:condCnt, direction:a?.direction||"NONE", signal:condCnt>=reqCnt, reqCnt }}
                        onMlPredict={async(sym)=>{
                          const d=symDataRef.current[sym]; const a=d?.analysis;
                          const sltp=a?computeSLTP(a,d?.aiData):{}; const si=sessionRef.current;
                          const payload=a?{symbol:sym,price:a.price,ema13:a.ema13,ema200:a.ema200,atr:a.atr,atr_sma:a.atr_sma,rsi:a.rsi,trend:a.trend,bos_choch:a.bos_choch||"none",liq_sweep:a.liq_sweep||false,direction:a.direction||"NONE",session:si?.name||"other",htf_ob:d?.htf15?.ob||false,htf_fvg:d?.htf15?.fvg||false,rr_ratio:sltp.rr||0,conditions_passed:d?.cnt||0,ema13_pullback:a.ema13_pullback_buy||a.ema13_pullback_sell||false,zone:a.zone||"neutral",candle_open:a.open,candle_high:a.high,candle_low:a.low,candle_close:a.close}:{symbol:sym};
                          try {
                            const r=await apiFetch("/api/ml/predict",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(payload)});
                            const result=await r.json();
                            const enriched={...result,prediction:typeof result.prediction==="number"?(a?.direction||"CALL"):result.prediction,atr:result.atr||a?.atr};
                            setMlPredictSym(sym); setMlPredictResult(enriched); return enriched;
                          } catch(e){console.error("ML predict:",e);return{error:e.message};}
                        }}
                      />

                      {/* Mini stats bar */}
                      {a&&(
                        <div style={{display:"flex",gap:10,padding:"5px 10px",borderTop:`1px solid ${C.border}`,background:"#050c1a",fontSize:10,flexWrap:"wrap",alignItems:"center"}}>
                          <span style={{color:C.muted}}>EMA13 <span style={{color:C.text,fontWeight:700}}>{fmtPrice(a.ema13)}</span></span>
                          <span style={{color:C.muted}}>RSI <span style={{color:a.rsi>70?C.red:a.rsi<30?C.green:C.text,fontWeight:700}}>{fmt(a.rsi,1)}</span></span>
                          <span style={{color:C.muted}}>ATR <span style={{color:C.text,fontWeight:700}}>{fmt(a.atr,a.atr>1?4:6)}</span></span>
                          {d.htf15&&(()=>{const h=d.htf15;const tCol=h.trend==="bullish"?C.green:h.trend==="bearish"?C.red:C.muted;return <span style={{color:C.muted}}>HTF <span style={{color:tCol,fontWeight:700}}>{h.trend?.toUpperCase()}</span></span>;})()}
                          {!isLive&&tf!=="1m"&&tf!=="15m"&&<span style={{color:C.border,marginLeft:"auto",fontSize:9}}>static — add to watchlist for live data</span>}
                          {isLive&&liveCandles&&<span style={{color:C.green,marginLeft:"auto",fontSize:9}}>● live</span>}
                        </div>
                      )}
                    </div>
                  );
                })}
                {/* Empty slot placeholders for dual/quad */}
                {[...Array(Math.max(0,emptySlots))].map((_,i)=>(
                  <div key={`empty_${i}`} style={{background:`${C.panel}66`,border:`2px dashed ${C.border}`,borderRadius:10,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:gridH+56,gap:8,cursor:"pointer"}}
                    onClick={()=>{
                      if (chartSlots.length>=4) return;
                      const newSlots=[...chartSlots,{sym:newSlotSym,tf:newSlotTf}];
                      setChartSlots(newSlots); setChartFocusKey(`${newSlotSym}_${newSlotTf}`); fetchChartCandles(newSlotSym,newSlotTf);
                    }}>
                    <span style={{fontSize:28,color:C.border}}>＋</span>
                    <span style={{fontSize:12,color:C.dim,fontWeight:700}}>Add Chart</span>
                    <span style={{fontSize:10,color:C.border}}>Click any symbol above</span>
                  </div>
                ))}
              </div>
            );
          })()}

          {tab==="data_old"&&(()=>{
            const GRANS = ["1m","5m","15m","1h","4h","1d"];
            const GRAN_MAP = {60:"1m",300:"5m",900:"15m",3600:"1h",14400:"4h",86400:"1d"};
            const START_EPOCH = 1704067200; // 2024-01-01 UTC
            const nowEpoch = Math.floor(Date.now()/1000);
            const fmtCount = n => n>=1000000?(n/1000000).toFixed(1)+"M":n>=1000?(n/1000).toFixed(0)+"k":String(n||0);
            const fmtMB = kb => (kb/1024).toFixed(1)+" MB";

            // Build lookup: symbol -> gran -> cell
            const lookup = {};
            if (dataStatus?.coverage) {
              dataStatus.coverage.forEach(row => {
                const gl = GRAN_MAP[row.granularity]||row.granLabel;
                if (!lookup[row.symbol]) lookup[row.symbol]={};
                lookup[row.symbol][gl]=row;
              });
            }
            const syms = Object.keys(lookup).length>0 ? Object.keys(lookup).sort() : ["R_10","R_25","R_50","R_75","R_100","JD50","BOOM500","CRASH500","BOOM1000","CRASH1000","XAUUSD","EURUSD","GBPUSD","USDJPY","AUDJPY","USDMXN"];

            const getPct = cell => {
              if (!cell||!cell.to||!cell.from) return 0;
              const fromEp = Math.floor(new Date(cell.from+"T00:00:00Z").getTime()/1000);
              const toEp   = Math.floor(new Date(cell.to  +"T23:59:59Z").getTime()/1000);
              return Math.min(100,Math.max(0,(toEp-fromEp)/(nowEpoch-START_EPOCH)*100));
            };
            const pctColor = pct => pct>=85?C.green:pct>=50?C.amber:pct>0?C.red:"#1e3a5f";
            const cellBg = pct => pct>=85?`${C.green}12`:pct>=50?`${C.amber}10`:pct>0?`${C.red}10`:"transparent";

            const totalPct = syms.length>0 && GRANS.length>0
              ? syms.reduce((acc,s)=>acc+GRANS.reduce((a2,g)=>a2+getPct(lookup[s]?.[g]),0),0)/(syms.length*GRANS.length)
              : 0;

            return (
              <div style={{padding:"0 0 20px 0"}}>
                {/* Header */}
                <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:14,flexWrap:"wrap"}}>
                  <span style={{fontSize:13,fontWeight:700,color:C.amber,letterSpacing:1}}>DATA FEEDER STATUS</span>
                  {dataStatusLoading&&<span style={{fontSize:10,color:C.blue}}>⏳ refreshing…</span>}
                  {!dataStatusLoading&&<span style={{fontSize:10,color:C.dim}}>auto ⏱ 5s</span>}
                  <span style={{flex:1}}/>
                  {dataStatus&&<>
                    <span style={{fontSize:10,color:C.dim,fontFamily:"monospace"}}>{dataStatus.dbPath}</span>
                    <span style={{fontSize:11,color:C.text,fontWeight:700}}>{fmtMB(dataStatus.dbSizeKB||0)}</span>
                    <span style={{fontSize:11,color:C.text,fontWeight:700}}>{fmtCount(dataStatus.totalCandles||0)} candles</span>
                    <span style={{fontSize:11,color:pctColor(totalPct),fontWeight:700}}>{totalPct.toFixed(0)}% overall</span>
                  </>}
                </div>

                {/* Overall progress bar */}
                {dataStatus&&(
                  <div style={{height:6,background:C.border,borderRadius:3,overflow:"hidden",marginBottom:16}}>
                    <div style={{width:`${totalPct}%`,height:"100%",background:pctColor(totalPct),transition:"width 1s",borderRadius:3}}/>
                  </div>
                )}

                {!dataStatus&&!dataStatusLoading&&(
                  <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:20,textAlign:"center",color:C.dim,fontSize:13}}>
                    <div style={{marginBottom:10}}>📊 <strong>345 MB Database</strong></div>
                    <div style={{marginBottom:10}}>3.3M+ Candles Collected</div>
                    <div style={{fontSize:11,color:C.muted}}>EURUSD: 363k • GBPUSD: 363k • R_10: 528k • XAUUSD: 345k</div>
                    <div style={{fontSize:10,color:C.border,marginTop:15}}>Loading data status...</div>
                  </div>
                )}

                {dataStatus&&(
                  <div style={{overflowX:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                      <thead>
                        <tr>
                          <th style={{textAlign:"left",padding:"6px 10px",color:C.muted,fontWeight:700,borderBottom:`1px solid ${C.border}`,whiteSpace:"nowrap"}}>SYMBOL</th>
                          {GRANS.map(g=>(
                            <th key={g} style={{textAlign:"center",padding:"6px 10px",color:C.muted,fontWeight:700,borderBottom:`1px solid ${C.border}`,minWidth:90}}>{g}</th>
                          ))}
                          <th style={{textAlign:"right",padding:"6px 10px",color:C.muted,fontWeight:700,borderBottom:`1px solid ${C.border}`}}>LAST UPDATE</th>
                        </tr>
                      </thead>
                      <tbody>
                        {syms.map(sym=>{
                          const symCells = GRANS.map(g=>lookup[sym]?.[g]||null);
                          const latestUpdate = symCells
                            .filter(Boolean)
                            .map(c=>c.lastUpdated)
                            .filter(Boolean)
                            .sort()
                            .pop();
                          const luStr = latestUpdate ? `${formatJaDate(new Date(latestUpdate))} ${formatJaTime(new Date(latestUpdate))}` : "—";
                          return (
                            <tr key={sym} style={{borderBottom:`1px solid ${C.border}22`}}>
                              <td style={{padding:"8px 10px",color:C.amber,fontWeight:700,whiteSpace:"nowrap"}}>{sym}</td>
                              {symCells.map((cell,gi)=>{
                                const pct=getPct(cell);
                                const col=pctColor(pct);
                                const bg=cellBg(pct);
                                const tip=cell?(cell.from||"?")+" → "+(cell.to||"?")+" ("+fmtCount(cell.candleCount)+")":"No data";
                                return (
                                  <td key={gi} style={{padding:"6px 8px",background:bg,textAlign:"center"}} title={tip}>
                                    <div style={{height:4,background:`${C.border}44`,borderRadius:2,overflow:"hidden",marginBottom:4}}>
                                      <div style={{width:`${pct}%`,height:"100%",background:col,borderRadius:2,transition:"width 0.8s"}}/>
                                    </div>
                                    <span style={{color:col,fontWeight:700,fontSize:10}}>{pct>0?pct.toFixed(0)+"%":"—"}</span>
                                    {cell?.candleCount>0&&<span style={{color:C.dim,fontSize:9,display:"block"}}>{fmtCount(cell.candleCount)}</span>}
                                    {cell?.from&&<span style={{color:C.dim,fontSize:9,display:"block",marginTop:1,opacity:0.7}}>from {cell.from.slice(5)}</span>}
                                  </td>
                                );
                              })}
                              <td style={{padding:"6px 10px",color:C.dim,textAlign:"right",fontSize:10,whiteSpace:"nowrap"}}>{luStr}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Legend */}
                <div style={{display:"flex",gap:16,marginTop:14,flexWrap:"wrap",alignItems:"center"}}>
                  <span style={{fontSize:10,color:C.dim}}>Coverage since 2024-01-01:</span>
                  {[["≥85%","Complete",C.green],["50–84%","Partial",C.amber],["<50%","Sparse",C.red],["—","No data","#1e3a5f"]].map(([label,desc,col])=>(
                    <span key={label} style={{display:"flex",alignItems:"center",gap:5,fontSize:10}}>
                      <span style={{width:10,height:10,background:col,borderRadius:2,display:"inline-block"}}/>
                      <span style={{color:C.dim}}>{label} {desc}</span>
                    </span>
                  ))}
                  <span style={{flex:1}}/>
                  <span style={{fontSize:9,color:C.border}}>Hover cell for date range</span>
                </div>
              </div>
            );
          })()}

          {/* ── Focused Symbol Deep Panel ── */}
          {focusedSym&&symData[focusedSym]?.analysis&&(()=>{
            const d=symData[focusedSym]; const a=d.analysis; const h=d.htf15;
            const acc=a.direction==="CALL"?C.green:a.direction==="PUT"?C.red:C.muted;
            const condLabels=strategySchema?.conditions?.map(c=>c.label)??CONDITIONS;
            const condReqCnt=reqCnt;
            return (
              <div className="kara-focused-panel" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>

                {/* Conditions */}
                <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:14}}>
                  <div style={{fontSize:11,color:C.muted,letterSpacing:2,marginBottom:8}}>CONDITIONS — <span style={{color:C.amber}}>{focusedSym}</span></div>
                  <div style={{height:5,background:C.border,borderRadius:3,overflow:"hidden",marginBottom:10}}>
                    <div style={{width:`${Math.min(100,(d.cnt||0)/condReqCnt*100)}%`,height:"100%",background:(d.cnt||0)>=condReqCnt?C.green:(d.cnt||0)>=condReqCnt-2?C.amber:C.red,transition:"width 0.6s"}}/>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:5}}>
                    {condLabels.map((cn,i)=>{
                      const c=d.conds?.[i]??"pending";
                      return (
                        <div key={i} style={{display:"flex",alignItems:"center",gap:7,padding:"4px 7px",background:c==="pass"?`${C.green}0a`:c==="warn"?`${C.amber}0a`:C.bg,borderRadius:3}}>
                          <span style={{fontSize:13}}>{c==="pass"?"✅":c==="warn"?"⚠️":c==="fail"?"❌":"⏳"}</span>
                          <span style={{fontSize:12,color:c==="pass"?C.green:c==="warn"?C.amber:c==="fail"?C.red:C.muted,flex:1}}>{cn}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* HTF + Price Levels */}
                <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:14,display:"flex",flexDirection:"column",gap:10}}>
                  <div style={{fontSize:11,color:C.muted,letterSpacing:2}}>PRICE LEVELS — {focusedSym}</div>
                  {[
                    ["Price",  fmtPrice(a.price), acc],
                    ["EMA13",  fmtPrice(a.ema13), C.blue],
                    ["EMA200", a.ema200?fmtPrice(a.ema200):"< 50 bars", C.purple],
                    ["TP1",    a.tp1?fmtPrice(a.tp1):"—", C.green],
                    ["TP2",    a.tp2?fmtPrice(a.tp2):"—", C.green],
                    ["TP3",    a.tp3?fmtPrice(a.tp3):"—", C.green],
                    ["SL",     a.sl?fmtPrice(a.sl):"—", C.red],
                    ["R:R",    a.rr_ratio!=null?`${fmt(a.rr_ratio,1)}x`:"—", a.rr_ratio>=2?C.green:C.amber],
                  ].map(([l,v,col])=>(
                    <div key={l} style={{display:"flex",justifyContent:"space-between",borderBottom:`1px solid ${C.border}`,paddingBottom:4}}>
                      <span style={{fontSize:11,color:C.muted}}>{l}</span>
                      <span style={{fontSize:13,fontWeight:700,color:col,fontFamily:"'Courier New',monospace"}}>{v}</span>
                    </div>
                  ))}
                  {h&&(
                    <div style={{padding:"6px 10px",background:"#050d1a",border:`1px solid ${h.ob&&h.fvg?C.green+"55":C.amber+"44"}`,borderRadius:5}}>
                      <div style={{fontSize:10,color:C.amber,letterSpacing:2,marginBottom:4}}>15M HTF GATE</div>
                      <div style={{display:"flex",gap:8,fontSize:11}}>
                        <span style={{color:h.trend==="bullish"?C.green:h.trend==="bearish"?C.red:C.muted,fontWeight:700}}>{h.trend?.toUpperCase()}</span>
                        <span style={{color:h.ob?C.green:C.red}}>OB {h.ob?"✅":"❌"}</span>
                        <span style={{color:h.fvg?C.green:C.red}}>FVG {h.fvg?"✅":"❌"}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Signal + Quick Actions */}
                <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:14,display:"flex",flexDirection:"column",gap:10}}>
                  <div style={{fontSize:11,color:C.muted,letterSpacing:2}}>SIGNAL — {focusedSym}</div>
                  <div style={{padding:"10px 14px",borderRadius:6,background:`${acc}11`,border:`2px solid ${acc}44`,textAlign:"center"}}>
                    <div style={{fontSize:22,fontWeight:900,color:acc}}>{a.direction==="CALL"?"📈 CALL — BUY":a.direction==="PUT"?"📉 PUT — SELL":"⚖ NO BIAS"}</div>
                    <div style={{fontSize:11,color:C.muted,marginTop:4}}>{(d.cnt||0)}/{condReqCnt} conditions • {a.trend?.toUpperCase()||"—"}</div>
                  </div>
                  {/* Quick action buttons */}
                  <button onClick={()=>runAIScan(focusedSym)}
                    disabled={!!aiScanning}
                    style={{padding:"8px",borderRadius:5,background:`${C.blue}11`,border:`1px solid ${C.blue}44`,color:C.blue,cursor:aiScanning?"not-allowed":"pointer",fontFamily:"inherit",fontWeight:700,fontSize:12}}>
                    🔬 Deep AI Scan
                  </button>
                  <button onClick={()=>{ const sym=focusedSym; const _req=getStrategyRequiredCount(strategySchema); if((symData[sym]?.cnt||0)>=_req) sendToKara(`Validate signal for ${sym}`); else speak(`Only ${symData[sym]?.cnt||0} of ${_req} conditions met for ${sym}.`); }}
                    style={{padding:"8px",borderRadius:5,background:`${C.green}11`,border:`1px solid ${C.green}44`,color:C.green,cursor:"pointer",fontFamily:"inherit",fontWeight:700,fontSize:12}}>
                    ✅ Validate Signal
                  </button>
                  <button onClick={()=>{ setFocusSym(focusedSym); setTab("scanner"); }}
                    style={{padding:"8px",borderRadius:5,background:`${C.amber}11`,border:`1px solid ${C.amber}44`,color:C.amber,cursor:"pointer",fontFamily:"inherit",fontWeight:700,fontSize:12}}>
                    🔍 View Full Card
                  </button>
                  {autoTrade&&(d.cnt||0)>=getStrategyRequiredCount(strategySchema)&&(
                    <button onClick={()=>setPopup({symbol:focusedSym,direction:a.direction,stake:tradeStake})}
                      style={{padding:"8px",borderRadius:5,background:`${C.red}22`,border:`2px solid ${C.red}88`,color:C.red,cursor:"pointer",fontFamily:"inherit",fontWeight:900,fontSize:12}}>
                      🤖 EXECUTE TRADE
                    </button>
                  )}
                </div>
              </div>
            );
          })()}

          {/* ── Kara Chart Sub-Agent Panel ── */}
          <div style={{background:C.panel,border:`1px solid ${chartAgentOpen?C.blue:C.border}`,borderRadius:8,overflow:"hidden",transition:"border-color 0.2s"}}>
            <div onClick={()=>setChartAgentOpen(o=>!o)}
              style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",cursor:"pointer",userSelect:"none",background:chartAgentOpen?"#0a1628":"transparent"}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:14}}>🤖</span>
                <span style={{fontSize:12,fontWeight:800,color:C.blue,letterSpacing:1.5}}>AMBER — CHART CONTROL SUB-AGENT</span>
                {chartAgentBusy&&<span style={{fontSize:10,color:C.amber,animation:"pulse 1s infinite"}}>⏳ running…</span>}
                {chartAgentHistory.length>0&&!chartAgentBusy&&<span style={{fontSize:10,color:C.dim,background:"#0f1c2e",borderRadius:3,padding:"1px 6px"}}>{Math.floor(chartAgentHistory.length/2)} turns</span>}
              </div>
              <span style={{color:C.border,fontSize:13}}>{chartAgentOpen?"▲":"▼"}</span>
            </div>
            {chartAgentOpen&&(
              <div style={{padding:"12px 14px",borderTop:`1px solid ${C.border}`,display:"flex",flexDirection:"column",gap:10}}>
                {/* Quick tasks */}
                <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                  {[
                    ["📸 Snapshot",    `Take a screenshot of the current chart state for ${focusedSym}`],
                    ["📊 Read Chart",  `Analyze the current price action on ${focusedSym} chart and describe what you see`],
                    ["🔄 Refresh Data",`Refresh and reload market data for all active chart symbols`],
                    ["📐 Spot Levels", `Identify key support/resistance levels and SMC structures on the ${focusedSym} chart`],
                    ["💡 Bias Check",  `Based on the current chart data for ${focusedSym}, confirm or deny the ${focusedSym} AI bias direction`],
                  ].map(([label,task])=>(
                    <button key={label} disabled={chartAgentBusy} onClick={()=>runChartAgent(task)}
                      style={{padding:"5px 11px",borderRadius:4,background:"#0f1c2e",border:"1px solid #1e3a5f",color:chartAgentBusy?"#334155":"#64748b",cursor:chartAgentBusy?"not-allowed":"pointer",fontFamily:"inherit",fontSize:11,whiteSpace:"nowrap"}}
                      onMouseEnter={e=>{if(!chartAgentBusy){e.currentTarget.style.borderColor=C.blue;e.currentTarget.style.color=C.blue;}}}
                      onMouseLeave={e=>{e.currentTarget.style.borderColor="#1e3a5f";e.currentTarget.style.color=chartAgentBusy?"#334155":"#64748b";}}>
                      {label}
                    </button>
                  ))}
                </div>
                {/* Input */}
                <div style={{display:"flex",gap:6}}>
                  <input value={chartAgentTask} onChange={e=>setChartAgentTask(e.target.value)}
                    onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey&&chartAgentTask.trim()){e.preventDefault();runChartAgent(chartAgentTask);}}}
                    placeholder={`Instruct chart agent… e.g. "Analyze ${focusedSym} for entry setup"`}
                    disabled={chartAgentBusy}
                    style={{flex:1,padding:"8px 11px",borderRadius:4,background:"#0a1628",border:`1px solid ${chartAgentBusy?C.amber+"55":C.border}`,color:C.text,fontFamily:"inherit",fontSize:12,outline:"none"}}/>
                  <button disabled={chartAgentBusy||!chartAgentTask.trim()} onClick={()=>runChartAgent(chartAgentTask)}
                    style={{padding:"8px 16px",borderRadius:4,background:chartAgentBusy?"#0f1c2e":C.border,border:`1px solid ${C.blue}55`,color:chartAgentBusy?C.amber:C.blue,cursor:chartAgentBusy?"not-allowed":"pointer",fontFamily:"inherit",fontWeight:800,fontSize:12}}>
                    {chartAgentBusy?"⏳":"RUN ▶"}
                  </button>
                  {chartAgentHistory.length>0&&(
                    <button onClick={()=>{setChartAgentHistory([]);setChartAgentLog([]);}}
                      style={{padding:"8px 11px",borderRadius:4,background:"transparent",border:`1px solid ${C.border}`,color:C.dim,cursor:"pointer",fontFamily:"inherit",fontSize:13}}>🗑</button>
                  )}
                </div>
                {/* Log */}
                {chartAgentLog.length>0&&(
                  <div style={{maxHeight:220,overflowY:"auto",display:"flex",flexDirection:"column",gap:6}}>
                    {[...chartAgentLog].reverse().map((entry,i)=>(
                      <div key={i} style={{background:entry.role==="user"?"#0f1c2e":entry.role==="error"?"#1c0a0a":"#071426",border:`1px solid ${entry.role==="user"?C.border:entry.role==="error"?C.red+"33":"#1e293b"}`,borderRadius:5,padding:"8px 11px"}}>
                        <div style={{fontSize:10,color:entry.role==="user"?C.muted:entry.role==="error"?C.red:C.blue,marginBottom:3,letterSpacing:1}}>{entry.role==="user"?"YOU":entry.role==="error"?"ERROR":"AMBER"} <span style={{color:C.dim,fontWeight:400}}>{entry.time}</span></div>
                        <div style={{fontSize:12,color:entry.role==="error"?C.red:C.text,whiteSpace:"pre-wrap",lineHeight:1.5}}>{entry.text}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

        </div>
        );
      })()}

      <div style={{marginTop:14,textAlign:"center",fontSize:11,color:C.border,letterSpacing:2}}>
        AMBER v4.0 &nbsp;•&nbsp; REAL DERIV DATA &nbsp;•&nbsp; URUS SMC EMA13 &nbsp;•&nbsp; DOCKER MCP CLIENT &nbsp;•&nbsp; AMBER AI CO-PILOT
      </div>
    </div>
  );
}

// ── LoginPage ──────────────────────────────────────────────────────────────
function LoginPage({ onLogin, error, loading }) {
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const aiName = username.toLowerCase() === "lando" ? "LALA" : "KARA";
  const inp = { width:"100%", padding:"10px 14px", background:C.panel, border:`1px solid ${C.border}`, borderRadius:6, color:C.text, fontSize:14, outline:"none", boxSizing:"border-box" };
  const handleSubmit = (e) => { e.preventDefault(); if (username.trim() && password) onLogin({ username: username.trim(), password }); };
  return (
    <div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center",padding:"16px"}}>
      <div style={{width:"100%",maxWidth:360,background:C.panel,borderRadius:12,padding:36,border:`1px solid ${C.border}`,boxSizing:"border-box"}}>
        <div style={{textAlign:"center",marginBottom:28}}>
          <div style={{fontSize:28,marginBottom:6}}>⚡</div>
          <div style={{fontSize:20,fontWeight:700,color:C.text,letterSpacing:2}}>{aiName} AI</div>
          <div style={{fontSize:11,color:C.muted,letterSpacing:3,marginTop:2}}>ADVANCED TRADING CO PILOT</div>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{marginBottom:14}}>
            <div style={{fontSize:11,color:C.muted,marginBottom:6,letterSpacing:1}}>USERNAME OR EMAIL</div>
            <input style={inp} type="text" autoComplete="username" value={username} onChange={e=>setUsername(e.target.value)} placeholder="Username or email" disabled={loading} />
          </div>
          <div style={{marginBottom:20}}>
            <div style={{fontSize:11,color:C.muted,marginBottom:6,letterSpacing:1}}>PASSWORD</div>
            <input style={inp} type="password" autoComplete="current-password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="••••••••••" disabled={loading} />
          </div>
          {error && <div style={{color:C.red,fontSize:12,marginBottom:14,textAlign:"center"}}>{error}</div>}
          <button type="submit" disabled={loading||!username||!password}
            style={{width:"100%",padding:"11px",background:C.green,border:"none",borderRadius:6,color:"#000",fontWeight:700,fontSize:14,cursor:loading?"not-allowed":"pointer",opacity:loading||!username||!password?0.6:1,letterSpacing:1}}>
            {loading ? "SIGNING IN…" : "SIGN IN"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Auth gate ──────────────────────────────────────────────────────────────
function KaraAuthGate() {
  const [authToken, setAuthToken] = useState(() => localStorage.getItem("kara_auth_token"));
  const [authUser, setAuthUser]   = useState(() => { try { return JSON.parse(localStorage.getItem("kara_auth_user")); } catch { return null; } });
  const [loginError, setLoginError]   = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  const handleLogout = useCallback(() => {
    localStorage.removeItem("kara_auth_token");
    localStorage.removeItem("kara_auth_user");
    setAuthToken(null); setAuthUser(null);
  }, []);

  const handleLogin = async ({ username, password }) => {
    setLoginLoading(true); setLoginError("");
    try {
      const r = await fetch("/api/auth/login", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ username, password }) });
      const d = await r.json();
      if (!r.ok) { setLoginError(d.error || "Login failed"); setLoginLoading(false); return; }
      localStorage.setItem("kara_auth_token", d.token);
      localStorage.setItem("kara_auth_user", JSON.stringify(d.user));
      _logoutInFlight = false;
      _apiFetchLogout = handleLogout;
      // Sync server settings into localStorage on login
      try {
        const settingsRes = await fetch("/api/user/settings", { headers: { "Authorization": `Bearer ${d.token}` } });
        if (settingsRes.ok) {
          const srv = await settingsRes.json();
          if (srv.alertsEnabled !== undefined) localStorage.setItem("amber_alerts", String(srv.alertsEnabled));
          if (srv.alertVoice !== undefined) localStorage.setItem("amber_alert_voice", String(srv.alertVoice));
          if (srv.autoTrade !== undefined) localStorage.setItem("amber_auto_trade", String(srv.autoTrade));
          if (srv.mlAutoTrade !== undefined) { localStorage.setItem("amber_ml_auto_trade", String(srv.mlAutoTrade)); }
          if (srv.mlAutoConfidence !== undefined) { const pct=Math.min(99,Math.max(55,Math.round(srv.mlAutoConfidence*100))); localStorage.setItem("amber_ml_auto_confidence",String(pct)); }
          if (srv.stake !== undefined) localStorage.setItem("amber_trade_stake", String(srv.stake));
          if (srv.personalityMode !== undefined) localStorage.setItem("amber_personality_mode", srv.personalityMode);
          if (srv.tradingTf) localStorage.setItem("amber_trading_tf", srv.tradingTf);
          else localStorage.removeItem("amber_trading_tf");
          localStorage.setItem("kara_user_settings", JSON.stringify(srv));
        }
      } catch {}
      setAuthToken(d.token); setAuthUser(d.user);
    } catch { setLoginError("Network error — try again"); }
    setLoginLoading(false);
  };

  useEffect(() => { _apiFetchLogout = handleLogout; }, [handleLogout]);

  if (!authToken || !authUser) {
    return <LoginPage onLogin={handleLogin} error={loginError} loading={loginLoading} />;
  }
  return (
    <ErrorBoundary>
      <KaraScannerMain authUser={authUser} onLogout={handleLogout} />
    </ErrorBoundary>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// STRATEGY LAB PANEL — Autonomous Strategy Improvement Agent (ASIA) UI
// ════════════════════════════════════════════════════════════════════════════

// Available symbols with candle data in the DB
const LAB_SYMBOLS = ["XAUUSD","EURUSD","GBPUSD","USDJPY","AUDJPY","USDMXN","R_100","R_75","R_50","R_25","R_10","JD50","BOOM500","CRASH500","BOOM1000","CRASH1000"];
const LAB_TFS = [["60","1m"],["300","5m"],["900","15m"],["3600","1h"],["14400","4h"],["86400","1D"]];
const MARKET_BADGE = { synthetic:"🎲 Synthetic", metals:"🥇 Metals/Gold", forex:"💱 Forex", indices:"📈 Indices", mixed:"🔀 Mixed" };
const MARKET_COLOR = { synthetic:"#00d4aa", metals:"#f59e0b", forex:"#38bdf8", indices:"#a78bfa", mixed:"#6b7280" };

// ── Win Rate Gauge — high contrast progress toward 80% target ─────────────────
function WinRateRing({ winRate = 0, target = 0.80, size = 84, C }) {
  const pct    = Math.min(winRate / target, 1);
  const r      = (size - 14) / 2;
  const circ   = 2 * Math.PI * r;
  const dash   = circ * pct;
  const color  = winRate >= target ? C.green : winRate >= target * 0.75 ? C.blue : C.amber;
  const label  = winRate > 0 ? (winRate * 100).toFixed(1) + "%" : "—";
  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)", flexShrink: 0 }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#0d1f38" strokeWidth={10} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={10}
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
        style={{ transition: "stroke-dasharray 0.8s cubic-bezier(0.4, 0, 0.2, 1)", filter: `drop-shadow(0 0 3px ${color}66)` }} />
      <text x={size/2} y={size/2 + 5} textAnchor="middle"
        style={{ transform: `rotate(90deg)`, transformOrigin: `${size/2}px ${size/2}px`,
                 fill: color, fontSize: 13, fontWeight: 900, fontFamily: "monospace" }}>
        {label}
      </text>
    </svg>
  );
}

// Phase display metadata
const PHASE_META = {
  idle:        { label: "⏸ Idle",          color: "#6b7280" },
  analyzing:   { label: "🔍 Analyzing",    color: "#3b82f6" },
  researching: { label: "🌐 Researching",  color: "#8b5cf6" },
  backtesting: { label: "📊 Backtesting",  color: "#f59e0b" },
  writing:     { label: "✍️ Rewriting",     color: "#06b6d4" },
  evaluating:  { label: "⚖️ Evaluating",   color: "#3b82f6" },
  complete:    { label: "🎯 Target Met",   color: "#22c55e" },
  error:       { label: "❌ Error",         color: "#ef4444" },
};

const LAB_SYMBOLS_CLAUDE = ["XAUUSD","EURUSD","GBPUSD","USDJPY","AUDJPY","USDMXN","R_100","R_75","R_50","R_25","R_10","JD50","BOOM500","CRASH500","BOOM1000","CRASH1000"];

// ── QuickActionsPanel — 6-card action panel for manual triggers ────────────
// ── TradingViewPanel ─────────────────────────────────────────────────────────
function TradingViewPanel({ C, tradeStake = 1 }) {
  const EXCHANGES = ["BINANCE","COINBASE","KRAKEN","BYBIT","KUCOIN","NASDAQ","NYSE","FOREX","FX"];
  const CHIPS = ["XAUUSD","EURUSD","BTCUSDT","ETHUSDT","SOLUSDT","AAPL","TSLA","GBPUSD"];
  const [panel, setPanel]         = React.useState("analysis");
  const [symbol, setSymbol]       = React.useState("XAUUSD");
  const [exchange, setExchange]   = React.useState("BINANCE");
  const [tvStatus, setTvStatus]   = React.useState({ ok: false });
  const [toolResult, setToolResult] = React.useState(null);
  const [toolLoading, setToolLoading] = React.useState(false);
  // Screener panel
  const [scrExchange, setScrExchange] = React.useState("BINANCE");
  const [scrLimit, setScrLimit]   = React.useState(20);
  const [ratingFilter, setRatingFilter] = React.useState("STRONG_BUY");
  const [consecCount, setConsecCount] = React.useState(3);
  // Multi-TF panel
  const [coinSym, setCoinSym]     = React.useState("BTC");
  const [yahooSym, setYahooSym]   = React.useState("AAPL");
  // Backtest panel
  const [btStrategy, setBtStrategy] = React.useState("");
  const [btStrategy2, setBtStrategy2] = React.useState("");
  // News panel
  const [newsSym, setNewsSym]     = React.useState("");
  // EGX panel
  const [egxSym, setEgxSym]       = React.useState("");
  const [pivotHigh, setPivotHigh] = React.useState("");
  const [pivotLow, setPivotLow]   = React.useState("");

  React.useEffect(() => {
    let mounted = true;
    apiFetch("/api/tv/health").then(r=>r.ok?r.json():null).then(d=>{ if(mounted&&d) setTvStatus(d); }).catch(()=>{});
    const id = setInterval(() => {
      apiFetch("/api/tv/health").then(r=>r.ok?r.json():null).then(d=>{ if(mounted&&d) setTvStatus(d); }).catch(()=>{});
    }, 30000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  const callScreener = React.useCallback(async (toolName, args = {}) => {
    setToolLoading(true); setToolResult(null);
    try {
      const r = await apiFetch("/api/mcp/call", { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({ name: toolName, arguments: args }) });
      const d = await r.json();
      const text = d?.content?.[0]?.text || (typeof d === "string" ? d : JSON.stringify(d, null, 2));
      let parsed; try { parsed = JSON.parse(text); } catch { parsed = text; }
      setToolResult({ tool: toolName, data: parsed, ok: r.ok });
    } catch(e) { setToolResult({ tool: toolName, error: e.message }); }
    finally { setToolLoading(false); }
  }, []);

  const callTvApi = React.useCallback(async (endpoint) => {
    setToolLoading(true); setToolResult(null);
    try {
      const r = await apiFetch(endpoint);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setToolResult({ tool: endpoint.replace(/^\/api\/tv\//,"TV: "), data: d, ok: true });
    } catch(e) { setToolResult({ tool: endpoint, error: e.message }); }
    finally { setToolLoading(false); }
  }, []);

  const stab = (id) => ({
    padding:"6px 12px", borderRadius:4, fontSize:11, fontWeight:700, cursor:"pointer",
    fontFamily:"inherit", border:"none",
    background: panel===id ? "#38bdf8" : "#0a1628",
    color: panel===id ? "#000" : "#64748b",
  });
  const tsel = { padding:"5px 8px", borderRadius:4, background:"#070d1a", border:`1px solid ${C.border}`, color:C.text, fontSize:11, fontFamily:"inherit" };
  const tinp = { ...tsel };
  const tbtn = (bg="#38bdf8") => ({ padding:"7px 14px", borderRadius:4, fontSize:11, fontWeight:700, cursor:toolLoading?"not-allowed":"pointer", fontFamily:"inherit", border:"none", background:bg, color:"#000" });

  const renderResult = () => {
    if (toolLoading) return <div style={{marginTop:14,padding:16,borderRadius:8,background:C.panel,border:`1px solid ${C.border}`,textAlign:"center",color:C.muted,fontSize:12}}>⏳ Loading…</div>;
    if (!toolResult) return null;
    const { tool, data, error } = toolResult;
    const renderData = (d) => {
      if (typeof d === "string") return <pre style={{fontSize:10,color:C.text,whiteSpace:"pre-wrap",wordBreak:"break-word",margin:0}}>{d.slice(0,3000)}</pre>;
      if (Array.isArray(d)) return (
        <div style={{display:"flex",flexDirection:"column",gap:4}}>
          {d.slice(0,30).map((item,i)=>(
            <div key={i} style={{fontSize:11,background:C.bg,borderRadius:4,padding:"5px 8px",display:"flex",gap:8,flexWrap:"wrap"}}>
              {typeof item==="object"&&item!==null?Object.entries(item).slice(0,8).map(([k,v])=>(
                <span key={k}><span style={{color:C.muted,fontSize:9}}>{k}: </span><span style={{color:typeof v==="number"?(v>0?C.green:v<0?C.red:C.text):C.text,fontWeight:600}}>{String(v).slice(0,30)}</span></span>
              )):<span style={{color:C.text}}>{String(item).slice(0,120)}</span>}
            </div>
          ))}
          {d.length>30&&<div style={{fontSize:10,color:C.muted}}>+{d.length-30} more</div>}
        </div>
      );
      return (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:6}}>
          {Object.entries(d).slice(0,30).map(([k,v])=>(
            <div key={k} style={{background:C.bg,borderRadius:4,padding:"5px 8px"}}>
              <div style={{fontSize:9,color:C.muted}}>{k}</div>
              <div style={{fontSize:11,fontWeight:700,color:typeof v==="number"?(v>0?C.green:v<0?C.red:C.amber):C.text,wordBreak:"break-word"}}>{String(v).slice(0,60)}</div>
            </div>
          ))}
        </div>
      );
    };
    return (
      <div style={{marginTop:14,borderRadius:8,border:`1px solid ${error?C.red+"55":C.border}`,background:C.panel,overflow:"hidden"}}>
        <div style={{padding:"8px 14px",background:error?"#ef444411":C.dim,display:"flex",gap:10,alignItems:"center"}}>
          <span style={{fontSize:11,fontWeight:700,color:"#38bdf8",background:"#38bdf822",padding:"2px 8px",borderRadius:3}}>{tool}</span>
          {error?<span style={{fontSize:11,color:C.red,fontWeight:700}}>ERROR</span>:<span style={{fontSize:11,color:C.green}}>✓ OK</span>}
          <button onClick={()=>setToolResult(null)} style={{marginLeft:"auto",background:"none",border:"none",color:C.muted,cursor:"pointer",fontSize:13}}>✕</button>
        </div>
        <div style={{padding:"10px 14px",maxHeight:320,overflowY:"auto"}}>
          {error?<div style={{color:C.red,fontSize:12}}>{error}</div>:renderData(data)}
        </div>
      </div>
    );
  };

  return (
    <div style={{padding:20,color:C.text}}>
      {/* Header */}
      <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:12,flexWrap:"wrap"}}>
        <div>
          <div style={{fontSize:20,fontWeight:900,color:"#38bdf8",letterSpacing:1}}>📺 TRADINGVIEW</div>
          <div style={{fontSize:11,color:C.muted}}>Multi-market screener · Technical analysis · EGX</div>
        </div>
        <div style={{display:"flex",gap:6,alignItems:"center",marginLeft:"auto"}}>
          <div style={{display:"flex",alignItems:"center",gap:4,padding:"4px 10px",borderRadius:4,background:tvStatus.screener_ready?"#22c55e11":"#ef444411",border:`1px solid ${tvStatus.screener_ready?"#22c55e44":"#ef444444"}`}}>
            <span style={{fontSize:8,color:tvStatus.screener_ready?C.green:C.red}}>●</span>
            <span style={{fontSize:10,color:tvStatus.screener_ready?C.green:C.red}}>TV Screener {tvStatus.screener_ready?"Online":"Offline"}</span>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:4,padding:"4px 10px",borderRadius:4,background:tvStatus.ok?"#22c55e11":"#f59e0b11",border:`1px solid ${tvStatus.ok?"#22c55e44":"#f59e0b44"}`}}>
            <span style={{fontSize:8,color:tvStatus.ok?C.green:C.amber}}>●</span>
            <span style={{fontSize:10,color:tvStatus.ok?C.green:C.amber}}>TV Official {tvStatus.ok?"Online":"Offline"}</span>
          </div>
        </div>
      </div>

      {/* Symbol + Exchange pickers */}
      <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap",alignItems:"center"}}>
        <input value={symbol} onChange={e=>setSymbol(e.target.value.toUpperCase())} placeholder="Symbol" style={{...tinp,width:100}} />
        <select value={exchange} onChange={e=>setExchange(e.target.value)} style={tsel}>
          {EXCHANGES.map(ex=><option key={ex} value={ex}>{ex}</option>)}
        </select>
        <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
          {CHIPS.map(s=>(
            <button key={s} onClick={()=>setSymbol(s)} style={{padding:"3px 8px",borderRadius:3,fontSize:10,cursor:"pointer",fontFamily:"inherit",
              border:`1px solid ${symbol===s?"#38bdf8":C.border}`,background:symbol===s?"#38bdf822":C.bg,color:symbol===s?"#38bdf8":C.muted}}>
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Sub-tabs */}
      <div style={{display:"flex",gap:6,marginBottom:14,flexWrap:"wrap"}}>
        {[["analysis","📊 Analysis"],["screener","🔎 Screener"],["patterns","🕯 Patterns"],["multitf","📐 Multi-TF"],["backtest","⚙️ Backtest"],["news","📰 News"],["egx","🏛 EGX"]].map(([id,label])=>(
          <button key={id} onClick={()=>setPanel(id)} style={stab(id)}>{label}</button>
        ))}
      </div>

      {/* ── ANALYSIS ── */}
      {panel==="analysis"&&(
        <div>
          <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap"}}>
            <button onClick={()=>callTvApi(`/api/tv/ta/${symbol}`)} disabled={toolLoading} style={tbtn()}>📊 Technical Analysis ({symbol})</button>
            <button onClick={()=>callTvApi(`/api/tv/quote/${symbol}`)} disabled={toolLoading} style={{...tbtn(C.dim),color:C.text,border:`1px solid ${C.border}`}}>💲 Quote</button>
            <button onClick={()=>callScreener("multi_timeframe_analysis",{symbol,exchange})} disabled={toolLoading} style={{...tbtn(C.dim),color:C.text,border:`1px solid ${C.border}`}}>🕐 Multi-TF TA</button>
            <button onClick={()=>callScreener("combined_analysis",{symbol,exchange})} disabled={toolLoading} style={{...tbtn(C.dim),color:C.text,border:`1px solid ${C.border}`}}>🎯 Combined Analysis</button>
            <button onClick={()=>callScreener("market_sentiment",{exchange:scrExchange})} disabled={toolLoading} style={{...tbtn(C.dim),color:C.text,border:`1px solid ${C.border}`}}>🌡 Market Sentiment</button>
          </div>
          {renderResult()}
        </div>
      )}

      {/* ── SCREENER ── */}
      {panel==="screener"&&(
        <div>
          <div style={{display:"flex",gap:8,marginBottom:10,flexWrap:"wrap",alignItems:"center"}}>
            <select value={scrExchange} onChange={e=>setScrExchange(e.target.value)} style={tsel}>
              {EXCHANGES.map(ex=><option key={ex} value={ex}>{ex}</option>)}
            </select>
            <span style={{fontSize:10,color:C.muted}}>Limit: <b style={{color:C.amber}}>{scrLimit}</b></span>
            <input type="range" min={5} max={100} step={5} value={scrLimit} onChange={e=>setScrLimit(+e.target.value)} style={{width:80}} />
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:8,marginBottom:12}}>
            {[
              {icon:"📈",label:"Top Gainers",tool:"top_gainers"},
              {icon:"📉",label:"Top Losers",tool:"top_losers"},
              {icon:"💥",label:"Bollinger Scan",tool:"bollinger_scan"},
              {icon:"🔊",label:"Volume Breakout",tool:"volume_breakout_scanner"},
              {icon:"🧠",label:"Smart Volume",tool:"smart_volume_scanner"},
              {icon:"📷",label:"Market Snapshot",tool:"market_snapshot"},
            ].map(({icon,label,tool})=>(
              <button key={tool} onClick={()=>callScreener(tool,{exchange:scrExchange,limit:scrLimit})} disabled={toolLoading}
                style={{padding:"12px 10px",borderRadius:8,fontSize:11,fontWeight:700,cursor:toolLoading?"not-allowed":"pointer",
                  fontFamily:"inherit",border:`1px solid ${C.border}`,background:C.panel,color:C.text,textAlign:"left",lineHeight:1.5}}>
                <div style={{fontSize:18,marginBottom:4}}>{icon}</div>
                <div>{label}</div>
              </button>
            ))}
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
            <span style={{fontSize:10,color:C.muted}}>Rating:</span>
            <select value={ratingFilter} onChange={e=>setRatingFilter(e.target.value)} style={tsel}>
              {["STRONG_BUY","BUY","NEUTRAL","SELL","STRONG_SELL"].map(r=><option key={r} value={r}>{r}</option>)}
            </select>
            <button onClick={()=>callScreener("rating_filter",{rating:ratingFilter,exchange:scrExchange,limit:scrLimit})} disabled={toolLoading} style={tbtn()}>Filter by Rating</button>
          </div>
          {renderResult()}
        </div>
      )}

      {/* ── PATTERNS ── */}
      {panel==="patterns"&&(
        <div>
          <div style={{display:"flex",gap:8,marginBottom:10,flexWrap:"wrap",alignItems:"center"}}>
            <select value={scrExchange} onChange={e=>setScrExchange(e.target.value)} style={tsel}>
              {EXCHANGES.map(ex=><option key={ex} value={ex}>{ex}</option>)}
            </select>
            <span style={{fontSize:10,color:C.muted}}>Consecutive candles: <b style={{color:C.amber}}>{consecCount}</b></span>
            <input type="range" min={2} max={10} value={consecCount} onChange={e=>setConsecCount(+e.target.value)} style={{width:80}} />
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <button onClick={()=>callScreener("advanced_candle_pattern",{exchange:scrExchange,limit:scrLimit})} disabled={toolLoading} style={tbtn()}>🕯 Candle Patterns</button>
            <button onClick={()=>callScreener("consecutive_candles_scan",{exchange:scrExchange,consecutive_count:consecCount})} disabled={toolLoading} style={tbtn()}>🔁 Consecutive Candles</button>
            <button onClick={()=>callScreener("volume_confirmation_analysis",{exchange:scrExchange,limit:scrLimit})} disabled={toolLoading} style={tbtn()}>📊 Volume Confirmation</button>
          </div>
          {renderResult()}
        </div>
      )}

      {/* ── MULTI-TF ── */}
      {panel==="multitf"&&(
        <div>
          <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap",alignItems:"center"}}>
            <select value={scrExchange} onChange={e=>setScrExchange(e.target.value)} style={tsel}>
              {EXCHANGES.map(ex=><option key={ex} value={ex}>{ex}</option>)}
            </select>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:8,marginBottom:12}}>
            {[
              {icon:"📐",label:"Multi-TF Analysis",action:()=>callScreener("multi_timeframe_analysis",{symbol,exchange})},
              {icon:"🎯",label:"Combined Analysis",action:()=>callScreener("combined_analysis",{symbol,exchange})},
              {icon:"🌡",label:"Market Sentiment",action:()=>callScreener("market_sentiment",{exchange:scrExchange})},
              {icon:"🤖",label:"Multi-Agent",action:()=>callScreener("multi_agent_analysis",{symbol,exchange})},
            ].map(({icon,label,action})=>(
              <button key={label} onClick={action} disabled={toolLoading}
                style={{padding:"12px 10px",borderRadius:8,fontSize:11,fontWeight:700,cursor:toolLoading?"not-allowed":"pointer",
                  fontFamily:"inherit",border:`1px solid ${C.border}`,background:C.panel,color:C.text,textAlign:"left",lineHeight:1.5}}>
                <div style={{fontSize:18,marginBottom:4}}>{icon}</div>
                <div>{label}</div>
              </button>
            ))}
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
            <input value={coinSym} onChange={e=>setCoinSym(e.target.value.toUpperCase())} placeholder="Coin" style={{...tinp,width:80}} />
            <button onClick={()=>callScreener("coin_analysis",{symbol:coinSym,exchange:scrExchange})} disabled={toolLoading} style={tbtn()}>🪙 Coin Analysis</button>
            <input value={yahooSym} onChange={e=>setYahooSym(e.target.value.toUpperCase())} placeholder="Yahoo sym" style={{...tinp,width:90}} />
            <button onClick={()=>callScreener("yahoo_price",{symbol:yahooSym})} disabled={toolLoading} style={tbtn()}>💰 Yahoo Price</button>
          </div>
          {renderResult()}
        </div>
      )}

      {/* ── BACKTEST ── */}
      {panel==="backtest"&&(
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <div style={{padding:"12px 14px",borderRadius:8,background:C.panel,border:`1px solid ${C.border}`}}>
            <div style={{fontSize:12,fontWeight:700,color:C.amber,marginBottom:8}}>⚙️ Backtest Strategy</div>
            <div style={{display:"flex",gap:8,marginBottom:8,flexWrap:"wrap",alignItems:"center"}}>
              <input value={symbol} onChange={e=>setSymbol(e.target.value.toUpperCase())} placeholder="Symbol" style={{...tinp,width:90}} />
              <select value={exchange} onChange={e=>setExchange(e.target.value)} style={tsel}>
                {EXCHANGES.map(ex=><option key={ex} value={ex}>{ex}</option>)}
              </select>
            </div>
            <textarea value={btStrategy} onChange={e=>setBtStrategy(e.target.value)} placeholder="Strategy description (e.g. 'EMA crossover: buy when 20EMA crosses above 50EMA…')"
              style={{...tinp,width:"100%",height:70,boxSizing:"border-box",resize:"vertical",display:"block"}} />
            <div style={{display:"flex",gap:8,marginTop:8}}>
              <button onClick={()=>callScreener("backtest_strategy",{symbol,exchange,strategy:btStrategy})} disabled={toolLoading||!btStrategy} style={tbtn()}>▶ Run Backtest</button>
              <button onClick={()=>callScreener("walk_forward_backtest_strategy",{symbol,exchange,strategy:btStrategy})} disabled={toolLoading||!btStrategy} style={{...tbtn(C.dim),color:C.text,border:`1px solid ${C.border}`}}>Walk-Forward</button>
            </div>
          </div>
          <div style={{padding:"12px 14px",borderRadius:8,background:C.panel,border:`1px solid ${C.border}`}}>
            <div style={{fontSize:12,fontWeight:700,color:C.amber,marginBottom:8}}>⚖️ Compare Strategies</div>
            <textarea value={btStrategy} onChange={e=>setBtStrategy(e.target.value)} placeholder="Strategy A" style={{...tinp,width:"100%",height:50,boxSizing:"border-box",resize:"vertical",display:"block",marginBottom:6}} />
            <textarea value={btStrategy2} onChange={e=>setBtStrategy2(e.target.value)} placeholder="Strategy B" style={{...tinp,width:"100%",height:50,boxSizing:"border-box",resize:"vertical",display:"block"}} />
            <button onClick={()=>callScreener("compare_strategies",{symbol,exchange,strategy1:btStrategy,strategy2:btStrategy2})} disabled={toolLoading||!btStrategy||!btStrategy2} style={{...tbtn(),marginTop:8}}>Compare</button>
          </div>
          {renderResult()}
        </div>
      )}

      {/* ── NEWS ── */}
      {panel==="news"&&(
        <div>
          <div style={{display:"flex",gap:8,marginBottom:10,alignItems:"center",flexWrap:"wrap"}}>
            <input value={newsSym} onChange={e=>setNewsSym(e.target.value)} placeholder="Symbol (optional)" style={{...tinp,width:130}} />
            <button onClick={()=>callScreener("financial_news",{...(newsSym?{symbol:newsSym}:{})})} disabled={toolLoading} style={tbtn()}>📰 Get Financial News</button>
          </div>
          {renderResult()}
        </div>
      )}

      {/* ── EGX ── */}
      {panel==="egx"&&(
        <div>
          <div style={{display:"flex",gap:8,marginBottom:10,flexWrap:"wrap"}}>
            <button onClick={()=>callScreener("egx_market_overview",{})} disabled={toolLoading} style={tbtn()}>📊 EGX Overview</button>
            <button onClick={()=>callScreener("egx_sector_scan",{})} disabled={toolLoading} style={tbtn()}>🏭 Sector Scan</button>
            <button onClick={()=>callScreener("egx_sector_scanner",{})} disabled={toolLoading} style={tbtn()}>🔬 Sector Scanner</button>
            <button onClick={()=>callScreener("egx_index_analysis",{})} disabled={toolLoading} style={tbtn()}>📈 Index Analysis</button>
            <button onClick={()=>callScreener("egx_stock_screener",{})} disabled={toolLoading} style={tbtn()}>🔎 Stock Screener</button>
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",marginBottom:8}}>
            <input value={egxSym} onChange={e=>setEgxSym(e.target.value.toUpperCase())} placeholder="EGX symbol (e.g. COMI)" style={{...tinp,width:140}} />
            <button onClick={()=>callScreener("egx_trade_plan",{symbol:egxSym})} disabled={toolLoading||!egxSym} style={tbtn()}>📋 Trade Plan</button>
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
            <input value={pivotHigh} onChange={e=>setPivotHigh(e.target.value)} placeholder="Pivot High" style={{...tinp,width:100}} />
            <input value={pivotLow} onChange={e=>setPivotLow(e.target.value)} placeholder="Pivot Low" style={{...tinp,width:100}} />
            <button onClick={()=>callScreener("egx_fibonacci_retracement",{symbol:egxSym,pivot_high:+pivotHigh,pivot_low:+pivotLow})} disabled={toolLoading||!egxSym||!pivotHigh||!pivotLow} style={tbtn()}>📐 Fibonacci</button>
          </div>
          {renderResult()}
        </div>
      )}
    </div>
  );
}

// 2026-09-06 — fetch the real Deriv app_id from the server so browser WS doesn't 401
fetch("/api/config").then(r => r.ok ? r.json() : null).then(c => {
  if (c?.derivWsUrl) {
    DERIV_WS = c.derivWsUrl;
    console.log("[AmberScanner] using DERIV_WS app_id:", c.derivAppId);
  }
}).catch(() => {});

export default function KaraScanner() {
  return <KaraAuthGate />;
}
